


import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  deleteUser,
} from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  getDocs,
  deleteDoc,
  writeBatch,
  arrayUnion,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript,
} from "@react-google-maps/api";
import { auth, db, storage } from "./firebase";
import * as aiTrust from "./aiTrust";

import {
  DEFAULT_MAP_CENTER,
  PROPERTY_CACHE_KEY,
  SEEDED_PROPERTY_TARGET,
  buildSeededProperty,
  normalizePropertyAddress,
  isBlockedPropertyAddress,
  isFiniteCoordinate,
  sanitizePropertyForMap,
  dedupePropertiesByAddress,
  loadCachedProperties,
  saveCachedProperties,
  parseAddressComponents,
  formatCityState,
  formatFullAddress,
} from "./utils/propertyUtils";

import {
  canReviewTargetRole,
  canRoleClaimProperty,
  getDefaultProfileStatus,
  getProfileStatusOptions,
  getProfileStatusMeta,
  normalizeAreaTags,
  getPublicAreaSummary,
  matchesAreaFilter,
} from "./utils/profileUtils";

import { getManagerTenantScorecard } from "./utils/managerUtils";
import { getFeedTimestampMs, formatFeedTimeAgo } from "./utils/feedUtils";
import useProfileSync from "./hooks/useProfileSync";
import usePropertyData from "./hooks/usePropertyData";
import useReviewData from "./hooks/useReviewData";
import useNotifications from "./hooks/useNotifications";
import useClaims from "./hooks/useClaims";
import NotificationDropdown from "./components/Notifications/NotificationDropdown";
import ReviewDetailModal from "./components/Modals/ReviewDetailModal";
import RentDuoAIAssistant from "./components/AI/RentDuoAIAssistant";
import AICenter from "./components/AI/AICenter";
import MapTypeControls from "./components/Map/MapTypeControls";
import ClaimPropertyButton from "./components/Properties/ClaimPropertyButton";
import TenantClaimsPanel from "./components/Properties/TenantClaimsPanel";
import RightsResources from "./components/Shared/RightsResources";
import LanguageToggle from "./components/Shared/LanguageToggle";
import TranslatableText from "./components/Shared/TranslatableText";
import PreviewMode from "./components/Preview/PreviewMode";
import { LANGUAGE_STORAGE_KEY, normalizeLanguage, translate } from "./utils/i18n";
import {
  BLOCKED_REVIEW_PATTERNS,
  LIVING_EXPERIENCE_FIELDS,
  RENTAL_FEEDBACK_DEFAULT,
  RENTAL_FEEDBACK_OPTIONS,
  RENTDUO_AI_FEED_CARDS,
  REVIEW_COOLDOWN_MS,
  REVIEW_COOLDOWN_PERSON_MESSAGE,
  REVIEW_COOLDOWN_PROPERTY_MESSAGE,
  REVIEW_SAFETY_WARNING,
} from "./config/constants";
import {
  REVIEW_CATEGORY_CONFIG,
  getReviewCategoriesForRole,
  buildInitialCategoryRatings,
  calculateCategoryOverall,
  getCategoryLabel,
  formatTimeAgo,
  formatReviewDateTime,
  getTextSimilarityScore,
  getSimilarReviewMatches,
  getCrossAccountScamSignals,
  buildProfessionalReplyText,
  calculateRentScore,
} from "./utils/reviewUtils";

const GOOGLE_MAPS_LIBRARIES = ["places"];

const REFERRAL_STORAGE_KEY = "rentduo_referral_code_v1";
const DISCLAIMER_ACCEPTED_STORAGE_KEY = "rentduo_disclaimer_accepted_v1";
const ACTIVE_TAB_STORAGE_KEY = "rentduo_active_tab_v1";
const FAVORITE_PROPERTIES_STORAGE_KEY = "rentduo_favorite_properties_v1";
const LEGAL_GUIDANCE_DISCLAIMER =
  "RentDuo provides general rental information and AI-assisted guidance. RentDuo is not a law firm and does not provide legal advice. Laws vary by location. Contact a licensed attorney or official housing agency for advice about your specific situation.";
const REVIEW_LEGAL_AWARENESS_DISCLAIMER =
  "RentDuo provides general rental information and review-safety guidance only. RentDuo is not a law firm and does not provide legal advice. Laws vary by location. Contact a licensed attorney or official housing agency for advice about your specific situation.";
const VERIFICATION_UPLOAD_FIELDS = {
  tenant_verification: [
    "Lease Agreement",
    "Proof of Residence",
    "Utility Bill",
    "Optional ID / Selfie",
  ],
  landlord_verification: [
    "Property Ownership Proof",
    "Deed / Tax Record / Closing Document",
    "Optional ID / Selfie",
  ],
  property_manager_verification: [
    "Management Authorization Proof",
    "Company/Owner Authorization Letter",
    "Optional ID / Selfie",
  ],
  property_ownership_verification: [
    "Property Ownership Proof",
    "Deed / Tax Record / Closing Document",
    "Optional ID / Selfie",
  ],
};
const FOUNDER_EMAILS = (process.env.REACT_APP_FOUNDER_EMAILS || "")
  .split(",")
  .map((item) => item.trim().toLowerCase())
  .filter(Boolean);

const buildPublicProfilePayload = ({
  currentUser,
  source = {},
  trustedSource = source,
  role = "",
  displayName = "",
  city = "",
  state = "",
  profileStatus = "",
  statusVisibility = true,
  preferredAreasText = "",
  serviceAreasText = "",
  bio = "",
  photoURL = "",
} = {}) => ({
  uid: currentUser?.uid || source?.uid || "",
  role: source?.role || role || "",
  displayName: displayName || source?.displayName || "",
  city: city || source?.city || "",
  state: state || source?.state || "",
  profileStatus: profileStatus || source?.profileStatus || (source?.role || role ? getDefaultProfileStatus(source?.role || role) : ""),
  statusVisibility: statusVisibility !== false,
  preferredAreasText: (source?.role || role) === "tenant" ? preferredAreasText || source?.preferredAreasText || "" : "",
  serviceAreasText: (source?.role || role) === "tenant" ? "" : serviceAreasText || source?.serviceAreasText || "",
  bio: bio || source?.bio || "",
  photoURL: photoURL || source?.photoURL || "",
  verified: Boolean(trustedSource?.verified),
  verificationStatus: trustedSource?.verificationStatus || "unverified",
  rentScore: Number(trustedSource?.rentScore || 0),
  trustScore: Number(trustedSource?.trustScore || trustedSource?.rentScore || 0),
  trustBadge: trustedSource?.trustBadge || "",
  referredBy: source?.referredBy || "",
  updatedAt: serverTimestamp(),
});

const getPublicDisplayName = (item = {}) =>
  item?.displayName ||
  item?.reviewerDisplayName ||
  item?.targetDisplayName ||
  item?.actorDisplayName ||
  item?.name ||
  "RentDuo member";

const buildSafePublicProfile = (source = {}, uidValue = "") => ({
  uid: uidValue || source?.uid || source?.id || "",
  id: uidValue || source?.uid || source?.id || "",
  displayName: getPublicDisplayName(source),
  role: source?.role || "",
  city: source?.city || "",
  state: source?.state || "",
  profileStatus: source?.profileStatus || "",
  statusVisibility: source?.statusVisibility !== false,
  bio: source?.bio || "",
  photoURL: source?.photoURL || source?.reviewerPhotoURL || source?.actorPhotoURL || "",
  verified: Boolean(source?.verified || source?.reviewerVerified || source?.targetVerified),
  verificationStatus:
    source?.verificationStatus ||
    source?.targetVerificationStatus ||
    (source?.verified || source?.reviewerVerified || source?.targetVerified ? "verified" : "unverified"),
  rentScore: Number(source?.rentScore || 0),
  trustScore: Number(source?.trustScore || source?.rentScore || 0),
  trustBadge: source?.trustBadge || "",
  preferredAreasText: source?.preferredAreasText || "",
  serviceAreasText: source?.serviceAreasText || "",
});

const buildProfileLocationPayload = ({
  location = {},
  fallbackCity = "",
  fallbackState = "",
} = {}) => {
  const state = String(location?.state || fallbackState || "").trim().toUpperCase();
  const city = String(location?.city || fallbackCity || "").trim();
  return {
    state,
    county: String(location?.county || "").trim(),
    city,
    town: String(location?.town || "").trim(),
    zip: String(location?.zip || "").trim(),
    formattedAddress: String(location?.formattedAddress || [city, state].filter(Boolean).join(", ")).trim(),
    placeId: String(location?.placeId || "").trim(),
  };
};

const buildSafePublicReview = (item = {}) => ({
  id: item?.id || "",
  rating: Number(item?.rating || 0),
  text: item?.text || "",
  status: item?.status || "",
  createdAt: item?.createdAt || null,
  updatedAt: item?.updatedAt || null,
  reviewerUserId: item?.reviewerUserId || "",
  targetUserId: item?.targetUserId || "",
  reviewerDisplayName: item?.reviewerDisplayName || "Anonymous",
  targetDisplayName: item?.targetDisplayName || "RentDuo member",
  reviewerPhotoURL: item?.reviewerPhotoURL || "",
  reviewerVerified: Boolean(item?.reviewerVerified),
  targetVerified: Boolean(item?.targetVerified),
  propertyId: item?.propertyId || "",
  propertyTitle: item?.propertyTitle || "",
  isUpdatedReview: Boolean(item?.isUpdatedReview),
  affectsScore: item?.affectsScore !== false,
  disputeStatus: item?.disputeStatus || "",
});

function validateSafeReviewText(text = "") {
  const value = String(text || "").trim();

  if (!value) return { ok: true, message: "" };

  if (value.length > 1200) {
    return { ok: false, message: "Review notes must be 1,200 characters or less." };
  }

  const matched = BLOCKED_REVIEW_PATTERNS.find((pattern) => pattern.test(value));
  if (matched) {
    return {
      ok: false,
      message:
        "This review contains wording that may create legal or safety risk. Please rewrite it using neutral, factual language.",
    };
  }

  return { ok: true, message: "" };
}

function makeReviewTextSafer(text = "") {
  return String(text || "")
    .replace(/\bnightmare\b/gi, "difficult experience")
    .replace(/\btrashed\b/gi, "left significant condition concerns")
    .replace(/\bliar\b/gi, "disputed information")
    .replace(/\bscammer\b/gi, "disputed conduct")
    .replace(/\bcrazy\b/gi, "difficult")
    .replace(/\bpsycho\b/gi, "difficult")
    .trim();
}

function getReviewTimestampMs(review = {}) {
  const value = review?.createdAt || review?.updatedAt;
  if (!value) return 0;
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function getReviewCooldownState(priorReviews = []) {
  const activeReviews = priorReviews
    .filter((item) => item?.status !== "deleted")
    .sort((a, b) => getReviewTimestampMs(b) - getReviewTimestampMs(a));
  const latestReview = activeReviews[0] || null;
  const latestReviewMs = getReviewTimestampMs(latestReview);
  const withinCooldown =
    latestReview && latestReviewMs > 0 && Date.now() - latestReviewMs < REVIEW_COOLDOWN_MS;
  const highestVersion = activeReviews.reduce(
    (max, item) => Math.max(max, Number(item?.reviewVersion || 1)),
    0
  );

  return {
    activeReviews,
    latestReview,
    withinCooldown,
    reviewVersion: Math.max(1, highestVersion + 1),
    previousReviewId: latestReview?.id || "",
    isUpdatedReview: activeReviews.length > 0,
  };
}

function buildRentalFeedbackSummary(feedback = {}) {
  return Object.entries(feedback)
    .filter(([, value]) => String(value || "").trim())
    .map(([key, value]) => ({ key, value }))
    .reduce((acc, item) => ({ ...acc, [item.key]: item.value }), {});
}

function buildReviewSafetyMetadata({ accepted = false, verified = false, structured = true, locationContext = {} } = {}) {
  return {
    type: "user_submitted_rental_history_feedback",
    userSubmittedOpinion: true,
    structuredReview: Boolean(structured),
    userCertifiedExperience: Boolean(accepted),
    includesRightToRespond: true,
    legalDisclaimerAccepted: Boolean(accepted),
    verifiedReviewWeighting: Boolean(verified),
    disputeEligible: true,
    evidenceMayBeRequested: true,
    safetyWarningVersion: "2026-04-26",
    locationContext,
  };
}

function isReviewScoreEligible(item = {}) {
  return (
    item?.status === "approved" &&
    item?.affectsScore !== false &&
    item?.disputeStatus !== "open" &&
    item?.status !== "disputed"
  );
}

function getReviewPublicDisclaimer(item = {}) {
  if (item?.disputeStatus === "open" || item?.status === "disputed") {
    return "This review is currently disputed and does not affect the score while under review.";
  }
  return "User-submitted rental-history feedback. RentDuo does not independently verify every statement. Both parties may respond or dispute.";
}


const safeGetReviewRisk = (payload) => {
  if (typeof aiTrust?.getReviewRiskScore === "function") {
    return aiTrust.getReviewRiskScore(payload);
  }
  return { score: 0, label: "low", reasons: [] };
};

const safeGetUserTrustScore = (payload) => {
  if (typeof aiTrust?.getUserTrustScore === "function") {
    return aiTrust.getUserTrustScore(payload);
  }
  return 50;
};

const safeGetTrustBadge = (score) => {
  if (typeof aiTrust?.getTrustBadge === "function") {
    return aiTrust.getTrustBadge(score);
  }
  if (score >= 85) return "Elite";
  if (score >= 70) return "Trusted";
  if (score >= 50) return "Solid";
  if (score >= 30) return "Watch";
  return "High Risk";
};

const isVerifiedProfileStatus = (statusValue = "") =>
  ["verified", "tenant_verified", "landlord_verified", "property_manager_verified"].includes(
    String(statusValue || "")
  );

const createFeedEvent = async (payload = {}) => {
  try {
    await addDoc(collection(db, "feed"), {
      ...payload,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Feed event failed:", error);
  }
};

const scrollAppToTop = (behavior = "smooth") => {
  if (typeof window === "undefined") return;

  window.requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0, behavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document.getElementById("root")?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
  });
};

class RentDuoErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("RentDuo render error:", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            background: "#f8fbff",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 520,
              borderRadius: 24,
              padding: 24,
              background: "#fff",
              border: "1px solid #dbeafe",
              boxShadow: "0 24px 60px rgba(15,23,42,0.12)",
              color: "#0f172a",
            }}
          >
            <h1 style={{ margin: "0 0 8px", fontSize: 24 }}>RentDuo hit a display issue</h1>
            <p style={{ margin: 0, color: "#475569", lineHeight: 1.5 }}>
              Refresh the page to continue. The app caught the error so one bad property cannot blank the entire screen.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function RentDuoApp() {
  const {
    user,
    profile,
    role,
    displayName,
    city,
    profileLocation,
    profileStatus,
    statusVisibility,
    preferredAreasText,
    serviceAreasText,
    privateAddress,
    bio,
    photoURL,
    verificationStatus,
    verificationNote,
    verificationDocs,
    profileHydrated,
    loading,
    setProfile,
    setRole,
    setDisplayName,
    setCity,
    setProfileLocation,
    setProfileStatus,
    setStatusVisibility,
    setPreferredAreasText,
    setServiceAreasText,
    setPrivateAddress,
    setBio,
    setPhotoURL,
    setVerificationStatus,
    setVerificationNote,
    setVerificationDocs,
  } = useProfileSync();

  const [authMode, setAuthMode] = useState("login");
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [activeLoginAiPrompt, setActiveLoginAiPrompt] = useState("");
  const [language, setLanguage] = useState(() =>
    normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY))
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showAuthDisclaimer, setShowAuthDisclaimer] = useState(false);
  const [pendingAuthAction, setPendingAuthAction] = useState(null);
  const [disclaimerHasScrolledToEnd, setDisclaimerHasScrolledToEnd] = useState(false);
  const disclaimerScrollRef = useRef(null);

  const [isAdmin, setIsAdmin] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState("");
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);

  const [idFile, setIdFile] = useState(null);
  const [deedFile, setDeedFile] = useState(null);
  const [leaseFile, setLeaseFile] = useState(null);
  const [residenceProofFile, setResidenceProofFile] = useState(null);
  const [utilityBillFile, setUtilityBillFile] = useState(null);
  const [ownershipRecordFile, setOwnershipRecordFile] = useState(null);
  const [managementAuthFile, setManagementAuthFile] = useState(null);
  const [authorizationLetterFile, setAuthorizationLetterFile] = useState(null);
  const [verificationType, setVerificationType] = useState("tenant_verification");
  const [isSubmittingVerification, setIsSubmittingVerification] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchArea, setSearchArea] = useState("");
  const [searchRoleFilter, setSearchRoleFilter] = useState("all");
  const [searchStatusFilter, setSearchStatusFilter] = useState("all");
  const [foundUser, setFoundUser] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);
  const [publicProfileModal, setPublicProfileModal] = useState(null);
  const [publicProfileTab, setPublicProfileTab] = useState("about");
  const publicProfileModalRef = useRef(null);

  const [ratingValue, setRatingValue] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewCategoryRatings, setReviewCategoryRatings] = useState(buildInitialCategoryRatings("tenant"));
  const [rentalFeedback, setRentalFeedback] = useState(RENTAL_FEEDBACK_DEFAULT);
  const [reviewSafetyAccepted, setReviewSafetyAccepted] = useState(false);
  const [lastReviewRiskWarningText, setLastReviewRiskWarningText] = useState("");
  const [reviewReplyDrafts, setReviewReplyDrafts] = useState({});
  const [submittedReviewTargetIds, setSubmittedReviewTargetIds] = useState({});
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewReplySubmittingId, setReviewReplySubmittingId] = useState("");
  const [reviewReplyImprovingId, setReviewReplyImprovingId] = useState("");
  const [activeReplyReview, setActiveReplyReview] = useState(null);
  const [activeReviewDetail, setActiveReviewDetail] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showVerifyPrompt, setShowVerifyPrompt] = useState(false);
  const [reviewAnalysis, setReviewAnalysis] = useState(null);
  const [improvingReview, setImprovingReview] = useState(false);
  const [aiReviewDraft, setAiReviewDraft] = useState("");
  const [aiOriginalReviewText, setAiOriginalReviewText] = useState("");
  const [aiTone, setAiTone] = useState("professional");
  const [referralCount, setReferralCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [appResumeTick, setAppResumeTick] = useState(0);
  const [allReviewsCount, setAllReviewsCount] = useState(0);
  const [allMessagesCount, setAllMessagesCount] = useState(0);
  const [allPropertyReviewsCount, setAllPropertyReviewsCount] = useState(0);
  const [partnerClickCount, setPartnerClickCount] = useState(0);
  const [growthBaseline, setGrowthBaseline] = useState(null);
  const [showGrowthBaselineModal, setShowGrowthBaselineModal] = useState(false);
  const [showRawGrowthTotals, setShowRawGrowthTotals] = useState(false);
  const [isResettingGrowthBaseline, setIsResettingGrowthBaseline] = useState(false);
  const [liveFeedFilter, setLiveFeedFilter] = useState("all");
  const [publicFeedProfiles, setPublicFeedProfiles] = useState([]);
  const [publicFeedEvents, setPublicFeedEvents] = useState([]);
  const [feedLoadingSources, setFeedLoadingSources] = useState({
    profiles: true,
    reviews: true,
    propertyReviews: true,
    feed: true,
  });
  const [feedError, setFeedError] = useState("");

  const [messageText, setMessageText] = useState("");
  const messageInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pendingMessagesRef = useRef([]);
  const authFormRef = useRef(null);
  const [messageFolderTab, setMessageFolderTab] = useState("inbox");
  const EMPTY_ARRAY = [];
  const [messages, setMessages] = useState([]);
  const [inboxThreads, setInboxThreads] = useState([]);
  const [outboxThreads, setOutboxThreads] = useState([]);

  const [propertyTitle, setPropertyTitle] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [propertyAddressMeta, setPropertyAddressMeta] = useState({
    streetLine: "",
    city: "",
    state: "",
    zip: "",
  });
  const [propertyDescription, setPropertyDescription] = useState("");
  const [propertyImageUrl, setPropertyImageUrl] = useState("");
  const [propertyImageFile, setPropertyImageFile] = useState(null);
  const [propertyImagePreviewUrl, setPropertyImagePreviewUrl] = useState("");
  const [isUploadingPropertyImage, setIsUploadingPropertyImage] = useState(false);
  const [propertyIsForRent, setPropertyIsForRent] = useState(false);
  const [propertyMonthlyRent, setPropertyMonthlyRent] = useState("");
  const [propertyAvailableDate, setPropertyAvailableDate] = useState("");
  const [propertyBedrooms, setPropertyBedrooms] = useState("");
  const [propertyBathrooms, setPropertyBathrooms] = useState("");
  const [propertyLeaseType, setPropertyLeaseType] = useState("");
  const [rentalEditDraft, setRentalEditDraft] = useState({
    isForRent: false,
    monthlyRent: "",
    availableDate: "",
    bedrooms: "",
    bathrooms: "",
    leaseType: "",
  });
  const [isSavingRentalListing, setIsSavingRentalListing] = useState(false);
  const [isSavingResidencyClaim, setIsSavingResidencyClaim] = useState(false);
  const profileLocationInputRef = useRef(null);
  const privateAddressInputRef = useRef(null);
  const propertyAddressInputRef = useRef(null);
  const propertyCreateCardRef = useRef(null);
  const propertyMapInstanceRef = useRef(null);
  const propertyProfileModalRef = useRef(null);
  const propertyProfileTopRef = useRef(null);
  const reviewAreaInputRef = useRef(null);
  const reviewUserSearchInputRef = useRef(null);
  const homeSearchInputRef = useRef(null);


  const [propertyRatingValue, setPropertyRatingValue] = useState(0);
  const [propertyReviewText, setPropertyReviewText] = useState("");
  const [livingExperience, setLivingExperience] = useState({
    noiseLevel: 0,
    neighborBehavior: 0,
    safety: 0,
    buildingCulture: 0,
    parking: 0,
    sharedSpaces: 0,
    notes: "",
  });


  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [allVerifications, setAllVerifications] = useState([]);
  const [myVerificationSubmissions, setMyVerificationSubmissions] = useState([]);
  const [pendingVerificationSubmissions, setPendingVerificationSubmissions] = useState([]);
  const [adminNoteDrafts, setAdminNoteDrafts] = useState({});

  const [reportedUsers, setReportedUsers] = useState([]);
  const [selectedManagerTenant, setSelectedManagerTenant] = useState(null);
  const [managerFlagType, setManagerFlagType] = useState("late_payment");
  const [managerFlagNote, setManagerFlagNote] = useState("");
  const [issues, setIssues] = useState([]);
  const [newIssueType, setNewIssueType] = useState("maintenance");
  const [newIssueText, setNewIssueText] = useState("");
  const [issueResponseDrafts, setIssueResponseDrafts] = useState({});
  const [propertyRiskLoadingId, setPropertyRiskLoadingId] = useState("");
  const [propertyRiskError, setPropertyRiskError] = useState("");
  const [maintenanceAnalysisLoadingId, setMaintenanceAnalysisLoadingId] = useState("");
  const [propertyReviewImproving, setPropertyReviewImproving] = useState(false);
  const [propertyScamLoadingId, setPropertyScamLoadingId] = useState("");
  const [propertyScamError, setPropertyScamError] = useState("");
  const [leaseText, setLeaseText] = useState("");
  const [leaseExplanation, setLeaseExplanation] = useState(null);
  const [leaseExplaining, setLeaseExplaining] = useState(false);
  const [leaseError, setLeaseError] = useState("");
  const [propertyReputationLoadingId, setPropertyReputationLoadingId] = useState("");
  const [profileReputationLoadingId, setProfileReputationLoadingId] = useState("");
  const [profileReputationInsights, setProfileReputationInsights] = useState({});
  const [messageScamAnalysis, setMessageScamAnalysis] = useState(null);
  const [messageScamLoading, setMessageScamLoading] = useState(false);
  const [messageScamError, setMessageScamError] = useState("");
  const [neighborhoodLoadingId, setNeighborhoodLoadingId] = useState("");
  const [neighborhoodError, setNeighborhoodError] = useState("");
  const [marketRentLoadingId, setMarketRentLoadingId] = useState("");
  const [marketRentError, setMarketRentError] = useState("");
  const [moveChecklistMode, setMoveChecklistMode] = useState("move_in");
  const [moveChecklist, setMoveChecklist] = useState(null);
  const [moveChecklistLoading, setMoveChecklistLoading] = useState(false);
  const [moveChecklistError, setMoveChecklistError] = useState("");
  const [smartMatchPreferences, setSmartMatchPreferences] = useState({
    budget: "",
    preferredCity: "",
    petsRequired: false,
    quietPreferred: false,
    riskTolerance: "moderate",
  });
  const [smartMatches, setSmartMatches] = useState([]);
  const [smartMatchLoading, setSmartMatchLoading] = useState(false);
  const [smartMatchError, setSmartMatchError] = useState("");
  const [smartMatchDisclaimer, setSmartMatchDisclaimer] = useState("");

  const [statusMessage, setStatusMessage] = useState("");
  const [activeTab, setActiveTab] = useState(null);
  const activeTabInitializedRef = useRef(false);
  const notifiedIncomingMessageIdsRef = useRef(new Set());
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [mapType, setMapType] = useState("roadmap");
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [isPhoneMobile, setIsPhoneMobile] = useState(() => window.innerWidth < 600);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState("");
  const [deleteAccountConfirmText, setDeleteAccountConfirmText] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isSubmittingDispute, setIsSubmittingDispute] = useState(false);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(false);
  const [isAiSuggestedOpen, setIsAiSuggestedOpen] = useState(true);
  const [dismissedAiFeedCardIds, setDismissedAiFeedCardIds] = useState([]);
  const [aiAssistantInput, setAiAssistantInput] = useState("");
  const [aiAssistantLoading, setAiAssistantLoading] = useState(false);
  const [aiAssistantError, setAiAssistantError] = useState("");
  const [aiAssistantMessages, setAiAssistantMessages] = useState([
    {
      role: "assistant",
      text: "Hi, I'm RentDuo AI. Ask me about verified reviews, property history, lease red flags, profile setup, claims, or professional rental responses.",
    },
  ]);
  const aiAssistantMessagesRef = useRef(null);
  const aiAssistantMessagesEndRef = useRef(null);

  const functions = getFunctions();

  const setFeedSourceLoading = (source, isLoading) => {
    setFeedLoadingSources((prev) => ({ ...prev, [source]: isLoading }));
  };

  const t = (key) => translate(language, key);
  const loginAiPrompts = [
    {
      key: "account",
      question: "How do I create an account?",
      answer: "Choose Sign Up, enter your email and password, accept the RentDuo disclaimer, then complete your Profile tab. Your role starts blank so you stay in control.",
    },
    {
      key: "role",
      question: "Which role should I choose?",
      answer: "Choose Tenant if you rent or are looking for a place, Landlord if you own or manage your rental, and Property Manager if you manage rentals for others.",
    },
    {
      key: "verification",
      question: "How does verification work?",
      answer: "Verification helps RentDuo show stronger trust signals. Submit only your own accurate information; admins review verification and property claims before special controls unlock.",
    },
  ];

  useEffect(() => {
    if (!isAiAssistantOpen) return;
    const scrollChatToBottom = () => {
      const container = aiAssistantMessagesRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    };

    const timer = window.setTimeout(scrollChatToBottom, 40);
    return () => window.clearTimeout(timer);
  }, [aiAssistantMessages, isAiAssistantOpen]);

  useEffect(() => {
    if (!isAiAssistantOpen) return;

    if (isMobile) {
      setIsAiSuggestedOpen(false);
    }

    const styleId = "rentduo-ai-modal-open-style";
    if (!document.getElementById(styleId)) {
      const styleTag = document.createElement("style");
      styleTag.id = styleId;
      styleTag.textContent = "body.ai-modal-open { overflow: hidden; }";
      document.head.appendChild(styleTag);
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyTouchAction = document.body.style.touchAction;
    document.body.classList.add("ai-modal-open");
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.classList.remove("ai-modal-open");
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.touchAction = previousBodyTouchAction;
    };
  }, [isAiAssistantOpen, isMobile]);

  const handleLanguageChange = (nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguage(normalized);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
  };
  const moderateReviewCallable = httpsCallable(functions, "moderateReview");
  const analyzeReviewCallable = httpsCallable(functions, "analyzeReview");
  const improveReviewCallable = httpsCallable(functions, "improveReview");
  const askRentDuoAICallable = httpsCallable(functions, "askRentDuoAI");
  const analyzeVerificationSubmissionCallable = httpsCallable(functions, "analyzeVerificationSubmission");
  const analyzePropertyRiskCallable = httpsCallable(functions, "analyzePropertyRisk");
  const analyzeMaintenanceIssueCallable = httpsCallable(functions, "analyzeMaintenanceIssue");
  const analyzeScamRiskCallable = httpsCallable(functions, "analyzeScamRisk");
  const explainLeaseCallable = httpsCallable(functions, "explainLease");
  const summarizeReputationTimelineCallable = httpsCallable(functions, "summarizeReputationTimeline");
  const analyzeNeighborhoodIntelligenceCallable = httpsCallable(functions, "analyzeNeighborhoodIntelligence");
  const estimateMarketRentCallable = httpsCallable(functions, "estimateMarketRent");
  const getSmartMatchesCallable = httpsCallable(functions, "getSmartMatches");
  const generateMoveChecklistCallable = httpsCallable(functions, "generateMoveChecklist");
  const runRentDuoAIFeatureCallable = httpsCallable(functions, "runRentDuoAIFeature");

  const {
    isLoaded: isMapLoaded,
    loadError: mapLoadError,
  } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const {
    properties,
    setProperties,
    selectedProperty,
    setSelectedProperty,
    showMobilePropertyModal,
    setShowMobilePropertyModal,
    showClaimVerificationModal,
    setShowClaimVerificationModal,
    propertyClaimTarget,
    setPropertyClaimTarget,
    hoveredProperty,
    setHoveredProperty,
    pendingPropertyLocation,
    setPendingPropertyLocation,
    propertyMapCenter,
    setPropertyMapCenter,
    propertyMapZoom,
    setPropertyMapZoom,
    hasUserMapLocation,
    setHasUserMapLocation,
    favoritePropertyIds,
    setFavoritePropertyIds,
    visibleProperties,
    isFavoriteProperty,
    toggleFavoriteProperty,
  } = usePropertyData({
    isMapLoaded,
    isMobile,
    role,
    preferredAreasText,
    serviceAreasText,
    city,
    profile,
    user,
    setStatusMessage,
  });

  const {
    reviews,
    setReviews,
    myReceivedReviews,
    setMyReceivedReviews,
    publicFeedUserReviews,
    setPublicFeedUserReviews,
    propertyReviews,
    setPropertyReviews,
    allPropertyReviews,
    setAllPropertyReviews,
    pendingReviews,
    setPendingReviews,
    reportedReviews,
    setReportedReviews,
  } = useReviewData({
    selectedUser,
    user,
    isAdmin,
    selectedProperty,
    setStatusMessage,
  });

  const {
    notifications,
    unreadNotificationCount,
    createNotification,
    markNotificationAsRead,
    markAllNotificationsAsRead,
  } = useNotifications({ user });
  const totalUnreadCount = Math.max(unreadNotificationCount, unreadMessageCount);

  const {
    tenantResidencyClaims,
    propertyResidencyClaims,
  } = useClaims({
    db,
    userId: user?.uid || "",
    selectedPropertyId: selectedProperty?.id || "",
    canViewPropertyClaims: canManageRentalListing(selectedProperty),
  });

 useEffect(() => {
  if (!isMapLoaded) return;
  if (!window.google?.maps?.places) return;

  let profileAutocomplete = null;
  let profileListener = null;
  let propertyAttachInterval = null;
  let propertyAutocompleteInstance = null;
  let propertyListener = null;
  let reviewAreaAutocomplete = null;
  let reviewAreaListener = null;
  let reviewUserAutocomplete = null;
  let reviewUserListener = null;
  let homeAutocomplete = null;
  let homeListener = null;
  let privateAddressAutocomplete = null;
  let privateAddressListener = null;

  if (profileLocationInputRef.current) {
    profileAutocomplete = new window.google.maps.places.Autocomplete(
      profileLocationInputRef.current,
      {
        types: ["(cities)"],
        componentRestrictions: { country: "us" },
        fields: ["formatted_address", "place_id", "address_components"],
      }
    );

    profileListener = profileAutocomplete.addListener("place_changed", () => {
      window.setTimeout(() => {
        const place = profileAutocomplete.getPlace();
        const components = place?.address_components || [];

        const selectedCity =
          components.find((c) => c.types.includes("locality"))?.long_name ||
          components.find((c) => c.types.includes("postal_town"))?.long_name ||
          "";

        const selectedState =
          components.find((c) =>
            c.types.includes("administrative_area_level_1")
          )?.short_name || "";

        const parsed = parseAddressComponents(components);
        const nextValue = [selectedCity, selectedState].filter(Boolean).join(", ");

        if (profileLocationInputRef.current) {
          profileLocationInputRef.current.value = nextValue;
        }

        setCity(nextValue);
        setProfileLocation(
          buildProfileLocationPayload({
            location: {
              state: selectedState || parsed.state || "",
              county: parsed.county || "",
              city: selectedCity || parsed.city || "",
              town: components.find((c) => c.types.includes("postal_town"))?.long_name || "",
              zip: parsed.zip || "",
              formattedAddress: place?.formatted_address || nextValue,
              placeId: place?.place_id || "",
            },
            fallbackCity: nextValue,
            fallbackState: selectedState || parsed.state || "",
          })
        );
      }, 10);
    });
  }

  if (reviewAreaInputRef.current) {
    reviewAreaAutocomplete = new window.google.maps.places.Autocomplete(
      reviewAreaInputRef.current,
      {
        types: ["(cities)"],
        componentRestrictions: { country: "us" },
        fields: ["address_components"],
      }
    );

    reviewAreaListener = reviewAreaAutocomplete.addListener("place_changed", () => {
      window.setTimeout(() => {
        const place = reviewAreaAutocomplete.getPlace();
        const components = place?.address_components || [];

        const selectedCity =
          components.find((c) => c.types.includes("locality"))?.long_name ||
          components.find((c) => c.types.includes("postal_town"))?.long_name ||
          "";

        const selectedState =
          components.find((c) => c.types.includes("administrative_area_level_1"))?.short_name || "";

        const nextValue = [selectedCity, selectedState].filter(Boolean).join(", ");

        if (reviewAreaInputRef.current) {
          reviewAreaInputRef.current.value = nextValue;
        }

        setSearchArea(nextValue);
      }, 10);
    });
  }

  if (reviewUserSearchInputRef.current) {
    reviewUserAutocomplete = new window.google.maps.places.Autocomplete(
      reviewUserSearchInputRef.current,
      {
        types: ["geocode"],
        componentRestrictions: { country: "us" },
        fields: ["formatted_address", "address_components", "name"],
      }
    );

    reviewUserListener = reviewUserAutocomplete.addListener("place_changed", () => {
      window.setTimeout(() => {
        const place = reviewUserAutocomplete.getPlace();
        const parsed = parseAddressComponents(place?.address_components || []);
        const nextValue =
          place?.formatted_address ||
          [parsed.streetLine, parsed.city, parsed.state, parsed.zip].filter(Boolean).join(", ") ||
          place?.name ||
          "";

        if (reviewUserSearchInputRef.current) {
          reviewUserSearchInputRef.current.value = nextValue;
        }

        setSearchQuery(nextValue);
      }, 10);
    });
  }


  if (homeSearchInputRef.current) {
    homeAutocomplete = new window.google.maps.places.Autocomplete(
      homeSearchInputRef.current,
      {
        types: ["geocode"],
        componentRestrictions: { country: "us" },
        fields: ["formatted_address", "geometry", "address_components", "name"],
      }
    );

    homeListener = homeAutocomplete.addListener("place_changed", () => {
      window.setTimeout(() => {
        const place = homeAutocomplete.getPlace();
        const parsed = parseAddressComponents(place?.address_components || []);
        const geometry = place?.geometry?.location;
        const nextValue =
          place?.formatted_address ||
          [parsed.streetLine, parsed.city, parsed.state, parsed.zip].filter(Boolean).join(", ") ||
          place?.name ||
          "";

        if (homeSearchInputRef.current) {
          homeSearchInputRef.current.value = nextValue;
        }

        setSearchArea(nextValue);

        if (geometry) {
          const lat = Number(geometry.lat());
          const lng = Number(geometry.lng());
          setPropertyMapCenter({ lat, lng });
          setPropertyMapZoom(14);
        }

        const normalized = normalizePropertyAddress(nextValue);
        const exactProperty = visibleProperties.find((prop) => {
          const values = [prop?.address, prop?.fullAddress, prop?.title].map((v) => normalizePropertyAddress(v || ""));
          return normalized && values.some((value) => value && (value === normalized || value.includes(normalized) || normalized.includes(value)));
        });

        if (exactProperty) {
          handleSelectProperty(exactProperty);
          setStatusMessage("Property found. Open the property card to view reviews and history.");
        } else if (nextValue) {
          setStatusMessage("Area selected. Browse matching properties below or add this property if it is missing.");
        }
      }, 10);
    });
  }

  if (privateAddressInputRef.current) {
    privateAddressAutocomplete = new window.google.maps.places.Autocomplete(
      privateAddressInputRef.current,
      {
        types: ["address"],
        componentRestrictions: { country: "us" },
        fields: ["formatted_address", "address_components"],
      }
    );

    privateAddressListener = privateAddressAutocomplete.addListener("place_changed", () => {
      window.setTimeout(() => {
        const place = privateAddressAutocomplete.getPlace();
        const parsed = parseAddressComponents(place?.address_components || []);
        const nextValue =
          place?.formatted_address ||
          [parsed.streetLine, parsed.city, parsed.state, parsed.zip].filter(Boolean).join(", ") ||
          "";

        if (privateAddressInputRef.current) {
          privateAddressInputRef.current.value = nextValue;
        }

        setPrivateAddress(nextValue);
      }, 10);
    });
  }

  const attachPropertyAutocomplete = () => {
    const input = propertyAddressInputRef.current;
    if (!input) return false;
    if (input.dataset.autocompleteAttached === "true") return true;

    propertyAutocompleteInstance = new window.google.maps.places.Autocomplete(
      input,
      {
        types: ["address"],
        componentRestrictions: { country: "us" },
        fields: ["formatted_address", "geometry", "address_components"],
      }
    );

    input.dataset.autocompleteAttached = "true";

    propertyListener = propertyAutocompleteInstance.addListener("place_changed", () => {
      window.setTimeout(() => {
        const place = propertyAutocompleteInstance.getPlace();
        const components = place?.address_components || [];
        const geometry = place?.geometry?.location;

        const parsed = parseAddressComponents(components);
        const fullAddress = place?.formatted_address || parsed.streetLine || "";

        if (propertyAddressInputRef.current) {
          propertyAddressInputRef.current.value = fullAddress;
        }

        setPropertyAddress(fullAddress);
        setPropertyAddressMeta(parsed);

        if (geometry) {
          const lat = Number(geometry.lat());
          const lng = Number(geometry.lng());
          setPendingPropertyLocation({ lat, lng });
          setPropertyMapCenter({ lat, lng });
          setPropertyMapZoom(15);
        }

        setPropertyTitle((prev) =>
          !prev.trim() && parsed.streetLine ? parsed.streetLine : prev
        );

        setPropertyDescription((prev) => {
          if (prev.trim()) return prev;

          const locationLine = [parsed.city, parsed.state, parsed.zip]
            .filter(Boolean)
            .join(", ");

          return `Rental property located at ${parsed.streetLine || fullAddress}${
            locationLine ? ` in ${locationLine}` : ""
          }.`;
        });

        setStatusMessage("Address selected and pin placed automatically.");
      }, 10);
    });

    return true;
  };

  if (!attachPropertyAutocomplete()) {
    propertyAttachInterval = window.setInterval(() => {
      if (attachPropertyAutocomplete()) {
        window.clearInterval(propertyAttachInterval);
      }
    }, 300);
  }

  return () => {
    if (profileListener) {
      window.google.maps.event.removeListener(profileListener);
    }
    if (propertyListener) {
      window.google.maps.event.removeListener(propertyListener);
    }
    if (reviewAreaListener) {
      window.google.maps.event.removeListener(reviewAreaListener);
    }
    if (reviewUserListener) {
      window.google.maps.event.removeListener(reviewUserListener);
    }
    if (homeListener) {
      window.google.maps.event.removeListener(homeListener);
    }
    if (privateAddressListener) {
      window.google.maps.event.removeListener(privateAddressListener);
    }
    if (profileAutocomplete) {
      window.google.maps.event.clearInstanceListeners(profileAutocomplete);
    }
    if (propertyAutocompleteInstance) {
      window.google.maps.event.clearInstanceListeners(propertyAutocompleteInstance);
    }
    if (reviewAreaAutocomplete) {
      window.google.maps.event.clearInstanceListeners(reviewAreaAutocomplete);
    }
    if (reviewUserAutocomplete) {
      window.google.maps.event.clearInstanceListeners(reviewUserAutocomplete);
    }
    if (homeAutocomplete) {
      window.google.maps.event.clearInstanceListeners(homeAutocomplete);
    }
    if (privateAddressAutocomplete) {
      window.google.maps.event.clearInstanceListeners(privateAddressAutocomplete);
    }
    if (propertyAttachInterval) {
      window.clearInterval(propertyAttachInterval);
    }

    const input = propertyAddressInputRef.current;
    if (input?.dataset?.autocompleteAttached) {
      delete input.dataset.autocompleteAttached;
    }
  };
}, [isMapLoaded, activeTab, user, loading, visibleProperties]);

useEffect(() => {
    if (profileLocationInputRef.current && document.activeElement !== profileLocationInputRef.current) {
      profileLocationInputRef.current.value = city || "";
    }
  }, [city]);

  useEffect(() => {
    if (privateAddressInputRef.current && document.activeElement !== privateAddressInputRef.current) {
      privateAddressInputRef.current.value = privateAddress || "";
    }
  }, [privateAddress]);

  useEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [activeTab]);

  useEffect(() => {
    const previousHtmlOverflowX = document.documentElement.style.overflowX;
    const previousBodyOverflowX = document.body.style.overflowX;
    const previousHtmlOverscroll = document.documentElement.style.overscrollBehaviorX;
    const previousBodyOverscroll = document.body.style.overscrollBehaviorX;
    document.documentElement.style.overflowX = "hidden";
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overscrollBehaviorX = "none";
    document.body.style.overscrollBehaviorX = "none";
    return () => {
      document.documentElement.style.overflowX = previousHtmlOverflowX;
      document.body.style.overflowX = previousBodyOverflowX;
      document.documentElement.style.overscrollBehaviorX = previousHtmlOverscroll;
      document.body.style.overscrollBehaviorX = previousBodyOverscroll;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth || document.documentElement.clientWidth || 0;
      setIsMobile(width <= 768);
      setIsPhoneMobile(width < 600);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleAppResume = () => {
      if (document.visibilityState === "visible") {
        setAppResumeTick((value) => value + 1);
      }
    };

    document.addEventListener("visibilitychange", handleAppResume);
    window.addEventListener("focus", handleAppResume);

    return () => {
      document.removeEventListener("visibilitychange", handleAppResume);
      window.removeEventListener("focus", handleAppResume);
    };
  }, []);

  useEffect(() => {
    if (activeTab !== "home") return;
    if (isMobile) return;

    const timer = window.setTimeout(() => {
      if (!searchArea && homeSearchInputRef.current) {
        homeSearchInputRef.current.focus();
      }
    }, 180);

    return () => window.clearTimeout(timer);
  }, [activeTab, isMobile, searchArea]);


  useEffect(() => {
    const styleId = "rentduo-pac-zindex";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `.pac-container { z-index: 99999 !important; max-height: 40vh !important; overflow-y: auto !important; overscroll-behavior: contain !important; }`;
    document.head.appendChild(style);

    return () => {
      const existing = document.getElementById(styleId);
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const refCode = (params.get("ref") || "").trim();
      if (refCode) {
        window.localStorage.setItem(REFERRAL_STORAGE_KEY, refCode);
      }
    } catch (error) {
      console.error("Referral capture error:", error);
    }
  }, []);

  useEffect(() => {
    const styleId = "rentduo-ui-polish";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        min-height: 100%;
        max-width: 100%;
        overflow-x: hidden !important;
        overflow-y: auto !important;
        overscroll-behavior-x: none;
        touch-action: pan-y pinch-zoom;
        background: linear-gradient(180deg, #f8fbff 0%, #f7fbf8 100%);
        color: #0f172a;
        font-family: Inter, Arial, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      * {
        box-sizing: border-box;
        max-width: 100%;
        min-width: 0;
      }

      img,
      svg,
      video,
      canvas {
        max-width: 100%;
      }

      p,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      span,
      div,
      button,
      label {
        overflow-wrap: break-word;
        word-break: normal;
        writing-mode: horizontal-tb;
      }

      #root {
        width: 100%;
        height: 100%;
        min-height: 100vh;
        max-width: 100%;
        overflow-x: hidden !important;
        overflow-y: auto !important;
        overscroll-behavior-x: none;
        touch-action: pan-y pinch-zoom;
      }

      input, textarea, select, button {
        font: inherit;
      }

      input::placeholder,
      textarea::placeholder {
        color: #94a3b8;
      }

      button, input, textarea, select {
        transition: box-shadow 0.18s ease, border-color 0.18s ease, transform 0.18s ease, background 0.18s ease;
      }

      button,
      [role="button"] {
        position: relative;
        z-index: 1;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        transform-origin: center center;
      }

      button > *,
      [role="button"] > * {
        pointer-events: none;
      }

      button:hover {
        transform: translateY(-1px);
      }

      button:active {
        transform: translateY(0);
      }

      @media (max-width: 599px) {
        html,
        body,
        #root,
        .rentduo-app {
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: hidden !important;
          overscroll-behavior-x: none !important;
        }

        body {
          position: relative !important;
        }

        button,
        [role="button"],
        input,
        select,
        textarea {
          max-width: 100% !important;
          box-sizing: border-box !important;
          min-width: 0 !important;
          font-size: 16px;
        }

        button,
        [role="button"] {
          min-height: 44px;
          align-items: center;
          justify-content: center;
          text-align: center;
          white-space: normal;
          line-height: 1.2;
          overflow: visible;
          transform: none !important;
        }

        input,
        select,
        textarea {
          width: 100% !important;
        }

        button:hover,
        button:active {
          transform: none !important;
        }
      }

      button:focus-visible,
      input:focus-visible,
      textarea:focus-visible,
      select:focus-visible {
        outline: none;
        border-color: #93c5fd !important;
        box-shadow: 0 0 0 4px rgba(59,130,246,0.14) !important;
      }

      ::selection {
        background: rgba(59,130,246,0.18);
      }

      .rentduo-scrollbar,
      .rentduo-scrollbar * {
        scrollbar-width: thin;
        scrollbar-color: rgba(148, 163, 184, 0.9) transparent;
      }

      .rentduo-scrollbar::-webkit-scrollbar {
        width: 10px;
      }

      .rentduo-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }

      .rentduo-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(148, 163, 184, 0.75);
        border-radius: 999px;
        border: 2px solid transparent;
        background-clip: padding-box;
      }

      .review-search-input::-webkit-calendar-picker-indicator,
      .review-search-input::-webkit-contacts-auto-fill-button,
      .review-search-input::-webkit-credentials-auto-fill-button {
        display: none !important;
        opacity: 0 !important;
        pointer-events: none !important;
        width: 0 !important;
      }

      .review-search-input::-ms-clear,
      .review-search-input::-ms-reveal {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }

      @keyframes rentduoFadeUp {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes rentduoSoftPulse {
        0% { box-shadow: 0 0 0 0 rgba(15,111,255,0.14); }
        70% { box-shadow: 0 0 0 10px rgba(15,111,255,0); }
        100% { box-shadow: 0 0 0 0 rgba(15,111,255,0); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existing = document.getElementById(styleId);
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setReferralCount(0);
      return;
    }

    const q = query(
      collection(db, "publicProfiles"),
      where("referredBy", "==", user.uid)
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const count = snapshot.docs.filter((docSnap) => docSnap.id !== user.uid).length;
        setReferralCount(count);
      },
      (error) => {
        console.error("Referral count listener error:", error);
        setReferralCount(0);
      }
    );

    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user?.uid) return;

    const profileRef = doc(db, "profiles", user.uid);
    setDoc(
      profileRef,
      {
        lastActiveAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    ).catch((error) => {
      console.error("Active presence update failed:", error);
    });
  }, [user?.uid, activeTab]);

  useEffect(() => {
    if (!user?.uid || !profile) return;

    setDoc(
      doc(db, "publicProfiles", user.uid),
      buildPublicProfilePayload({
        currentUser: user,
        source: profile,
        role: profile?.role || role || "",
        displayName: profile?.displayName || displayName,
        city: profile?.city || city,
        state: profile?.state || "",
        profileStatus: profile?.profileStatus || profileStatus,
        statusVisibility: profile?.statusVisibility !== false,
        preferredAreasText: profile?.preferredAreasText || preferredAreasText,
        serviceAreasText: profile?.serviceAreasText || serviceAreasText,
        bio: profile?.bio || bio,
        photoURL: profile?.photoURL || photoURL,
      }),
      { merge: true }
    ).catch((error) => {
      console.error("Public profile sync failed:", error);
    });
  }, [user, profile, role, displayName, city, profileStatus, preferredAreasText, serviceAreasText, bio, photoURL]);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const emailValue = String(user.email || profile?.email || "").toLowerCase();
        if (
          profile?.isAdmin === true ||
          profile?.founder === true ||
          profile?.role === "admin" ||
          FOUNDER_EMAILS.includes(emailValue)
        ) {
          setIsAdmin(true);
          return;
        }

        const token = await user.getIdTokenResult().catch(() => null);
        if (token?.claims?.admin === true) {
          setIsAdmin(true);
          return;
        }

        const adminRef = doc(db, "admins", user.uid);
        const snap = await getDoc(adminRef);

        if (snap.exists() && snap.data().isAdmin === true) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error("Admin check error:", error);
        setIsAdmin(false);
      }
    };

    checkAdmin();
  }, [user, profile]);

  useEffect(() => {
    if (!isAdmin && activeTab === "admin") {
      setActiveTab("profile");
    }
  }, [isAdmin, activeTab]);

  useEffect(() => {
    if (!user?.uid || !profileHydrated || activeTabInitializedRef.current) return;
    const savedTab = window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    const allowedTabs = new Set(["profile", "home", "feed", "reviews", "messages", "manager", "admin"]);
    const nextTab = savedTab && allowedTabs.has(savedTab) ? savedTab : "profile";
    setActiveTab(nextTab === "home" ? "profile" : nextTab);
    activeTabInitializedRef.current = true;
  }, [user?.uid, profileHydrated]);

  useEffect(() => {
    if (!user?.uid || !profileHydrated) return;
    if (activeTab) window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab, user?.uid, profileHydrated]);

  useEffect(() => {
    setFeedSourceLoading("profiles", true);
    const unsub = onSnapshot(
      collection(db, "publicProfiles"),
      (snapshot) => {
        const items = snapshot.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .filter((item) => item?.displayName)
          .sort((a, b) => getFeedTimestampMs(b?.updatedAt || b?.createdAt) - getFeedTimestampMs(a?.updatedAt || a?.createdAt));

        setPublicFeedProfiles(items);
        setFeedSourceLoading("profiles", false);
        setFeedError("");
      },
      (error) => {
        console.error("Public feed profiles listener error:", error);
        setPublicFeedProfiles([]);
        setFeedSourceLoading("profiles", false);
        setFeedError(
          error?.code === "permission-denied"
            ? "Permission blocked public profile activity. Check Firestore publicProfiles read rules."
            : "Could not load public profile activity."
        );
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    setFeedSourceLoading("feed", true);
    const unsub = onSnapshot(
      collection(db, "feed"),
      (snapshot) => {
        const items = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((item) => item?.type && item?.type !== "private_message")
          .sort((a, b) => getFeedTimestampMs(b?.createdAt || b?.updatedAt) - getFeedTimestampMs(a?.createdAt || a?.updatedAt));

        setPublicFeedEvents(items);
        setFeedSourceLoading("feed", false);
        setFeedError("");
      },
      (error) => {
        console.error("Feed listener error:", error);
        setPublicFeedEvents([]);
        setFeedSourceLoading("feed", false);
        setFeedError(
          error?.code === "permission-denied"
            ? "Permission blocked the live feed. Check Firestore feed read rules."
            : "Could not load live feed activity."
        );
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setMyVerificationSubmissions([]);
      return;
    }

    const unsub = onSnapshot(
      query(collection(db, "verificationSubmissions"), where("userId", "==", user.uid)),
      (snapshot) => {
        const items = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => {
            const aTime = a.createdAt?.seconds || 0;
            const bTime = b.createdAt?.seconds || 0;
            return bTime - aTime;
          });
        setMyVerificationSubmissions(items);
      },
      (error) => {
        console.error("Verification submissions listener error:", error);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if (!isAdmin) {
      setPendingVerifications([]);
      setAllVerifications([]);
      setPendingVerificationSubmissions([]);
      return;
    }

    const unsub = onSnapshot(
      collection(db, "profiles"),
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const pending = items.filter(
          (item) => item.verificationStatus === "pending_review"
        );

        pending.sort((a, b) => {
          const aTime = a.verificationSubmittedAt?.seconds || 0;
          const bTime = b.verificationSubmittedAt?.seconds || 0;
          return bTime - aTime;
        });

        items.sort((a, b) => {
          const aTime = a.updatedAt?.seconds || 0;
          const bTime = b.updatedAt?.seconds || 0;
          return bTime - aTime;
        });

        setPendingVerifications(pending);
        setAllVerifications(items);

        setAdminNoteDrafts((prev) => {
          const next = { ...prev };
          items.forEach((item) => {
            const targetId = item.uid || item.id;
            if (targetId && next[targetId] === undefined) {
              next[targetId] = item.adminVerificationNote || "";
            }
          });
          return next;
        });
      },
      (error) => {
        console.error("Admin verifications listener error:", error);
      }
    );

    return () => unsub();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      setPendingVerificationSubmissions([]);
      return;
    }

    const unsub = onSnapshot(
      collection(db, "verificationSubmissions"),
      (snapshot) => {
        const items = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => {
            const aTime = a.createdAt?.seconds || a.reviewedAt?.seconds || 0;
            const bTime = b.createdAt?.seconds || b.reviewedAt?.seconds || 0;
            return bTime - aTime;
          });

        const pending = items.filter((item) => item.adminStatus === "pending_review");
        setPendingVerificationSubmissions(pending);
        setAdminNoteDrafts((prev) => {
          const next = { ...prev };
          pending.forEach((item) => {
            if (item.id && next[item.id] === undefined) {
              next[item.id] = item.adminNotes || "";
            }
          });
          return next;
        });
      },
      (error) => {
        console.error("Verification submissions admin listener error:", error);
      }
    );

    return () => unsub();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      setReportedUsers([]);
      return;
    }

    const unsub = onSnapshot(
      collection(db, "userReports"),
      (snapshot) => {
        const all = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        all.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        setReportedUsers(all);
      },
      (error) => {
        console.error("User reports listener error:", error);
      }
    );

    return () => unsub();
  }, [isAdmin]);

  useEffect(() => {
    if (!user?.uid) {
      setInboxThreads([]);
      setOutboxThreads([]);
      setUnreadMessageCount(0);
      return;
    }

    const rebuildThreads = (items = []) => {
      const all = items.filter(
        (item) => item.senderId === user.uid || item.receiverId === user.uid
      );

      all.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

      const inboxMap = {};
      const outboxMap = {};

      all.forEach((item) => {
          const isInbox = item.receiverId === user.uid;
          const isOutbox = item.senderId === user.uid;

          if (isInbox && !inboxMap[item.conversationId]) {
            inboxMap[item.conversationId] = {
              conversationId: item.conversationId,
              uid: item.senderId || "",
              email: item.senderEmail || "",
              displayName: item.senderDisplayName || item.senderEmail || "User",
              photoURL: item.senderPhotoURL || "",
              verified: Boolean(item.senderVerified),
              verificationStatus: item.senderVerificationStatus || (item.senderVerified ? "verified" : "unverified"),
              lastMessage: item.text || "",
              createdAt: item.createdAt || null,
            };
          }

          if (isOutbox && !outboxMap[item.conversationId]) {
            outboxMap[item.conversationId] = {
              conversationId: item.conversationId,
              uid: item.receiverId || "",
              email: item.receiverEmail || "",
              displayName:
                item.receiverDisplayName || item.receiverEmail || "User",
              photoURL: item.receiverPhotoURL || "",
              verified: Boolean(item.receiverVerified),
              verificationStatus: item.receiverVerificationStatus || (item.receiverVerified ? "verified" : "unverified"),
              lastMessage: item.text || "",
              createdAt: item.createdAt || null,
            };
          }
        });

        setInboxThreads(Object.values(inboxMap));
        setOutboxThreads(Object.values(outboxMap));
    };

    const incomingQuery = query(collection(db, "messages"), where("receiverId", "==", user.uid));
    const outgoingQuery = query(collection(db, "messages"), where("senderId", "==", user.uid));
    let incomingItems = [];
    let outgoingItems = [];

    const handleError = (error) => {
      console.error("Inbox/outbox listener error:", error);
    };

    const unsubIncoming = onSnapshot(
      incomingQuery,
      (snapshot) => {
        incomingItems = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        const unreadIncoming = incomingItems.filter((item) => {
          const readBy = Array.isArray(item?.readBy) ? item.readBy : [];
          return item.senderId !== user.uid && !readBy.includes(user.uid);
        });
        setUnreadMessageCount(unreadIncoming.length);
        unreadIncoming.forEach((item) => {
          if (!item?.id || notifiedIncomingMessageIdsRef.current.has(item.id)) return;
          notifiedIncomingMessageIdsRef.current.add(item.id);
          createNotification({
            userId: user.uid,
            title: "New Message",
            body: `${item.senderDisplayName || "Someone"} sent you a message.`,
            type: "message",
            meta: {
              conversationId: item.conversationId || "",
              messageId: item.id,
              senderId: item.senderId || "",
              senderUserId: item.senderId || "",
            },
          });
        });
        rebuildThreads([...incomingItems, ...outgoingItems]);
      },
      handleError
    );

    const unsubOutgoing = onSnapshot(
      outgoingQuery,
      (snapshot) => {
        outgoingItems = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        rebuildThreads([...incomingItems, ...outgoingItems]);
      },
      handleError
    );

    return () => {
      unsubIncoming();
      unsubOutgoing();
    };
  }, [user?.uid, appResumeTick]);

  useEffect(() => {
    setFeedSourceLoading("propertyReviews", true);
    const unsubPropertyReviews = onSnapshot(
      query(collection(db, "propertyReviews"), where("status", "in", ["approved", "published"])),
      (snapshot) => {
        const items = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((item) => item?.status !== "deleted");

        items.sort((a, b) => getFeedTimestampMs(b?.createdAt || b?.updatedAt) - getFeedTimestampMs(a?.createdAt || a?.updatedAt));
        setAllPropertyReviews(items);
        setAllPropertyReviewsCount(items.length);
        setFeedSourceLoading("propertyReviews", false);
        setFeedError("");
      },
      (error) => {
        console.error("Property reviews listener error:", error);
        setAllPropertyReviews([]);
        setAllPropertyReviewsCount(0);
        setFeedSourceLoading("propertyReviews", false);
        setFeedError(
          error?.code === "permission-denied"
            ? "Permission blocked public property reviews. Check Firestore propertyReviews read rules."
            : "Could not load property review activity."
        );
      }
    );

    return () => unsubPropertyReviews();
  }, [appResumeTick, setAllPropertyReviews]);

  useEffect(() => {
    setFeedSourceLoading("reviews", true);
    const unsubReviews = onSnapshot(
      query(collection(db, "reviews"), where("status", "==", "approved")),
      (snapshot) => {
        const items = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((item) => item?.status !== "deleted");

        items.sort((a, b) => getFeedTimestampMs(b?.createdAt || b?.updatedAt) - getFeedTimestampMs(a?.createdAt || a?.updatedAt));
        setAllReviewsCount(items.length);
        setPublicFeedUserReviews(items);
        setFeedSourceLoading("reviews", false);
        setFeedError("");
      },
      (error) => {
        console.error("Public reviews listener error:", error);
        setPublicFeedUserReviews([]);
        setAllReviewsCount(0);
        setFeedSourceLoading("reviews", false);
        setFeedError(
          error?.code === "permission-denied"
            ? "Permission blocked public reviews. Check Firestore reviews read rules."
            : "Could not load review activity."
        );
      }
    );

    return () => unsubReviews();
  }, [appResumeTick, setPublicFeedUserReviews]);

  useEffect(() => {
    if (!isAdmin) {
      setAllMessagesCount(0);
      setPartnerClickCount(0);
      setGrowthBaseline(null);
      return;
    }

    const unsubMessages = onSnapshot(collection(db, "messages"), (snapshot) => {
      setAllMessagesCount(snapshot.size);
    }, (error) => console.error("All messages stats listener error:", error));

    const unsubPartnerClicks = onSnapshot(collection(db, "partnerClicks"), (snapshot) => {
      setPartnerClickCount(snapshot.size);
    }, (error) => console.error("Partner click stats listener error:", error));

    const unsubGrowthBaseline = onSnapshot(
      doc(db, "analytics", "growthBaseline"),
      (snapshot) => {
        setGrowthBaseline(snapshot.exists() ? snapshot.data() : null);
      },
      (error) => console.error("Growth baseline listener error:", error)
    );

    return () => {
      unsubMessages();
      unsubPartnerClicks();
      unsubGrowthBaseline();
    };
  }, [isAdmin, appResumeTick]);

  useEffect(() => {
    if (!user?.uid) {
      setIssues([]);
      return;
    }

    const issueBuckets = {};
    const mergeIssues = (key, snapshot) => {
      issueBuckets[key] = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const merged = Object.values(issueBuckets)
        .flat()
        .reduce((acc, item) => {
          acc.set(item.id, item);
          return acc;
        }, new Map());

      const items = Array.from(merged.values()).sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

      setIssues(items);
    };

    const listeners = [];
    const listen = (key, issuesQuery) => {
      listeners.push(
        onSnapshot(
          issuesQuery,
          (snapshot) => mergeIssues(key, snapshot),
          (error) => console.error(`Issues listener error (${key}):`, error)
        )
      );
    };

    if (isAdmin) {
      listen("admin", collection(db, "issues"));
    } else {
      listen("tenant", query(collection(db, "issues"), where("tenantUserId", "==", user.uid)));
      listen("owner", query(collection(db, "issues"), where("propertyOwnerId", "==", user.uid)));
    }

    return () => listeners.forEach((unsub) => unsub());
  }, [user?.uid, isAdmin]);

  useEffect(() => {
    if (!selectedUser?.uid) return;
    const timer = window.setTimeout(() => {
      messageInputRef.current?.focus?.();
    }, 80);
    return () => window.clearTimeout(timer);
  }, [selectedUser, activeTab]);

  useEffect(() => {
    if (!selectedUser?.uid) return;
    const timer = window.setTimeout(() => {
      messagesEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [messages, selectedUser]);

  const conversationId = useMemo(() => {
    if (!user?.uid || !selectedUser?.uid) return null;
    return [user.uid, selectedUser.uid].sort().join("_");
  }, [user, selectedUser]);

  useEffect(() => {
    if (!conversationId) {
      pendingMessagesRef.current = [];
      setMessages(EMPTY_ARRAY);
      return;
    }

    pendingMessagesRef.current = pendingMessagesRef.current.filter(
      (item) => item?.conversationId === conversationId
    );

    const q = query(
      collection(db, "messages"),
      where("conversationId", "==", conversationId)
    );

    const sortMessages = (items = []) =>
      [...items].sort((a, b) => {
        const aTime = a.createdAt?.seconds || Math.floor((a.createdAtMs || 0) / 1000);
        const bTime = b.createdAt?.seconds || Math.floor((b.createdAtMs || 0) / 1000);
        return aTime - bTime;
      });

    const mergePendingMessages = (items = []) => {
      const savedClientIds = new Set(items.map((item) => item?.clientMessageId).filter(Boolean));
      const pending = pendingMessagesRef.current.filter(
        (item) => item?.conversationId === conversationId && !savedClientIds.has(item.clientMessageId)
      );
      return sortMessages([...items, ...pending]);
    };

    getDocs(q)
      .then((snapshot) => {
        const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMessages(mergePendingMessages(items));
      })
      .catch((error) => console.error("Messages fallback fetch error:", error));

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setMessages(mergePendingMessages(items));
      },
      (error) => {
        console.error("Messages listener error:", error);
      }
    );

    return () => unsub();
  }, [conversationId, appResumeTick]);

  const referralLink = useMemo(() => {
    if (!user?.uid) return "";
    return `${window.location.origin}?ref=${user.uid}`;
  }, [user]);

  useEffect(() => {
    scrollAppToTop("smooth");
  }, [activeTab]);

  useEffect(() => {
    if (!user?.uid || activeTab !== "messages") return;
    getDocs(query(collection(db, "messages"), where("receiverId", "==", user.uid)))
      .then((snapshot) => {
        snapshot.docs.forEach((docSnap) => {
          const item = docSnap.data() || {};
          const readBy = Array.isArray(item?.readBy) ? item.readBy : [];
          if (!readBy.includes(user.uid)) {
            setDoc(
              doc(db, "messages", docSnap.id),
              {
                readBy: arrayUnion(user.uid),
                readAt: serverTimestamp(),
              },
              { merge: true }
            ).catch((error) => console.error("Mark inbox message read failed:", error));
          }
        });
      })
      .catch((error) => console.error("Load inbox read status failed:", error));

    const unreadVisibleMessages = messages.filter((item) => {
      const readBy = Array.isArray(item?.readBy) ? item.readBy : [];
      return item?.id && item.receiverId === user.uid && !readBy.includes(user.uid);
    });
    unreadVisibleMessages.forEach((item) => {
      setDoc(
        doc(db, "messages", item.id),
        {
          readBy: arrayUnion(user.uid),
          readAt: serverTimestamp(),
        },
        { merge: true }
      ).catch((error) => console.error("Mark message read failed:", error));
    });
  }, [activeTab, messages, user?.uid]);

  const handleShareRentDuo = async () => {
    const shareUrl = referralLink || window.location.origin;
    const shareText =
      "Check out RentDuo — a rental review platform for tenants, landlords, property managers, and properties.";

    try {
      if (navigator.share) {
        await navigator.share({ title: "RentDuo", text: shareText, url: shareUrl });
        setStatusMessage("RentDuo share opened.");
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setStatusMessage("RentDuo link copied to clipboard.");
        return;
      }
    } catch (error) {
      console.error("Share RentDuo failed:", error);
    }

    window.prompt("Copy your RentDuo invite link:", shareUrl);
  };

  const goToAddProperty = () => {
    setActiveTab("home");
    setStatusMessage("Add a property by selecting an address. The title, city/state, description, and map pin will auto-fill after you choose a Google address.");

    const focusPropertyForm = () => {
      propertyCreateCardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      propertyAddressInputRef.current?.focus?.();
    };

    window.setTimeout(focusPropertyForm, 250);
    window.setTimeout(focusPropertyForm, 650);
  };




  const isCurrentUserVerified = useMemo(() => {
    const currentStatus = profile?.verificationStatus || verificationStatus || "";

    return (
      profile?.verified === true ||
      currentStatus === "verified" ||
      currentStatus === "tenant_verified" ||
      currentStatus === "landlord_verified" ||
      currentStatus === "property_manager_verified"
    );
  }, [profile, verificationStatus]);

  const profileCompletionItems = useMemo(
    () => [
      { label: "Name", done: Boolean(String(displayName || profile?.displayName || "").trim()) },
      { label: "Role", done: Boolean(role) },
      { label: "City", done: Boolean(String(city || profile?.city || "").trim()) },
      { label: "Bio", done: Boolean(String(bio || profile?.bio || "").trim()) },
      { label: "Verification", done: Boolean(isCurrentUserVerified) },
    ],
    [displayName, profile, role, city, bio, isCurrentUserVerified]
  );

  const profileCompletionPercent = useMemo(() => {
    const doneCount = profileCompletionItems.filter((item) => item.done).length;
    return Math.round((doneCount / Math.max(profileCompletionItems.length, 1)) * 100);
  }, [profileCompletionItems]);


  const getEffectiveVerificationStatus = (item = {}) => {
    const rawStatus = String(item?.verificationStatus || "").trim();

    if (
      rawStatus === "verified" ||
      rawStatus === "tenant_verified" ||
      rawStatus === "landlord_verified" ||
      rawStatus === "property_manager_verified" ||
      rawStatus === "pending_review"
    ) {
      return rawStatus;
    }

    if (item?.verified === true || item?.isVerified === true) {
      return "verified";
    }

    return "unverified";
  };

  const getRoleLabel = (value = "") => {
    const normalized = String(value || "");
    if (normalized === "property_manager") return "Property Manager";
    return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "Role not selected";
  };

  const getPublicStatusText = (item = {}) => {
    if (item?.statusVisibility === false || !item?.profileStatus) return "No public status posted yet.";
    const meta = getProfileStatusMeta(item.profileStatus);
    const roleValue = item?.role || "";

    if (roleValue === "tenant") {
      return `${meta.label} — looking for a place to stay.`;
    }

    if (roleValue === "landlord") {
      return `${meta.label} — may be actively looking for tenants.`;
    }

    if (roleValue === "property_manager") {
      return `${meta.label} — may be managing or filling rentals.`;
    }

    return meta.label || "Public status posted.";
  };

  const getRightsStateCode = (...values) => {
    for (const value of values) {
      const rawValue = typeof value === "object" && value !== null
        ? value.state || value.city || value.fullAddress || value.address || ""
        : value;
      const normalized = String(rawValue || "").trim().toUpperCase();
      const explicitState = normalized.match(/\b[A-Z]{2}\b/)?.[0];
      if (explicitState) return explicitState;
    }

    return "NJ";
  };

  const getReviewSafetyLocationContext = () => {
    const savedLocation = profile?.location || profileLocation || {};
    return {
      state: savedLocation.state || profile?.state || getRightsStateCode(profile?.state, profile?.city, city),
      county: savedLocation.county || "",
      city: savedLocation.city || profile?.city || city || "",
      town: savedLocation.town || "",
      zip: savedLocation.zip || "",
      role: role || profile?.role || "",
    };
  };

  const getReviewSafetyResources = () => {
    const locationContext = getReviewSafetyLocationContext();
    const stateCode = getRightsStateCode(locationContext.state);
    const resources = [
      {
        title: "HUD Fair Housing",
        url: "https://www.hud.gov/program_offices/fair_housing_equal_opp",
      },
    ];

    if (stateCode === "NJ") {
      resources.push(
        {
          title: "NJ Landlord-Tenant Court Self-Help",
          url: "https://www.njcourts.gov/self-help/landlord-tenant",
        },
        {
          title: "NJ Division of Consumer Affairs",
          url: "https://www.njconsumeraffairs.gov/",
        },
        {
          title: "Legal Services of New Jersey",
          url: "https://www.lsnjlaw.org/",
        },
        {
          title: "NJ DCA Landlord-Tenant Information",
          url: "https://www.nj.gov/dca/home/landlord-tenant.shtml",
        }
      );
      if (String(locationContext.county || "").toLowerCase().includes("sussex")) {
        resources.push({
          title: "Sussex County Housing Resources",
          url: "https://www.sussex.nj.us/504/Housing",
        });
      }
    }

    return resources;
  };

  const getRoleReviewSafetyWarnings = () => {
    const activeRole = role || profile?.role || "";
    if (activeRole === "tenant") {
      return [
        "Stick to rent, repairs, communication, lease terms, safety, deposits, and habitability issues.",
        "Avoid unsupported accusations or claims you cannot document.",
      ];
    }
    if (activeRole === "landlord") {
      return [
        "Stick to documented facts like payment history, lease violations, property condition, communication, and move-out issues.",
        "Avoid discrimination, retaliation, family status, disability, medical, race, religion, or protected-class comments.",
      ];
    }
    if (activeRole === "property_manager") {
      return [
        "Stick to documented business facts only.",
        "Avoid emotional, personal, retaliatory, or protected-class claims.",
      ];
    }
    return ["Choose a role in your profile so RentDuo can show the most relevant review-safety guidance."];
  };

  const isSevereReviewModeration = (moderationData = {}) => {
    const moderation = moderationData?.moderation || moderationData || {};
    const categories = Array.isArray(moderation.categories)
      ? moderation.categories.map((item) => String(item || "").toLowerCase())
      : [];
    const severeCategories = ["threat", "threats", "harassment", "doxxing", "hate", "explicit", "illegal"];
    return (
      moderation.flagged === true &&
      (String(moderation.severity || "").toLowerCase() === "severe" ||
        categories.some((category) => severeCategories.some((term) => category.includes(term))))
    );
  };

  const shouldWarnForReviewRisk = (analysisData = {}, moderationData = {}) => {
    const analysis = analysisData?.analysis || analysisData || {};
    const moderation = moderationData?.moderation || moderationData || {};
    const riskSignals = [
      ...(Array.isArray(analysis.riskSignals) ? analysis.riskSignals : []),
      ...(Array.isArray(moderation.categories) ? moderation.categories : []),
    ].map((item) => String(item || "").toLowerCase());
    const riskLevel = String(analysis.riskLevel || moderation.severity || "").toLowerCase();
    return (
      ["high", "severe"].includes(riskLevel) ||
      moderation.flagged === true ||
      riskSignals.some((signal) =>
        ["defamation", "criminal", "retaliation", "private", "medical", "unsupported"].some((term) => signal.includes(term))
      )
    );
  };

  const runPreSubmitReviewRiskCheck = async ({ text = "", rating = 0, targetRole = "", targetId = "" }) => {
    const trimmedText = String(text || "").trim();
    if (!trimmedText) return { ok: true, analysis: null, moderation: null };

    const [analysisResult, moderationResult] = await Promise.allSettled([
      analyzeReviewCallable({
        reviewText: trimmedText,
        rating: Number(rating || 0),
        targetRole,
        reviewerRole: role || profile?.role || "",
      }),
      moderateReviewCallable({
        reviewText: trimmedText,
        rating: Number(rating || 0),
        targetUserId: targetId || "",
      }),
    ]);

    const analysisData = analysisResult.status === "fulfilled" ? analysisResult.value?.data?.analysis || null : null;
    const moderationData = moderationResult.status === "fulfilled" ? moderationResult.value?.data || null : null;

    if (analysisResult.status === "rejected") console.error("Pre-submit analyzeReview failed:", analysisResult.reason);
    if (moderationResult.status === "rejected") console.error("Pre-submit moderateReview failed:", moderationResult.reason);

    if (isSevereReviewModeration(moderationData)) {
      return {
        ok: false,
        severe: true,
        analysis: analysisData,
        moderation: moderationData,
        message: "This review contains severe policy issues such as threats, harassment, hate, doxxing, private information, or illegal advice. Please revise before posting.",
      };
    }

    if (shouldWarnForReviewRisk(analysisData, moderationData) && lastReviewRiskWarningText !== trimmedText) {
      setLastReviewRiskWarningText(trimmedText);
      return {
        ok: false,
        severe: false,
        analysis: analysisData,
        moderation: moderationData,
        message: "This review may create legal or safety risk. Please revise it to be factual and professional. If you believe it is already accurate, review it once more and submit again.",
      };
    }

    return { ok: true, analysis: analysisData, moderation: moderationData };
  };

  const renderReviewLegalAwarenessCard = (contextLabel = "review") => {
    const locationContext = getReviewSafetyLocationContext();
    const locationLabel = [
      locationContext.city || locationContext.town,
      locationContext.county ? `${locationContext.county} County` : "",
      locationContext.state,
      locationContext.zip,
    ].filter(Boolean).join(", ") || "your saved profile location";

    return (
      <div style={{ ...styles.safeReviewCard, marginTop: 14, marginBottom: 14 }}>
        <div style={styles.safeReviewTitle}>Review Safety & Legal Awareness</div>
        <p style={styles.safeReviewText}>
          Showing general review-safety guidance for {locationContext.role ? `${getRoleLabel(locationContext.role)} in ` : ""}{locationLabel}.
        </p>
        <ul style={styles.reviewSafetyList}>
          <li>Write only truthful, first-hand experiences.</li>
          <li>Avoid threats, harassment, hate speech, private personal information, medical information, and unsupported criminal accusations.</li>
          <li>Do not exaggerate or make claims you cannot support.</li>
          <li>Keep {contextLabel} feedback professional, specific, and factual.</li>
          <li>Laws vary by state, county, and city.</li>
        </ul>
        <div style={styles.safeReviewWarning}>{REVIEW_LEGAL_AWARENESS_DISCLAIMER}</div>
        <div style={styles.reviewSafetyList}>
          {getRoleReviewSafetyWarnings().map((warning) => (
            <div key={warning} style={styles.reviewSafetyResourceText}>• {warning}</div>
          ))}
        </div>
        <div style={styles.reviewSafetyResourceGrid}>
          {getReviewSafetyResources().map((resource) => (
            <button
              key={resource.url}
              type="button"
              style={styles.linkPill}
              onClick={() => openExternal(resource.url)}
            >
              {resource.title}
            </button>
          ))}
        </div>
        <label style={styles.safeReviewCheckboxRow}>
          <input
            type="checkbox"
            checked={reviewSafetyAccepted}
            onChange={(event) => setReviewSafetyAccepted(event.target.checked)}
          />
          <span>
            I understand this is general information, not legal advice, and I agree to write a truthful, factual review based on my own experience.
          </span>
        </label>
      </div>
    );
  };

  const officialApprovedReviews = useMemo(
    () => reviews.filter((item) => isReviewScoreEligible(item)),
    [reviews]
  );

  const officialMyReceivedReviews = useMemo(
    () =>
      myReceivedReviews.filter((item) => isReviewScoreEligible(item)),
    [myReceivedReviews]
  );

  const averageRating = useMemo(() => {
    if (!officialApprovedReviews.length) return 0;
    const total = officialApprovedReviews.reduce(
      (sum, item) => sum + Number(item.rating || 0),
      0
    );
    return total / officialApprovedReviews.length;
  }, [officialApprovedReviews]);

  const myAverageRating = useMemo(() => {
    if (!officialMyReceivedReviews.length) return 0;
    const total = officialMyReceivedReviews.reduce(
      (sum, item) => sum + Number(item.rating || 0),
      0
    );
    return total / officialMyReceivedReviews.length;
  }, [officialMyReceivedReviews]);

  const userReviewSummary = useMemo(() => {
    const officialReviews = reviews.filter((item) => isReviewScoreEligible(item));
    const communityReviews = reviews.filter(
      (item) => item?.status === "approved" && item?.affectsScore === false
    );
    const helpfulVotes = reviews.reduce(
      (sum, item) => sum + Number(item?.helpfulCount || 0),
      0
    );

    return {
      officialAverage: officialReviews.length
        ? officialReviews.reduce((sum, item) => sum + Number(item?.rating || 0), 0) / officialReviews.length
        : 0,
      communityAverage: communityReviews.length
        ? communityReviews.reduce((sum, item) => sum + Number(item?.rating || 0), 0) / communityReviews.length
        : 0,
      verifiedCount: officialReviews.length,
      communityCount: communityReviews.length,
      helpfulVotes,
    };
  }, [reviews]);

  const propertyReviewSummary = useMemo(() => {
    const officialReviews = propertyReviews.filter((item) => isReviewScoreEligible(item));
    const communityReviews = propertyReviews.filter((item) => item?.affectsScore === false);
    const helpfulVotes = propertyReviews.reduce(
      (sum, item) => sum + Number(item?.helpfulCount || 0),
      0
    );

    return {
      officialAverage: officialReviews.length
        ? officialReviews.reduce((sum, item) => sum + Number(item?.rating || 0), 0) / officialReviews.length
        : 0,
      communityAverage: communityReviews.length
        ? communityReviews.reduce((sum, item) => sum + Number(item?.rating || 0), 0) / communityReviews.length
        : 0,
      verifiedCount: officialReviews.length,
      communityCount: communityReviews.length,
      helpfulVotes,
    };
  }, [propertyReviews]);

  const propertyLivingExperienceSummary = useMemo(
    () => getPropertyLivingExperienceSummary(propertyReviews),
    [propertyReviews]
  );

  const selectedUserRentScore = useMemo(
    () => calculateRentScore(reviews),
    [reviews]
  );

  const myRentScore = useMemo(
    () => calculateRentScore(myReceivedReviews),
    [myReceivedReviews]
  );

  const propertyRentScore = useMemo(
    () => calculateRentScore(propertyReviews),
    [propertyReviews]
  );

  const weeklyActiveUserCount = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return allVerifications.filter((item) => {
      const ts = item?.lastActiveAt?.seconds ? item.lastActiveAt.seconds * 1000 : 0;
      return ts >= cutoff;
    }).length;
  }, [allVerifications]);

  const verifiedUserCount = useMemo(
    () =>
      allVerifications.filter((item) =>
        ["landlord_verified", "property_manager_verified", "verified"].includes(item?.verificationStatus)
      ).length,
    [allVerifications]
  );

  const rawDashboardTotals = useMemo(
    () => ({
      users: allVerifications.length,
      reviews: allReviewsCount,
      messages: allMessagesCount,
      properties: properties.length,
      propertyReviews: allPropertyReviewsCount,
      activeWeek: weeklyActiveUserCount,
      verified: verifiedUserCount,
      clicks: partnerClickCount,
    }),
    [
      allVerifications.length,
      allReviewsCount,
      allMessagesCount,
      properties.length,
      allPropertyReviewsCount,
      weeklyActiveUserCount,
      verifiedUserCount,
      partnerClickCount,
    ]
  );

  const growthTrackingSinceLabel = useMemo(() => {
    const resetAt = growthBaseline?.resetAt;
    if (!resetAt) return "All time";
    const dateValue = resetAt?.seconds
      ? new Date(resetAt.seconds * 1000)
      : new Date(resetAt);
    if (Number.isNaN(dateValue.getTime())) return "Baseline set";
    return dateValue.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [growthBaseline]);

  const baselineDashboardTotals = useMemo(
    () => ({
      users: Number(growthBaseline?.baselineUsers || 0),
      reviews: Number(growthBaseline?.baselineReviews || 0),
      messages: Number(growthBaseline?.baselineMessages || 0),
      properties: Number(growthBaseline?.baselineProperties || 0),
      propertyReviews: Number(growthBaseline?.baselinePropertyReviews || 0),
      activeWeek: Number(growthBaseline?.baselineActiveWeek || 0),
      verified: Number(growthBaseline?.baselineVerified || 0),
      clicks: Number(growthBaseline?.baselineClicks || 0),
    }),
    [growthBaseline]
  );

  const displayedDashboardTotals = useMemo(() => {
    if (showRawGrowthTotals) return rawDashboardTotals;

    return {
      users: Math.max(0, rawDashboardTotals.users - baselineDashboardTotals.users),
      reviews: Math.max(0, rawDashboardTotals.reviews - baselineDashboardTotals.reviews),
      messages: Math.max(0, rawDashboardTotals.messages - baselineDashboardTotals.messages),
      properties: Math.max(0, rawDashboardTotals.properties - baselineDashboardTotals.properties),
      propertyReviews: Math.max(0, rawDashboardTotals.propertyReviews - baselineDashboardTotals.propertyReviews),
      activeWeek: Math.max(0, rawDashboardTotals.activeWeek - baselineDashboardTotals.activeWeek),
      verified: Math.max(0, rawDashboardTotals.verified - baselineDashboardTotals.verified),
      clicks: Math.max(0, rawDashboardTotals.clicks - baselineDashboardTotals.clicks),
    };
  }, [rawDashboardTotals, baselineDashboardTotals, showRawGrowthTotals]);

  useEffect(() => {
    if (!isAdmin) return;
    console.log("Growth Dashboard totals", {
      currentTotals: rawDashboardTotals,
      baselineTotals: baselineDashboardTotals,
      displayedTotals: displayedDashboardTotals,
      showRawGrowthTotals,
    });
    console.log("Growth Dashboard adjusted displayed totals", displayedDashboardTotals);
  }, [isAdmin, rawDashboardTotals, baselineDashboardTotals, displayedDashboardTotals, showRawGrowthTotals]);

  const dashboardStats = useMemo(
    () => [
      { label: "Users", value: displayedDashboardTotals.users, accent: "blue" },
      { label: "Reviews", value: displayedDashboardTotals.reviews, accent: "green" },
      { label: "Messages", shortLabel: "Msgs", value: displayedDashboardTotals.messages, accent: "blue" },
      { label: "Properties", shortLabel: "Props", value: displayedDashboardTotals.properties, accent: "green" },
      { label: "Prop Reviews", value: displayedDashboardTotals.propertyReviews, accent: "blue" },
      { label: "Active Week", value: displayedDashboardTotals.activeWeek, accent: "green" },
      { label: "Verified", value: displayedDashboardTotals.verified, accent: "blue" },
      { label: "Partner Clicks", shortLabel: "Clicks", value: displayedDashboardTotals.clicks, accent: "green" },
    ],
    [displayedDashboardTotals]
  );

  const responsiveStatGridStyle = useMemo(
    () => ({
      display: "grid",
      gridTemplateColumns: isPhoneMobile
        ? "repeat(2, minmax(0, 1fr))"
        : "repeat(auto-fit, minmax(140px, 1fr))",
      gap: isMobile ? 12 : 16,
      width: "100%",
      marginBottom: 18,
      alignItems: "stretch",
    }),
    [isMobile, isPhoneMobile]
  );

  const responsiveStatCardStyle = useMemo(
    () => ({
      ...styles.mapSelectionBox,
      marginTop: 0,
      minHeight: 110,
      padding: 16,
      borderRadius: 18,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      whiteSpace: "normal",
      wordBreak: "normal",
      overflowWrap: "normal",
      hyphens: "none",
      lineHeight: 1.2,
    }),
    []
  );

  const responsiveStatLabelStyle = useMemo(
    () => ({
      ...styles.strongHeading,
      whiteSpace: "normal",
      wordBreak: "normal",
      overflowWrap: "normal",
      hyphens: "none",
      lineHeight: 1.2,
      maxWidth: "100%",
    }),
    []
  );

  const responsiveStatValueStyle = useMemo(
    () => ({
      ...styles.smallText,
      whiteSpace: "normal",
      wordBreak: "normal",
      overflowWrap: "normal",
      hyphens: "none",
      lineHeight: 1.2,
      fontWeight: 800,
    }),
    []
  );

  const buildRentDuoAIContext = () => {
    const selectedPropertyReviews = selectedProperty?.id
      ? allPropertyReviews
          .filter((item) => item?.propertyId === selectedProperty.id)
          .slice(0, 5)
          .map((item) => ({
            rating: Number(item?.rating || 0),
            verified: Boolean(item?.reviewerVerified),
            summary: String(item?.text || "").slice(0, 240),
          }))
      : [];

    return {
      userRole: role || "",
      verificationStatus: verificationStatus || profile?.verificationStatus || "",
      userLocation: {
        role: role || profile?.role || "",
        state: profileLocation?.state || profile?.location?.state || profile?.state || "",
        county: profileLocation?.county || profile?.location?.county || "",
        city: profileLocation?.city || profile?.location?.city || profile?.city || city || "",
        town: profileLocation?.town || profile?.location?.town || "",
        zip: profileLocation?.zip || profile?.location?.zip || "",
        formattedAddress: profileLocation?.formattedAddress || profile?.location?.formattedAddress || profile?.city || city || "",
      },
      selectedProperty: selectedProperty
        ? {
            id: selectedProperty.id || "",
            title: selectedProperty.title || "",
            address: selectedProperty.address || selectedProperty.fullAddress || "",
            city: selectedProperty.city || "",
            state: selectedProperty.state || "",
            claimStatus: selectedProperty.claimStatus || "",
            reviewCount: getPropertyRatingData(selectedProperty.id).count,
          }
        : null,
      propertyReviews: selectedPropertyReviews,
      trustScore: Number(profile?.trustScore || profile?.rentScore || 0),
      riskScore: selectedTenantManagerScore?.score || 0,
      propertyHistorySummary: selectedPropertyReviews.length
        ? `${selectedPropertyReviews.length} recent public property review(s) are available for this selected property.`
        : "",
      pendingReviewText: reviewText || propertyReviewText || "",
    };
  };

  const handleAskRentDuoAI = async (promptOverride = "") => {
    const prompt = String(promptOverride || aiAssistantInput || "").trim();
    if (!prompt) {
      setAiAssistantError("Ask RentDuo AI a question first.");
      return;
    }

    if (!user?.uid) {
      setIsAiSuggestedOpen(false);
      setAiAssistantMessages((prev) => [
        ...prev,
        { role: "user", text: prompt },
        { role: "assistant", text: "Please log in to use RentDuo AI so guidance can stay tied to your account safely." },
      ]);
      setAiAssistantInput("");
      return;
    }

    const trimmedPrompt = prompt.slice(0, 2000);
    setIsAiSuggestedOpen(false);
    setAiAssistantInput("");
    setAiAssistantError("");
    setAiAssistantLoading(true);
    setAiAssistantMessages((prev) => [...prev, { role: "user", text: trimmedPrompt }]);

    try {
      const result = await askRentDuoAICallable({
        prompt: trimmedPrompt,
        context: buildRentDuoAIContext(),
      });
      const answer = String(result.data.answer || "").trim();
      setAiAssistantMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: answer || "I could not generate an answer right now. Try asking a shorter RentDuo-specific question.",
        },
      ]);
    } catch (error) {
      console.error("RentDuo AI failed:", error);
      const errorDetails = error?.details
        ? typeof error.details === "string"
          ? error.details
          : JSON.stringify(error.details)
        : "";
      const fallbackMessage =
        [
          `code: ${error?.code || "unknown"}`,
          `message: ${error?.message || "RentDuo AI is unavailable right now. Check the Firebase function and OPENAI_API_KEY secret."}`,
          `details: ${errorDetails || "none"}`,
        ].join("\n");
      setAiAssistantError(fallbackMessage);
      setAiAssistantMessages((prev) => [
        ...prev,
        { role: "assistant", text: fallbackMessage },
      ]);
    } finally {
      setAiAssistantLoading(false);
    }
  };

  const selectedUserTrustSnapshot = useMemo(
    () =>
      buildUserTrustSnapshot({
        targetProfile: selectedUser || {},
        approvedReviews: reviews,
      }),
    [selectedUser, reviews]
  );

  const myTrustSnapshot = useMemo(
    () =>
      buildUserTrustSnapshot({
        targetProfile: profile || {},
        approvedReviews: myReceivedReviews,
      }),
    [profile, myReceivedReviews]
  );

  const foundUserTrustSnapshot = useMemo(
    () =>
      buildUserTrustSnapshot({
        targetProfile: foundUser || {},
        approvedReviews: [],
      }),
    [foundUser]
  );

  const selectedReviewCategories = useMemo(
    () => getReviewCategoriesForRole(selectedUser?.role || ""),
    [selectedUser]
  );

  const searchAutoPopulateOptions = useMemo(() => {
    const values = new Set();

    properties.forEach((property) => {
      const address = String(property?.address || "").trim();
      const title = String(property?.title || "").trim();
      const cityValue = String(property?.city || "").trim();
      const stateValue = String(property?.state || "").trim();

      if (address) values.add(address);
      if (title) values.add(title);
      if (cityValue && stateValue) values.add(`${cityValue}, ${stateValue}`);
      if (cityValue) values.add(cityValue);
    });

    [profile, foundUser, selectedUser].forEach((item) => {
      if (!item) return;
      const cityValue = String(item?.city || "").trim();
      const stateValue = String(item?.state || "").trim();
      const publicAreas = getPublicAreaSummary(item);
      if (cityValue && stateValue) values.add(`${cityValue}, ${stateValue}`);
      if (cityValue) values.add(cityValue);
      if (publicAreas) {
        normalizeAreaTags(publicAreas).forEach((area) => values.add(area));
      }
    });

    return Array.from(values).slice(0, 200);
  }, [properties, profile, foundUser, selectedUser]);


  const selectedUserReviewAllowed = useMemo(() => {
    if (!selectedUser) return false;
    return canReviewTargetRole(role, selectedUser?.role || "");
  }, [role, selectedUser]);

  const selectedUserAlreadyReviewed = useMemo(() => {
    if (!user?.uid || !selectedUser?.uid) return false;

    if (submittedReviewTargetIds?.[selectedUser.uid]) return true;

    const priorReviews = reviews.filter(
      (item) => item?.reviewerUserId === user.uid && item?.targetUserId === selectedUser.uid
    );
    return getReviewCooldownState(priorReviews).withinCooldown;
  }, [submittedReviewTargetIds, reviews, user, selectedUser]);

  const reviewOverallRating = useMemo(
    () => calculateCategoryOverall(reviewCategoryRatings),
    [reviewCategoryRatings]
  );

  useEffect(() => {
    const nextRatings = buildInitialCategoryRatings(selectedUser?.role || "");
    setReviewCategoryRatings(nextRatings);
    setRatingValue(calculateCategoryOverall(nextRatings) || 0);
    setRentalFeedback(RENTAL_FEEDBACK_DEFAULT);
    setReviewSafetyAccepted(false);
  }, [selectedUser]);

  useEffect(() => {
    if (activeTab !== "reviews") return;
    if (!selectedProperty?.address) return;
    if (searchQuery.trim()) return;

    setSearchQuery(selectedProperty.address);
  }, [activeTab, selectedProperty, searchQuery]);

  useEffect(() => {
    const available = getProfileStatusOptions(role).map((item) => item.value);
    if (!available.includes(profileStatus)) {
      setProfileStatus(getDefaultProfileStatus(role));
    }
  }, [role, profileStatus]);


  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    if (showReplyModal) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [showReplyModal]);

  useEffect(() => {
    if (!["reviews", "messages"].includes(activeTab)) return;

    const term = String(searchQuery || "").trim();
    const area = String(searchArea || "").trim();
    const hasFilters = searchRoleFilter !== "all" || searchStatusFilter !== "all";

    if (!term && !area && !hasFilters) {
      setFoundUser(null);
      setSearchResults([]);
      setSearchMessage("");
      return;
    }

    const timer = window.setTimeout(() => {
      handleFindUser();
    }, 250);

    return () => window.clearTimeout(timer);
  }, [searchQuery, searchArea, searchRoleFilter, searchStatusFilter, activeTab]);

  const liveFeedItems = useMemo(() => {
    const propertyById = new Map(
      visibleProperties.map((item) => [item.id, item])
    );
    const propertyReviewCountById = new Map();
    allPropertyReviews.forEach((item) => {
      const propertyId = item?.propertyId || "";
      if (!propertyId) return;
      propertyReviewCountById.set(propertyId, (propertyReviewCountById.get(propertyId) || 0) + 1);
    });
    const publicProfileByUid = new Map();
    publicFeedProfiles.forEach((item) => {
      const uidValue = item?.uid || item?.id || "";
      if (uidValue) publicProfileByUid.set(uidValue, item);
    });

    const isSpecificPublicProfileVerified = (item = null) =>
      Boolean(item && (item?.verified === true || item?.verificationStatus === "verified"));

    const getPublicFeedProfile = (uidValue = "") =>
      uidValue ? publicProfileByUid.get(uidValue) || null : null;

    const getPublicFeedName = (uidValue = "", fallback = "RentDuo member") => {
      const feedProfile = getPublicFeedProfile(uidValue);
      return feedProfile?.displayName || fallback || "RentDuo member";
    };

    const getPublicFeedPhoto = (uidValue = "", fallback = "") => {
      const feedProfile = getPublicFeedProfile(uidValue);
      return feedProfile?.photoURL || fallback || "";
    };

    const getPublicFeedVerified = (uidValue = "") =>
      isSpecificPublicProfileVerified(getPublicFeedProfile(uidValue));

    const getPublicFeedVerificationStatus = (uidValue = "") =>
      getPublicFeedVerified(uidValue) ? "verified" : "unverified";

    const areaTerm = String(searchArea || city || profile?.city || "")
      .trim()
      .toLowerCase();

    const inArea = (item = {}) => {
      if (!areaTerm) return true;
      const haystack = [
        item.city || "",
        item.state || "",
        item.title || "",
        item.subtitle || "",
        item.text || "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(areaTerm);
    };

    const storedFeedEvents = publicFeedEvents.map((item) => {
      const isPropertyActivity = ["property_added", "property_claimed", "property_review", "property_rental_listed"].includes(item?.type);
      const isProfileActivity = ["user_review", "verified", "profile_update"].includes(item?.type);
      const actorUserId = item?.actorUserId || item?.reviewerUserId || item?.authorId || item?.createdBy || item?.userId || "";
      const targetUserId = item?.targetUserId || item?.targetId || item?.reviewTargetUserId || "";
      const linkedProperty = propertyById.get(item?.propertyId || "");

      return {
        id: `feed-${item.id}`,
        type: item?.type || "activity",
        sortMs: getFeedTimestampMs(item?.createdAt || item?.updatedAt),
        heading:
          item?.heading ||
          (item?.type === "property_rental_listed"
            ? "New rental listing posted"
            : item?.type === "property_added"
            ? "New property added"
            : item?.type === "property_claimed"
            ? "Property claim activity"
            : item?.type === "verified"
            ? "New verified member"
            : "RentDuo activity"),
        title: item?.title || item?.propertyTitle || item?.targetDisplayName || "RentDuo activity",
        subtitle: item?.subtitle || item?.propertyAddress || formatCityState(item) || "Live update",
        text: item?.text || "New public activity on RentDuo.",
        rating: Number(item?.rating || 0) || null,
        actorUserId,
        actorDisplayName: getPublicFeedName(actorUserId, item?.actorDisplayName || item?.reviewerDisplayName || "RentDuo member"),
        actorPhotoURL: getPublicFeedPhoto(actorUserId, item?.actorPhotoURL || item?.reviewerPhotoURL || ""),
        actorVerified: getPublicFeedVerified(actorUserId),
        city: item?.city || linkedProperty?.city || "",
        state: item?.state || linkedProperty?.state || "",
        propertyId: item?.propertyId || "",
        propertyImageUrl: item?.propertyImageUrl || item?.imageUrl || linkedProperty?.imageUrl || "",
        propertyAddress: item?.propertyAddress || linkedProperty?.fullAddress || linkedProperty?.address || "",
        isForRent: item?.isForRent === true || linkedProperty?.isForRent === true,
        monthlyRent: Number.isFinite(Number(item?.monthlyRent ?? linkedProperty?.monthlyRent))
          ? Number(item?.monthlyRent ?? linkedProperty?.monthlyRent)
          : null,
        propertyReviewCount: propertyReviewCountById.get(item?.propertyId || "") || 0,
        targetUserId,
        targetDisplayName: getPublicFeedName(targetUserId, item?.targetDisplayName || ""),
        targetVerified: getPublicFeedVerified(targetUserId),
        targetVerificationStatus: getPublicFeedVerificationStatus(targetUserId),
        actionLabel: isPropertyActivity ? "Open Property" : isProfileActivity ? "View Profile" : "View Activity",
        actionTone: item?.type === "verified" ? "verified" : isPropertyActivity ? "property" : "review",
        timestampLabel: formatFeedTimeAgo(item?.createdAt || item?.updatedAt),
      };
    });

    const propertyEvents = visibleProperties.map((item) => {
      const eventType = item?.isForRent === true
        ? "property_rental_listed"
        : ["claimed", "approved", "verified", "pending", "pending_claim", "pending_review"].includes(String(item?.claimStatus || ""))
        ? "property_claimed"
        : "property_added";
      const actorUserId =
        item?.claimRequestedByUserId ||
        item?.communityAddedByUserId ||
        item?.claimedByUserId ||
        item?.ownerId ||
        "";
      return {
        id: `property-${item.id}-${eventType}`,
        type: eventType,
        sortMs: getFeedTimestampMs(item?.claimRequestedAt || item?.createdAt || item?.updatedAt),
        heading: eventType === "property_rental_listed" ? "New rental listing posted" : eventType === "property_claimed" ? "Property claim activity" : "New property added",
        title: item?.title || item?.address || "Property",
        subtitle: item?.fullAddress || formatCityState(item) || item?.address || "Community added property",
        text:
          eventType === "property_rental_listed"
            ? `${item?.title || item?.address || "A property"} is listed for rent${item?.monthlyRent ? ` at ${formatMonthlyRent(item.monthlyRent)}` : ""}.`
            : item?.description ||
          (eventType === "property_claimed"
            ? "A landlord or property manager started claiming this property."
            : "A new property was added to the community map."),
        rating: null,
        actorDisplayName:
          getPublicFeedName(
            actorUserId,
            item?.claimRequestedByDisplayName ||
              item?.communityAddedByDisplayName ||
              item?.ownerDisplayName ||
              "RentDuo member"
          ),
        actorUserId,
        actorPhotoURL: getPublicFeedPhoto(actorUserId, item?.ownerPhotoURL || ""),
        actorVerified: getPublicFeedVerified(actorUserId),
        city: item?.city || "",
        state: item?.state || "",
        propertyId: item?.id || "",
        propertyImageUrl: item?.imageUrl || "",
        propertyAddress: item?.fullAddress || item?.address || "",
        isForRent: item?.isForRent === true,
        monthlyRent: Number.isFinite(Number(item?.monthlyRent)) ? Number(item.monthlyRent) : null,
        propertyReviewCount: propertyReviewCountById.get(item?.id || "") || 0,
        actionLabel: "Open Property",
        actionTone: "property",
        timestampLabel: formatFeedTimeAgo(item?.claimRequestedAt || item?.createdAt || item?.updatedAt),
      };
    });

    const propertyReviewEvents = allPropertyReviews.map((item) => {
      const linkedProperty = propertyById.get(item?.propertyId);
      const actorUserId = item?.reviewerUserId || item?.authorId || "";
      const actorVerified = getPublicFeedVerified(actorUserId);
      return {
        id: `property-review-${item.id}`,
        type: "property_review",
        sortMs: getFeedTimestampMs(item?.createdAt || item?.updatedAt),
        heading: item?.isUpdatedReview
          ? "Updated Review"
          : actorVerified
          ? "Verified property review"
          : "Community property rating",
        title:
          item?.propertyTitle ||
          linkedProperty?.title ||
          item?.propertyAddress ||
          "Property review",
        subtitle:
          item?.propertyAddress ||
          linkedProperty?.fullAddress ||
          formatCityState(linkedProperty || {}) ||
          "Property activity",
        text:
          item?.text ||
          `${actorVerified ? "Verified" : "Community"} reviewer left ${Number(item?.rating || 0)} stars.`,
        rating: Number(item?.rating || 0),
        actorUserId,
        actorDisplayName: getPublicFeedName(actorUserId, item?.reviewerDisplayName || "Anonymous"),
        actorPhotoURL: getPublicFeedPhoto(actorUserId, item?.reviewerPhotoURL || ""),
        actorVerified,
        city: linkedProperty?.city || "",
        state: linkedProperty?.state || "",
        propertyId: item?.propertyId || "",
        propertyImageUrl: linkedProperty?.imageUrl || item?.propertyImageUrl || "",
        propertyAddress:
          item?.propertyAddress ||
          linkedProperty?.fullAddress ||
          linkedProperty?.address ||
          "",
        propertyReviewCount: propertyReviewCountById.get(item?.propertyId || "") || 0,
        sourceReview: { id: item.id, ...item },
        sourceCollection: "propertyReviews",
        actionLabel: "View Property",
        actionTone: "review",
        timestampLabel: formatFeedTimeAgo(item?.createdAt || item?.updatedAt),
      };
    });

    const userReviewEvents = publicFeedUserReviews.map((item) => {
      const actorUserId = item?.reviewerUserId || item?.authorId || "";
      const targetUserId = item?.targetUserId || item?.targetId || "";
      const actorVerified = getPublicFeedVerified(actorUserId);
      const targetVerified = getPublicFeedVerified(targetUserId);

      return {
        id: `user-review-${item.id}`,
        type: "user_review",
        sortMs: getFeedTimestampMs(item?.createdAt || item?.updatedAt),
        heading: item?.isUpdatedReview
          ? "Updated Review"
          : actorVerified
          ? "Verified user review"
          : "Community user rating",
        title: getPublicFeedName(targetUserId, item?.targetDisplayName || "User review"),
        subtitle: "Public profile review",
        text:
          item?.text ||
          `${actorVerified ? "Verified" : "Community"} reviewer left ${Number(item?.rating || 0)} stars.`,
        rating: Number(item?.rating || 0),
        actorUserId,
        actorDisplayName: getPublicFeedName(actorUserId, item?.reviewerDisplayName || "Anonymous"),
        actorPhotoURL: getPublicFeedPhoto(actorUserId, item?.reviewerPhotoURL || ""),
        actorVerified,
        city: "",
        state: "",
        targetUserId,
        targetEmail: "",
        targetDisplayName: getPublicFeedName(targetUserId, item?.targetDisplayName || ""),
        targetVerified,
        targetVerificationStatus: getPublicFeedVerificationStatus(targetUserId),
        sourceReview: { id: item.id, ...item },
        sourceCollection: "reviews",
        actionLabel: "View Profile",
        actionTone: "review",
        timestampLabel: formatFeedTimeAgo(item?.createdAt || item?.updatedAt),
      };
    });

    const reviewReplyEvents = [...publicFeedUserReviews, ...allPropertyReviews]
      .filter((item) => item?.response?.text)
      .map((item) => {
        const responderUserId = item?.response?.responderUserId || item?.targetUserId || item?.ownerId || "";
        const isPropertyReply = Boolean(item?.propertyId);
        return {
          id: `reply-${item.id}`,
          type: "review_reply",
          sortMs: getFeedTimestampMs(item?.response?.updatedAt || item?.response?.createdAt || item?.updatedAt),
          heading: "New review reply",
          title: isPropertyReply ? item?.propertyTitle || "Property reply" : item?.targetDisplayName || "Profile reply",
          subtitle: "Right-to-respond activity",
          text: item?.response?.text || "A public response was added to a review.",
          rating: Number(item?.rating || 0),
          actorUserId: responderUserId,
          actorDisplayName: getPublicFeedName(responderUserId, item?.response?.responderDisplayName || "RentDuo member"),
          actorPhotoURL: getPublicFeedPhoto(responderUserId, ""),
          actorVerified: getPublicFeedVerified(responderUserId),
          city: "",
          state: "",
          propertyId: item?.propertyId || "",
          targetUserId: item?.targetUserId || "",
          targetDisplayName: getPublicFeedName(item?.targetUserId || "", item?.targetDisplayName || ""),
          targetVerified: getPublicFeedVerified(item?.targetUserId || ""),
          targetVerificationStatus: getPublicFeedVerificationStatus(item?.targetUserId || ""),
          actionLabel: isPropertyReply ? "Open Property" : "View Profile",
          actionTone: "review",
          timestampLabel: formatFeedTimeAgo(item?.response?.updatedAt || item?.response?.createdAt || item?.updatedAt),
        };
      });

    const verificationEvents = publicFeedProfiles
      .filter((item) => isSpecificPublicProfileVerified(item))
      .map((item) => ({
        id: `verified-${item.uid || item.id}`,
        type: "verified",
        sortMs: getFeedTimestampMs(item?.updatedAt || item?.verificationSubmittedAt || item?.createdAt),
        heading: "New verified member",
        title: item?.displayName || "Verified member",
        subtitle: formatCityState(item) || "RentDuo",
        text:
          item?.role === "property_manager"
            ? "A property manager profile is now verified on RentDuo."
            : item?.role === "landlord"
            ? "A landlord profile is now verified on RentDuo."
            : "A member profile is now verified on RentDuo.",
        rating: null,
        actorDisplayName: item?.displayName || "Verified member",
        actorPhotoURL: item?.photoURL || "",
        actorVerified: true,
        city: item?.city || "",
        state: item?.state || "",
        targetUserId: item?.uid || item?.id || "",
        targetEmail: "",
        targetDisplayName: item?.displayName || "",
        targetVerified: true,
        targetVerificationStatus: getEffectiveVerificationStatus(item),
        actionLabel: "View Profile",
        actionTone: "verified",
        timestampLabel: formatFeedTimeAgo(item?.updatedAt || item?.verificationSubmittedAt || item?.createdAt),
      }));

    const combined = [
      ...storedFeedEvents,
      ...propertyEvents,
      ...propertyReviewEvents,
      ...userReviewEvents,
      ...reviewReplyEvents,
      ...verificationEvents,
    ].filter((item) => item.sortMs > 0);

    const byFilter = combined.filter((item) => {
      if (liveFeedFilter === "reviews") {
        return item.type === "property_review" || item.type === "user_review" || item.type === "review_reply";
      }
      if (liveFeedFilter === "properties") {
        return item.type === "property_added" || item.type === "property_claimed" || item.type === "property_rental_listed";
      }
      if (liveFeedFilter === "verified") {
        return item.type === "verified";
      }
      if (liveFeedFilter === "nearby") {
        return inArea(item);
      }
      return true;
    });

    const withArea =
      liveFeedFilter === "nearby" && areaTerm
        ? byFilter.filter((item) => inArea(item))
        : byFilter;

    return withArea
      .sort((a, b) => b.sortMs - a.sortMs)
      .slice(0, 12);
  }, [
    visibleProperties,
    publicFeedEvents,
    allPropertyReviews,
    publicFeedUserReviews,
    publicFeedProfiles,
    searchArea,
    city,
    profile?.city,
    liveFeedFilter,
  ]);

  const isFeedLoading = useMemo(
    () => Object.values(feedLoadingSources).some(Boolean),
    [feedLoadingSources]
  );

const averagePropertyRating = useMemo(() => {
    const officialPropertyReviews = propertyReviews.filter(
      (item) => item?.affectsScore !== false
    );

    if (!officialPropertyReviews.length) return 0;

    const total = officialPropertyReviews.reduce(
      (sum, item) => sum + Number(item.rating || 0),
      0
    );
    return total / officialPropertyReviews.length;
  }, [propertyReviews]);

  const propertyRatingsById = useMemo(() => {
    const grouped = {};

    allPropertyReviews.forEach((item) => {
      const propertyId = item.propertyId;
      if (!propertyId) return;

      const rating = Number(item.rating || 0);
      const affectsScore = isReviewScoreEligible(item);

      if (!grouped[propertyId]) {
        grouped[propertyId] = {
          officialTotal: 0,
          officialCount: 0,
          communityTotal: 0,
          communityCount: 0,
          reviews: [],
        };
      }

      if (affectsScore) {
        grouped[propertyId].officialTotal += rating;
        grouped[propertyId].officialCount += 1;
      } else {
        grouped[propertyId].communityTotal += rating;
        grouped[propertyId].communityCount += 1;
      }

      grouped[propertyId].reviews.push(item);
    });

    const result = {};

    Object.keys(grouped).forEach((propertyId) => {
      const bucket = grouped[propertyId];
      result[propertyId] = {
        average: bucket.officialCount ? bucket.officialTotal / bucket.officialCount : 0,
        count: bucket.officialCount,
        communityAverage: bucket.communityCount
          ? bucket.communityTotal / bucket.communityCount
          : 0,
        communityCount: bucket.communityCount,
        reviews: bucket.reviews.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        }),
      };
    });

    return result;
  }, [allPropertyReviews]);

  const getPropertyRatingData = (propertyId) => {
    return (
      propertyRatingsById[propertyId] || {
        average: 0,
        count: 0,
        communityAverage: 0,
        communityCount: 0,
        reviews: [],
      }
    );
  };

  const getPropertyReviewBadgeLabel = (item) => {
    if (item?.reviewerVerified) return "Verified Reviewer";
    return "Unverified Reviewer";
  };

  const getPropertyReviewBadgeStyle = (item) => {
    if (item?.reviewerVerified) {
      return styles.propertyReviewVerifiedBadge;
    }
    return styles.propertyReviewUnverifiedBadge;
  };

  const managedProperties = useMemo(() => {
    if (!user?.uid) return [];
    return visibleProperties.filter((item) => {
      if (!item) return false;
      return (
        item.ownerId === user.uid ||
        item.claimedByUserId === user.uid ||
        item.claimRequestedByUserId === user.uid ||
        item.managerUserId === user.uid ||
        (role === "property_manager" && item.communityAddedByUserId === user.uid)
      );
    });
  }, [visibleProperties, user, role]);

  const managerPortfolioStats = useMemo(() => {
    return {
      totalProperties: managedProperties.length,
      claimedProperties: managedProperties.filter(
        (item) => hasApprovedPropertyClaim(item)
      ).length,
      pendingClaims: managedProperties.filter((item) => ["pending", "pending_claim", "pending_review"].includes(String(item?.claimStatus || ""))).length,
      portfolioReviewCount: allPropertyReviews.filter((item) =>
        managedProperties.some((property) => property.id === item?.propertyId)
      ).length,
    };
  }, [managedProperties, allPropertyReviews]);

  const tenantProfilesForManager = useMemo(() => {
    const seen = new Map();

    const addCandidate = (candidate) => {
      if (!candidate?.uid) return;
      if ((candidate?.role || "") !== "tenant") return;
      if (!seen.has(candidate.uid)) {
        const scorecard = getManagerTenantScorecard({
          targetUser: candidate,
          reviews,
          properties,
        });
        seen.set(candidate.uid, { ...candidate, managerScorecard: scorecard });
      }
    };

    if (selectedUser?.uid) addCandidate(selectedUser);
    if (foundUser?.uid) addCandidate(foundUser);

    return Array.from(seen.values()).sort(
      (a, b) => Number(a?.managerScorecard?.score || 0) - Number(b?.managerScorecard?.score || 0)
    );
  }, [selectedUser, foundUser, reviews, properties]);

  const selectedTenantManagerScore = useMemo(() => {
    const activeTenant = selectedManagerTenant || selectedUser;
    if (role !== "property_manager" || !activeTenant?.uid || (activeTenant?.role || "") !== "tenant") {
      return null;
    }

    return getManagerTenantScorecard({
      targetUser: activeTenant,
      reviews,
      properties,
    });
  }, [role, selectedManagerTenant, selectedUser, reviews, properties]);

  const managerInsights = useMemo(() => {
    const riskyTenants = tenantProfilesForManager.filter(
      (item) =>
        item?.managerScorecard?.label === "Moderate Risk" ||
        item?.managerScorecard?.label === "High Risk"
    ).length;

    const totalApprovedReviews = tenantProfilesForManager.reduce(
      (sum, item) => sum + Number(item?.managerScorecard?.approvedReviewCount || 0),
      0
    );

    const totalLateSignals = tenantProfilesForManager.reduce(
      (sum, item) => sum + Number(item?.managerScorecard?.latePaymentMentions || 0),
      0
    );

    const latePaymentRate = totalApprovedReviews
      ? Math.round((totalLateSignals / totalApprovedReviews) * 100)
      : 0;

    return {
      riskyTenants,
      latePaymentRate,
      topPropertyTitle: managedProperties[0]?.title || "No property yet",
    };
  }, [tenantProfilesForManager, managedProperties]);

  function buildUserTrustSnapshot({ targetProfile = {}, approvedReviews = [] }) {
    const averageRatingValue = approvedReviews.length
      ? approvedReviews.reduce((sum, item) => sum + Number(item?.rating || 0), 0) /
        approvedReviews.length
      : Number(targetProfile?.averageRating || 0);

    const reviewCountValue = approvedReviews.length || Number(targetProfile?.reviewCount || 0);
    const flaggedReviewCount = approvedReviews.filter((item) => item?.reported === true).length;
    const averageAiRiskScore = approvedReviews.length
      ? approvedReviews.reduce((sum, item) => sum + Number(item?.aiRiskScore || 0), 0) /
        approvedReviews.length
      : Number(targetProfile?.averageAiRiskScore || 0);

    const safeAverageRating = Math.max(0, Math.min(5, Number(averageRatingValue || 0)));
    const safeFlagCount = Math.max(
      flaggedReviewCount,
      Number(targetProfile?.flagCount || 0)
    );
    const isVerifiedUser = Boolean(targetProfile?.verified || targetProfile?.isVerified);

    const fallbackScore = Math.max(
      0,
      Math.min(
        100,
        Math.round(
          safeAverageRating * 20 +
            (reviewCountValue >= 10 ? 5 : reviewCountValue >= 3 ? 2 : 0) +
            (isVerifiedUser ? 10 : 0) -
            safeFlagCount * 5 -
            (averageAiRiskScore / 100) * 12
        )
      )
    );

    let trustScoreValue = Number(
      safeGetUserTrustScore({
        averageRating: safeAverageRating,
        reviewCount: reviewCountValue,
        isVerified: isVerifiedUser,
        flagCount: safeFlagCount,
        aiRiskScore: averageAiRiskScore,
      })
    );

    if (!Number.isFinite(trustScoreValue)) {
      trustScoreValue = fallbackScore;
    }

    trustScoreValue = Math.max(0, Math.min(100, Math.round(trustScoreValue)));

    return {
      averageRating: Number(safeAverageRating.toFixed(2)),
      reviewCount: reviewCountValue,
      averageAiRiskScore: Number(averageAiRiskScore.toFixed(2)),
      flagCount: safeFlagCount,
      rentScore: trustScoreValue,
      trustScore: trustScoreValue,
      trustBadge: safeGetTrustBadge(trustScoreValue),
      trustLevel:
        trustScoreValue >= 80 ? "High" : trustScoreValue >= 50 ? "Medium" : "Low",
    };
  }

  const syncUserTrustProfile = async (targetUserId) => {
    if (!targetUserId) return null;

    const profileRef = doc(db, "publicProfiles", targetUserId);
    const [profileSnap, reviewsSnap] = await Promise.all([
      getDoc(profileRef),
      getDocs(query(collection(db, "reviews"), where("targetUserId", "==", targetUserId))),
    ]);

    const targetProfile = profileSnap.exists()
      ? { uid: targetUserId, ...profileSnap.data() }
      : { uid: targetUserId };

    const approvedReviews = reviewsSnap.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .filter((item) => item?.status === "approved");

    const trustSnapshot = buildUserTrustSnapshot({
      targetProfile,
      approvedReviews,
    });

    // Trust metrics are displayed locally here. Persisting score fields from the
    // browser would let a user forge trust/admin-adjacent data; server code should
    // own durable trust recalculation.

    if (user?.uid === targetUserId) {
      setProfile((prev) => ({ ...(prev || {}), ...trustSnapshot }));
    }

    if (selectedUser?.uid === targetUserId) {
      setSelectedUser((prev) => ({ ...(prev || {}), ...trustSnapshot }));
    }

    if (foundUser?.uid === targetUserId) {
      setFoundUser((prev) => ({ ...(prev || {}), ...trustSnapshot }));
    }

    return trustSnapshot;
  };

  const switchAuthMode = (nextMode) => {
    setAuthMode(nextMode);
    window.setTimeout(() => {
      authFormRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const saveDisclaimerAcceptance = async (uid) => {
    if (!uid) return;

    await setDoc(
      doc(db, "profiles", uid),
      {
        disclaimerAccepted: true,
        disclaimerAcceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const hasLocalDisclaimerAcceptance = () =>
    window.localStorage.getItem(DISCLAIMER_ACCEPTED_STORAGE_KEY) === "true";

  const acceptDisclaimerLocally = () => {
    window.localStorage.setItem(DISCLAIMER_ACCEPTED_STORAGE_KEY, "true");
    setShowAuthDisclaimer(false);
    setDisclaimerHasScrolledToEnd(false);
  };

  const handleAcceptDisclaimer = async () => {
    acceptDisclaimerLocally();

    if (user?.uid) {
      try {
        await saveDisclaimerAcceptance(user.uid);
      } catch (error) {
        console.error("Disclaimer acceptance save failed:", error);
      }
    }

    if (pendingAuthAction || !user?.uid) {
      handleAuth(true);
    }
  };

  const resetNewAccountOnboardingState = () => {
    window.localStorage.removeItem("activeTab");
    window.localStorage.removeItem("role");
    window.localStorage.removeItem("selectedRole");
    window.sessionStorage.removeItem("activeTab");
    window.sessionStorage.removeItem("role");
    window.sessionStorage.removeItem("selectedRole");

    setActiveTab("profile");
    setRole("");
    setProfileStatus("");
    setSelectedUser(null);
    setFoundUser(null);
    setSearchResults([]);
    setSelectedManagerTenant(null);
    setSelectedProperty(null);
    setPropertyClaimTarget(null);
    setHoveredProperty(null);
    setPendingPropertyLocation(null);
    setShowMobilePropertyModal(false);
    setShowClaimVerificationModal(false);
    setShowVerifyPrompt(false);
    scrollAppToTop("auto");
  };

  const openPropertyFromNotification = async (propertyId = "") => {
    const targetProperty = visibleProperties.find((item) => item?.id === propertyId);
    if (targetProperty) {
      handleSelectProperty(targetProperty);
      setShowMobilePropertyModal(true);
      setActiveTab("home");
      scrollAppToTop("auto");
      return true;
    }

    if (!propertyId) return false;

    try {
      const propertySnap = await getDoc(doc(db, "properties", propertyId));
      if (!propertySnap.exists()) return false;
      const safeProperty = sanitizePropertyForMap({ id: propertySnap.id, ...propertySnap.data() });
      handleSelectProperty(safeProperty);
      setShowMobilePropertyModal(true);
      setActiveTab("home");
      scrollAppToTop("auto");
      return true;
    } catch (error) {
      console.error("Open notification property failed:", error);
      return false;
    }
  };

  const openPublicProfileFromNotification = async (userId = "") => {
    if (!userId) return false;
    await openPublicProfile({ uid: userId });
    return true;
  };

  const openReviewDetailFromNotification = async (reviewId = "", collectionName = "reviews") => {
    if (!reviewId) return false;

    const localReview = [...reviews, ...myReceivedReviews, ...publicFeedUserReviews, ...allPropertyReviews, ...propertyReviews]
      .find((item) => item?.id === reviewId);

    if (localReview) {
      setActiveReviewDetail({ ...localReview, collectionName });
      setActiveTab(collectionName === "propertyReviews" ? "home" : "reviews");
      scrollAppToTop("auto");
      return true;
    }

    try {
      const reviewSnap = await getDoc(doc(db, collectionName, reviewId));
      if (!reviewSnap.exists()) return false;
      const reviewData = { id: reviewSnap.id, ...reviewSnap.data(), collectionName };
      setActiveReviewDetail(buildSafePublicReview(reviewData));
      setActiveTab(collectionName === "propertyReviews" ? "home" : "reviews");
      scrollAppToTop("auto");
      return true;
    } catch (error) {
      console.error("Open notification review failed:", error);
      return false;
    }
  };

  const handleNotificationClick = async (notificationItem) => {
    if (!notificationItem?.id) return;
    await markNotificationAsRead(notificationItem.id);

    try {
      await addDoc(collection(db, "notificationClicks"), {
        notificationId: notificationItem.id,
        userId: user?.uid || "",
        type: notificationItem?.type || "general",
        createdAt: serverTimestamp(),
        readBy: [user.uid],
      });
    } catch (error) {
      console.error("Notification click tracking failed:", error);
    }

    setShowNotifications(false);

    const meta = notificationItem?.meta || {};
    const propertyId =
      meta.propertyId ||
      meta.propertyTargetId ||
      notificationItem?.relatedPropertyId ||
      notificationItem?.propertyId ||
      "";
    const reviewId =
      meta.reviewId ||
      meta.reviewTargetId ||
      notificationItem?.relatedReviewId ||
      notificationItem?.reviewId ||
      "";
    const userId =
      meta.senderUserId ||
      meta.reviewerUserId ||
      meta.reviewTargetUserId ||
      meta.targetUserId ||
      notificationItem?.relatedUserId ||
      notificationItem?.senderId ||
      "";

    if (notificationItem?.type === "message") {
      const messageUserId = userId || meta.otherUserId || "";
      if (messageUserId) {
        setActiveTab("messages");
        await openConversationWithUser({ uid: messageUserId, displayName: meta.senderDisplayName || "User" });
        return;
      }
      setActiveTab("messages");
      setStatusMessage("Open Messages to continue the conversation.");
      return;
    }

    if (notificationItem?.type === "property_review") {
      const openedProperty = await openPropertyFromNotification(propertyId);
      if (reviewId) await openReviewDetailFromNotification(reviewId, "propertyReviews");
      if (!openedProperty && !reviewId) setStatusMessage("That property activity is no longer available.");
      return;
    }

    if (["review", "review_reply"].includes(notificationItem?.type)) {
      if (reviewId) {
        const openedReview = await openReviewDetailFromNotification(reviewId, "reviews");
        if (openedReview) return;
      }
      if (userId) {
        await openPublicProfileFromNotification(userId);
        return;
      }
      setActiveTab("reviews");
      setStatusMessage("That review activity is no longer available.");
      return;
    }

    if (["property_claim", "claim", "property"].includes(notificationItem?.type)) {
      const openedProperty = await openPropertyFromNotification(propertyId);
      if (!openedProperty) setStatusMessage("That property is no longer available.");
      return;
    }

    if (["profile", "verified", "user"].includes(notificationItem?.type)) {
      const openedProfile = await openPublicProfileFromNotification(userId);
      if (!openedProfile) setStatusMessage("That public profile is no longer available.");
    }
  };



  const getSafeExternalUrl = (url = "") => {
    try {
      const parsed = new URL(String(url || "").trim());
      return ["https:", "http:"].includes(parsed.protocol) ? parsed.toString() : "";
    } catch (error) {
      return "";
    }
  };

  const openExternal = (url) => {
    const safeUrl = getSafeExternalUrl(url);
    if (!safeUrl) {
      setStatusMessage("That external link is unavailable right now.");
      return;
    }
    window.open(safeUrl, "_blank", "noopener,noreferrer");
  };
  const handlePartnerClick = async ({ partnerType = "general", placement = "app", url = "" }) => {
    const safeUrl = getSafeExternalUrl(url);
    if (!safeUrl) {
      setStatusMessage("Partner website is unavailable right now.");
      console.warn("Missing or invalid partner URL", { partnerType, placement, url });
      return;
    }

    try {
      await addDoc(collection(db, "partnerClicks"), {
        userId: user?.uid || "guest",
        partnerType,
        placement,
        url: safeUrl,
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Partner click tracking failed:", error);
    }

    window.open(safeUrl, "_blank", "noopener,noreferrer");
  };

  const renderPartnerServiceCards = (placement = "property") => (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <h3 style={{ ...styles.subsectionHeading, margin: 0, fontSize: isMobile ? 18 : styles.subsectionHeading.fontSize }}>Helpful Services</h3>
        <span style={{ ...styles.smallText, color: "#0f766e", fontWeight: 700 }}>RentDuo Partners</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12 }}>
        <div style={{ background: "linear-gradient(135deg, rgba(15,111,255,0.12) 0%, rgba(59,130,246,0.06) 100%)", border: "1px solid rgba(59,130,246,0.16)", borderRadius: 18, padding: 16, boxShadow: "0 10px 24px rgba(15,23,42,0.08)" }}>
          <div style={{ fontSize: 26, marginBottom: 8 }}>🚚</div>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>Moving Help</div>
          <div style={{ ...styles.smallText, marginTop: 6 }}>Compare moving options and request quotes for your next place.</div>
          <div style={{ ...styles.smallText, marginTop: 6, color: "#0f766e", fontWeight: 800 }}>Opens partner website.</div>
          <button
            style={{ ...styles.primaryButton, width: "100%", marginTop: 14, background: "linear-gradient(135deg, #0f6fff 0%, #14b8a6 100%)" }}
            onClick={() =>
              handlePartnerClick({
                partnerType: "moving_help",
                placement,
                url: "https://www.uhaul.com/MovingHelp/",
              })
            }
          >
            Get Moving Help
          </button>
        </div>

        <div style={{ background: "linear-gradient(135deg, rgba(20,184,106,0.14) 0%, rgba(16,185,129,0.06) 100%)", border: "1px solid rgba(16,185,129,0.18)", borderRadius: 18, padding: 16, boxShadow: "0 10px 24px rgba(15,23,42,0.08)" }}>
          <div style={{ fontSize: 26, marginBottom: 8 }}>🛡️</div>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>Background Check</div>
          <div style={{ ...styles.smallText, marginTop: 6 }}>Run tenant screening and background checks through a trusted partner.</div>
          <div style={{ ...styles.smallText, marginTop: 6, color: "#0f766e", fontWeight: 800 }}>Opens partner website.</div>
          <button
            style={{ ...styles.secondaryButton, width: "100%", marginTop: 14, background: "#ecfdf5", borderColor: "#a7f3d0", color: "#065f46" }}
            onClick={() =>
              handlePartnerClick({
                partnerType: "background_check",
                placement,
                url: "https://www.mysmartmove.com/",
              })
            }
          >
            Run Background Check
          </button>
        </div>
      </div>
    </div>
  );

  const handleHelpfulReview = async (review, collectionName = "reviews") => {
    if (!user?.uid || !review?.id) {
      setStatusMessage("Log in first.");
      return;
    }

    if (review?.reviewerUserId === user.uid) {
      setStatusMessage("You can't vote on your own review.");
      return;
    }

    const helpfulBy = Array.isArray(review?.helpfulBy) ? review.helpfulBy : [];
    if (helpfulBy.includes(user.uid)) {
      setStatusMessage("You already marked this review as helpful.");
      return;
    }

    try {
      await setDoc(
        doc(db, collectionName, review.id),
        {
          helpfulCount: Number(review?.helpfulCount || 0) + 1,
          helpfulBy: [...helpfulBy, user.uid],
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setStatusMessage("Marked as helpful.");
    } catch (error) {
      console.error("Helpful vote failed:", error);
      setStatusMessage("Failed to save helpful vote.");
    }
  };

  const canReplyToReview = (review) => {
    if (!user?.uid || !review?.id) return false;
    if (review?.targetUserId !== user.uid) return false;
    if (review?.status && review.status === "deleted") return false;
    return true;
  };

  const handleReviewReplyDraftChange = (reviewId, value) => {
    setReviewReplyDrafts((prev) => ({
      ...prev,
      [reviewId]: value,
    }));
  };

  const openReplyModal = (review) => {
    if (!review?.id) return;
    setActiveReplyReview(review);
    setReviewReplyDrafts((prev) => ({
      ...prev,
      [review.id]: prev?.[review.id] ?? review?.response?.text ?? "",
    }));
    setShowReplyModal(true);
  };

  const closeReplyModal = () => {
    setShowReplyModal(false);
    setActiveReplyReview(null);
  };

  const handleSubmitReviewReply = async (review) => {
    if (!review?.id || !user?.uid) {
      setStatusMessage("Log in first.");
      return;
    }

    if (!canReplyToReview(review)) {
      setStatusMessage("Only the person being reviewed can reply.");
      return;
    }

    const trimmedReply = String(reviewReplyDrafts?.[review.id] || "").trim();

    if (!trimmedReply) {
      setStatusMessage("Write a reply first.");
      return;
    }

    if (trimmedReply.length > 500) {
      setStatusMessage("Reply must be 500 characters or less.");
      return;
    }

    try {
      setReviewReplySubmittingId(review.id);

      let replyModeration = {
        aiRiskScore: 0,
        aiRiskLabel: "low",
        aiReasons: [],
        moderationStatus: "not_run",
        finalStatus: "approved",
        finalFlag: false,
      };

      try {
        const moderationResponse = await moderateReviewCallable({
          reviewId: review.id,
          reviewText: trimmedReply,
          rating: Number(review?.rating || 0),
          targetUserId: review?.targetUserId || user.uid,
          contentType: "review_reply",
        });

        const moderationData = moderationResponse?.data || {};
        replyModeration = {
          aiRiskScore: Number(
            moderationData?.aiRiskScore ??
            moderationData?.riskScore ??
            moderationData?.reviewRiskScore ??
            0
          ),
          aiRiskLabel:
            moderationData?.aiRiskLabel ||
            moderationData?.riskLabel ||
            moderationData?.label ||
            "low",
          aiReasons: Array.isArray(moderationData?.aiReasons)
            ? moderationData.aiReasons
            : Array.isArray(moderationData?.reasons)
            ? moderationData.reasons
            : [],
          moderationStatus: moderationData?.openAIModerationStatus || moderationData?.moderationStatus || "completed",
          finalStatus: moderationData?.finalStatus || (moderationData?.finalFlag ? "pending" : "approved"),
          finalFlag: Boolean(moderationData?.finalFlag),
        };
      } catch (moderationError) {
        console.error("Reply moderation failed:", moderationError);
        replyModeration = {
          aiRiskScore: 0,
          aiRiskLabel: "unknown",
          aiReasons: [moderationError?.message || "Moderation failed."],
          moderationStatus: "failed",
          finalStatus: "pending",
          finalFlag: true,
        };
      }

      await setDoc(
        doc(db, "reviews", review.id),
        {
          response: {
            text: trimmedReply,
            responderUserId: user.uid,
            responderDisplayName: profile?.displayName || user.email || "User",
            responderRole: role || profile?.role || "",
            responderVerified: Boolean(profile?.verified),
            createdAt: review?.response?.createdAt || serverTimestamp(),
            updatedAt: serverTimestamp(),
            moderationStatus: replyModeration.moderationStatus,
            aiRiskScore: replyModeration.aiRiskScore,
            aiRiskLabel: replyModeration.aiRiskLabel,
            aiReasons: replyModeration.aiReasons,
            finalStatus: replyModeration.finalStatus,
            flaggedForReview: replyModeration.finalFlag,
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setReviewReplyDrafts((prev) => ({
        ...prev,
        [review.id]: "",
      }));

      setActiveReplyReview((prev) => (prev?.id === review.id ? { ...review, response: { ...(review?.response || {}), text: trimmedReply } } : prev));
      closeReplyModal();

      if (review?.reviewerUserId && review.reviewerUserId !== user.uid) {
        await createNotification({
          userId: review.reviewerUserId,
          title: "New Review Reply",
          body: `${profile?.displayName || "Someone"} replied to your review.`,
          type: "review_reply",
          meta: {
            reviewId: review.id,
            reviewerUserId: review.reviewerUserId,
            targetUserId: review?.targetUserId || user.uid,
          },
        });
      }

      if (replyModeration.finalFlag || replyModeration.finalStatus === "pending") {
        setStatusMessage("Reply saved and flagged for admin review.");
      } else if (review?.response?.text) {
        setStatusMessage("Reply updated with AI moderation.");
      } else {
        setStatusMessage("Reply posted with AI moderation.");
      }
    } catch (error) {
      console.error("Review reply failed:", error);
      setStatusMessage("Failed to save reply.");
    } finally {
      setReviewReplySubmittingId("");
    }
  };

  const handleImproveReviewReply = async (review, tone = aiTone || "professional") => {
    if (!review?.id) return;

    const baseReply = String(
      reviewReplyDrafts?.[review.id] ?? review?.response?.text ?? ""
    ).trim();

    if (!baseReply) {
      setStatusMessage("Write a reply first.");
      return;
    }

    setAiTone(tone);

    const localReply = buildProfessionalReplyText(baseReply, tone)
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 500);

    setReviewReplyImprovingId(review.id);
    setReviewReplyDrafts((prev) => ({
      ...prev,
      [review.id]: localReply,
    }));

    setActiveReplyReview((prev) =>
      prev?.id === review.id
        ? {
            ...prev,
            draftResponseText: localReply,
          }
        : prev
    );

    setStatusMessage(`${tone.charAt(0).toUpperCase() + tone.slice(1)} response ready.`);

    window.setTimeout(async () => {
      try {
        const response = await improveReviewCallable({
          reviewText: baseReply,
          tone,
          contentType: "review_reply",
        });
        const data = response?.data || {};
        const aiReply = String(
          data?.improvedText ||
          data?.improvedReview ||
          data?.text ||
          data?.result ||
          ""
        ).trim();

        if (aiReply) {
          const finalReply = aiReply.replace(/\s+/g, " ").trim().slice(0, 500);
          setReviewReplyDrafts((prev) => ({
            ...prev,
            [review.id]: finalReply,
          }));
          setActiveReplyReview((prev) =>
            prev?.id === review.id
              ? {
                  ...prev,
                  draftResponseText: finalReply,
                }
              : prev
          );
          setStatusMessage(`${tone.charAt(0).toUpperCase() + tone.slice(1)} AI response ready.`);
        }
      } catch (error) {
        console.error("Reply improve fallback used:", error);
      } finally {
        setReviewReplyImprovingId("");
      }
    }, 120);
  };

  const handleAuth = async (forceProceed = false) => {
    setStatusMessage("");
    setAuthErrorMessage("");

    if (!email.trim() || !password.trim()) {
      const message = "Enter email and password.";
      setStatusMessage(message);
      setAuthErrorMessage(message);
      return;
    }

    if (!forceProceed && !hasLocalDisclaimerAcceptance()) {
      setPendingAuthAction(authMode);
      setShowAuthDisclaimer(true);
      scrollAppToTop("auto");
      return;
    }

    try {
      const modeToRun = pendingAuthAction || authMode;

      if (modeToRun === "signup") {
        const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        const savedReferralCode = (window.localStorage.getItem(REFERRAL_STORAGE_KEY) || "").trim();
        const safeReferralCode =
          savedReferralCode && savedReferralCode !== userCredential.user.uid ? savedReferralCode : "";

        await setDoc(
          doc(db, "profiles", userCredential.user.uid),
          {
            uid: userCredential.user.uid,
            email: userCredential.user.email || email.trim(),
            role: "",
            displayName: "",
            city: "",
            state: "",
            location: buildProfileLocationPayload(),
            profileStatus: "",
            statusVisibility: true,
            preferredAreasText: "",
            serviceAreasText: "",
            privateAddress: "",
            bio: "",
            photoURL: "",
            verified: false,
            verificationStatus: "unverified",
            verificationNote: "",
            verificationDocs: {
              idUrl: "",
              deedUrl: "",
            },
            referredBy: safeReferralCode || "",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        await setDoc(
          doc(db, "publicProfiles", userCredential.user.uid),
          buildPublicProfilePayload({
            currentUser: userCredential.user,
            source: { role: "", verified: false, verificationStatus: "unverified" },
          }),
          { merge: true }
        );

        try {
          await saveDisclaimerAcceptance(userCredential.user.uid);
        } catch (disclaimerError) {
          console.error("Disclaimer acceptance profile save failed:", disclaimerError);
        }
        window.localStorage.setItem(DISCLAIMER_ACCEPTED_STORAGE_KEY, "true");
        resetNewAccountOnboardingState();
        scrollAppToTop("auto");
        setStatusMessage(
          safeReferralCode ? "Account created with referral applied. Complete your profile to get started." : "Account created. Complete your profile to get started."
        );
      } else {
        const loginResult = await signInWithEmailAndPassword(auth, email.trim(), password);
        const profileRef = doc(db, "profiles", loginResult.user.uid);
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists()) {
          const newProfile = {
            uid: loginResult.user.uid,
            email: loginResult.user.email || email.trim(),
            role: "",
            displayName: "",
            city: "",
            state: "",
            location: buildProfileLocationPayload(),
            profileStatus: "",
            statusVisibility: true,
            preferredAreasText: "",
            serviceAreasText: "",
            privateAddress: "",
            bio: "",
            photoURL: "",
            verified: false,
            verificationStatus: "unverified",
            verificationNote: "",
            verificationDocs: {
              idUrl: "",
              deedUrl: "",
            },
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            lastActiveAt: serverTimestamp(),
          };

          await setDoc(profileRef, newProfile, { merge: true });
          await setDoc(
            doc(db, "publicProfiles", loginResult.user.uid),
            buildPublicProfilePayload({ currentUser: loginResult.user, source: newProfile }),
            { merge: true }
          );
        }

        try {
          await saveDisclaimerAcceptance(loginResult.user.uid);
        } catch (disclaimerError) {
          console.error("Disclaimer acceptance profile save failed:", disclaimerError);
        }
        window.localStorage.setItem(DISCLAIMER_ACCEPTED_STORAGE_KEY, "true");
        setActiveTab((window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) || "profile") === "home" ? "profile" : (window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) || "profile"));
        scrollAppToTop("auto");
        setStatusMessage("Logged in.");
      }

      setShowAuthDisclaimer(false);
      setPendingAuthAction(null);
    } catch (error) {
      console.error(error);
      const authMessage =
        error?.code === "auth/invalid-credential" || error?.code === "auth/wrong-password"
          ? "Email or password is incorrect."
          : error?.code === "auth/user-not-found"
          ? "No RentDuo account was found for that email."
          : error?.code === "auth/invalid-email"
          ? "Enter a valid email address."
          : error?.code === "auth/too-many-requests"
          ? "Too many attempts. Please wait a moment and try again."
          : error?.message || "Login failed. Please try again.";
      setStatusMessage(authMessage);
      setAuthErrorMessage(authMessage);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedPhotoFile(file);
  };

  const handleVerificationAuxFileChange = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setter(file);
  };

  const handlePropertyImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!String(file.type || "").startsWith("image/")) {
      setStatusMessage("Please choose an image file for the property photo.");
      return;
    }

    setPropertyImageFile(file);
    setPropertyImageUrl("");

    try {
      const previewUrl = URL.createObjectURL(file);
      setPropertyImagePreviewUrl(previewUrl);
    } catch (error) {
      console.error("Property image preview error:", error);
      setPropertyImagePreviewUrl("");
    }
  };

  const clearPropertyImageSelection = () => {
    if (propertyImagePreviewUrl) {
      try {
        URL.revokeObjectURL(propertyImagePreviewUrl);
      } catch (error) {
        console.error("Property image preview cleanup error:", error);
      }
    }

    setPropertyImageFile(null);
    setPropertyImagePreviewUrl("");
    setPropertyImageUrl("");
  };

  const uploadProfilePhotoIfNeeded = async () => {
    if (!user || !selectedPhotoFile) return photoURL || "";

    const fileRef = ref(
      storage,
      `profilePhotos/${user.uid}/${Date.now()}_${selectedPhotoFile.name}`
    );

    await uploadBytes(fileRef, selectedPhotoFile);
    const downloadURL = await getDownloadURL(fileRef);
    return downloadURL;
  };

  const uploadVerificationFile = async (file, type) => {
    if (!user || !file) return "";

    const fileRef = ref(
      storage,
      `verificationDocs/${user.uid}/${type}/${Date.now()}_${file.name}`
    );

    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  const buildUploadedFileRecord = (url, file, docType) => ({
    docType,
    name: file?.name || docType,
    url,
    contentType: file?.type || "",
    size: Number(file?.size || 0),
  });

  const getVerificationTypeLabel = (type) => {
    const labels = {
      tenant_verification: "Tenant verification",
      landlord_verification: "Landlord verification",
      property_manager_verification: "Property manager verification",
      property_ownership_verification: "Property ownership verification",
    };
    return labels[type] || "Verification";
  };

  const getVerificationTypeHelp = (type) => {
    const help = {
      tenant_verification: "Tenant verification usually needs a lease, proof of residence, or a utility bill. Optional ID/selfie can help admins confirm the submission belongs to you.",
      landlord_verification: "Landlord verification should focus on ownership proof, such as a deed, tax record, closing document, or similar ownership record.",
      property_manager_verification: "Property manager verification should include management authorization or a company/owner authorization letter showing you can manage this property.",
      property_ownership_verification: "Property ownership verification should include ownership proof, such as a deed, tax record, closing document, or similar property record.",
    };
    return help[type] || "Choose the verification type that matches your goal.";
  };

  const getVerificationUploadFields = (selectedVerificationType) => {
    const setterByLabel = {
      "Lease Agreement": setLeaseFile,
      "Proof of Residence": setResidenceProofFile,
      "Utility Bill": setUtilityBillFile,
      "Property Ownership Proof": setDeedFile,
      "Deed / Tax Record / Closing Document": setOwnershipRecordFile,
      "Management Authorization Proof": setManagementAuthFile,
      "Company/Owner Authorization Letter": setAuthorizationLetterFile,
      "Optional ID / Selfie": setIdFile,
    };

    return (VERIFICATION_UPLOAD_FIELDS[selectedVerificationType] || VERIFICATION_UPLOAD_FIELDS.tenant_verification).map((label) => ({
      key: label.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
      label,
      setter: setterByLabel[label] || setIdFile,
    }));
  };

  const getVerificationDisplayStatus = () => {
    const latestSubmission = myVerificationSubmissions[0];
    if (latestSubmission?.adminStatus === "approved") return { label: "Verified", tone: "success", badgeStatus: "verified" };
    if (latestSubmission?.adminStatus === "denied") return { label: "Verification denied", tone: "danger", badgeStatus: "unverified" };
    if (latestSubmission?.adminStatus === "pending_review" || verificationStatus === "pending_review") {
      return { label: "Pending verification", tone: "pending", badgeStatus: "pending_review" };
    }
    if (isCurrentUserVerified) return { label: "Verified", tone: "success", badgeStatus: getEffectiveVerificationStatus({ ...(profile || {}), verificationStatus, verified: true }) };
    return { label: "Not verified", tone: "muted", badgeStatus: "unverified" };
  };

  const selectedVerificationType = verificationType || "tenant_verification";

  useEffect(() => {
    const activeLabels = new Set(VERIFICATION_UPLOAD_FIELDS[selectedVerificationType] || VERIFICATION_UPLOAD_FIELDS.tenant_verification);
    if (!activeLabels.has("Lease Agreement")) setLeaseFile(null);
    if (!activeLabels.has("Proof of Residence")) setResidenceProofFile(null);
    if (!activeLabels.has("Utility Bill")) setUtilityBillFile(null);
    if (!activeLabels.has("Property Ownership Proof")) setDeedFile(null);
    if (!activeLabels.has("Deed / Tax Record / Closing Document")) setOwnershipRecordFile(null);
    if (!activeLabels.has("Management Authorization Proof")) setManagementAuthFile(null);
    if (!activeLabels.has("Company/Owner Authorization Letter")) setAuthorizationLetterFile(null);
    if (!activeLabels.has("Optional ID / Selfie")) {
      setIdFile(null);
    }
  }, [selectedVerificationType]);

  const uploadPropertyImageIfNeeded = async () => {
    if (!user || !propertyImageFile) return propertyImageUrl.trim();

    const safeName = String(propertyImageFile.name || "property-photo").replace(/[^a-zA-Z0-9._-]/g, "_");
    const fileRef = ref(
      storage,
      `propertyPhotos/${user.uid}/${Date.now()}_${safeName}`
    );

    await uploadBytes(fileRef, propertyImageFile);
    return await getDownloadURL(fileRef);
  };


  const resetDeleteAccountForm = () => {
    setDeleteAccountPassword("");
    setDeleteAccountConfirmText("");
    setIsDeletingAccount(false);
  };

  const handleDeleteAccount = async () => {
    if (!user?.email) {
      setStatusMessage("You must be logged in to delete your account.");
      return;
    }

    if (deleteAccountConfirmText.trim().toUpperCase() !== "DELETE") {
      setStatusMessage('Type DELETE in all caps to confirm account deletion.');
      return;
    }

    if (!deleteAccountPassword.trim()) {
      setStatusMessage("Enter your password to confirm account deletion.");
      return;
    }

    const confirmed = window.confirm(
      "This permanently deletes your RentDuo account and related data. This cannot be undone."
    );

    if (!confirmed) return;

    setIsDeletingAccount(true);
    setStatusMessage("Deleting account...");

    try {
      const credential = EmailAuthProvider.credential(user.email, deleteAccountPassword);
      await reauthenticateWithCredential(user, credential);

      const userId = user.uid;

      const safeDeleteQueryDocs = async (collectionName, fieldNames = []) => {
        for (const fieldName of fieldNames) {
          const snapshot = await getDocs(
            query(collection(db, collectionName), where(fieldName, "==", userId))
          );
          for (const docSnap of snapshot.docs) {
            await deleteDoc(doc(db, collectionName, docSnap.id));
          }
        }
      };

      await deleteDoc(doc(db, "profiles", userId)).catch(() => {});

      await safeDeleteQueryDocs("messages", ["senderId", "receiverId"]);
      await safeDeleteQueryDocs("reviews", ["reviewerUserId", "targetUserId"]);
      await safeDeleteQueryDocs("propertyReviews", ["reviewerUserId"]);
      await safeDeleteQueryDocs("notifications", ["userId"]);
      await safeDeleteQueryDocs("userReports", ["reportedUserId", "reporterUserId"]);
      await safeDeleteQueryDocs("issues", ["createdByUserId", "userId", "reporterUserId"]);
      await safeDeleteQueryDocs("properties", [
        "ownerId",
        "claimedByUserId",
        "claimRequestedByUserId",
        "managerUserId",
        "communityAddedByUserId",
      ]);

      await deleteUser(user);

      resetDeleteAccountForm();
      setStatusMessage("Your account has been deleted.");
    } catch (error) {
      console.error("Delete account error:", error);
      const code = error?.code || "";
      if (code.includes("wrong-password") || code.includes("invalid-credential")) {
        setStatusMessage("Password incorrect. Please try again.");
      } else if (code.includes("requires-recent-login")) {
        setStatusMessage("Please log in again and retry deleting your account.");
      } else {
        setStatusMessage(error?.message || "Could not delete account.");
      }
      setIsDeletingAccount(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setStatusMessage("");

    try {
      let finalPhotoURL = photoURL || "";

      if (selectedPhotoFile) {
        finalPhotoURL = await uploadProfilePhotoIfNeeded();
      }

      const trimmedDisplayName = displayName.trim();
      const trimmedCity = city.trim();
      const trimmedPreferredAreas = preferredAreasText.trim();
      const trimmedServiceAreas = serviceAreasText.trim();
      const cityParts = trimmedCity.split(",").map((item) => item.trim()).filter(Boolean);
      const parsedState = cityParts.length > 1 ? cityParts[cityParts.length - 1] : "";
      const parsedCity = cityParts.length > 1 ? cityParts.slice(0, -1).join(", ") : trimmedCity;
      const nextLocation = buildProfileLocationPayload({
        location: profileLocation,
        fallbackCity: parsedCity,
        fallbackState: parsedState || profile?.state || "",
      });
      const isLocked =
        profile?.verificationStatus === "landlord_verified" ||
        profile?.verificationStatus === "property_manager_verified";

      if (!trimmedDisplayName || !trimmedCity) {
        setStatusMessage("Display name and city/state are required.");
        return;
      }

      if (!role) {
        setStatusMessage("Choose Tenant, Landlord, or Property Manager to complete your profile.");
        return;
      }

      const safeVerificationStatus = profile?.verificationStatus || "unverified";
      const profileRole = profile?.role || role;

      const payload = {
        uid: user.uid,
        email: user.email || "",
        role: profileRole,
        displayName: isLocked ? profile?.displayName || trimmedDisplayName : trimmedDisplayName,
        city: trimmedCity,
        state: nextLocation.state || parsedState || profile?.state || "",
        location: nextLocation,
        profileStatus,
        statusVisibility,
        preferredAreasText: profileRole === "tenant" ? trimmedPreferredAreas : "",
        serviceAreasText: profileRole === "tenant" ? "" : trimmedServiceAreas,
        privateAddress: privateAddress.trim(),
        bio: bio.trim(),
        photoURL: finalPhotoURL || profile?.photoURL || "",
        verificationNote: verificationNote.trim(),
        referredBy: profile?.referredBy || "",
        createdAt: profile?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "profiles", user.uid), payload, { merge: true });
      await setDoc(
        doc(db, "publicProfiles", user.uid),
        buildPublicProfilePayload({
          currentUser: user,
          source: { ...(profile || {}), ...payload },
          trustedSource: profile || {},
          role: payload.role,
          displayName: payload.displayName,
          city: payload.city,
          state: payload.state,
          profileStatus: payload.profileStatus,
          statusVisibility: payload.statusVisibility,
          preferredAreasText: payload.preferredAreasText,
          serviceAreasText: payload.serviceAreasText,
          bio: payload.bio,
          photoURL: payload.photoURL,
        }),
        { merge: true }
      );
      await syncUserTrustProfile(user.uid);

      setProfile((prev) => ({
        ...(prev || {}),
        ...payload,
        updatedAt: new Date(),
      }));

      setRole(payload.role);
      setDisplayName(payload.displayName);
      setCity(payload.city);
      setProfileLocation(payload.location);
      setProfileStatus(payload.profileStatus || getDefaultProfileStatus(payload.role));
      setStatusVisibility(payload.statusVisibility !== false);
      setPreferredAreasText(payload.preferredAreasText || "");
      setServiceAreasText(payload.serviceAreasText || "");
      setPhotoURL(payload.photoURL || "");
      setVerificationStatus(safeVerificationStatus);
      setSelectedPhotoFile(null);
      setStatusMessage("Profile saved.");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleSubmitVerification = async () => {
    if (!user) return;

    setStatusMessage("");
    setIsSubmittingVerification(true);

    try {
      let nextDocs = { ...verificationDocs };
      const uploadedFiles = [];

      if (idFile) {
        const url = await uploadVerificationFile(idFile, "id");
        nextDocs.idUrl = url;
        uploadedFiles.push(buildUploadedFileRecord(url, idFile, "optional_id_selfie"));
      }

      if (deedFile) {
        const url = await uploadVerificationFile(deedFile, "deed");
        nextDocs.deedUrl = url;
        uploadedFiles.push(buildUploadedFileRecord(url, deedFile, "property_ownership_proof"));
      }

      const optionalUploads = [
        { file: leaseFile, type: "lease_agreement" },
        { file: residenceProofFile, type: "proof_of_residence" },
        { file: utilityBillFile, type: "utility_bill" },
        { file: ownershipRecordFile, type: "deed_tax_record_closing_document" },
        { file: managementAuthFile, type: "management_authorization_proof" },
        { file: authorizationLetterFile, type: "company_owner_authorization_letter" },
      ];

      for (const item of optionalUploads) {
        if (!item.file) continue;
        const url = await uploadVerificationFile(item.file, item.type);
        uploadedFiles.push(buildUploadedFileRecord(url, item.file, item.type));
      }

      const nextStatus = "pending_review";
      const submissionVerificationType =
        verificationType ||
        (role === "landlord"
          ? "landlord_verification"
          : role === "property_manager"
          ? "property_manager_verification"
          : "tenant_verification");

      const payload = {
        verificationStatus: nextStatus,
        verificationNote: verificationNote.trim(),
        privateAddress: privateAddress.trim(),
        verificationDocs: nextDocs,
        propertyClaimTargetId: propertyClaimTarget?.id || "",
        propertyClaimTargetTitle: propertyClaimTarget?.title || "",
        propertyClaimTargetAddress: propertyClaimTarget?.address || "",
        verificationSubmittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "profiles", user.uid), payload, { merge: true });

      const submissionRef = await addDoc(collection(db, "verificationSubmissions"), {
        userId: user.uid,
        userEmail: user.email || "",
        userDisplayName: profile?.displayName || displayName || user.email || "RentDuo user",
        userPhotoURL: profile?.photoURL || photoURL || "",
        role: role || profile?.role || "",
        propertyId: propertyClaimTarget?.id || "",
        propertyTitle: propertyClaimTarget?.title || "",
        propertyAddress: propertyClaimTarget?.address || privateAddress.trim() || "",
        verificationType: submissionVerificationType,
        uploadedFiles,
        aiAnalysis: null,
        aiRiskLevel: "pending",
        aiFlags: [],
        adminStatus: "pending_review",
        adminNotes: "",
        createdAt: serverTimestamp(),
        reviewedAt: null,
        reviewedBy: "",
      });

      analyzeVerificationSubmissionCallable({ submissionId: submissionRef.id }).catch((error) => {
        console.error("AI verification analysis failed:", error);
      });

      if (propertyClaimTarget?.id && (role === "landlord" || role === "property_manager")) {
        const claimState = getPropertyClaimState(propertyClaimTarget);
        if (!claimState.canOpenClaim) {
          setStatusMessage(claimState.label);
        } else {
          const claimPatch = {
            claimStatus: "pending",
            claimRequestedByUserId: user.uid,
            claimRequestedByEmail: user.email || "",
            claimRequestedByDisplayName: profile?.displayName || user.email || "User",
            claimRequestedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

        await setDoc(
          doc(db, "properties", propertyClaimTarget.id),
          claimPatch,
          { merge: true }
        );

        const localClaimPatch = {
          ...claimPatch,
          claimRequestedAt: new Date(),
          updatedAt: new Date(),
        };
        const nextClaimTarget = { ...propertyClaimTarget, ...localClaimPatch };
        setPropertyClaimTarget(nextClaimTarget);
        setSelectedProperty((prev) =>
          prev?.id === propertyClaimTarget.id ? { ...prev, ...localClaimPatch } : prev
        );
        setProperties((prev) =>
          prev.map((property) =>
            property.id === propertyClaimTarget.id ? { ...property, ...localClaimPatch } : property
          )
        );

        const claimRecipientId =
          propertyClaimTarget.ownerId ||
          propertyClaimTarget.claimedByUserId ||
          propertyClaimTarget.managerUserId ||
          "";
        if (claimRecipientId && claimRecipientId !== user.uid) {
          await createNotification({
            userId: claimRecipientId,
            title: "Property Claim Submitted",
            body: `${profile?.displayName || "Someone"} submitted a claim for your property.`,
            type: "property_claim",
            meta: {
              propertyId: propertyClaimTarget.id,
              propertyTitle: propertyClaimTarget.title || "",
            },
          });
        }
        }
      }

      setVerificationDocs(nextDocs);
      setVerificationStatus(nextStatus);
      setProfile((prev) => ({
        ...(prev || {}),
        ...payload,
        verificationSubmittedAt: new Date(),
        updatedAt: new Date(),
      }));

      setIdFile(null);
      setDeedFile(null);
      setLeaseFile(null);
      setResidenceProofFile(null);
      setUtilityBillFile(null);
      setOwnershipRecordFile(null);
      setManagementAuthFile(null);
      setAuthorizationLetterFile(null);

      setShowClaimVerificationModal(false);
      setStatusMessage("Verification submitted. RentDuo AI will summarize it for admin review, but admins make the final decision.");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    } finally {
      setIsSubmittingVerification(false);
    }
  };

  const handleAdminNoteChange = (targetId, value) => {
    setAdminNoteDrafts((prev) => ({
      ...prev,
      [targetId]: value,
    }));
  };

  const handleAdminUpdateVerification = async (targetUser, nextStatus) => {
    const targetId = targetUser?.uid || targetUser?.id;
    if (!isAdmin || !targetId) return;

    try {
      const adminNote = adminNoteDrafts[targetId] || "";
      const shouldShowVerifiedBadge =
        nextStatus !== "unverified" && nextStatus !== "pending_review";

      setDoc(
        doc(db, "profiles", targetId),
        {
          uid: targetId,
          verificationStatus: nextStatus,
          role:
            nextStatus === "property_manager_verified"
              ? "property_manager"
              : nextStatus === "landlord_verified"
              ? "landlord"
              : targetUser?.role || "",
          adminVerificationNote: adminNote.trim(),
          verified: shouldShowVerifiedBadge,
          verificationReviewedAt: serverTimestamp(),
          verificationReviewedBy: user.email || "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      if (
        shouldShowVerifiedBadge &&
        (nextStatus === "landlord_verified" || nextStatus === "property_manager_verified")
      ) {
        const propertyClaimsSnap = await getDocs(collection(db, "properties"));
        const matchingClaims = propertyClaimsSnap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((item) => item.claimRequestedByUserId === targetId);

        for (const item of matchingClaims) {
          setDoc(
            doc(db, "properties", item.id),
            {
              ownerId: targetId,
              ownerEmail: targetUser?.email || "",
              ownerDisplayName: targetUser?.displayName || targetUser?.email || "Verified Owner",
              ownerPhotoURL: targetUser?.photoURL || "",
              ownerVerified: true,
              claimedByUserId: targetId,
              claimStatus: "approved",
              claimApprovedAt: serverTimestamp(),
              claimApprovedBy: user.email || "",
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
      }

      await createNotification({
        userId: targetId,
        title: shouldShowVerifiedBadge ? "Verification Approved" : "Verification Updated",
        body: shouldShowVerifiedBadge
          ? "Your RentDuo verification was approved."
          : "Your RentDuo verification status was updated.",
        type: shouldShowVerifiedBadge ? "verification_approved" : "verification_denied",
        meta: { targetUserId: targetId },
      });

      setStatusMessage(
        `Updated ${targetUser?.email || targetId} to ${nextStatus}.`
      );
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleAdminReviewVerificationSubmission = async (submission, nextAdminStatus) => {
    if (!isAdmin || !submission?.id || !submission?.userId) return;

    try {
      const adminNote = adminNoteDrafts[submission.id] || "";
      const approvedStatus =
        submission.verificationType === "property_manager_verification"
          ? "property_manager_verified"
          : submission.verificationType === "landlord_verification" ||
            submission.verificationType === "property_ownership_verification"
          ? "landlord_verified"
          : "tenant_verified";
      const profileStatusToApply = nextAdminStatus === "approved" ? approvedStatus : "unverified";

      await setDoc(
        doc(db, "verificationSubmissions", submission.id),
        {
          adminStatus: nextAdminStatus,
          adminNotes: adminNote.trim(),
          reviewedAt: serverTimestamp(),
          reviewedBy: user.email || user.uid,
        },
        { merge: true }
      );

      await handleAdminUpdateVerification(
        {
          uid: submission.userId,
          id: submission.userId,
          email: submission.userEmail || "",
          displayName: submission.userDisplayName || "",
          role: submission.role || "",
        },
        profileStatusToApply
      );
    } catch (error) {
      console.error("Verification submission review error:", error);
      setStatusMessage(error.message || "Could not update verification submission.");
    }
  };

  const handleAdminApprovePropertyClaim = async (property, approvedRole = "landlord") => {
    if (!isAdmin || !property?.id || !property?.claimRequestedByUserId) return;

    try {
      const targetId = property.claimRequestedByUserId;
      await setDoc(
        doc(db, "properties", property.id),
        {
          ownerId: targetId,
          ownerEmail: property.claimRequestedByEmail || "",
          ownerDisplayName: property.claimRequestedByDisplayName || "Verified Owner",
          ownerVerified: true,
          claimedBy: targetId,
          claimedByUserId: targetId,
          managerUserId: approvedRole === "property_manager" ? targetId : property.managerUserId || "",
          claimStatus: "approved",
          claimApprovedAt: serverTimestamp(),
          claimApprovedBy: user?.email || user?.uid || "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "profiles", targetId),
        {
          role: approvedRole,
          verified: true,
          verificationStatus: approvedRole === "property_manager" ? "property_manager_verified" : "landlord_verified",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setProperties((prev) =>
        prev.map((item) =>
          item.id === property.id
            ? {
                ...item,
                ownerId: targetId,
                ownerEmail: property.claimRequestedByEmail || "",
                ownerDisplayName: property.claimRequestedByDisplayName || "Verified Owner",
                ownerVerified: true,
                claimedBy: targetId,
                claimedByUserId: targetId,
                managerUserId: approvedRole === "property_manager" ? targetId : item.managerUserId || "",
                claimStatus: "approved",
              }
            : item
        )
      );
      setStatusMessage("Property claim approved.");
    } catch (error) {
      console.error("Property claim approval failed:", error);
      setStatusMessage(error.message || "Could not approve property claim.");
    }
  };

  const handleAdminRejectPropertyClaim = async (property) => {
    if (!isAdmin || !property?.id) return;

    try {
      await setDoc(
        doc(db, "properties", property.id),
        {
          claimStatus: "rejected",
          claimReviewedAt: serverTimestamp(),
          claimReviewedBy: user?.email || user?.uid || "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setProperties((prev) =>
        prev.map((item) => (item.id === property.id ? { ...item, claimStatus: "rejected" } : item))
      );
      setStatusMessage("Property claim rejected.");
    } catch (error) {
      console.error("Property claim rejection failed:", error);
      setStatusMessage(error.message || "Could not reject property claim.");
    }
  };

  const handleFindUser = async () => {
    setStatusMessage("");
    setSearchMessage("");
    setFoundUser(null);
    setSearchResults([]);

    const term = searchQuery.trim().toLowerCase();
    const areaTerm = searchArea.trim().toLowerCase();

    if (!term && !areaTerm && searchRoleFilter === "all" && searchStatusFilter === "all") {
      setSearchMessage("Enter a name, email, city, property address, or choose an area filter.");
      return;
    }

    try {
      const [profilesSnap, propertiesSnap] = await Promise.all([
        getDocs(collection(db, "publicProfiles")),
        getDocs(collection(db, "properties")),
      ]);

      const allUsers = profilesSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const filteredUsers = allUsers.filter((data) => {
        if (data.uid === user?.uid) return false;

        const email = String(data.email || "").toLowerCase();
        const name = String(data.displayName || "").toLowerCase();
        const cityValue = String(data.city || "").toLowerCase();
        const stateValue = String(data.state || "").toLowerCase();
        const publicAreaSummary = String(getPublicAreaSummary(data) || "").toLowerCase();
        const textMatch =
          !term ||
          email.includes(term) ||
          name.includes(term) ||
          cityValue.includes(term) ||
          stateValue.includes(term) ||
          publicAreaSummary.includes(term);

        const roleMatch = searchRoleFilter === "all" || (data.role || "") === searchRoleFilter;
        const statusMatch =
          searchStatusFilter === "all" ||
          (data.statusVisibility !== false && (data.profileStatus || "") === searchStatusFilter);
        const areaMatch = matchesAreaFilter(data, areaTerm);

        return textMatch && roleMatch && statusMatch && areaMatch;
      });

      if (filteredUsers.length) {
        const rankedUsers = [...filteredUsers].sort((a, b) => {
          const aVerified = a?.verified ? 1 : 0;
          const bVerified = b?.verified ? 1 : 0;
          const aScore = Number(a?.rentScore || a?.trustScore || 0);
          const bScore = Number(b?.rentScore || b?.trustScore || 0);
          if (bVerified !== aVerified) return bVerified - aVerified;
          if (bScore !== aScore) return bScore - aScore;
          return String(a?.displayName || a?.email || "").localeCompare(String(b?.displayName || b?.email || ""));
        });

        const directMatch = rankedUsers[0];
        setFoundUser(directMatch);
        setSearchResults(rankedUsers.slice(0, 6));
        setSearchMessage(
          `${rankedUsers.length} user${rankedUsers.length === 1 ? "" : "s"} found${areaTerm ? ` in ${searchArea.trim()}` : ""}.`
        );
        return;
      }

      const allProperties = propertiesSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const matchingProperty = allProperties.find((property) => {
        const address = String(property.address || "").toLowerCase();
        const title = String(property.title || "").toLowerCase();
        const description = String(property.description || "").toLowerCase();
        const cityValue = String(property.city || "").toLowerCase();
        const stateValue = String(property.state || "").toLowerCase();
        const textMatch =
          !term ||
          address.includes(term) ||
          title.includes(term) ||
          description.includes(term) ||
          cityValue.includes(term) ||
          stateValue.includes(term);

        const propertyAreaText = [cityValue, stateValue].join(" ");
        const areaMatch = !areaTerm || propertyAreaText.includes(areaTerm) || address.includes(areaTerm);

        return textMatch && areaMatch;
      });

      if (matchingProperty?.ownerId) {
        const ownerProfile = allUsers.find((data) => {
          if (data.uid !== matchingProperty.ownerId || data.uid === user?.uid) return false;
          const roleMatch = searchRoleFilter === "all" || (data.role || "") === searchRoleFilter;
          const statusMatch =
            searchStatusFilter === "all" ||
            (data.statusVisibility !== false && (data.profileStatus || "") === searchStatusFilter);
          const areaMatch = matchesAreaFilter(data, areaTerm || matchingProperty.city || "");
          return roleMatch && statusMatch && areaMatch;
        });

        if (ownerProfile) {
          setFoundUser(ownerProfile);
          setSearchResults([ownerProfile]);
          setSearchMessage(
            `User found from property match: ${ownerProfile.displayName || ownerProfile.email}`
          );
          return;
        }
      }

      setSearchResults([]);
      setSearchMessage("No user found with those filters.");
    } catch (error) {
      console.error(error);
      setSearchMessage(error.message);
    }
  };

  const openPublicProfile = async (userData = {}) => {
    const targetUid = userData?.uid || userData?.id || userData?.reviewerUserId || userData?.targetUserId || "";
    if (!targetUid) {
      setStatusMessage("Could not open this public profile.");
      return;
    }

    try {
      const profileSnap = await getDoc(doc(db, "publicProfiles", targetUid));
      const safeProfile = buildSafePublicProfile(
        profileSnap.exists() ? { uid: targetUid, id: targetUid, ...profileSnap.data() } : userData,
        targetUid
      );

      const [receivedSnap, writtenSnap] = await Promise.all([
        getDocs(query(collection(db, "reviews"), where("targetUserId", "==", targetUid))),
        getDocs(query(collection(db, "reviews"), where("reviewerUserId", "==", targetUid))),
      ]);

      const reviewsReceived = receivedSnap.docs
        .map((docSnap) => buildSafePublicReview({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => getReviewTimestampMs(b) - getReviewTimestampMs(a));
      const reviewsWritten = writtenSnap.docs
        .map((docSnap) => buildSafePublicReview({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => getReviewTimestampMs(b) - getReviewTimestampMs(a));
      const propertyReviewsWritten = allPropertyReviews
        .filter((item) => item?.reviewerUserId === targetUid)
        .map(buildSafePublicReview)
        .sort((a, b) => getReviewTimestampMs(b) - getReviewTimestampMs(a));
      const activeProperties = visibleProperties.filter(
        (item) =>
          item?.ownerId === targetUid ||
          item?.claimedByUserId === targetUid ||
          item?.managerUserId === targetUid ||
          item?.communityAddedByUserId === targetUid ||
          item?.claimRequestedByUserId === targetUid
      );

      setPublicProfileTab("about");
      setPublicProfileModal({
        profile: safeProfile,
        reviewsReceived,
        reviewsWritten,
        propertyReviewsWritten,
        activeProperties,
      });
      scrollAppToTop("auto");

      window.setTimeout(() => {
        const modal = publicProfileModalRef.current;
        if (modal) {
          modal.scrollTop = 0;
          modal.scrollTo?.({ top: 0, behavior: "auto" });
        }
      }, 0);
    } catch (error) {
      console.error("Open public profile failed:", error);
      setStatusMessage("Could not open this public profile.");
    }
  };

  const closePublicProfile = () => {
    setPublicProfileModal(null);
    setPublicProfileTab("about");
  };

  const openConversationWithUser = async (userData) => {
    if (!userData?.uid) return;

    try {
      const profileRef = doc(db, "publicProfiles", userData.uid);
      const snap = await getDoc(profileRef);

      if (snap.exists()) {
        const profileData = snap.data();
        setSelectedUser({ uid: userData.uid, id: userData.uid, ...profileData });
        setSearchMessage(
          `Opened conversation with ${
            profileData.displayName || profileData.email || "User"
          }.`
        );
      } else {
        setSelectedUser(userData);
        setSearchMessage(
          `Opened conversation with ${
            userData.displayName || userData.email || "User"
          }.`
        );
      }

      setActiveTab("messages");
      scrollAppToTop("auto");
    } catch (error) {
      console.error(error);
      setSelectedUser(userData);
      setActiveTab("messages");
      scrollAppToTop("auto");
    }
  };

  const autoPopulateSelectedUserFromProperty = async (property) => {
    if (!property) return;

    const candidateUid =
      property.ownerId ||
      property.claimedByUserId ||
      property.claimRequestedByUserId ||
      "";

    try {
      if (candidateUid) {
        const profileSnap = await getDoc(doc(db, "publicProfiles", candidateUid));
        if (profileSnap.exists()) {
          const nextUser = profileSnap.data();
          if (canReviewTargetRole(role, nextUser?.role || "")) {
            setSelectedUser(nextUser);
            setSearchMessage(
              `Selected user auto-filled from property: ${nextUser.displayName || nextUser.email || "User"}.`
            );
          }
          return;
        }
      }

      const candidateEmail = (
        property.ownerEmail ||
        property.claimRequestedByEmail ||
        ""
      ).trim().toLowerCase();

      if (candidateEmail) {
        const profilesSnap = await getDocs(collection(db, "publicProfiles"));
        const emailMatch = profilesSnap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .find((item) => (item.email || "").trim().toLowerCase() === candidateEmail);

        if (emailMatch && canReviewTargetRole(role, emailMatch?.role || "")) {
          setSelectedUser(emailMatch);
          setSearchMessage(
            `Selected user auto-filled from property: ${emailMatch.displayName || emailMatch.email || "User"}.`
          );
        }
      }
    } catch (error) {
      console.error("Auto populate selected user from property failed:", error);
    }
  };

  useEffect(() => {
    if (!selectedProperty) return;
    autoPopulateSelectedUserFromProperty(selectedProperty);
  }, [selectedProperty, role]);

  useEffect(() => {
    if (!selectedProperty || !showMobilePropertyModal) return;

    const scrollPropertyProfileToTop = () => {
      const modal = propertyProfileModalRef.current;
      if (modal) {
        modal.scrollTop = 0;
        modal.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
      }

      propertyProfileTopRef.current?.scrollIntoView?.({
        behavior: "auto",
        block: "start",
      });
    };

    scrollPropertyProfileToTop();
    scrollAppToTop("auto");
    const timer = window.setTimeout(scrollPropertyProfileToTop, 80);
    return () => window.clearTimeout(timer);
  }, [selectedProperty, showMobilePropertyModal]);

  const selectReviewUser = async (userData) => {
    if (!userData?.uid) return;

    const targetRole = userData?.role || "";
    if (!canReviewTargetRole(role, targetRole)) {
      setStatusMessage(
        "Review not allowed. Tenants can review landlords and property managers. Landlords and property managers can only review tenants."
      );
      return;
    }

    try {
      const profileRef = doc(db, "publicProfiles", userData.uid);
      const snap = await getDoc(profileRef);

      const resolvedUser = snap.exists() ? snap.data() : userData;
      const resolvedRole = resolvedUser?.role || "";

      if (!canReviewTargetRole(role, resolvedRole)) {
        setStatusMessage(
          "Review not allowed. Tenants can review landlords and property managers. Landlords and property managers can only review tenants."
        );
        return;
      }

      setSelectedUser(resolvedUser);
      setSearchMessage(
        `Selected ${resolvedUser.displayName || resolvedUser.email || "User"} for review.`
      );
      setActiveTab("reviews");
    } catch (error) {
      console.error(error);
      if (!canReviewTargetRole(role, targetRole)) {
        setStatusMessage(
          "Review not allowed. Tenants can review landlords and property managers. Landlords and property managers can only review tenants."
        );
        return;
      }
      setSelectedUser(userData);
      setActiveTab("reviews");
    }
  };

  const handleImproveReview = async (tone = "professional") => {
    const trimmedReviewText = reviewText.trim();

    if (!trimmedReviewText) {
      setStatusMessage("Write your review first.");
      return;
    }

    try {
      setImprovingReview(true);
      setAiTone(tone);
      setAiOriginalReviewText(trimmedReviewText);
      setStatusMessage("Improving review...");

      const response = await improveReviewCallable({
        reviewText: trimmedReviewText,
        tone,
      });

      const responseData = response?.data || {};
      const improvedText = String(
        responseData?.improvedText ||
          responseData?.improvedReview ||
          responseData?.reviewText ||
          responseData?.text ||
          responseData?.outputText ||
          responseData?.output ||
          ""
      ).trim();

      if (!improvedText) {
        setAiReviewDraft(trimmedReviewText);
        setStatusMessage("AI returned no text. Kept your original draft.");
        return;
      }

      setAiReviewDraft(improvedText);
      setStatusMessage(
        improvedText !== trimmedReviewText
          ? "AI draft ready."
          : "AI reviewed your text. You can still use it or verify to make it count."
      );
    } catch (error) {
      console.error("Improve review failed:", error);
      setAiReviewDraft(trimmedReviewText);
      setStatusMessage("AI failed. Kept your original draft.");
    } finally {
      setImprovingReview(false);
    }
  };

  const handleMakePropertyReviewSafer = async () => {
    const trimmedReviewText = String(propertyReviewText || livingExperience.notes || "").trim();
    if (!trimmedReviewText) {
      setStatusMessage("Write property review details first.");
      return;
    }

    try {
      setPropertyReviewImproving(true);
      setStatusMessage("Checking property review wording...");
      const response = await improveReviewCallable({
        reviewText: trimmedReviewText,
        tone: "review-safety",
      });
      const improved = String(response?.data?.improved || response?.data?.improvedText || "").trim();
      if (improved) {
        setPropertyReviewText(improved);
        setStatusMessage("Safer review draft ready. Confirm it still reflects your experience.");
      } else {
        setStatusMessage("No rewrite was returned. Please keep your review factual and professional.");
      }
    } catch (error) {
      console.error("Property review safety rewrite failed:", error);
      setStatusMessage("Could not rewrite this review right now. You can still revise it manually.");
    } finally {
      setPropertyReviewImproving(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!user || !selectedUser) {
      setStatusMessage("Select a user first.");
      return;
    }

    if (!selectedUserReviewAllowed) {
      setStatusMessage(
        "Review not allowed. Only tenants can review landlords or property managers. Landlords and property managers can only review tenants, and tenants cannot review other tenants."
      );
      return;
    }

    if (selectedUserAlreadyReviewed) {
      setStatusMessage(REVIEW_COOLDOWN_PERSON_MESSAGE);
      return;
    }

    if (Number(ratingValue || 0) <= 0) {
      setStatusMessage("Select stars before posting a review.");
      return;
    }

    try {
      setSubmittingReview(true);
      const isVerifiedUser = profile?.verified === true;
      const rawReviewText = reviewText.trim();
      const safeReviewText = makeReviewTextSafer(rawReviewText);
      const trimmedReviewText = safeReviewText;
      const reviewSafetyLocationContext = getReviewSafetyLocationContext();

      if (!reviewSafetyAccepted) {
        setStatusMessage("Please confirm the Review Safety & Legal Awareness statement before posting.");
        return;
      }

      const reviewSafetyCheck = validateSafeReviewText(trimmedReviewText);
      if (!reviewSafetyCheck.ok) {
        setStatusMessage(reviewSafetyCheck.message);
        return;
      }

      if (isVerifiedUser && !trimmedReviewText) {
        setStatusMessage("Write a review first.");
        return;
      }

      const preSubmitRiskCheck = isVerifiedUser
        ? await runPreSubmitReviewRiskCheck({
            text: trimmedReviewText,
            rating: Number(ratingValue),
            targetRole: selectedUser?.role || "",
            targetId: selectedUser.uid,
          })
        : { ok: true, analysis: null, moderation: null };

      if (!preSubmitRiskCheck.ok) {
        setReviewAnalysis(preSubmitRiskCheck.analysis || null);
        setStatusMessage(preSubmitRiskCheck.message || "This review may create legal or safety risk. Please revise it to be factual and professional.");
        return;
      }

      const priorUserReviewsSnap = await getDocs(
        query(
          collection(db, "reviews"),
          where("reviewerUserId", "==", user.uid),
          where("targetUserId", "==", selectedUser.uid)
        )
      );
      const priorUserReviews = priorUserReviewsSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      const cooldownState = getReviewCooldownState(priorUserReviews);

      if (cooldownState.withinCooldown) {
        setSubmittedReviewTargetIds((prev) => ({
          ...prev,
          [selectedUser.uid]: true,
        }));
        setStatusMessage(REVIEW_COOLDOWN_PERSON_MESSAGE);
        return;
      }

      const allReviewsSnap = await getDocs(collection(db, "reviews"));
      const allExistingReviews = allReviewsSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const reviewerCreatedAtSeconds =
        profile?.createdAt?.seconds || Math.floor(Date.now() / 1000);
      const nowSeconds = Math.floor(Date.now() / 1000);
      const reviewerAccountAgeDays = Math.max(
        0,
        Math.floor((nowSeconds - reviewerCreatedAtSeconds) / 86400)
      );

      const aiRisk = isVerifiedUser
        ? safeGetReviewRisk({
            reviewText: trimmedReviewText,
            rating: Number(ratingValue),
            reviewerAccountAgeDays,
            reviewerVerified: isVerifiedUser,
            priorReviewCount: allExistingReviews.filter(
              (item) => item?.reviewerUserId === user.uid
            ).length,
          })
        : { score: 0, label: "low", reasons: [] };

      const similarityMatches = isVerifiedUser
        ? getSimilarReviewMatches({
            existingReviews: allExistingReviews,
            reviewText: trimmedReviewText,
            targetUserId: selectedUser.uid,
            reviewerUserId: user.uid,
          })
        : [];

      const scamSignals = isVerifiedUser
        ? getCrossAccountScamSignals({
            existingReviews: allExistingReviews,
            reviewText: trimmedReviewText,
            reviewerUserId: user.uid,
            targetUserId: selectedUser.uid,
          })
        : {
            sameTextOtherTargetsCount: 0,
            reportsAgainstTarget: 0,
            repeatedExtremesByReviewer: 0,
          };

      const similarityRiskPoints = similarityMatches.length
        ? Math.min(24, Math.round(similarityMatches[0].similarity * 20))
        : 0;

      const scamRiskPoints =
        (scamSignals.sameTextOtherTargetsCount > 0 ? 18 : 0) +
        (scamSignals.reportsAgainstTarget >= 3 ? 12 : 0) +
        (scamSignals.repeatedExtremesByReviewer >= 8 ? 10 : 0);

      const localRiskScore = isVerifiedUser
        ? Math.max(
            0,
            Math.min(
              100,
              Number(aiRisk?.score || 0) + similarityRiskPoints + scamRiskPoints
            )
          )
        : 0;

      const riskReasons = isVerifiedUser
        ? [
            ...(Array.isArray(aiRisk?.reasons) ? aiRisk.reasons : []),
            ...(similarityMatches.length
              ? [
                  `Similar to ${similarityMatches.length} existing review${
                    similarityMatches.length !== 1 ? "s" : ""
                  }`,
                ]
              : []),
            ...(scamSignals.sameTextOtherTargetsCount > 0
              ? ["Same reviewer posted very similar complaints on other accounts"]
              : []),
            ...(scamSignals.reportsAgainstTarget >= 3
              ? ["Target account has multiple prior reported reviews"]
              : []),
            ...(scamSignals.repeatedExtremesByReviewer >= 8
              ? ["Reviewer has many extreme ratings over time"]
              : []),
          ]
        : ["Community rating only. Verify account to make reviews count."];

      const localRiskLabel = isVerifiedUser
        ? localRiskScore >= 70
          ? "high"
          : localRiskScore >= 35
          ? "medium"
          : "low"
        : "low";

      const localAutoApprove = isVerifiedUser && localRiskScore < 30;
      const localAutoFlag = isVerifiedUser && localRiskScore >= 60;
      const affectsScore = isVerifiedUser;
      const categoryPayload = isVerifiedUser
        ? reviewCategoryRatings
        : buildInitialCategoryRatings(selectedUser?.role || "");

      const reviewRef = await addDoc(collection(db, "reviews"), {
        authorId: user.uid,
        reviewerUserId: user.uid,
        reviewerEmail: user.email || "",
        reviewerDisplayName: profile?.displayName || user.email || "Anonymous",
        reviewerPhotoURL: profile?.photoURL || "",
        reviewerVerified: isVerifiedUser,

        targetUserId: selectedUser.uid,
        targetEmail: selectedUser.email || "",
        targetDisplayName: selectedUser.displayName || selectedUser.email || "",
        targetVerified: Boolean(selectedUser.verified),

        rating: Number(ratingValue),
        reviewVersion: cooldownState.reviewVersion,
        previousReviewId: cooldownState.previousReviewId,
        isUpdatedReview: cooldownState.isUpdatedReview,
        reviewLabel: cooldownState.isUpdatedReview ? "Updated Review" : "Review",
        categoryRatings: categoryPayload,
        rentalHistoryFeedback: buildRentalFeedbackSummary(rentalFeedback),
        reviewSafety: buildReviewSafetyMetadata({
          accepted: reviewSafetyAccepted,
          verified: isVerifiedUser,
          structured: true,
          locationContext: reviewSafetyLocationContext,
        }),
        reviewSafetyAcknowledged: true,
        reviewSafetyAcknowledgedAt: serverTimestamp(),
        reviewSafetyLocationContext,
        disputeStatus: "none",
        evidenceStatus: "not_requested",
        text: isVerifiedUser ? trimmedReviewText : "",
        isCommunityRating: !isVerifiedUser,
        affectsScore,
        verificationRequiredToCount: !isVerifiedUser,

        status: localAutoApprove || !isVerifiedUser ? "approved" : "pending",
        reported: localAutoFlag,

        aiRiskScore: localRiskScore,
        aiRiskLabel: localRiskLabel,
        aiReasons: riskReasons,
        aiBaseRiskScore: Number(aiRisk?.score || 0),
        similarityMatchCount: similarityMatches.length,
        similarityTopScore: similarityMatches[0]?.similarity || 0,
        flaggedForSimilarity: similarityMatches.length > 0,
        sameTextOtherTargetsCount: scamSignals.sameTextOtherTargetsCount,
        reportsAgainstTarget: scamSignals.reportsAgainstTarget,
        repeatedExtremesByReviewer: scamSignals.repeatedExtremesByReviewer,
        scamSignalDetected:
          scamSignals.sameTextOtherTargetsCount > 0 ||
          scamSignals.reportsAgainstTarget >= 3 ||
          scamSignals.repeatedExtremesByReviewer >= 8,
        openAIModerationStatus: isVerifiedUser ? "completed" : "not_required",
        openAIClassificationStatus: isVerifiedUser ? "completed" : "completed",
        moderationPending: false,
        aiStructuredAnalysis: preSubmitRiskCheck.analysis || null,
        aiPreSubmitModeration: preSubmitRiskCheck.moderation?.moderation || preSubmitRiskCheck.moderation || null,

        response: {
          text: "",
          responderUserId: "",
          responderDisplayName: "",
          responderRole: "",
          responderVerified: false,
          createdAt: null,
          updatedAt: null,
        },

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      let nextStatusMessage = !isVerifiedUser
        ? "Community rating submitted. Verify your account to make future reviews count."
        : localAutoFlag
        ? "Review submitted and flagged for admin review."
        : localAutoApprove
        ? "Review posted instantly."
        : "Review submitted for admin approval.";

      if (isVerifiedUser) {
        setReviewAnalysis(preSubmitRiskCheck.analysis || null);
        if (preSubmitRiskCheck.moderation?.finalFlag) {
          nextStatusMessage = "Review submitted and flagged for admin review.";
        } else if (preSubmitRiskCheck.moderation?.finalStatus === "pending") {
          nextStatusMessage = "Review submitted for admin approval.";
        } else {
          nextStatusMessage = "Review posted instantly.";
        }
      } else {
        setReviewAnalysis(null);
        setShowVerifyPrompt(true);
      }

      if (localAutoApprove || !isVerifiedUser) {
        await syncUserTrustProfile(selectedUser.uid);
      }

      if (selectedUser.uid !== user.uid) {
        await createNotification({
          userId: selectedUser.uid,
          title: "New Review",
          body: `${profile?.displayName || "Someone"} left you a review.`,
          type: "review",
          meta: { reviewId: reviewRef.id, reviewerUserId: user.uid, reviewTargetUserId: selectedUser.uid },
        });
      }

      const clearedCategoryRatings = buildInitialCategoryRatings(
        selectedUser?.role || ""
      );
      setReviewText("");
      setAiReviewDraft("");
      setAiOriginalReviewText("");
      setReviewCategoryRatings(clearedCategoryRatings);
      setRatingValue(0);
      setRentalFeedback(RENTAL_FEEDBACK_DEFAULT);
      setReviewSafetyAccepted(false);
      setLastReviewRiskWarningText("");
      setSubmittedReviewTargetIds((prev) => ({
        ...prev,
        [selectedUser.uid]: true,
      }));
      setStatusMessage(nextStatusMessage + " Review submitted once and locked for this user.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleReportReview = async (reviewId) => {
    if (!user) return;

    try {
      setDoc(
        doc(db, "reviews", reviewId),
        {
          reported: true,
          reportedAt: serverTimestamp(),
          reportedBy: user.uid,
        },
        { merge: true }
      );

      setStatusMessage("Review reported.");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleDisputeReview = async (review, collectionName = "reviews") => {
    if (!user?.uid || !review?.id) {
      setStatusMessage("Log in first.");
      return;
    }

    const isInvolved =
      review?.targetUserId === user.uid ||
      review?.reviewerUserId === user.uid ||
      review?.propertyOwnerId === user.uid ||
      review?.claimedByUserId === user.uid;

    if (!isInvolved && !isAdmin) {
      setStatusMessage("Only someone involved in this review can dispute it.");
      return;
    }

    const reason = window.prompt(
      "Why are you disputing this review? Keep it factual. Evidence can be requested by admin.",
      ""
    );

    if (reason === null) return;
    const trimmedReason = String(reason || "").trim();

    if (trimmedReason.length < 10) {
      setStatusMessage("Please provide a short factual dispute reason.");
      return;
    }

    const safeReason = makeReviewTextSafer(trimmedReason);
    const safetyCheck = validateSafeReviewText(safeReason);
    if (!safetyCheck.ok) {
      setStatusMessage(safetyCheck.message);
      return;
    }

    try {
      setIsSubmittingDispute(true);

      await setDoc(
        doc(db, collectionName, review.id),
        {
          priorStatus: review?.status || "approved",
          priorAffectsScore: review?.affectsScore !== false,
          status: "disputed",
          disputeStatus: "open",
          affectsScore: false,
          reported: true,
          disputedAt: serverTimestamp(),
          disputedBy: user.uid,
          disputedByEmail: user.email || "",
          disputeReason: safeReason,
          evidenceStatus: "available_on_request",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await addDoc(collection(db, "reviewDisputes"), {
        reviewId: review.id,
        collectionName,
        targetUserId: review?.targetUserId || "",
        reviewerUserId: review?.reviewerUserId || "",
        disputedBy: user.uid,
        disputedByEmail: user.email || "",
        reason: safeReason,
        status: "open",
        evidenceStatus: "available_on_request",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (review?.targetUserId && review.targetUserId !== user.uid) {
        await createNotification({
          userId: review.targetUserId,
          title: "Review Disputed",
          body: "A review connected to your profile was disputed and paused from scoring while under review.",
          type: "review_dispute",
          meta: { reviewId: review.id, collectionName },
        });
      }

      if (review?.reviewerUserId && review.reviewerUserId !== user.uid) {
        await createNotification({
          userId: review.reviewerUserId,
          title: "Review Disputed",
          body: "Your review was disputed and may require supporting context.",
          type: "review_dispute",
          meta: { reviewId: review.id, collectionName },
        });
      }

      if (review?.targetUserId) {
        await syncUserTrustProfile(review.targetUserId);
      }

      setStatusMessage("Review disputed. It will not affect scoring while under review.");
    } catch (error) {
      console.error("Dispute review failed:", error);
      setStatusMessage(error.message || "Failed to dispute review.");
    } finally {
      setIsSubmittingDispute(false);
    }
  };

  const handleRequestReviewEvidence = async (review, collectionName = "reviews") => {
    if (!isAdmin || !review?.id) return;

    try {
      await setDoc(
        doc(db, collectionName, review.id),
        {
          evidenceStatus: "requested",
          evidenceRequestedAt: serverTimestamp(),
          evidenceRequestedBy: user?.email || "admin",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      if (review?.reviewerUserId) {
        await createNotification({
          userId: review.reviewerUserId,
          title: "Evidence Requested",
          body: "RentDuo admin requested supporting context for a disputed review.",
          type: "review_evidence",
          meta: { reviewId: review.id, collectionName },
        });
      }

      setStatusMessage("Evidence requested.");
    } catch (error) {
      console.error("Evidence request failed:", error);
      setStatusMessage(error.message || "Failed to request evidence.");
    }
  };

  const handleResolveDisputedReview = async (review, decision = "keep", collectionName = "reviews") => {
    if (!isAdmin || !review?.id) return;

    try {
      const keepReview = decision === "keep";
      const nextStatus = keepReview ? "approved" : "deleted";
      const nextAffectsScore = keepReview && review?.reviewerVerified === true;

      await setDoc(
        doc(db, collectionName, review.id),
        {
          status: nextStatus,
          disputeStatus: "resolved",
          disputeResolvedAt: serverTimestamp(),
          disputeResolvedBy: user?.email || "admin",
          disputeDecision: decision,
          affectsScore: nextAffectsScore,
          reported: false,
          reviewedAt: serverTimestamp(),
          reviewedBy: user?.email || "admin",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await addDoc(collection(db, "reviewDisputeResolutions"), {
        reviewId: review.id,
        collectionName,
        decision,
        resolvedBy: user?.uid || "",
        resolvedByEmail: user?.email || "",
        createdAt: serverTimestamp(),
      });

      if (review?.targetUserId) {
        await syncUserTrustProfile(review.targetUserId);
      }

      setStatusMessage(keepReview ? "Dispute resolved. Review kept." : "Dispute resolved. Review removed.");
    } catch (error) {
      console.error("Resolve dispute failed:", error);
      setStatusMessage(error.message || "Failed to resolve dispute.");
    }
  };

  const handleApproveReview = async (reviewId) => {
    try {
      const reviewSnap = await getDoc(doc(db, "reviews", reviewId));
      const targetUserId = reviewSnap.exists() ? reviewSnap.data()?.targetUserId || "" : "";

      await setDoc(
        doc(db, "reviews", reviewId),
        {
          status: "approved",
          reported: false,
          reviewedAt: serverTimestamp(),
          reviewedBy: user.email || "",
        },
        { merge: true }
      );

      if (targetUserId) {
        await syncUserTrustProfile(targetUserId);
      }

      setStatusMessage("Review approved.");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleRejectReview = async (reviewId) => {
    try {
      const reviewSnap = await getDoc(doc(db, "reviews", reviewId));
      const targetUserId = reviewSnap.exists() ? reviewSnap.data()?.targetUserId || "" : "";

      await setDoc(
        doc(db, "reviews", reviewId),
        {
          status: "deleted",
          reported: false,
          reviewedAt: serverTimestamp(),
          reviewedBy: user.email || "",
        },
        { merge: true }
      );

      if (targetUserId) {
        await syncUserTrustProfile(targetUserId);
      }

      setStatusMessage("Review removed.");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleReportUser = async (targetUser = selectedUser) => {
    if (!user || !targetUser?.uid) {
      setStatusMessage("Select a user first.");
      return;
    }

    try {
      await addDoc(collection(db, "userReports"), {
        reportedUserId: targetUser.uid,
        reportedUserEmail: "",
        reportedUserDisplayName:
          targetUser.displayName || "RentDuo member",

        reporterUserId: user.uid,
        reporterEmail: user.email || "",
        reporterDisplayName: profile?.displayName || user.email || "User",

        reason: "User reported from profile",
        createdAt: serverTimestamp(),
        status: "open",
      });

      setStatusMessage("User reported to admin.");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const PublicProfileNameButton = ({ userData, children, style = {} }) => {
    const uidValue = userData?.uid || userData?.id || userData?.reviewerUserId || userData?.targetUserId || "";
    if (!uidValue) return <span style={style}>{children}</span>;

    return (
      <button
        type="button"
        onClick={() => openPublicProfile(userData)}
        style={{ ...styles.publicProfileLinkButton, ...style }}
      >
        {children}
      </button>
    );
  };

  const PublicProfileModal = () => {
    if (!publicProfileModal?.profile) return null;

    const { profile: publicProfile, reviewsReceived, reviewsWritten, propertyReviewsWritten, activeProperties } =
      publicProfileModal;
    const safeReceived = reviewsReceived || [];
    const safeWritten = [...(reviewsWritten || []), ...(propertyReviewsWritten || [])].sort(
      (a, b) => getReviewTimestampMs(b) - getReviewTimestampMs(a)
    );
    const reviewCount = safeReceived.length;
    const profileReputationTimeline = profileReputationInsights[publicProfile.uid];
    const scoreValue = Number(publicProfile.trustScore || publicProfile.rentScore || 0);
    const locationLabel = [publicProfile.city, publicProfile.state].filter(Boolean).join(", ");
    const profileStatusMeta =
      publicProfile.statusVisibility !== false && publicProfile.profileStatus
        ? getProfileStatusMeta(publicProfile.profileStatus)
        : null;
    const statusLabel = profileStatusMeta?.label
      ? profileStatusMeta.label
      : publicProfile.role === "tenant"
      ? "Looking for place"
      : publicProfile.role === "landlord" || publicProfile.role === "property_manager"
      ? "Looking for tenant"
      : "Not active";
    const coverInitial = String(publicProfile.displayName || "R").charAt(0).toUpperCase();
    const tabItems = [
      { value: "about", label: t("about") },
      { value: "reviews", label: t("reviews") },
      { value: "properties", label: t("properties") },
      { value: "activity", label: language === "es" ? "Actividad" : "Activity" },
    ];

    const renderPublicReviewCard = (item, mode = "received") => (
      <div key={`${mode}-${item.id}`} style={styles.publicProfileListCard}>
        <div style={styles.profileTitleRowWrap}>
          <strong style={styles.strongHeading}>
            {mode === "written"
              ? item.propertyTitle || item.targetDisplayName || "Review"
              : item.reviewerDisplayName || "Anonymous"}
          </strong>
          {item.isUpdatedReview ? <span style={styles.communityReviewTag}>Updated Review</span> : null}
        </div>
        <div style={{ margin: "8px 0" }}>{renderStars(Number(item.rating || 0), false, null, 20)}</div>
        {item.text ? (
          <TranslatableText text={item.text} label="Review" bodyStyle={styles.bodyCopy} compact />
        ) : (
          <div style={styles.smallText}>{t("starOnlyRating")}</div>
        )}
        <div style={styles.reviewTimestamp}>
          {item?.createdAt?.seconds ? formatReviewDateTime(item.createdAt) : "Recent review"}
        </div>
      </div>
    );

    return (
      <div style={styles.publicProfileOverlay} onClick={closePublicProfile}>
        <div
          ref={publicProfileModalRef}
          style={{
            ...styles.publicProfileModal,
            borderRadius: isPhoneMobile ? 0 : 28,
            height: isPhoneMobile ? "100dvh" : "min(92dvh, 920px)",
            maxHeight: isPhoneMobile ? "100dvh" : "92dvh",
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div style={styles.publicProfileTopBar}>
            <button type="button" style={styles.mobileModalCloseButton} onClick={closePublicProfile}>
              {t("close")}
            </button>
          </div>

          <div style={styles.publicProfileHero}>
            <div style={styles.publicProfileCover}>
              <span>{coverInitial}</span>
            </div>
            <div style={styles.publicProfileIdentityRow}>
              <div style={styles.publicProfileAvatarWrap}>
                <Avatar src={publicProfile.photoURL} name={publicProfile.displayName} size={isPhoneMobile ? 92 : 112} />
              </div>
              <div style={styles.publicProfileIdentityText}>
                <div style={styles.profileTitleRowWrap}>
                  <h2 style={styles.publicProfileName}>{publicProfile.displayName}</h2>
                  {publicProfile.verified ? <VerifiedBadge label="Verified" /> : null}
                </div>
                <div style={styles.publicProfileMetaRow}>
                  <span>{getRoleLabel(publicProfile.role) || "Member"}</span>
                  {locationLabel ? <span>{locationLabel}</span> : null}
                </div>
                <div style={styles.publicProfileStatusPill}>
                  {statusLabel || "Not active"}
                </div>
              </div>
            </div>
          </div>

          <div style={styles.publicProfileStatGrid}>
            <div style={styles.publicProfileStatCard}>
              <strong>{scoreValue ? scoreValue.toFixed(0) : "0"}</strong>
              <span>Trust score</span>
            </div>
            <div style={styles.publicProfileStatCard}>
              <strong>{reviewCount}</strong>
              <span>Review{reviewCount === 1 ? "" : "s"}</span>
            </div>
            <div style={styles.publicProfileStatCard}>
              <strong>{activeProperties.length}</strong>
              <span>Linked props</span>
            </div>
          </div>

          <div style={styles.publicProfileActionRow}>
            <button
              type="button"
              style={{ ...styles.primaryButton, marginTop: 0, flex: "1 1 160px" }}
              onClick={() => {
                closePublicProfile();
                openConversationWithUser(publicProfile);
              }}
            >
              Message
            </button>
            <button
              type="button"
              style={{ ...styles.rejectButton, marginTop: 0, flex: "1 1 160px" }}
              onClick={() => handleReportUser(publicProfile)}
            >
              Report
            </button>
          </div>

          <div style={styles.publicProfileTabs}>
            {tabItems.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setPublicProfileTab(tab.value)}
                style={publicProfileTab === tab.value ? styles.publicProfileTabActive : styles.publicProfileTabButton}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {publicProfileTab === "about" ? (
            <div style={styles.publicProfileSection}>
              <div style={styles.publicProfileListCard}>
                <strong style={styles.strongHeading}>About</strong>
                <p style={styles.bodyCopy}>{publicProfile.bio || "No about info yet."}</p>
              </div>

              {getPublicAreaSummary(publicProfile) ? (
                <div style={styles.publicProfileListCard}>
                  <strong style={styles.strongHeading}>
                    {publicProfile.role === "tenant" ? "Preferred areas" : "Service areas"}
                  </strong>
                  <p style={styles.bodyCopy}>{getPublicAreaSummary(publicProfile)}</p>
                </div>
              ) : null}

              <RightsResources
                stateCode={getRightsStateCode(publicProfile.state, publicProfile.city, profile?.state, profile?.city)}
                defaultRole={publicProfile.role || ""}
                location={publicProfile.location || profile?.location || profileLocation}
                title={language === "es" ? "Conozca sus derechos" : "Know Your Rights"}
                language={language}
              />
            </div>
          ) : null}

          {publicProfileTab === "reviews" ? (
            <div style={styles.publicProfileSection}>
              <h3 style={styles.subsectionHeading}>Reviews received</h3>
              {safeReceived.length ? safeReceived.map((item) => renderPublicReviewCard(item, "received")) : (
                <div style={styles.publicProfileEmpty}>{t("noReviewsYet")}</div>
              )}
              <h3 style={styles.subsectionHeading}>Reviews written</h3>
              {safeWritten.length ? safeWritten.map((item) => renderPublicReviewCard(item, "written")) : (
                <div style={styles.publicProfileEmpty}>{t("noReviewsYet")}</div>
              )}
            </div>
          ) : null}

          {publicProfileTab === "properties" ? (
            <div style={styles.publicProfileSection}>
              {activeProperties.length ? (
                activeProperties.map((property) => (
                  <button
                    key={property.id}
                    type="button"
                    style={styles.publicProfilePropertyButton}
                    onClick={() => {
                      closePublicProfile();
                      handleSelectProperty(property);
                      if (isMobile) setShowMobilePropertyModal(true);
                      setActiveTab("home");
                    }}
                  >
                    <strong>{property.title || property.address || "Property"}</strong>
                    <span>{formatCityState(property) || property.address || "Community property"}</span>
                  </button>
                ))
              ) : (
                <div style={styles.publicProfileEmpty}>{t("noPropertiesLinked")}</div>
              )}
            </div>
          ) : null}

          {publicProfileTab === "activity" ? (
            <div style={styles.publicProfileSection}>
              <div style={styles.publicProfileListCard}>
                <strong style={styles.strongHeading}>Verification status</strong>
                <p style={styles.bodyCopy}>
                  {publicProfile.verified ? "Verified profile" : getEffectiveVerificationStatus(publicProfile)}
                </p>
              </div>
              <div style={styles.publicProfileListCard}>
                <strong style={styles.strongHeading}>Public activity</strong>
                <p style={styles.bodyCopy}>
                  {reviewCount} review{reviewCount === 1 ? "" : "s"} received, {safeWritten.length} public review{safeWritten.length === 1 ? "" : "s"} written, {activeProperties.length} linked propert{activeProperties.length === 1 ? "y" : "ies"}.
                </p>
              </div>
              {(publicProfile.role === "landlord" || publicProfile.role === "property_manager") ? (
                <div style={styles.aiRiskCard}>
                  <div style={styles.aiDraftEyebrow}>RentDuo AI</div>
                  <strong style={styles.strongHeading}>Reputation Timeline</strong>
                  {profileReputationTimeline ? (
                    <>
                      <p style={styles.smallText}>
                        Trend: {String(profileReputationTimeline.trend || "limited_data").replace("_", " ")}
                      </p>
                      <p style={styles.smallText}>{profileReputationTimeline.summary}</p>
                      {profileReputationTimeline.commonPraise?.length ? (
                        <p style={styles.smallText}>Common praise: {profileReputationTimeline.commonPraise.join(" | ")}</p>
                      ) : null}
                      {profileReputationTimeline.commonComplaints?.length ? (
                        <p style={styles.smallText}>Common concerns: {profileReputationTimeline.commonComplaints.join(" | ")}</p>
                      ) : null}
                      <p style={styles.aiRiskDisclaimer}>{profileReputationTimeline.disclaimer}</p>
                    </>
                  ) : (
                    <p style={styles.smallText}>Summarize public review patterns over time when enough activity is available.</p>
                  )}
                  <button
                    type="button"
                    style={{ ...styles.secondaryButton, width: "100%", marginTop: 10 }}
                    onClick={() => handleAnalyzeProfileReputation(publicProfile)}
                    disabled={profileReputationLoadingId === publicProfile.uid}
                  >
                    {profileReputationLoadingId === publicProfile.uid
                      ? "Summarizing..."
                      : profileReputationTimeline
                      ? "Refresh Timeline Summary"
                      : "Summarize Public Reputation"}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderRentDuoAIAssistant = () => (
    <RentDuoAIAssistant
      isOpen={isAiAssistantOpen}
      onClose={() => setIsAiAssistantOpen(false)}
      styles={styles}
      isMobile={isMobile}
      isPhoneMobile={isPhoneMobile}
      isSuggestedOpen={isAiSuggestedOpen}
      setIsSuggestedOpen={setIsAiSuggestedOpen}
      messages={aiAssistantMessages}
      messagesRef={aiAssistantMessagesRef}
      messagesEndRef={aiAssistantMessagesEndRef}
      loading={aiAssistantLoading}
      error={aiAssistantError}
      input={aiAssistantInput}
      setInput={setAiAssistantInput}
      onAsk={handleAskRentDuoAI}
    />
  );

  const handleResolveUserReport = async (reportId) => {
    try {
      setDoc(
        doc(db, "userReports", reportId),
        {
          status: "resolved",
          resolvedAt: serverTimestamp(),
          resolvedBy: user.email || "",
        },
        { merge: true }
      );
      setStatusMessage("User report resolved.");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleManagerOpenTenant = (tenant) => {
    if (!tenant?.uid) return;
    setSelectedManagerTenant(tenant);
    setSelectedUser(tenant);
  };

  const handleSubmitManagerFlag = async () => {
    if (!user?.uid || role !== "property_manager") {
      setStatusMessage("Only property managers can flag tenant issues.");
      return;
    }

    const activeTenant = selectedManagerTenant || selectedUser;

    if (!activeTenant?.uid || (activeTenant?.role || "") !== "tenant") {
      setStatusMessage("Select a tenant first.");
      return;
    }

    try {
      await addDoc(collection(db, "managerFlags"), {
        managerUserId: user.uid,
        managerEmail: user.email || "",
        managerDisplayName: profile?.displayName || user.email || "Manager",
        tenantUserId: activeTenant.uid,
        tenantEmail: activeTenant.email || "",
        tenantDisplayName: activeTenant.displayName || activeTenant.email || "Tenant",
        flagType: managerFlagType,
        note: managerFlagNote.trim(),
        createdAt: serverTimestamp(),
        status: "open",
      });

      setManagerFlagNote("");
      setStatusMessage("Manager flag saved.");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleCreateIssue = async () => {
    if (!user?.uid) {
      setStatusMessage("Log in first to report an issue.");
      return;
    }

    if (!selectedProperty?.id) {
      setStatusMessage("Select a property first.");
      return;
    }

    if (!newIssueText.trim()) {
      setStatusMessage("Describe the issue first.");
      return;
    }

    try {
      const propertyOwnerId = getPropertyClaimedUserId(selectedProperty);
      const issueRef = await addDoc(collection(db, "issues"), {
        propertyId: selectedProperty.id,
        propertyTitle: selectedProperty.title || "",
        propertyAddress: selectedProperty.address || "",
        tenantUserId: user.uid,
        tenantDisplayName: profile?.displayName || user.email || "User",
        tenantName: profile?.displayName || user.email || "User",
        issueType: newIssueType,
        issueText: newIssueText.trim(),
        description: newIssueText.trim(),
        status: "open",
        propertyOwnerId: propertyOwnerId || "",
        landlordResponse: "",
        landlordRespondedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setNewIssueType("maintenance");
      setNewIssueText("");
      setStatusMessage("Issue submitted. RentDuo AI is preparing a maintenance summary.");
      setMaintenanceAnalysisLoadingId(issueRef.id);
      analyzeMaintenanceIssueCallable({ issueId: issueRef.id })
        .then(() => setStatusMessage("Issue submitted and AI maintenance summary prepared."))
        .catch((analysisError) => {
          console.error("Maintenance issue AI analysis failed:", analysisError);
          setStatusMessage("Issue submitted. AI analysis is unavailable right now.");
        })
        .finally(() => setMaintenanceAnalysisLoadingId(""));
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleAnalyzeMaintenanceIssue = async (issue) => {
    if (!issue?.id) return;
    try {
      setMaintenanceAnalysisLoadingId(issue.id);
      const result = await analyzeMaintenanceIssueCallable({ issueId: issue.id });
      if (result?.data?.analysis) {
        setStatusMessage("Maintenance analysis updated.");
      }
    } catch (error) {
      console.error("Maintenance issue AI analysis failed:", error);
      setStatusMessage("Could not analyze this issue right now.");
    } finally {
      setMaintenanceAnalysisLoadingId("");
    }
  };

  const handleAnalyzePropertyRisk = async () => {
    if (!selectedProperty?.id) return;
    try {
      setPropertyRiskLoadingId(selectedProperty.id);
      setPropertyRiskError("");
      const result = await analyzePropertyRiskCallable({ propertyId: selectedProperty.id });
      const intelligence = result?.data?.intelligence;
      if (!intelligence) {
        throw new Error("No risk intelligence was returned.");
      }
      const nextProperty = { ...selectedProperty, aiRiskIntelligence: intelligence };
      setSelectedProperty(nextProperty);
      setProperties((prev) => prev.map((property) =>
        property.id === selectedProperty.id ? { ...property, aiRiskIntelligence: intelligence } : property
      ));
      setStatusMessage("AI property risk intelligence updated.");
    } catch (error) {
      console.error("Property risk analysis failed:", error);
      setPropertyRiskError(error?.message || "Property risk analysis is unavailable right now.");
    } finally {
      setPropertyRiskLoadingId("");
    }
  };

  const handleAnalyzePropertyScamRisk = async () => {
    if (!selectedProperty?.id) return;
    try {
      setPropertyScamLoadingId(selectedProperty.id);
      setPropertyScamError("");
      const result = await analyzeScamRiskCallable({
        sourceType: "property",
        propertyId: selectedProperty.id,
      });
      const analysis = result?.data?.analysis;
      if (!analysis) throw new Error("No scam screening result was returned.");
      setSelectedProperty((prev) => prev?.id === selectedProperty.id ? { ...prev, aiScamIntelligence: analysis } : prev);
      setProperties((prev) => prev.map((property) =>
        property.id === selectedProperty.id ? { ...property, aiScamIntelligence: analysis } : property
      ));
      setStatusMessage("AI scam screening updated.");
    } catch (error) {
      console.error("Property scam analysis failed:", error);
      setPropertyScamError(error?.message || "Scam screening is unavailable right now.");
    } finally {
      setPropertyScamLoadingId("");
    }
  };

  const handleExplainLease = async () => {
    if (!leaseText.trim()) {
      setLeaseError("Paste lease language to explain first.");
      return;
    }
    try {
      setLeaseExplaining(true);
      setLeaseError("");
      const result = await explainLeaseCallable({ leaseText });
      const explanation = result?.data?.explanation;
      if (!explanation) throw new Error("No lease explanation was returned.");
      setLeaseExplanation(explanation);
    } catch (error) {
      console.error("Lease explanation failed:", error);
      setLeaseError(error?.message || "Lease explanation is unavailable right now.");
    } finally {
      setLeaseExplaining(false);
    }
  };

  const handleAnalyzePropertyReputation = async () => {
    if (!selectedProperty?.id) return;
    try {
      setPropertyReputationLoadingId(selectedProperty.id);
      const result = await summarizeReputationTimelineCallable({
        targetType: "property",
        targetId: selectedProperty.id,
      });
      const timeline = result?.data?.timeline;
      if (!timeline) throw new Error("No reputation timeline was returned.");
      setSelectedProperty((prev) => prev?.id === selectedProperty.id ? { ...prev, aiReputationTimeline: timeline } : prev);
      setProperties((prev) => prev.map((property) =>
        property.id === selectedProperty.id ? { ...property, aiReputationTimeline: timeline } : property
      ));
      setStatusMessage("AI reputation timeline updated.");
    } catch (error) {
      console.error("Property reputation summary failed:", error);
      setStatusMessage(error?.message || "Could not summarize this property history right now.");
    } finally {
      setPropertyReputationLoadingId("");
    }
  };

  const handleAnalyzeProfileReputation = async (publicProfile) => {
    if (!publicProfile?.uid) return;
    try {
      setProfileReputationLoadingId(publicProfile.uid);
      const result = await summarizeReputationTimelineCallable({
        targetType: "profile",
        targetId: publicProfile.uid,
      });
      if (result?.data?.timeline) {
        setProfileReputationInsights((prev) => ({ ...prev, [publicProfile.uid]: result.data.timeline }));
      }
    } catch (error) {
      console.error("Profile reputation summary failed:", error);
      setStatusMessage(error?.message || "Could not summarize public profile activity right now.");
    } finally {
      setProfileReputationLoadingId("");
    }
  };

  const handleAnalyzeMessageScamRisk = async (textToScreen = messageText) => {
    if (!String(textToScreen || "").trim()) {
      setMessageScamError("Paste or type a message before screening it.");
      return;
    }
    try {
      setMessageScamLoading(true);
      setMessageScamError("");
      const result = await analyzeScamRiskCallable({ sourceType: "message", text: String(textToScreen) });
      setMessageScamAnalysis(result?.data?.analysis || null);
    } catch (error) {
      console.error("Message scam screening failed:", error);
      setMessageScamError(error?.message || "Message screening is unavailable right now.");
    } finally {
      setMessageScamLoading(false);
    }
  };

  const handleAiCenterMakeReviewSafer = async (text) => {
    const result = await improveReviewCallable({ reviewText: String(text || ""), tone: "review-safety" });
    const improved = result?.data?.improved || result?.data?.improvedText || "";
    if (!improved) throw new Error("No safer draft was returned.");
    return improved;
  };

  const handleAiCenterTextScamScreen = async (text) => {
    const result = await analyzeScamRiskCallable({ sourceType: "message", text: String(text || "") });
    const analysis = result?.data?.analysis;
    if (!analysis) throw new Error("No scam screening result was returned.");
    return analysis;
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const readFileAsText = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => resolve("");
      reader.readAsText(file);
    });

  const handleRunRentDuoAIFeature = async ({ feature, payload = {}, files = [] } = {}) => {
    if (!user?.uid) {
      throw new Error("Create an account or sign in to use RentDuo AI tools.");
    }

    const safeFeature = String(feature || "ai_feature").replace(/[^a-z0-9_-]/gi, "_").slice(0, 80);
    const selectedFiles = Array.from(files || []).slice(0, 8);
    const uploadedFiles = [];
    const imageDataUrls = [];
    const textExtracts = [];

    for (const file of selectedFiles) {
      const safeName = `${Date.now()}_${String(file.name || "upload").replace(/[^a-z0-9._-]/gi, "_")}`;
      const uploadRef = ref(storage, `aiUploads/${user.uid}/${safeFeature}/${safeName}`);
      await uploadBytes(uploadRef, file);
      const url = await getDownloadURL(uploadRef);
      uploadedFiles.push({
        name: file.name || safeName,
        contentType: file.type || "application/octet-stream",
        size: file.size || 0,
        url,
      });

      if (String(file.type || "").startsWith("image/") && imageDataUrls.length < 4 && file.size <= 4 * 1024 * 1024) {
        imageDataUrls.push(await readFileAsDataUrl(file));
      } else if (
        (String(file.type || "").startsWith("text/") || String(file.name || "").toLowerCase().endsWith(".txt")) &&
        file.size <= 2 * 1024 * 1024
      ) {
        textExtracts.push(await readFileAsText(file));
      }
    }

    const result = await runRentDuoAIFeatureCallable({
      feature: safeFeature,
      payload,
      uploadedFiles,
      imageDataUrls,
      extractedText: textExtracts.join("\n\n").slice(0, 16000),
    });

    return result?.data?.result;
  };

  const handleAnalyzeNeighborhood = async () => {
    if (!selectedProperty?.id) return;
    try {
      setNeighborhoodLoadingId(selectedProperty.id);
      setNeighborhoodError("");
      const result = await analyzeNeighborhoodIntelligenceCallable({ propertyId: selectedProperty.id });
      const intelligence = result?.data?.intelligence;
      if (!intelligence) throw new Error("No neighborhood insight was returned.");
      setSelectedProperty((prev) => prev?.id === selectedProperty.id ? { ...prev, aiNeighborhoodIntelligence: intelligence } : prev);
      setProperties((prev) => prev.map((property) =>
        property.id === selectedProperty.id ? { ...property, aiNeighborhoodIntelligence: intelligence } : property
      ));
    } catch (error) {
      console.error("Neighborhood intelligence failed:", error);
      setNeighborhoodError(error?.message || "Neighborhood intelligence is unavailable right now.");
    } finally {
      setNeighborhoodLoadingId("");
    }
  };

  const handleEstimateMarketRent = async () => {
    if (!selectedProperty?.id) return;
    try {
      setMarketRentLoadingId(selectedProperty.id);
      setMarketRentError("");
      const result = await estimateMarketRentCallable({ propertyId: selectedProperty.id });
      const estimate = result?.data?.estimate;
      if (!estimate) throw new Error("No market rent estimate was returned.");
      setSelectedProperty((prev) => prev?.id === selectedProperty.id ? { ...prev, aiMarketRentEstimate: estimate } : prev);
      setProperties((prev) => prev.map((property) =>
        property.id === selectedProperty.id ? { ...property, aiMarketRentEstimate: estimate } : property
      ));
    } catch (error) {
      console.error("Market rent estimate failed:", error);
      setMarketRentError(error?.message || "Market rent comparison is unavailable right now.");
    } finally {
      setMarketRentLoadingId("");
    }
  };

  const handleGenerateMoveChecklist = async () => {
    if (!selectedProperty?.id) return;
    try {
      setMoveChecklistLoading(true);
      setMoveChecklistError("");
      const result = await generateMoveChecklistCallable({
        propertyId: selectedProperty.id,
        moveType: moveChecklistMode,
      });
      setMoveChecklist({ ...(result?.data?.plan || {}), id: result?.data?.planId });
    } catch (error) {
      console.error("Move checklist generation failed:", error);
      setMoveChecklistError(error?.message || "Could not create a checklist right now.");
    } finally {
      setMoveChecklistLoading(false);
    }
  };

  const handleToggleMoveChecklistItem = async (itemId) => {
    if (!moveChecklist?.id) return;
    const items = (moveChecklist.items || []).map((item) =>
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );
    setMoveChecklist((prev) => ({ ...prev, items }));
    try {
      await setDoc(doc(db, "moveAssistantPlans", moveChecklist.id), {
        items,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (error) {
      console.error("Move checklist update failed:", error);
      setMoveChecklistError("Could not save that checklist update.");
    }
  };

  const handleFindSmartMatches = async () => {
    try {
      setSmartMatchLoading(true);
      setSmartMatchError("");
      const result = await getSmartMatchesCallable({ preferences: smartMatchPreferences });
      setSmartMatches(result?.data?.matches || []);
      setSmartMatchDisclaimer(result?.data?.disclaimer || "");
    } catch (error) {
      console.error("Smart matching failed:", error);
      setSmartMatchError(error?.message || "Could not find matches right now.");
    } finally {
      setSmartMatchLoading(false);
    }
  };

  const updateIssueStatus = async (issueId, newStatus) => {
    try {
      await setDoc(
        doc(db, "issues", issueId),
        {
          status: newStatus,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "",
        },
        { merge: true }
      );
      setStatusMessage("Issue updated.");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleRespondToIssue = async (issue) => {
    if (!issue?.id) return;
    const responseText = (issueResponseDrafts[issue.id] || "").trim();
    if (!responseText) {
      setStatusMessage("Write a response before sending.");
      return;
    }

    try {
      await setDoc(
        doc(db, "issues", issue.id),
        {
          landlordResponse: responseText,
          landlordRespondedAt: serverTimestamp(),
          status: issue.status === "open" ? "in_progress" : issue.status || "in_progress",
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || "",
        },
        { merge: true }
      );
      setIssueResponseDrafts((prev) => ({ ...prev, [issue.id]: "" }));
      setStatusMessage("Issue response sent.");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleSendMessage = async () => {
    if (!user || !selectedUser || !conversationId) {
      setStatusMessage("Select a user first.");
      return;
    }

    const trimmedMessage = messageText.trim();
    if (!trimmedMessage) {
      setStatusMessage("Type a message first.");
      return;
    }

    const clientMessageId = `local_${user.uid}_${Date.now()}`;
    const optimisticMessage = {
      id: clientMessageId,
      clientMessageId,
      conversationId,
      senderId: user.uid,
      senderEmail: user.email || "",
      senderDisplayName: profile?.displayName || user.email || "User",
      senderPhotoURL: profile?.photoURL || "",
      senderVerified: Boolean(isCurrentUserVerified),
      senderVerificationStatus: getEffectiveVerificationStatus(profile || {}),
      receiverId: selectedUser.uid,
      receiverEmail: selectedUser.email || "",
      receiverDisplayName: selectedUser.displayName || selectedUser.email || "",
      receiverPhotoURL: selectedUser.photoURL || "",
      receiverVerified: isVerifiedProfileStatus(getEffectiveVerificationStatus(selectedUser || {})),
      receiverVerificationStatus: getEffectiveVerificationStatus(selectedUser || {}),
      text: trimmedMessage,
      helpfulCount: 0,
      helpfulBy: [],
      createdAtMs: Date.now(),
      pending: true,
    };

    pendingMessagesRef.current = [...pendingMessagesRef.current, optimisticMessage];
    setMessages((prev) => [...prev.filter((item) => item.id !== clientMessageId), optimisticMessage]);
    setMessageText("");

    try {
      const messageRef = await addDoc(collection(db, "messages"), {
        conversationId,
        clientMessageId,
        senderId: user.uid,
        senderEmail: user.email || "",
        senderDisplayName: profile?.displayName || user.email || "User",
        senderPhotoURL: profile?.photoURL || "",
        senderVerified: Boolean(isCurrentUserVerified),
        senderVerificationStatus: getEffectiveVerificationStatus(profile || {}),

        receiverId: selectedUser.uid,
        receiverEmail: selectedUser.email || "",
        receiverDisplayName: selectedUser.displayName || selectedUser.email || "",
        receiverPhotoURL: selectedUser.photoURL || "",
        receiverVerified: isVerifiedProfileStatus(getEffectiveVerificationStatus(selectedUser || {})),
        receiverVerificationStatus: getEffectiveVerificationStatus(selectedUser || {}),

        text: trimmedMessage,
        helpfulCount: 0,
        helpfulBy: [],
        createdAt: serverTimestamp(),
      });

      pendingMessagesRef.current = pendingMessagesRef.current.filter(
        (item) => item.clientMessageId !== clientMessageId
      );
      setMessages((prev) =>
        prev.map((item) =>
          item.id === clientMessageId
            ? { ...optimisticMessage, id: messageRef.id, pending: false }
            : item
        )
      );

      if (selectedUser.uid !== user.uid) {
        createNotification({
          userId: selectedUser.uid,
          title: "New Message",
          body: `${profile?.displayName || "Someone"} sent you a message.`,
          type: "message",
          meta: { conversationId, messageId: messageRef.id, senderId: user.uid, senderUserId: user.uid },
        });
      }

      setStatusMessage("Message sent.");
    } catch (error) {
      console.error(error);
      pendingMessagesRef.current = pendingMessagesRef.current.filter(
        (item) => item.clientMessageId !== clientMessageId
      );
      setMessages((prev) => prev.filter((item) => item.id !== clientMessageId));
      setMessageText(trimmedMessage);
      setStatusMessage(error.message);
    }
  };

  const geocodeAddressFromText = async (addressText) => {
    if (!window.google?.maps?.Geocoder) {
      throw new Error("Google geocoder is not ready yet.");
    }

    return await new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address: addressText }, (results, status) => {
        if (
          status === "OK" &&
          Array.isArray(results) &&
          results[0]?.geometry?.location
        ) {
          const location = results[0].geometry.location;
          resolve({
            lat: Number(location.lat()),
            lng: Number(location.lng()),
          });
          return;
        }

        reject(
          new Error(
            "Could not place the pin from that address. Select a Google dropdown result or click the map."
          )
        );
      });
    });
  };

  const handlePropertyMapClick = (event) => {
    if (!user) {
      setStatusMessage("Log in first to add a property.");
      return;
    }

    if (!event?.latLng) return;

    const lat = Number(event.latLng.lat());
    const lng = Number(event.latLng.lng());

    setPendingPropertyLocation({ lat, lng });
    setPropertyMapCenter({ lat, lng });
    setStatusMessage(
      `Pin dropped at lat ${lat.toFixed(5)}, lng ${lng.toFixed(5)}.`
    );
  };

  useEffect(() => {
    if (!selectedProperty?.id) return;
    setRentalEditDraft({
      isForRent: Boolean(selectedProperty.isForRent),
      monthlyRent: selectedProperty.monthlyRent ?? "",
      availableDate: selectedProperty.availableDate || "",
      bedrooms: selectedProperty.bedrooms ?? "",
      bathrooms: selectedProperty.bathrooms ?? "",
      leaseType: selectedProperty.leaseType || "",
    });
  }, [selectedProperty?.id, selectedProperty?.isForRent, selectedProperty?.monthlyRent, selectedProperty?.availableDate, selectedProperty?.bedrooms, selectedProperty?.bathrooms, selectedProperty?.leaseType]);

  useEffect(() => {
    if (!user?.uid || !selectedProperty?.id) {
      setMoveChecklist(null);
      return;
    }
    let isCurrent = true;
    const planId = `${user.uid}_${selectedProperty.id}_${moveChecklistMode}`;
    setMoveChecklist(null);
    setMoveChecklistError("");
    getDoc(doc(db, "moveAssistantPlans", planId))
      .then((snapshot) => {
        if (isCurrent && snapshot.exists()) {
          setMoveChecklist({ id: snapshot.id, ...snapshot.data() });
        }
      })
      .catch((error) => {
        console.error("Move checklist load failed:", error);
      });
    return () => {
      isCurrent = false;
    };
  }, [user?.uid, selectedProperty?.id, moveChecklistMode]);

  const handleSaveRentalListing = async () => {
    if (!selectedProperty?.id || !canManageRentalListing(selectedProperty)) {
      setStatusMessage("Only the verified property owner/manager can edit rental availability.");
      return;
    }

    const rentalPayload = {
      ...normalizeRentalListingDraft(rentalEditDraft),
      updatedAt: serverTimestamp(),
    };

    setIsSavingRentalListing(true);
    try {
      await setDoc(doc(db, "properties", selectedProperty.id), rentalPayload, { merge: true });
      setSelectedProperty((prev) => prev ? { ...prev, ...rentalPayload, updatedAt: new Date().toISOString() } : prev);
      setProperties((prev) =>
        prev.map((item) => item.id === selectedProperty.id ? { ...item, ...rentalPayload, updatedAt: new Date().toISOString() } : item)
      );
      if (rentalPayload.isForRent) {
        await createFeedEvent({
          type: "property_rental_listed",
          actorUserId: user.uid,
          actorDisplayName: profile?.displayName || user.email || "User",
          actorPhotoURL: profile?.photoURL || "",
          actorVerified: Boolean(profile?.verified),
          propertyId: selectedProperty.id,
          propertyAddress: selectedProperty.fullAddress || selectedProperty.address || "",
          normalizedAddress: selectedProperty.normalizedAddress || normalizePropertyAddress(selectedProperty.fullAddress || selectedProperty.address || ""),
          title: selectedProperty.title || selectedProperty.address || "Property",
          heading: "New rental listing posted",
          text: `${selectedProperty.title || selectedProperty.address || "A property"} is now listed for rent${rentalPayload.monthlyRent ? ` at ${formatMonthlyRent(rentalPayload.monthlyRent)}` : ""}.`,
          city: selectedProperty.city || "",
          state: selectedProperty.state || "",
          isForRent: true,
          monthlyRent: rentalPayload.monthlyRent,
          rentCurrency: "USD",
        });
      }
      setStatusMessage("Rental listing details saved.");
    } catch (error) {
      console.error("Rental listing save failed:", error);
      setStatusMessage(error.message || "Could not save rental listing details.");
    } finally {
      setIsSavingRentalListing(false);
    }
  };

  const getTenantResidencyClaimForSelectedProperty = () =>
    tenantResidencyClaims.find(
      (claim) =>
        claim.propertyId === selectedProperty?.id &&
        claim.tenantUserId === user?.uid &&
        ["pending", "approved", "active"].includes(String(claim.status || "").toLowerCase())
    ) || null;

  const handleClaimResidency = async () => {
    if (!user?.uid || role !== "tenant" || !selectedProperty?.id) return;
    if (getTenantResidencyClaimForSelectedProperty()) {
      setStatusMessage("You already have a residency claim pending or approved for this property.");
      return;
    }

    setIsSavingResidencyClaim(true);
    try {
      await setDoc(doc(db, "propertyResidencyClaims", `${selectedProperty.id}_${user.uid}`), {
        propertyId: selectedProperty.id,
        tenantUserId: user.uid,
        tenantDisplayName: profile?.displayName || user.email || "Tenant",
        tenantPhotoURL: profile?.photoURL || "",
        status: "pending",
        claimedAt: serverTimestamp(),
        movedOutAt: null,
        visibleToLandlordOnly: true,
      });
      setStatusMessage("Residency claim submitted for landlord review.");
    } catch (error) {
      console.error("Residency claim failed:", error);
      setStatusMessage(error.message || "Could not save residency claim.");
    } finally {
      setIsSavingResidencyClaim(false);
    }
  };

  const handleMoveOutResidency = async (claim = null) => {
    const activeClaim = claim || getTenantResidencyClaimForSelectedProperty();
    if (!activeClaim?.id) return;

    setIsSavingResidencyClaim(true);
    try {
      await setDoc(
        doc(db, "propertyResidencyClaims", activeClaim.id),
        {
          status: "moved_out",
          movedOutAt: serverTimestamp(),
        },
        { merge: true }
      );
      setStatusMessage("Residence marked as moved out.");
    } catch (error) {
      console.error("Move-out update failed:", error);
      setStatusMessage(error.message || "Could not update move-out status.");
    } finally {
      setIsSavingResidencyClaim(false);
    }
  };

  const handleUpdateResidencyClaimStatus = async (claim, nextStatus) => {
    if (!claim?.id || !canViewConnectedTenants(selectedProperty)) return;

    setIsSavingResidencyClaim(true);
    try {
      const payload = {
        status: nextStatus,
        reviewedAt: serverTimestamp(),
        reviewedBy: user?.uid || "",
      };
      if (nextStatus === "moved_out") {
        payload.movedOutAt = serverTimestamp();
      }
      await setDoc(doc(db, "propertyResidencyClaims", claim.id), payload, { merge: true });
      setStatusMessage(
        nextStatus === "approved"
          ? "Tenant residency approved."
          : nextStatus === "rejected"
          ? "Tenant residency rejected."
          : "Tenant marked as moved out."
      );
    } catch (error) {
      console.error("Residency status update failed:", error);
      setStatusMessage(error.message || "Could not update residency claim.");
    } finally {
      setIsSavingResidencyClaim(false);
    }
  };

  const handleCreateProperty = async () => {
    if (!user) return;

    if (!propertyTitle.trim() || !propertyAddress.trim()) {
      setStatusMessage("Property title and address are required.");
      return;
    }

    const resolvedFullAddress = formatFullAddress(propertyAddressMeta) || propertyAddress.trim();
    const normalizedResolvedAddress = normalizePropertyAddress(resolvedFullAddress);

    if (!normalizedResolvedAddress) {
      setStatusMessage("Choose a valid property address.");
      return;
    }

    if (isBlockedPropertyAddress(resolvedFullAddress)) {
      setStatusMessage("That property address cannot be added.");
      return;
    }

    const existingVisibleProperty = visibleProperties.find((item) => {
      const normalizedExistingAddress = item?.normalizedAddress || normalizePropertyAddress(
        item?.fullAddress || item?.address || item?.streetLine || item?.title || ""
      );
      return normalizedExistingAddress && normalizedExistingAddress === normalizedResolvedAddress;
    });

    if (existingVisibleProperty) {
      handleSelectProperty(existingVisibleProperty);
      setSelectedProperty(existingVisibleProperty);
      setPropertyMapCenter({
        lat: Number(existingVisibleProperty.lat),
        lng: Number(existingVisibleProperty.lng),
      });
      setPropertyMapZoom(15);
      setShowMobilePropertyModal(Boolean(isMobile));
      setStatusMessage("This property already exists. Opening the existing profile.");
      return;
    }

    try {
      let normalizedDuplicate = null;
      try {
        const normalizedDuplicateSnapshot = await getDocs(
          query(collection(db, "properties"), where("normalizedAddress", "==", normalizedResolvedAddress))
        );
        normalizedDuplicate = normalizedDuplicateSnapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .find((item) => item?.propertyStatus !== "deleted") || null;
      } catch (duplicateQueryError) {
        console.warn("Normalized property duplicate query failed; falling back to address scan.", duplicateQueryError);
      }

      const existingPropertySnapshot = normalizedDuplicate
        ? { docs: [] }
        : await getDocs(collection(db, "properties"));
      const firestoreDuplicate = normalizedDuplicate || existingPropertySnapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .find((item) => {
          if (item?.propertyStatus === "deleted") return false;
          const normalizedExistingAddress = item?.normalizedAddress || normalizePropertyAddress(
            item?.fullAddress || item?.address || item?.streetLine || item?.title || ""
          );
          return normalizedExistingAddress && normalizedExistingAddress === normalizedResolvedAddress;
        });

      if (firestoreDuplicate) {
        const normalizedDuplicate = sanitizePropertyForMap(firestoreDuplicate);
        handleSelectProperty(normalizedDuplicate);
        setSelectedProperty(normalizedDuplicate);
        if (isFiniteCoordinate(normalizedDuplicate.lat) && isFiniteCoordinate(normalizedDuplicate.lng)) {
          setPropertyMapCenter({
            lat: Number(normalizedDuplicate.lat),
            lng: Number(normalizedDuplicate.lng),
          });
          setPropertyMapZoom(15);
        }
        setShowMobilePropertyModal(Boolean(isMobile));
        setStatusMessage("This property already exists. Opening the existing profile.");
        return;
      }

      let finalPropertyLocation = pendingPropertyLocation;
      let finalPropertyImageUrl = propertyImageUrl.trim();

      if (propertyImageFile) {
        setIsUploadingPropertyImage(true);
        finalPropertyImageUrl = await uploadPropertyImageIfNeeded();
      }

      if (!finalPropertyLocation && propertyAddress.trim()) {
        finalPropertyLocation = await geocodeAddressFromText(propertyAddress.trim());
      }

      if (!finalPropertyLocation) {
        setStatusMessage(
          "Select an address from the Google dropdown OR click the map to place the pin."
        );
        return;
      }

      const docRef = doc(collection(db, "properties"));
      const canAddRentalListing = role === "landlord" || role === "property_manager";
      const isForRent = Boolean(canAddRentalListing && propertyIsForRent);
      const monthlyRent = String(propertyMonthlyRent || "").replace(/[$,\s]/g, "");
      const rentValue = Number(monthlyRent);
      const parsedBedrooms = Number(String(propertyBedrooms || "").replace(/[^0-9.]/g, ""));
      const parsedBathrooms = Number(String(propertyBathrooms || "").replace(/[^0-9.]/g, ""));
      const rentalListingFields = {
        isForRent: Boolean(isForRent),
        monthlyRent: isForRent && Number.isFinite(rentValue) && rentValue > 0 ? rentValue : null,
        rentCurrency: "USD",
        availableDate: isForRent ? propertyAvailableDate || "" : "",
        leaseType: isForRent ? propertyLeaseType.trim() || "" : "",
        bedrooms: isForRent && Number.isFinite(parsedBedrooms) && parsedBedrooms > 0 ? parsedBedrooms : null,
        bathrooms: isForRent && Number.isFinite(parsedBathrooms) && parsedBathrooms > 0 ? parsedBathrooms : null,
      };

      const localProperty = {
        id: docRef.id,
        ownerId: "",
        ownerEmail: "",
        ownerDisplayName: "",
        ownerPhotoURL: "",
        ownerVerified: false,
        claimStatus: "",
        claimedByUserId: "",
        claimRequestedByUserId: "",
        claimRequestedByEmail: "",
        communityAddedByUserId: user.uid,
        communityAddedByEmail: user.email || "",
        communityAddedByDisplayName: profile?.displayName || user.email || "User",
        managerUserId: role === "property_manager" ? user.uid : "",
        managementType: role === "property_manager" ? "managed_property" : "community_added",
        propertyStatus: "active",
        title: propertyTitle.trim(),
        address: propertyAddress.trim(),
        fullAddress: formatFullAddress(propertyAddressMeta) || propertyAddress.trim(),
        normalizedAddress: normalizedResolvedAddress,
        description: propertyDescription.trim(),
        imageUrl: finalPropertyImageUrl,
        city: propertyAddressMeta.city || city.trim(),
        state: propertyAddressMeta.state || "",
        zip: propertyAddressMeta.zip || "",
        streetLine: propertyAddressMeta.streetLine || "",
        ...rentalListingFields,
        lat: Number(finalPropertyLocation.lat),
        lng: Number(finalPropertyLocation.lng),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isLocalPending: true,
      };

      setProperties((prev) => {
        const next = dedupePropertiesByAddress([
          localProperty,
          ...prev.filter((item) => item.id !== localProperty.id),
        ]);
        saveCachedProperties(next);
        return next;
      });
      setSelectedProperty(localProperty);
      setPendingPropertyLocation(finalPropertyLocation);
      setPropertyMapCenter(finalPropertyLocation);

      try {
        await setDoc(
          docRef,
          {
            ...localProperty,
            isLocalPending: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        setProperties((prev) =>
          prev.map((item) =>
            item.id === localProperty.id
              ? {
                  ...item,
                  isLocalPending: false,
                }
              : item
          )
        );

        await createFeedEvent({
          type: isForRent ? "property_rental_listed" : "property_added",
          actorUserId: user.uid,
          actorDisplayName: profile?.displayName || user.email || "User",
          actorPhotoURL: profile?.photoURL || "",
          actorVerified: Boolean(profile?.verified),
          propertyId: docRef.id,
          propertyAddress: formatFullAddress(propertyAddressMeta) || propertyAddress.trim(),
          normalizedAddress: normalizedResolvedAddress,
          title: propertyTitle.trim(),
          heading: isForRent ? "New rental listing posted" : "New property added",
          text: isForRent
            ? `${propertyTitle.trim()} is now listed for rent${rentalListingFields.monthlyRent ? ` at ${formatMonthlyRent(rentalListingFields.monthlyRent)}` : ""}.`
            : propertyDescription.trim() || "A new property was added to the community map.",
          city: propertyAddressMeta.city || city.trim(),
          state: propertyAddressMeta.state || "",
          isForRent,
          monthlyRent: rentalListingFields.monthlyRent,
          rentCurrency: "USD",
        });

        setStatusMessage("Property created, saved, and pinned on the map.");
      } catch (saveError) {
        console.error("CREATE PROPERTY FIRESTORE ERROR:", saveError);
        setStatusMessage(
          "Property pinned locally, but cloud save failed. Check Firestore rules/settings to sync across devices."
        );
      }

      setPropertyTitle("");
      setPropertyAddress("");
      setPropertyAddressMeta({ streetLine: "", city: "", state: "", zip: "" });
      if (propertyAddressInputRef.current) {
        propertyAddressInputRef.current.value = "";
      }
      setPropertyDescription("");
      setPropertyIsForRent(false);
      setPropertyMonthlyRent("");
      setPropertyAvailableDate("");
      setPropertyBedrooms("");
      setPropertyBathrooms("");
      setPropertyLeaseType("");
      clearPropertyImageSelection();
    } catch (error) {
      console.error("CREATE PROPERTY ERROR:", error);
      setStatusMessage(error.message || "Failed to create property.");
    } finally {
      setIsUploadingPropertyImage(false);
    }
  };


  const openClaimVerificationModal = (property = null) => {
    const nextProperty = property || selectedProperty;
    if (nextProperty) {
      setSelectedProperty(nextProperty);
      setPropertyClaimTarget(nextProperty);
    }

    const claimState = getPropertyClaimState(nextProperty);
    if (nextProperty?.id && !claimState.canOpenClaim) {
      setStatusMessage(claimState.label);
      return;
    }

    if (!canRoleClaimProperty(role, verificationStatus)) {
      setStatusMessage("Only landlords or property managers can claim a property.");
      return;
    }

    setShowClaimVerificationModal(true);
  };

  const closeClaimVerificationModal = () => {
    setShowClaimVerificationModal(false);
  };

  const handleClaimSelectedProperty = async () => {
    if (!user || !selectedProperty?.id) {
      setStatusMessage("Select a property first.");
      return;
    }

    if (!canRoleClaimProperty(role, verificationStatus)) {
      setStatusMessage("Only landlords or property managers can claim a property.");
      return;
    }

    const claimState = getPropertyClaimState(selectedProperty);
    if (!claimState.canOpenClaim) {
      setStatusMessage(claimState.label);
      return;
    }

    try {
      const claimPatch = {
        claimStatus: "pending",
        claimRequestedByUserId: user.uid,
        claimRequestedByEmail: user.email || "",
        claimRequestedByDisplayName: profile?.displayName || user.email || "User",
        claimRequestedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(
        doc(db, "properties", selectedProperty.id),
        claimPatch,
        { merge: true }
      );

      const localClaimPatch = {
        ...claimPatch,
        claimRequestedAt: new Date(),
        updatedAt: new Date(),
      };
      const nextSelectedProperty = { ...selectedProperty, ...localClaimPatch };
      setSelectedProperty(nextSelectedProperty);
      setPropertyClaimTarget(nextSelectedProperty);
      setProperties((prev) =>
        prev.map((property) =>
          property.id === selectedProperty.id ? { ...property, ...localClaimPatch } : property
        )
      );

      await createFeedEvent({
        type: "property_claimed",
        actorUserId: user.uid,
        actorDisplayName: profile?.displayName || user.email || "User",
        actorPhotoURL: profile?.photoURL || "",
        actorVerified: Boolean(profile?.verified),
        propertyId: selectedProperty.id,
        propertyAddress: selectedProperty.address || "",
        title: selectedProperty.title || "",
        text: "A landlord or property manager started claiming this property.",
        city: selectedProperty.city || "",
        state: selectedProperty.state || "",
      });

      const claimRecipientId =
        selectedProperty.ownerId ||
        selectedProperty.claimedByUserId ||
        selectedProperty.managerUserId ||
        "";
      if (claimRecipientId && claimRecipientId !== user.uid) {
        await createNotification({
          userId: claimRecipientId,
          title: "Property Claim Submitted",
          body: `${profile?.displayName || "Someone"} started a claim for your property.`,
          type: "property_claim",
          meta: {
            propertyId: selectedProperty.id,
            propertyTitle: selectedProperty.title || "",
          },
        });
      }

      setStatusMessage(
        "Property claim started. Now submit your verification docs below to complete the claim review."
      );
      setActiveTab("profile");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message || "Failed to start property claim.");
    }
  };

  const handleSelectProperty = (property) => {
    setSelectedProperty(property);
    setPropertyClaimTarget(property);
    setHoveredProperty(property);
    setActiveTab("home");
    setShowMobilePropertyModal(true);
    scrollAppToTop("auto");

    if (property?.lat != null && property?.lng != null) {
      setPropertyMapCenter({
        lat: Number(property.lat),
        lng: Number(property.lng),
      });
    }

    setStatusMessage(`Selected property: ${property.title}`);
  };

  const handleCloseMobilePropertyModal = () => {
    setShowMobilePropertyModal(false);
  };

  const updateLivingExperience = (key, value) => {
    setLivingExperience((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetLivingExperience = () => {
    setLivingExperience({
      noiseLevel: 0,
      neighborBehavior: 0,
      safety: 0,
      buildingCulture: 0,
      parking: 0,
      sharedSpaces: 0,
      notes: "",
    });
  };

  function getLivingExperienceAverage(data = {}) {
    const values = [
      Number(data?.noiseLevel || 0),
      Number(data?.neighborBehavior || 0),
      Number(data?.safety || 0),
      Number(data?.buildingCulture || 0),
      Number(data?.parking || 0),
      Number(data?.sharedSpaces || 0),
    ].filter((value) => value > 0);

    if (!values.length) return 0;

    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }

  function getLivingExperienceTag(summary = {}) {
    if ((summary?.parking || 0) > 0 && Number(summary.parking) < 3) {
      return "Parking Issues Reported";
    }
    if (Number(summary?.noiseLevel || 0) >= 4.2) {
      return "Quiet Building";
    }
    if (Number(summary?.safety || 0) >= 4.3) {
      return "Strong Safety Rating";
    }
    if (Number(summary?.buildingCulture || 0) >= 4) {
      return "Positive Building Culture";
    }
    return "Mixed Living Experience";
  };

  function getPropertyLivingExperienceSummary(reviews = []) {
    const propertyReviewsWithLiving = reviews.filter((review) => {
      if (!review?.livingExperience) return false;
      return [
        Number(review.livingExperience.noiseLevel || 0),
        Number(review.livingExperience.neighborBehavior || 0),
        Number(review.livingExperience.safety || 0),
        Number(review.livingExperience.buildingCulture || 0),
        Number(review.livingExperience.parking || 0),
        Number(review.livingExperience.sharedSpaces || 0),
      ].some((value) => value > 0);
    });

    if (!propertyReviewsWithLiving.length) {
      return null;
    }

    const averageFor = (key) => {
      const values = propertyReviewsWithLiving
        .map((review) => Number(review?.livingExperience?.[key] || 0))
        .filter((value) => value > 0);

      if (!values.length) return 0;
      return values.reduce((sum, value) => sum + value, 0) / values.length;
    };

    const summary = {
      noiseLevel: averageFor("noiseLevel"),
      neighborBehavior: averageFor("neighborBehavior"),
      safety: averageFor("safety"),
      buildingCulture: averageFor("buildingCulture"),
      parking: averageFor("parking"),
      sharedSpaces: averageFor("sharedSpaces"),
    };

    return {
      ...summary,
      overall: getLivingExperienceAverage(summary),
      tag: getLivingExperienceTag(summary),
      count: propertyReviewsWithLiving.length,
    };
  }

  const handleSubmitPropertyReview = async () => {
    if (!user || !selectedProperty) {
      setStatusMessage("Select a property first.");
      return;
    }

    if (Number(propertyRatingValue || 0) <= 0) {
      setStatusMessage("Select stars before posting a property review.");
      return;
    }

    const isVerifiedReviewer = Boolean(profile?.verified);

    if (!reviewSafetyAccepted) {
      setStatusMessage("Please confirm the Review Safety & Legal Awareness statement before posting.");
      return;
    }

    if (!isVerifiedReviewer && propertyReviewText.trim()) {
      setStatusMessage("Unverified users can leave a star-only community property rating.");
      return;
    }

    try {
      const reviewSafetyLocationContext = getReviewSafetyLocationContext();
      const propertyReviewCombinedText = [
        isVerifiedReviewer ? propertyReviewText.trim() : "",
        isVerifiedReviewer ? String(livingExperience.notes || "").trim() : "",
      ].filter(Boolean).join("\n\n");

      const preSubmitRiskCheck = isVerifiedReviewer
        ? await runPreSubmitReviewRiskCheck({
            text: propertyReviewCombinedText,
            rating: Number(propertyRatingValue),
            targetRole: "property",
            targetId: selectedProperty.id,
          })
        : { ok: true, analysis: null, moderation: null };

      if (!preSubmitRiskCheck.ok) {
        setReviewAnalysis(preSubmitRiskCheck.analysis || null);
        setStatusMessage(preSubmitRiskCheck.message || "This review may create legal or safety risk. Please revise it to be factual and professional.");
        return;
      }

      const priorPropertyReviewsSnap = await getDocs(
        query(
          collection(db, "propertyReviews"),
          where("reviewerUserId", "==", user.uid),
          where("propertyId", "==", selectedProperty.id)
        )
      );
      const priorPropertyReviews = priorPropertyReviewsSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      const cooldownState = getReviewCooldownState(priorPropertyReviews);

      if (cooldownState.withinCooldown) {
        setStatusMessage(REVIEW_COOLDOWN_PROPERTY_MESSAGE);
        return;
      }

      const propertyReviewRef = await addDoc(collection(db, "propertyReviews"), {
        authorId: user.uid,
        propertyId: selectedProperty.id,
        propertyTitle: selectedProperty.title || "",
        propertyAddress: selectedProperty.address || "",
        ownerId: selectedProperty.ownerId || "",
        ownerEmail: selectedProperty.ownerEmail || "",

        reviewerUserId: user.uid,
        reviewerEmail: user.email || "",
        reviewerDisplayName: profile?.displayName || user.email || "Anonymous",
        reviewerPhotoURL: profile?.photoURL || "",
        reviewerVerified: isVerifiedReviewer,
        reviewerVerificationLabel: isVerifiedReviewer
          ? "Verified Reviewer"
          : "Unverified Reviewer",

        rating: Number(propertyRatingValue),
        reviewVersion: cooldownState.reviewVersion,
        previousReviewId: cooldownState.previousReviewId,
        isUpdatedReview: cooldownState.isUpdatedReview,
        reviewLabel: cooldownState.isUpdatedReview ? "Updated Review" : "Review",
        text: isVerifiedReviewer ? propertyReviewText.trim() : "",
        affectsScore: isVerifiedReviewer,
        reviewType: isVerifiedReviewer ? "verified_review" : "community_rating",
        reviewSafety: buildReviewSafetyMetadata({
          accepted: reviewSafetyAccepted,
          verified: isVerifiedReviewer,
          structured: true,
          locationContext: reviewSafetyLocationContext,
        }),
        reviewSafetyAcknowledged: true,
        reviewSafetyAcknowledgedAt: serverTimestamp(),
        reviewSafetyLocationContext,
        aiStructuredAnalysis: preSubmitRiskCheck.analysis || null,
        aiPreSubmitModeration: preSubmitRiskCheck.moderation?.moderation || preSubmitRiskCheck.moderation || null,
        livingExperience: {
          noiseLevel: Number(livingExperience.noiseLevel || 0),
          neighborBehavior: Number(livingExperience.neighborBehavior || 0),
          safety: Number(livingExperience.safety || 0),
          buildingCulture: Number(livingExperience.buildingCulture || 0),
          parking: Number(livingExperience.parking || 0),
          sharedSpaces: Number(livingExperience.sharedSpaces || 0),
          notes: isVerifiedReviewer ? String(livingExperience.notes || "").trim() : "",
          average: getLivingExperienceAverage(livingExperience),
        },
        status: "published",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (selectedProperty?.ownerId && selectedProperty.ownerId !== user.uid) {
        await createNotification({
          userId: selectedProperty.ownerId,
          title: "New Property Review",
          body: `${profile?.displayName || "Someone"} reviewed your property.`,
          type: "property_review",
          meta: { propertyId: selectedProperty.id, propertyTitle: selectedProperty.title || "", reviewId: propertyReviewRef.id },
        });
      }

      setPropertyRatingValue(0);
      setPropertyReviewText("");
      setReviewSafetyAccepted(false);
      setLastReviewRiskWarningText("");
      resetLivingExperience();
      window.scrollTo({ top: 0, behavior: "smooth" });

      if (!isVerifiedReviewer) {
        setShowVerifyPrompt(true);
        setStatusMessage("Community property rating posted. Property review form cleared.");
      } else {
        setStatusMessage("Property review posted. Property review form cleared.");
      }
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setStatusMessage("Logged out.");
    } catch (error) {
      console.error(error);
      setStatusMessage(error.message);
    }
  };

  const handleConfirmResetGrowthBaseline = async () => {
    if (!isAdmin || !user?.uid) {
      setStatusMessage("Only the founder/admin can reset the growth baseline.");
      return;
    }

    setIsResettingGrowthBaseline(true);
    try {
      const resetBy = {
        uid: user.uid,
        email: user.email || "",
        displayName: profile?.displayName || "",
      };
      const baselinePayload = {
        baselineUsers: rawDashboardTotals.users,
        baselineReviews: rawDashboardTotals.reviews,
        baselineMessages: rawDashboardTotals.messages,
        baselineProperties: rawDashboardTotals.properties,
        baselinePropertyReviews: rawDashboardTotals.propertyReviews,
        baselineVerified: rawDashboardTotals.verified,
        baselineClicks: rawDashboardTotals.clicks,
        baselineActiveWeek: rawDashboardTotals.activeWeek,
        resetAt: serverTimestamp(),
        resetBy,
      };
      const localBaseline = {
        ...baselinePayload,
        resetAt: new Date(),
      };

      console.log("Growth baseline reset current totals before reset", rawDashboardTotals);
      await setDoc(
        doc(db, "analytics", "growthBaseline"),
        baselinePayload,
        { merge: true }
      );
      console.log("Growth baseline document written", {
        path: "analytics/growthBaseline",
        baseline: localBaseline,
      });
      console.log("Growth baseline adjusted displayed totals", {
        users: Math.max(0, rawDashboardTotals.users - localBaseline.baselineUsers),
        reviews: Math.max(0, rawDashboardTotals.reviews - localBaseline.baselineReviews),
        messages: Math.max(0, rawDashboardTotals.messages - localBaseline.baselineMessages),
        properties: Math.max(0, rawDashboardTotals.properties - localBaseline.baselineProperties),
        propertyReviews: Math.max(0, rawDashboardTotals.propertyReviews - localBaseline.baselinePropertyReviews),
        activeWeek: Math.max(0, rawDashboardTotals.activeWeek - localBaseline.baselineActiveWeek),
        verified: Math.max(0, rawDashboardTotals.verified - localBaseline.baselineVerified),
        clicks: Math.max(0, rawDashboardTotals.clicks - localBaseline.baselineClicks),
      });
      setGrowthBaseline(localBaseline);
      setShowGrowthBaselineModal(false);
      setShowRawGrowthTotals(false);
      setStatusMessage("Growth baseline reset.");
    } catch (error) {
      console.error("Growth baseline reset failed:", error);
      setStatusMessage(error?.message || "Could not reset growth baseline. Check Firestore rules for analytics/growthBaseline.");
    } finally {
      setIsResettingGrowthBaseline(false);
    }
  };

  const handleCopyReferralLink = async () => {
    if (!referralLink) {
      setStatusMessage("Referral link unavailable right now.");
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(referralLink);
      } else {
        const tempInput = document.createElement("input");
        tempInput.value = referralLink;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        document.body.removeChild(tempInput);
      }
      setStatusMessage("Referral link copied.");
    } catch (error) {
      console.error(error);
      setStatusMessage("Could not copy referral link.");
    }
  };

  const renderStars = (
    value,
    clickable = false,
    onClickStar = null,
    size = 24
  ) => {
    return (
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {[1, 2, 3, 4, 5].map((star) => {
          const active = star <= value;
          return (
            <span
              key={star}
              onClick={clickable ? () => onClickStar(star) : undefined}
              style={{
                cursor: clickable ? "pointer" : "default",
                color: active ? "#f5b301" : "#d1d5db",
                userSelect: "none",
                fontSize: size,
                transition:
                  "transform 0.18s ease, color 0.18s ease, text-shadow 0.18s ease",
                transform: active ? "scale(1.03)" : "scale(1)",
                textShadow: active
                  ? "0 0 10px rgba(245, 179, 1, 0.35)"
                  : "none",
                lineHeight: 1,
              }}
            >
              ★
            </span>
          );
        })}
      </div>
    );
  };

  const renderTrustSummary = (snapshot = {}, label = "Rent Score") => {
    const score = Number(snapshot?.rentScore || snapshot?.trustScore || 0);
    const reviewCountValue = Number(snapshot?.reviewCount || 0);
    const averageRatingValue = Number(snapshot?.averageRating || 0);
    const trustLevelValue = snapshot?.trustLevel || "Low";
    const trustBadgeValue = snapshot?.trustBadge || safeGetTrustBadge(score);

    return (
      <div
        style={{
          marginTop: 14,
          padding: 16,
          borderRadius: 18,
          background: "linear-gradient(135deg, rgba(15,111,255,0.08) 0%, rgba(20,184,166,0.1) 100%)",
          border: "1px solid rgba(148,163,184,0.22)",
          display: "grid",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "#0f6fff" }}>
              {label}
            </div>
            <div style={{ fontSize: 34, fontWeight: 800, color: "#0f172a", lineHeight: 1.05 }}>
              {score}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, color: "#475569" }}>Trust level</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{trustLevelValue}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#14b8a6" }}>{trustBadgeValue}</div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
          <div style={{ padding: 10, borderRadius: 14, background: "rgba(255,255,255,0.82)" }}>
            <div style={{ fontSize: 12, color: "#64748b" }}>Average rating</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "#0f172a" }}>
              {averageRatingValue ? averageRatingValue.toFixed(1) : "0.0"}
            </div>
          </div>
          <div style={{ padding: 10, borderRadius: 14, background: "rgba(255,255,255,0.82)" }}>
            <div style={{ fontSize: 12, color: "#64748b" }}>Approved reviews</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "#0f172a" }}>{reviewCountValue}</div>
          </div>
          <div style={{ padding: 10, borderRadius: 14, background: "rgba(255,255,255,0.82)" }}>
            <div style={{ fontSize: 12, color: "#64748b" }}>Trust badge</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>{trustBadgeValue}</div>
          </div>
        </div>
      </div>
    );
  };

  const bluePinIcon =
    isMapLoaded && window.google?.maps
      ? {
          url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
          scaledSize: new window.google.maps.Size(isMobile ? 42 : 40, isMobile ? 42 : 40),
          anchor: new window.google.maps.Point(isMobile ? 21 : 20, isMobile ? 42 : 40),
        }
      : undefined;

  const greenPinIcon =
    isMapLoaded && window.google?.maps
      ? {
          url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
          scaledSize: new window.google.maps.Size(isMobile ? 42 : 40, isMobile ? 42 : 40),
          anchor: new window.google.maps.Point(isMobile ? 21 : 20, isMobile ? 42 : 40),
        }
      : undefined;

  const buildPropertyMarkerIcon = (property = null, isActive = false) => {
    const pinColor = isRentalListing(property) ? "#10b981" : "#0f6fff";
    const fallbackIcon = isRentalListing(property) ? greenPinIcon : bluePinIcon;
    if (!isMapLoaded || !window.google?.maps || !property?.imageUrl) return fallbackIcon;

    const size = isActive ? 60 : isMobile ? 52 : 48;
    const inner = isActive ? 48 : isMobile ? 40 : 36;
    const border = isActive ? 4 : 3;
    const safeUrl = String(property.imageUrl).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size + 14}" viewBox="0 0 ${size} ${size + 14}">
        <defs>
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="6" stdDeviation="6" flood-color="rgba(15,23,42,0.28)"/>
          </filter>
        </defs>
        <g filter="url(#shadow)">
          <circle cx="${size/2}" cy="${size/2}" r="${size/2 - border}" fill="white" />
          <clipPath id="clip">
            <circle cx="${size/2}" cy="${size/2}" r="${inner/2}" />
          </clipPath>
          <image href="${safeUrl}" x="${(size-inner)/2}" y="${(size-inner)/2}" width="${inner}" height="${inner}" preserveAspectRatio="xMidYMid slice" clip-path="url(#clip)" />
          <circle cx="${size/2}" cy="${size/2}" r="${inner/2 + 1.5}" fill="none" stroke="${isActive ? '#10b981' : pinColor}" stroke-width="${border}" />
          <path d="M ${size/2 - 10} ${size-2} L ${size/2} ${size+10} L ${size/2 + 10} ${size-2} Z" fill="${isActive ? '#10b981' : pinColor}" />
        </g>
      </svg>`;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(size, size + 14),
      anchor: new window.google.maps.Point(size / 2, size + 12),
    };
  };

  const markerIcon = bluePinIcon;

  const handleGoogleMapLoad = (mapInstance) => {
    propertyMapInstanceRef.current = mapInstance;
    window.setTimeout(() => {
      if (!window.google?.maps || !mapInstance) return;

      window.google.maps.event.trigger(mapInstance, "resize");

      if (hasUserMapLocation && propertyMapCenter) {
        mapInstance.panTo(propertyMapCenter);
        mapInstance.setZoom(propertyMapZoom || (isMobile ? 15 : 14));
        return;
      }

      const validProperties = visibleProperties.filter(
        (item) => item && isFiniteCoordinate(item.lat) && isFiniteCoordinate(item.lng)
      );

      if (validProperties.length === 1) {
        mapInstance.panTo({
          lat: Number(validProperties[0].lat),
          lng: Number(validProperties[0].lng),
        });
        mapInstance.setZoom(isMobile ? 14 : 13);
        return;
      }

      if (validProperties.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();
        validProperties.forEach((item) => {
          bounds.extend({ lat: Number(item.lat), lng: Number(item.lng) });
        });

        if (!bounds.isEmpty()) {
          mapInstance.fitBounds(bounds, isMobile ? 28 : 48);
        }
      }
    }, 350);
  };

  const handlePropertyMapTypeChange = (nextMapTypeId) => {
    setMapType(nextMapTypeId);
    if (propertyMapInstanceRef.current?.setMapTypeId) {
      propertyMapInstanceRef.current.setMapTypeId(nextMapTypeId);
    }
  };

  useEffect(() => {
    if (!showAuthDisclaimer) {
      setDisclaimerHasScrolledToEnd(false);
      return;
    }

    scrollAppToTop("auto");

    const timer = window.setTimeout(() => {
      const el = disclaimerScrollRef.current;
      if (!el) return;

      el.scrollTop = 0;
      el.scrollTo?.({ top: 0, left: 0, behavior: "auto" });

      const canAcceptImmediately = el.scrollHeight <= el.clientHeight + 8;
      if (canAcceptImmediately) {
        setDisclaimerHasScrolledToEnd(true);
      }
    }, 60);

    return () => window.clearTimeout(timer);
  }, [showAuthDisclaimer]);

  const handleDisclaimerScroll = (event) => {
    const element = event.currentTarget;
    const reachedBottom =
      element.scrollTop + element.clientHeight >= element.scrollHeight - 8;

    if (reachedBottom) {
      setDisclaimerHasScrolledToEnd(true);
    }
  };

  const mobileCleanMapOptions = useMemo(
    () => ({
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: true,
      clickableIcons: false,
      gestureHandling: isMobile ? "greedy" : "auto",
      keyboardShortcuts: !isMobile,
      rotateControl: false,
      scaleControl: false,
      scrollwheel: true,
      tilt: 0,
      styles: [
        {
          featureType: "poi",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "transit",
          stylers: [{ visibility: "off" }],
        },
      ],
    }),
    [isMobile]
  );


  const Avatar = ({ src, name, size = 64 }) => {
    const initials = (name || "?")
      .split(" ")
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

    if (src) {
      return (
        <img
          src={src}
          alt={name || "Profile"}
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            objectFit: "cover",
            border: "2px solid rgba(255,255,255,0.9)",
            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.16)",
            background: "#fff",
            flexShrink: 0,
          }}
        />
      );
    }

    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #0f6fff 0%, #14b86a 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 800,
          fontSize: size > 50 ? 20 : 14,
          boxShadow: "0 12px 28px rgba(15, 23, 42, 0.18)",
          flexShrink: 0,
        }}
      >
        {initials}
      </div>
    );
  };

  const VerificationStatusBadge = ({ status }) => {
    const map = {
      unverified: {
        label: "Unverified",
        background: "#eef2f7",
        color: "#475569",
        border: "1px solid #d9e2ec",
      },
      pending_review: {
        label: "Pending Review",
        background: "#fff4d6",
        color: "#9a6700",
        border: "1px solid #ffe7a3",
      },
      tenant_verified: {
        label: "Tenant Verified",
        background: "#eaf8ef",
        color: "#117a43",
        border: "1px solid #ccefd8",
      },
      verified: {
        label: "Verified",
        background: "#eaf8ef",
        color: "#117a43",
        border: "1px solid #ccefd8",
      },
      landlord_verified: {
        label: "Landlord Verified",
        background: "#e7f1ff",
        color: "#0f6fff",
        border: "1px solid #cfe2ff",
      },
      property_manager_verified: {
        label: "Property Manager Verified",
        background: "#f1ebff",
        color: "#6d28d9",
        border: "1px solid #e2d5ff",
      },
    };

    const current = map[status] || map.unverified;

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          borderRadius: 999,
          background: current.background,
          color: current.color,
          fontWeight: 800,
          fontSize: 12,
          border: current.border,
          letterSpacing: 0.2,
          whiteSpace: "nowrap",
        }}
      >
        {current.label}
      </span>
    );
  };

  const ProfileStatusBadge = ({ role: badgeRole = "tenant", status = "", isPublic = true }) => {
    if (!status || isPublic === false) return null;

    const current = getProfileStatusMeta(status);

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          borderRadius: 999,
          background: current.background,
          color: current.color,
          fontWeight: 800,
          fontSize: 12,
          border: current.border,
          letterSpacing: 0.2,
          whiteSpace: "nowrap",
        }}
      >
        {badgeRole === "tenant" ? "🏠" : "📍"} {current.label}
      </span>
    );
  };

  const VerifiedBadge = ({ label = "Verified" }) => (
    <span style={styles.verifiedBadge}>{label}</span>
  );

  const SectionTitle = ({ title, subtitle }) => (
    <div style={styles.sectionTitleWrap}>
      <div style={styles.sectionEyebrow}>RentDuo</div>
      <h2 style={styles.sectionTitle}>{title}</h2>
      {subtitle ? <p style={styles.sectionSubtitle}>{subtitle}</p> : null}
    </div>
  );

  function isRentalListing(property = {}) {
    return property?.isForRent === true && hasApprovedPropertyClaim(property) && Boolean(getPropertyClaimedUserId(property));
  }

  function getPropertyClaimedUserId(property = {}) {
    return property?.claimedBy || property?.claimedByUserId || property?.ownerId || property?.managerUserId || "";
  }

  function hasApprovedPropertyClaim(property = {}) {
    return ["claimed", "verified", "approved"].includes(String(property?.claimStatus || "")) || property?.ownerVerified === true;
  }

  function getPropertyClaimState(property = {}) {
    const status = String(property?.claimStatus || "").toLowerCase();
    const currentUid = user?.uid || "";
    const approved = hasApprovedPropertyClaim(property);
    const pending = ["pending", "pending_claim", "pending_review"].includes(status);
    const denied = ["denied", "rejected"].includes(status);
    const claimedUid = getPropertyClaimedUserId(property);
    const requestedUid = property?.claimRequestedByUserId || property?.claimRequestedBy || "";
    const isClaimedByCurrentUser = Boolean(currentUid && claimedUid === currentUid);
    const isRequestedByCurrentUser = Boolean(currentUid && requestedUid === currentUid);

    if (approved && isClaimedByCurrentUser) {
      return { key: "managed", label: "Claimed / Manage Property", canOpenClaim: false, tone: "success" };
    }

    if (approved) {
      return { key: "claimed_by_other", label: "Property Claimed", canOpenClaim: false, tone: "muted" };
    }

    if (pending && isRequestedByCurrentUser) {
      return { key: "pending_current_user", label: "Claim Pending Review", canOpenClaim: false, tone: "pending" };
    }

    if (pending) {
      return { key: "pending_other", label: "Claim Under Review", canOpenClaim: false, tone: "pending" };
    }

    if (denied && isRequestedByCurrentUser) {
      return { key: "rejected_current_user", label: "Claim Rejected - Resubmit", canOpenClaim: true, tone: "danger" };
    }

    return { key: "claimable", label: "Claim Property", canOpenClaim: true, tone: "default" };
  }

  function getPropertyClaimBadgeStyle(property = {}) {
    const claimState = getPropertyClaimState(property);
    if (claimState.tone === "success") return styles.statusPillSuccess || styles.verifiedBadge;
    if (claimState.tone === "pending") return styles.statusPillPending;
    if (claimState.tone === "danger") return styles.statusPillDanger || styles.statusPillMuted;
    return styles.statusPillMuted;
  }

  function canManageRentalListing(property = {}) {
    if (!user?.uid || !property?.id) return false;
    if (!(role === "landlord" || role === "property_manager")) return false;
    if (!hasApprovedPropertyClaim(property)) return false;
    return [
      property?.claimedBy,
      property?.claimedByUserId,
      property?.ownerId,
      property?.managerUserId,
    ].filter(Boolean).includes(user.uid);
  }

  function canViewConnectedTenants(property = {}) {
    return canManageRentalListing(property);
  }

  function canManagePropertyIssues(property = {}) {
    return isAdmin || canManageRentalListing(property);
  }

  function getPropertyIssues(property = {}) {
    if (!property?.id) return [];
    return issues.filter((issue) => issue.propertyId === property.id);
  }

  function getVisiblePropertyIssues(property = {}) {
    const propertyIssues = getPropertyIssues(property);
    if (canManagePropertyIssues(property)) return propertyIssues;
    return propertyIssues.filter((issue) => issue.tenantUserId === user?.uid);
  }

  function getOpenPropertyIssueCount(property = {}) {
    return getPropertyIssues(property).filter((issue) => !["resolved", "closed"].includes(String(issue.status || "open"))).length;
  }

  function formatMonthlyRent(value) {
    const rentNumber = Number(value);
    const rentDisplay = Number.isFinite(rentNumber) && rentNumber > 0
      ? `$${rentNumber.toLocaleString("en-US", { maximumFractionDigits: 0 })}/mo`
      : "";
    return rentDisplay;
  }

  function formatAvailableDate(value) {
    if (!value) return "";
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  function formatBedsBaths(property = {}) {
    const beds = Number(property.bedrooms || 0);
    const baths = Number(property.bathrooms || 0);
    return [
      beds > 0 ? `${beds} bed${beds === 1 ? "" : "s"}` : "",
      baths > 0 ? `${baths} bath${baths === 1 ? "" : "s"}` : "",
    ].filter(Boolean).join(" • ");
  }

  function normalizeRentalListingDraft(draft = {}) {
    const isForRentValue = Boolean(draft.isForRent);
    const rentValue = Number(String(draft.monthlyRent || "").replace(/[$,\s]/g, ""));
    const bedroomsValue = Number(String(draft.bedrooms || "").replace(/[^0-9.]/g, ""));
    const bathroomsValue = Number(String(draft.bathrooms || "").replace(/[^0-9.]/g, ""));
    return {
      isForRent: isForRentValue,
      monthlyRent: isForRentValue && Number.isFinite(rentValue) && rentValue > 0 ? rentValue : null,
      rentCurrency: "USD",
      availableDate: isForRentValue ? draft.availableDate || "" : "",
      leaseType: isForRentValue ? String(draft.leaseType || "").trim() : "",
      bedrooms: isForRentValue && Number.isFinite(bedroomsValue) && bedroomsValue > 0 ? bedroomsValue : null,
      bathrooms: isForRentValue && Number.isFinite(bathroomsValue) && bathroomsValue > 0 ? bathroomsValue : null,
    };
  }

  const PropertyPreviewCard = ({ property, compact = false }) => {
    if (!property) return null;
    const ratingData = getPropertyRatingData(property.id);
    const propertyCoverLabel = property.city || property.state || "Rental";
    const propertyImageSrc = String(property?.imageUrl || "").trim();
    const claimState = getPropertyClaimState(property);

    if (compact) {
      return (
        <div style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", overflow: "hidden", borderRadius: 18, background: "#fff" }}>
          <div
            style={{
              minHeight: 118,
              borderRadius: 16,
              background: propertyImageSrc
                ? `linear-gradient(180deg, rgba(15,23,42,0.10), rgba(15,23,42,0.62)), url(${propertyImageSrc}) center/cover no-repeat`
                : "linear-gradient(135deg, #0f6fff 0%, #10b981 100%)",
              padding: 10,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 8,
              boxSizing: "border-box",
            }}
          >
            <span style={{ padding: "6px 9px", borderRadius: 999, background: "#fff", color: "#0f172a", fontSize: 11, fontWeight: 900 }}>
              {propertyCoverLabel}
            </span>
            <span style={{ padding: "6px 9px", borderRadius: 999, background: claimState.tone === "success" ? "#dcfce7" : claimState.tone === "pending" ? "#fef3c7" : "#f8fafc", color: claimState.tone === "success" ? "#166534" : claimState.tone === "pending" ? "#92400e" : "#475569", fontSize: 11, fontWeight: 900 }}>
              {claimState.label}
            </span>
          </div>
          <div style={{ display: "grid", gap: 8, padding: "10px 2px 2px" }}>
            {isRentalListing(property) ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span style={{ padding: "6px 9px", borderRadius: 999, background: "#ecfdf5", color: "#047857", fontSize: 11, fontWeight: 950 }}>
                  For Rent
                </span>
                {property.monthlyRent ? (
                  <span style={{ padding: "6px 9px", borderRadius: 999, background: "#eff6ff", color: "#0f6fff", fontSize: 11, fontWeight: 950 }}>
                    {formatMonthlyRent(property.monthlyRent)}
                  </span>
                ) : null}
              </div>
            ) : null}
            <div style={{ fontSize: 16, lineHeight: 1.18, fontWeight: 950, color: "#0f172a", overflowWrap: "break-word" }}>
              {property.title || property.address || "Property"}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.35, color: "#475569", overflowWrap: "break-word" }}>
              {property.fullAddress || property.address || "Address unavailable"}
            </div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#64748b" }}>
              {formatCityState(property) || "City/state not listed"}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <span style={{ color: "#eab308", fontSize: 16, letterSpacing: 1 }}>
                {"★".repeat(Math.round(ratingData.average || 0))}{"☆".repeat(Math.max(0, 5 - Math.round(ratingData.average || 0)))}
              </span>
              <span style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                {ratingData.average.toFixed(1)} · {ratingData.count} review{ratingData.count !== 1 ? "s" : ""}
              </span>
            </div>
            <button
              type="button"
              style={{ ...styles.secondaryButton, width: "100%", marginTop: 2, padding: "10px 12px" }}
              onClick={() => handleSelectProperty(property)}
            >
              View Full Property Profile
            </button>
            {claimState.canOpenClaim ? (
              <button
                type="button"
                style={{ ...styles.primaryButton, width: "100%", marginTop: 0, padding: "10px 12px" }}
                onClick={() => {
                  handleSelectProperty(property);
                  window.setTimeout(() => openClaimVerificationModal(property), 0);
                }}
              >
                {claimState.label}
              </button>
            ) : (
              <span style={{ ...getPropertyClaimBadgeStyle(property), width: "100%", justifyContent: "center", boxSizing: "border-box" }}>
                {claimState.label}
              </span>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        style={{
          ...styles.propertyShowcaseCard,
          padding: compact ? 14 : isMobile ? 14 : 18,
          minWidth: compact ? 240 : "auto",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <div style={styles.propertyMedia}>
          {propertyImageSrc ? (
            <img
              src={propertyImageSrc}
              alt={property.title || property.address || "Property"}
              style={styles.propertyMediaImage}
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : null}
          <div style={styles.propertyMediaGradient} />
          <button
            type="button"
            style={{
              ...styles.propertyFavoriteButton,
              ...(isFavoriteProperty(property.id) ? styles.propertyFavoriteButtonActive : {}),
            }}
            aria-label={isFavoriteProperty(property.id) ? "Remove saved property" : "Save property"}
            onClick={(event) => {
              event.stopPropagation();
              toggleFavoriteProperty(property.id);
            }}
          >
            {isFavoriteProperty(property.id) ? "♥" : "♡"}
          </button>
          <div style={styles.propertyMediaContent}>
            {isRentalListing(property) ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                <span style={{ padding: "7px 10px", borderRadius: 999, background: "#ecfdf5", color: "#047857", fontSize: 12, fontWeight: 950 }}>
                  For Rent
                </span>
                {property.monthlyRent ? (
                  <span style={{ padding: "7px 10px", borderRadius: 999, background: "#fff", color: "#0f6fff", fontSize: 12, fontWeight: 950 }}>
                    {formatMonthlyRent(property.monthlyRent)}
                  </span>
                ) : null}
              </div>
            ) : null}
            <div style={styles.propertyMediaChip}>{propertyImageSrc ? propertyCoverLabel : `${propertyCoverLabel} • No Photo Yet`}</div>
            <div style={styles.propertyMediaTitle}>{property.title || property.address || "Property"}</div>
            <div style={styles.propertyMediaSubtitle}>
              {formatCityState(property) || property.address || "Community added property"}
            </div>
          </div>
        </div>

        <div style={{ ...styles.propertyCardTop, ...(isPhoneMobile ? { flexDirection: "column", alignItems: "stretch" } : {}) }}>
          <div style={{ minWidth: 0, width: "100%", maxWidth: "100%", flex: "1 1 auto", display: "block", writingMode: "horizontal-tb" }}>
            <div style={{ ...styles.propertyTitle, whiteSpace: "normal", wordBreak: "normal", overflowWrap: "break-word", writingMode: "horizontal-tb", display: "block", width: "100%", minWidth: 0 }}>
              {property.title || property.address || "Property"}
            </div>
            <div style={{ ...styles.propertyAddress, whiteSpace: "normal", wordBreak: "normal", overflowWrap: "break-word", writingMode: "horizontal-tb", display: "block", width: "100%", minWidth: 0 }}>
              {property.fullAddress || property.address}
            </div>
          </div>
          <span style={{ ...getPropertyClaimBadgeStyle(property), ...(isPhoneMobile ? mobilePillSafe : {}) }}>
            {claimState.label === "Claim Property" ? "Community Added" : claimState.label}
          </span>
        </div>

        <div style={styles.propertyMetaRow}>
          <div>
            {renderStars(
              Math.round(ratingData.average),
              false,
              null,
              compact ? 20 : 22
            )}
          </div>
          <div style={styles.propertyRatingText}>
            {ratingData.average.toFixed(1)} · {ratingData.count} review{ratingData.count !== 1 ? "s" : ""}
          </div>
        </div>

        {ratingData.communityCount ? (
          <div style={styles.communityRatingBanner}>
            <span style={styles.communityRatingLabel}>Community Rating</span>
            <span style={styles.communityRatingValue}>
              {ratingData.communityAverage.toFixed(1)} · {ratingData.communityCount} unverified
            </span>
          </div>
        ) : null}

        <div
          style={{
            ...styles.rentScoreBanner,
            ...(calculateRentScore(ratingData.reviews).tone === "success"
              ? styles.rentScoreBannerSuccess
              : calculateRentScore(ratingData.reviews).tone === "warning"
              ? styles.rentScoreBannerWarning
              : calculateRentScore(ratingData.reviews).tone === "danger"
              ? styles.rentScoreBannerDanger
              : {}),
          }}
        >
          <span style={styles.rentScoreLabel}>Rent Score</span>
          <span style={styles.rentScoreValue}>
            {calculateRentScore(ratingData.reviews).score} · {calculateRentScore(ratingData.reviews).label}
          </span>
        </div>

        <div style={styles.propertyHighlightsRow}>
          {isFavoriteProperty(property.id) ? <span style={{ ...styles.highlightPillGreen, ...(isPhoneMobile ? mobilePillSafe : {}) }}>Saved</span> : null}
          {ratingData.average >= 4 ? <span style={{ ...styles.highlightPillBlue, ...(isPhoneMobile ? mobilePillSafe : {}) }}>Top Rated</span> : null}
          {ratingData.count >= 3 ? <span style={{ ...styles.highlightPillDark, ...(isPhoneMobile ? mobilePillSafe : {}) }}>Popular</span> : null}
        </div>

        {!!property.description && (
          <div style={styles.propertyDescription}>{property.description}</div>
        )}

        <div style={{ ...styles.miniMetaGrid, flexWrap: "wrap", rowGap: 8 }}>
          {isRentalListing(property) ? (
            <div style={{ ...styles.miniMetaPill, background: "#ecfdf5", color: "#047857", ...(isPhoneMobile ? mobilePillSafe : {}) }}>
              For Rent {property.monthlyRent ? `• ${formatMonthlyRent(property.monthlyRent)}` : ""}
            </div>
          ) : null}
          {isRentalListing(property) && formatBedsBaths(property) ? (
            <div style={{ ...styles.miniMetaPill, ...(isPhoneMobile ? mobilePillSafe : {}) }}>{formatBedsBaths(property)}</div>
          ) : null}
          <div style={{ ...styles.miniMetaPill, ...(isPhoneMobile ? mobilePillSafe : {}) }}>{property.city || "No city yet"}</div>
          <div style={{ ...styles.miniMetaPill, ...(isPhoneMobile ? mobilePillSafe : {}) }}>{property.state || "No state yet"}</div>
          <div style={{ ...styles.miniMetaPill, ...(isPhoneMobile ? mobilePillSafe : {}) }}>
            {property.isLocalPending
              ? "Syncing pin"
              : hasApprovedPropertyClaim(property)
              ? `Claimed by ${property.ownerDisplayName || property.ownerEmail || "verified owner"}`
              : ["pending", "pending_claim", "pending_review"].includes(String(property.claimStatus || ""))
              ? `Claim request by ${property.claimRequestedByDisplayName || property.claimRequestedByEmail || "pending"}`
              : "Claimable by verified owner"}
          </div>
        </div>

        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          <button
            style={{
              ...styles.secondaryButton,
              width: "100%",
              padding: "12px 14px",
            }}
            onClick={() => handleSelectProperty(property)}
          >
            View Full Property Profile
          </button>

          {claimState.canOpenClaim ? (
            <button
              style={{
                ...styles.primaryButton,
                width: "100%",
                padding: "12px 14px",
              }}
              onClick={() => {
                handleSelectProperty(property);
                setTimeout(() => {
                  openClaimVerificationModal(property);
                }, 0);
              }}
            >
              {claimState.label}
            </button>
          ) : (
            <span style={{ ...getPropertyClaimBadgeStyle(property), width: "100%", justifyContent: "center", boxSizing: "border-box" }}>
              {claimState.label}
            </span>
          )}
        </div>


        {showAuthDisclaimer ? (
          <div style={styles.fullscreenDisclaimer}>
            <div style={styles.fullscreenDisclaimerInner}>
              <div style={styles.disclaimerShell}>
                <div style={styles.disclaimerTopGlow} />

                <div style={styles.disclaimerHeaderBar}>
                  <div>
                    <p style={styles.disclaimerMiniLabel}>RentDuo</p>
                    <h1
                      style={{
                        ...styles.fullscreenDisclaimerTitle,
                        fontSize: isMobile ? 22 : styles.fullscreenDisclaimerTitle.fontSize,
                        marginBottom: 8,
                      }}
                    >
                      Disclaimer & User Acknowledgment
                    </h1>
                    <p style={styles.disclaimerHeaderText}>
                      Please review this carefully before entering the app.
                    </p>
                  </div>
                </div>

                <div
                  ref={disclaimerScrollRef}
                  onScroll={handleDisclaimerScroll}
                  style={styles.fullscreenDisclaimerBody}
                >
                  <p style={styles.fullscreenDisclaimerLead}>
                    Welcome to <strong>RentDuo</strong>. RentDuo is a user-driven platform
                    where tenants, landlords, and property managers can share reviews,
                    ratings, property information, and rental-related experiences.
                  </p>

                  <div style={styles.disclaimerSection}>
                    <h3 style={styles.disclaimerSectionTitle}>1. No Professional Advice</h3>
                    <p style={styles.fullscreenDisclaimerText}>
                      RentDuo does not provide legal, financial, real estate, or other
                      professional advice. Content in the app is provided for general
                      informational purposes only.
                    </p>
                  </div>

                  <div style={styles.disclaimerSection}>
                    <h3 style={styles.disclaimerSectionTitle}>2. User-Generated Content</h3>
                    <p style={styles.fullscreenDisclaimerText}>
                      Reviews, ratings, comments, claims, and profile information are
                      submitted by users. RentDuo does not guarantee that all content is
                      accurate, complete, current, or free from error.
                    </p>
                  </div>

                  <div style={styles.disclaimerSection}>
                    <h3 style={styles.disclaimerSectionTitle}>3. No Guarantee of Outcomes</h3>
                    <p style={styles.fullscreenDisclaimerText}>
                      RentDuo does not guarantee the quality of any landlord, tenant,
                      property manager, property, listing, or rental outcome. Users must
                      make their own decisions and perform their own due diligence.
                    </p>
                  </div>

                  <div style={styles.disclaimerSection}>
                    <h3 style={styles.disclaimerSectionTitle}>4. Platform Liability Limitation</h3>
                    <p style={styles.fullscreenDisclaimerText}>
                      To the fullest extent permitted by law, RentDuo is not responsible
                      for disputes, damages, losses, rental decisions, property conditions,
                      user conduct, or any actions taken based on information found in the app.
                    </p>
                  </div>

                  <div style={styles.disclaimerSection}>
                    <h3 style={styles.disclaimerSectionTitle}>5. Moderation & Content Removal</h3>
                    <p style={styles.fullscreenDisclaimerText}>
                      RentDuo may review, flag, limit, remove, or refuse content and may
                      suspend accounts that violate platform rules, but this does not mean
                      every harmful or inaccurate submission will be detected immediately.
                    </p>
                  </div>

                  <div style={styles.disclaimerSection}>
                    <h3 style={styles.disclaimerSectionTitle}>6. Verification & Unverified Content</h3>
                    <p style={styles.fullscreenDisclaimerText}>
                      Some accounts, properties, or submissions may be marked as verified,
                      unverified, or community added. Unverified material should be reviewed
                      carefully and should not be treated as guaranteed fact.
                    </p>
                  </div>

                  <div style={styles.disclaimerSection}>
                    <h3 style={styles.disclaimerSectionTitle}>7. Fair Use & User Responsibility</h3>
                    <p style={styles.fullscreenDisclaimerText}>
                      By continuing, you agree to provide honest information, avoid false,
                      abusive, threatening, discriminatory, defamatory, or illegal content,
                      and accept responsibility for all activity under your account.
                    </p>
                  </div>

                  <div style={styles.disclaimerSection}>
                    <h3 style={styles.disclaimerSectionTitle}>8. External Links</h3>
                    <p style={styles.fullscreenDisclaimerText}>
                      RentDuo may link to government or third-party websites for reference.
                      RentDuo is not responsible for the content, availability, or accuracy
                      of any outside site.
                    </p>
                  </div>

                  <div style={styles.disclaimerSection}>
                    <h3 style={styles.disclaimerSectionTitle}>9. Acceptance of Terms</h3>
                    <p style={styles.fullscreenDisclaimerText}>
                      By clicking <strong>Accept & Continue</strong>, you confirm that you
                      have read and understood this disclaimer, that you will use RentDuo
                      responsibly, and that you accept use of the platform at your own
                      discretion and risk.
                    </p>
                  </div>

                  <div style={styles.disclaimerFooterNote}>
                    RentDuo is not affiliated with any government agency or housing authority.
                  </div>
                </div>

                <div style={styles.fullscreenDisclaimerActions}>
                  <div style={styles.disclaimerScrollHint}>
                    {disclaimerHasScrolledToEnd
                      ? "You can now continue."
                      : "Review the disclaimer, then tap Accept & Continue."}
                  </div>

                  <div
                    style={{
                      ...styles.disclaimerActionRow,
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    <button
                      type="button"
                      style={{
                        ...styles.modalSecondaryButton,
                        ...styles.disclaimerActionButton,
                        width: isMobile ? "100%" : "auto",
                      }}
                      onClick={() => {
                        setShowAuthDisclaimer(false);
                        setPendingAuthAction(null);
                        setDisclaimerHasScrolledToEnd(false);
                      }}
                    >
                      Decline
                    </button>

                    <button
                      type="button"
                      style={{
                        ...styles.primaryButton,
                        ...styles.disclaimerActionButton,
                        width: isMobile ? "100%" : "auto",
                      }}
                      onClick={handleAcceptDisclaimer}
                    >
                      Accept & Continue
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const AICenterTab = () => (
    <AICenter
      selectedProperty={selectedProperty}
      propertyIssues={selectedProperty ? getVisiblePropertyIssues(selectedProperty) : []}
      isPhoneMobile={isPhoneMobile}
      onFindProperty={() => setActiveTab("home")}
      onAnalyzeRisk={handleAnalyzePropertyRisk}
      riskLoading={propertyRiskLoadingId === selectedProperty?.id}
      riskError={propertyRiskError}
      onMakeReviewSafer={handleAiCenterMakeReviewSafer}
      onScreenPropertyScam={handleAnalyzePropertyScamRisk}
      onScreenTextScam={handleAiCenterTextScamScreen}
      onRunAIFeature={handleRunRentDuoAIFeature}
      scamLoading={propertyScamLoadingId === selectedProperty?.id}
      scamError={propertyScamError}
      onAnalyzeMaintenanceIssue={handleAnalyzeMaintenanceIssue}
      maintenanceLoadingId={maintenanceAnalysisLoadingId}
      leaseText={leaseText}
      setLeaseText={setLeaseText}
      leaseExplanation={leaseExplanation}
      leaseLoading={leaseExplaining}
      leaseError={leaseError}
      onExplainLease={handleExplainLease}
      onAnalyzeNeighborhood={handleAnalyzeNeighborhood}
      neighborhoodLoading={neighborhoodLoadingId === selectedProperty?.id}
      neighborhoodError={neighborhoodError}
      onAnalyzeReputation={handleAnalyzePropertyReputation}
      reputationLoading={propertyReputationLoadingId === selectedProperty?.id}
      moveChecklistMode={moveChecklistMode}
      setMoveChecklistMode={setMoveChecklistMode}
      moveChecklist={moveChecklist}
      moveChecklistLoading={moveChecklistLoading}
      moveChecklistError={moveChecklistError}
      onCreateMoveChecklist={handleGenerateMoveChecklist}
      onToggleMoveItem={handleToggleMoveChecklistItem}
      smartMatchPreferences={smartMatchPreferences}
      setSmartMatchPreferences={setSmartMatchPreferences}
      smartMatches={smartMatches}
      smartMatchLoading={smartMatchLoading}
      smartMatchError={smartMatchError}
      smartMatchDisclaimer={smartMatchDisclaimer}
      onFindMatches={handleFindSmartMatches}
      onOpenMatchedProperty={(match) => {
        handleSelectProperty(properties.find((property) => property.id === match.id) || match);
        setActiveTab("home");
        if (isMobile) setShowMobilePropertyModal(true);
      }}
      formatMonthlyRent={formatMonthlyRent}
      onEstimateRent={handleEstimateMarketRent}
      rentLoading={marketRentLoadingId === selectedProperty?.id}
      rentError={marketRentError}
    />
  );

  const BottomNav = () => {
    const tabs = [
      { key: "ai_center", label: "AI Center", icon: "AI" },
      ...(isAdmin ? [{ key: "admin", label: "Admin", icon: "Admin" }] : []),
      { key: "profile", label: t("profile"), icon: "👤" },
      { key: "home", label: t("home"), icon: "🏠" },
      { key: "feed", label: t("feed"), icon: "📡" },
      ...(role === "property_manager" ? [{ key: "manager", label: t("manager"), icon: "🏢" }] : []),
      { key: "reviews", label: t("reviews"), icon: "⭐" },
      { key: "messages", label: t("messages"), icon: "💬" },
    ];

    return (
      <div
        style={{
          ...styles.bottomNav,
          ...(isPhoneMobile
            ? {
                left: 8,
                right: 8,
                bottom: 8,
                width: "calc(100vw - 16px)",
                maxWidth: "calc(100vw - 16px)",
                gap: 6,
                padding: "10px 8px calc(10px + env(safe-area-inset-bottom))",
                overflowX: "hidden",
                boxSizing: "border-box",
                gridTemplateColumns: `repeat(${Math.min(3, tabs.length)}, minmax(0, 1fr))`,
              }
            : {
                gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))`,
              }),
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => {
                if (document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
                setActiveTab(tab.key);
              }}
              style={{
                ...styles.bottomNavButton,
                ...(isPhoneMobile
                  ? {
                      minWidth: 0,
                      width: "100%",
                      maxWidth: "100%",
                      padding: "10px 4px",
                      boxSizing: "border-box",
                      textAlign: "center",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.2,
                      minHeight: "52px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      transform: "none",
                      position: "relative",
                      left: "auto",
                      right: "auto",
                      top: "auto",
                      bottom: "auto",
                      overflow: "visible",
                    }
                  : {}),
                ...(isActive ? styles.bottomNavButtonActive : {}),
                ...(isPhoneMobile ? { transform: "none" } : {}),
              }}
            >
              <span style={{ ...styles.bottomNavIcon, ...(isPhoneMobile ? { fontSize: 17 } : {}) }}>{tab.icon}</span>
              <span style={{ ...styles.bottomNavLabel, ...(isPhoneMobile ? { fontSize: 10, lineHeight: 1.15 } : {}) }}>{tab.label}</span>
            </button>
          );
        })}
      </div>
    );
  };


  const mobileCenteredRow = {
    display: "flex",
    flexDirection: isPhoneMobile ? "column" : "row",
    justifyContent: "center",
    alignItems: isPhoneMobile ? "stretch" : "center",
    gap: isPhoneMobile ? 10 : 12,
    flexWrap: "wrap",
    width: "100%",
    maxWidth: "100%",
    marginBottom: 16,
    marginLeft: 0,
    marginRight: 0,
    boxSizing: "border-box",
    overflowX: "hidden",
    overflowY: "visible",
  };
  const mobilePillSafe = {
    width: isPhoneMobile ? "100%" : "auto",
    maxWidth: isPhoneMobile ? 340 : "100%",
    margin: isPhoneMobile ? "0 auto" : 0,
    boxSizing: "border-box",
    paddingLeft: isPhoneMobile ? 12 : undefined,
    paddingRight: isPhoneMobile ? 12 : undefined,
    whiteSpace: isPhoneMobile ? "normal" : "nowrap",
    textAlign: "center",
    wordBreak: isPhoneMobile ? "break-word" : "normal",
    lineHeight: 1.2,
    minHeight: isPhoneMobile ? "44px" : "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transform: "none",
    position: "relative",
    left: "auto",
    right: "auto",
    top: "auto",
    bottom: "auto",
    overflow: "visible",
    flexShrink: 0,
    minWidth: 0,
  };
  const mobileButtonSafe = {
    width: isPhoneMobile || isMobile ? "100%" : "auto",
    maxWidth: isPhoneMobile ? 340 : isMobile ? 420 : "none",
    margin: isPhoneMobile ? "0 auto" : 0,
    boxSizing: "border-box",
    paddingLeft: isPhoneMobile ? 14 : undefined,
    paddingRight: isPhoneMobile ? 14 : undefined,
    whiteSpace: "normal",
    textAlign: "center",
    wordBreak: isPhoneMobile ? "break-word" : "normal",
    lineHeight: 1.2,
    minHeight: isPhoneMobile ? "44px" : "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transform: "none",
    position: "relative",
    left: "auto",
    right: "auto",
    top: "auto",
    bottom: "auto",
    overflow: "visible",
    flexShrink: 0,
    minWidth: 0,
  };
  const phoneSectionSafe = isPhoneMobile
    ? {
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        marginLeft: 0,
        marginRight: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
      }
    : {};

  const FeedTab = () => {
    const liveFeedFilters = [
      { value: "all", label: "All" },
      { value: "reviews", label: "Reviews" },
      { value: "properties", label: "Properties" },
      { value: "verified", label: "Verified" },
      { value: "nearby", label: "Near Me" },
    ];
    const liveFeedAreaLabel = searchArea || city || profile?.city || "your area";
    const aiFeedCards = RENTDUO_AI_FEED_CARDS.filter(
      (card) => !dismissedAiFeedCardIds.includes(card.id)
    );
    const injectAiFeedCards = (items) => {
      if (!items.length || !aiFeedCards.length) return items;
      const mixedItems = [];
      items.forEach((item, index) => {
        mixedItems.push(item);
        if ((index + 1) % 5 === 0 && index < items.length - 1) {
          const card = aiFeedCards[Math.floor(index / 5) % aiFeedCards.length];
          mixedItems.push({
            ...card,
            id: `ai-feed-${card.id}-${index}`,
            sourceId: card.id,
            type: "rentduo_ai_card",
            isAiFeedCard: true,
          });
        }
      });
      return mixedItems;
    };
    const feedStreamItems = injectAiFeedCards(liveFeedItems.slice(0, 12));
    const getFeedActivityLabel = (type = "") => {
      const labels = {
        property_review: "New review",
        user_review: "New review",
        property_claimed: "Property claimed",
        property_rental_listed: "New rental listing posted",
        review_reply: "Landlord response",
        verified: "Verification approved",
        property_added: "New property pinned",
        rentduo_ai_card: "AI insight",
      };
      return labels[type] || "Property activity";
    };
    const getFeedProperty = (item = {}) =>
      visibleProperties.find((prop) => prop?.id && prop.id === item?.propertyId) || null;
    const getFeedImage = (item = {}) => {
      const linkedProperty = getFeedProperty(item);
      return item?.propertyImageUrl || linkedProperty?.imageUrl || "";
    };
    const getFeedAddress = (item = {}) => {
      const linkedProperty = getFeedProperty(item);
      return item?.propertyAddress || linkedProperty?.fullAddress || linkedProperty?.address || item?.subtitle || "";
    };
    const isTrendingFeedItem = (item = {}) =>
      Number(item?.propertyReviewCount || 0) >= 2 ||
      (item?.type === "property_review" && Number(item?.rating || 0) >= 4);
    const handleFeedHelpful = (item = {}) => {
      if (item?.sourceReview?.id) {
        handleHelpfulReview(item.sourceReview, item.sourceCollection || "reviews");
        return;
      }
      setStatusMessage("Helpful feedback is available on review activity.");
    };
    const handleFeedReply = (item = {}) => {
      if (item?.sourceReview?.id && item.sourceCollection === "reviews" && canReplyToReview(item.sourceReview)) {
        openReplyModal(item.sourceReview);
        return;
      }
      openFeedItem(item);
    };
    const handleFeedReport = async (item = {}) => {
      if (!item?.sourceReview?.id) {
        setStatusMessage("Open this item to report it.");
        return;
      }
      if (item.sourceCollection === "propertyReviews") {
        try {
          await setDoc(
            doc(db, "propertyReviews", item.sourceReview.id),
            {
              reported: true,
              reportedAt: serverTimestamp(),
              reportedBy: user?.uid || "",
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          setStatusMessage("Property review reported.");
        } catch (error) {
          console.error("Property review report failed:", error);
          setStatusMessage("Failed to report property review.");
        }
        return;
      }
      handleReportReview(item.sourceReview.id);
    };

    const openFeedItem = (item) => {
      if (!item) return;

      if (["property_added", "property_claimed", "property_review", "property_rental_listed"].includes(item.type) || (item.type === "review_reply" && item.propertyId)) {
        const targetProperty = visibleProperties.find((prop) => prop.id === item.propertyId);
        if (targetProperty) {
          handleSelectProperty(targetProperty);
          setPropertyMapCenter({ lat: Number(targetProperty.lat), lng: Number(targetProperty.lng) });
          setPropertyMapZoom(15);
          if (isMobile) setShowMobilePropertyModal(true);
          setActiveTab("home");
        }
        return;
      }

      if (["user_review", "verified", "review_reply"].includes(item.type)) {
        openPublicProfile({
          uid: item.targetUserId || item.actorUserId || "",
          displayName: item.targetDisplayName || item.title || "User",
          photoURL: item.actorPhotoURL || "",
          verified: Boolean(item.targetVerified || item.actorVerified),
          verificationStatus: item.targetVerificationStatus || (item.targetVerified || item.actorVerified ? "verified" : "unverified"),
        });
      }
    };

    return (
      <div style={{ ...styles.cardWide, marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "#0f6fff" }}>Live feed</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>Everything happening on RentDuo</div>
            <div style={{ marginTop: 6, fontSize: 14, color: "#64748b", lineHeight: 1.55 }}>
              Recent reviews, new properties, verified members, and claim activity {liveFeedAreaLabel ? `around ${liveFeedAreaLabel}` : "across RentDuo"}.
            </div>
          </div>
          <div style={{ padding: "10px 14px", borderRadius: 999, background: "#eef2ff", color: "#4338ca", fontWeight: 800, fontSize: 13 }}>
            {liveFeedItems.length} live updates
          </div>
        </div>

        <div style={{ ...mobileCenteredRow, gap: 10, marginBottom: 14 }}>
          {liveFeedFilters.map((filterItem) => {
            const isActive = liveFeedFilter === filterItem.value;
            return (
              <button
                key={filterItem.value}
                type="button"
                onClick={() => setLiveFeedFilter(filterItem.value)}
                style={{
                  borderRadius: 999,
                  border: isActive ? "1px solid #0f6fff" : "1px solid #dbeafe",
                  background: isActive ? "#eff6ff" : "#ffffff",
                  color: isActive ? "#0f6fff" : "#475569",
                  fontWeight: 800,
                  fontSize: 13,
                  padding: "10px 14px",
                  cursor: "pointer",
                  boxShadow: isActive ? "0 10px 24px rgba(15,111,255,0.10)" : "none",
                  boxSizing: "border-box",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {filterItem.label}
              </button>
            );
          })}
        </div>

        {feedStreamItems.length ? (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: isPhoneMobile ? 12 : 18, ...phoneSectionSafe }}>
            {feedStreamItems.map((item) => {
              if (item.isAiFeedCard) {
                return (
                  <div
                    key={item.id}
                    style={{
                      ...styles.aiFeedCard,
                      ...(isPhoneMobile ? styles.aiFeedCardMobile : {}),
                    }}
                  >
                    <div style={styles.aiFeedCardTopRow}>
                      <div style={styles.aiFeedBadgeRow}>
                        <span style={styles.aiFeedBadge}>RentDuo AI</span>
                        <span style={styles.aiFeedTypeBadge}>{item.label}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setDismissedAiFeedCardIds((prev) =>
                            prev.includes(item.sourceId) ? prev : [...prev, item.sourceId]
                          )
                        }
                        style={styles.aiFeedDismissButton}
                        aria-label="Dismiss AI tip"
                      >
                        Skip
                      </button>
                    </div>
                    <div style={styles.aiFeedTitle}>{item.title}</div>
                    <p style={styles.aiFeedText}>{item.text}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAiAssistantOpen(true);
                        handleAskRentDuoAI(item.prompt);
                      }}
                      style={{
                        ...styles.aiFeedActionButton,
                        ...(isPhoneMobile ? mobileButtonSafe : {}),
                      }}
                    >
                      {item.action}
                    </button>
                  </div>
                );
              }

              const typePillStyles = {
                property_review: { background: "#ecfeff", color: "#0f766e" },
                user_review: { background: "#eff6ff", color: "#1d4ed8" },
                property_added: { background: "#f8fafc", color: "#475569" },
                property_rental_listed: { background: "#ecfdf5", color: "#047857" },
                property_claimed: { background: "#ecfdf5", color: "#047857" },
                verified: { background: "#f5f3ff", color: "#7c3aed" },
                review_reply: { background: "#fff7ed", color: "#c2410c" },
              };
              const pillStyle = typePillStyles[item.type] || { background: "#f8fafc", color: "#475569" };
              const reviewStars = Number(item.rating || 0) > 0 ? "?".repeat(Number(item.rating || 0)) + "?".repeat(Math.max(0, 5 - Number(item.rating || 0))) : "";
              const activityLabel = getFeedActivityLabel(item.type);
              const propertyImage = getFeedImage(item);
              const propertyAddress = getFeedAddress(item);
              const cityState = formatCityState(item);
              const trending = isTrendingFeedItem(item);
              const snippet = String(item.text || "").trim();

              return (
                <div
                  key={item.id}
                  style={{
                    borderRadius: 26,
                    border: "1px solid rgba(226,232,240,0.95)",
                    background: "rgba(255,255,255,0.98)",
                    boxShadow: "0 22px 48px rgba(15,23,42,0.09)",
                    padding: 0,
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      minHeight: isPhoneMobile ? 150 : 176,
                      padding: 14,
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                      background: propertyImage
                        ? "linear-gradient(180deg, rgba(15,23,42,0.18) 0%, rgba(15,23,42,0.74) 100%), url(" + propertyImage + ") center/cover no-repeat"
                        : "linear-gradient(135deg, rgba(15,111,255,0.90) 0%, rgba(16,185,129,0.88) 100%)",
                      color: "#fff",
                      boxSizing: "border-box",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
                      <span style={{ padding: "7px 10px", borderRadius: 999, background: "rgba(255,255,255,0.92)", color: "#0f172a", fontSize: 12, fontWeight: 900 }}>
                        {activityLabel}
                      </span>
                      {trending ? (
                        <span style={{ padding: "7px 10px", borderRadius: 999, background: "#ecfdf5", color: "#047857", fontSize: 12, fontWeight: 900 }}>
                          Trending
                        </span>
                      ) : null}
                    </div>
                    <span style={{ padding: "7px 10px", borderRadius: 999, background: "rgba(15,23,42,0.62)", color: "#fff", fontSize: 12, fontWeight: 900 }}>
                      {item.timestampLabel}
                    </span>
                  </div>

                  <div style={{ padding: isPhoneMobile ? 14 : 16, display: "grid", gap: 14 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0 }}>
                      <Avatar src={item.actorPhotoURL} name={item.actorDisplayName || item.title} size={44} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                          <span style={{ ...pillStyle, padding: "7px 10px", borderRadius: 999, fontSize: 12, fontWeight: 900 }}>
                            {item.type === "verified" ? "Verification approved" : activityLabel}
                          </span>
                          {item.actorVerified || item.targetVerified ? <VerifiedBadge label="Verified" /> : null}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 950, color: "#0f172a", lineHeight: 1.15, overflowWrap: "break-word" }}>
                          {item.title}
                        </div>
                        {propertyAddress ? (
                          <div style={{ marginTop: 6, fontSize: 14, color: "#475569", lineHeight: 1.45, overflowWrap: "break-word" }}>
                            {propertyAddress}
                          </div>
                        ) : null}
                        {cityState ? (
                          <div style={{ marginTop: 4, fontSize: 13, color: "#64748b", fontWeight: 800 }}>
                            {cityState}
                          </div>
                        ) : null}
                        {item.isForRent ? (
                          <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ padding: "7px 10px", borderRadius: 999, background: "#ecfdf5", color: "#047857", fontSize: 12, fontWeight: 950 }}>
                              For Rent
                            </span>
                            {item.monthlyRent ? (
                              <span style={{ padding: "7px 10px", borderRadius: 999, background: "#eff6ff", color: "#0f6fff", fontSize: 12, fontWeight: 950 }}>
                                {formatMonthlyRent(item.monthlyRent)}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", color: "#64748b", fontSize: 13 }}>
                          {item.actorUserId ? (
                            <PublicProfileNameButton
                              userData={{ uid: item.actorUserId, displayName: item.actorDisplayName, photoURL: item.actorPhotoURL, verified: item.actorVerified }}
                              style={{ color: "#64748b" }}
                            >
                              {item.actorDisplayName ? "By " + item.actorDisplayName : "RentDuo activity"}
                            </PublicProfileNameButton>
                          ) : (
                            <span>{item.actorDisplayName ? "By " + item.actorDisplayName : "RentDuo activity"}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      {reviewStars ? (
                        <div style={{ fontSize: 18, letterSpacing: 1.5, color: "#eab308", fontWeight: 900 }}>
                          {reviewStars}
                        </div>
                      ) : (
                        <span style={{ color: "#64748b", fontSize: 13, fontWeight: 800 }}>Property activity</span>
                      )}
                      {Number(item.propertyReviewCount || 0) > 0 ? (
                        <span style={{ padding: "7px 10px", borderRadius: 999, background: "#f8fafc", color: "#475569", fontSize: 12, fontWeight: 900 }}>
                          {item.propertyReviewCount} review{Number(item.propertyReviewCount) === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </div>

                    {snippet ? (
                      <div style={{ borderRadius: 18, background: "#f8fafc", border: "1px solid rgba(226,232,240,0.86)", padding: 12 }}>
                        <TranslatableText text={snippet} label="Review" bodyStyle={{ color: "#334155", fontSize: 14, lineHeight: 1.6 }} compact />
                      </div>
                    ) : null}

                    <div style={{ display: "grid", gridTemplateColumns: isPhoneMobile ? "1fr" : "repeat(4, minmax(0, auto))", gap: 8, alignItems: "center" }}>
                      <button type="button" onClick={() => handleFeedHelpful(item)} style={{ ...styles.feedActionButton, ...(isPhoneMobile ? mobileButtonSafe : {}) }}>Helpful</button>
                      <button type="button" onClick={() => handleFeedReply(item)} style={{ ...styles.feedActionButton, ...(isPhoneMobile ? mobileButtonSafe : {}) }}>Reply</button>
                      <button type="button" onClick={() => handleFeedReport(item)} style={{ ...styles.feedActionButton, ...(isPhoneMobile ? mobileButtonSafe : {}) }}>Report</button>
                      <button type="button" onClick={() => openFeedItem(item)} style={{ ...styles.feedOpenButton, ...(isPhoneMobile ? mobileButtonSafe : {}) }}>
                        {item.actionLabel}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ borderRadius: 24, border: "1px dashed #cbd5e1", background: "rgba(248,250,252,0.88)", padding: 20, color: "#64748b", fontSize: 15, lineHeight: 1.65 }}>
            {feedError || (isFeedLoading ? "Loading feed..." : "No activity yet")}
          </div>
        )}
      </div>
    );
  };

  const HomeTab = () => {
    const rankedProperties = [...visibleProperties]
      .filter((prop) => prop && isFiniteCoordinate(prop.lat) && isFiniteCoordinate(prop.lng))
      .map((prop) => {
        const ratingData = getPropertyRatingData(prop.id);
        return {
          ...prop,
          ratingData,
          rankScore: Number(ratingData.average || 0) * 10 + Number(ratingData.count || 0),
        };
      })
      .sort((a, b) => b.rankScore - a.rankScore);

    const featuredProperties = rankedProperties.slice(0, 4);
    const savedProperties = rankedProperties.filter((prop) => favoritePropertyIds.includes(prop.id)).slice(0, 4);
    const spotlightProperty = featuredProperties[0] || null;
    const propertyCountLabel = `${visibleProperties.length} propert${visibleProperties.length === 1 ? "y" : "ies"}`;
    const propertyReviewCountValue = allPropertyReviews.length || allPropertyReviewsCount || 0;
    const reviewCountLabel = `${propertyReviewCountValue} property review${propertyReviewCountValue === 1 ? "" : "s"}`;
    return (
      <>
        <div
          style={{
            ...styles.cardWide,
            marginBottom: 18,
            overflow: "visible",
            background:
              "radial-gradient(circle at top right, rgba(59,130,246,0.16), transparent 30%), radial-gradient(circle at left center, rgba(16,185,129,0.12), transparent 28%), linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
            border: "1px solid rgba(191, 219, 254, 0.95)",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.10)",
            ...phoneSectionSafe,
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.25fr 0.9fr", gap: isPhoneMobile ? 14 : isMobile ? 18 : 24, alignItems: "stretch", ...phoneSectionSafe }}>
            <div>
              <div style={styles.brandPill}>RentDuo Home</div>
              <h1 style={{ margin: "14px 0 10px", fontSize: isMobile ? 30 : 42, lineHeight: 1.02, fontWeight: 900, letterSpacing: -1.2, color: "#0f172a" }}>
                See who you’re renting from — and who you’re renting to.
              </h1>
              <p style={{ margin: 0, color: "#475569", fontSize: isMobile ? 15 : 17, lineHeight: 1.7, maxWidth: 720 }}>
                RentDuo is a rental review system for tenants, landlords, property managers, and properties. Start by searching your area, adding a property, or leaving your first review.
              </p>

              <div style={{ ...mobileCenteredRow, marginTop: 14 }}>
                <button type="button" onClick={goToAddProperty} style={{ ...styles.primaryButton, marginTop: 0, ...mobileButtonSafe }}>🏠 Add a property</button>
                <button type="button" onClick={() => setActiveTab("reviews")} style={{ ...styles.secondaryButton, marginTop: 0, ...mobileButtonSafe }}>⭐ Leave a review</button>
                <button type="button" onClick={handleShareRentDuo} style={{ ...styles.secondaryButton, marginTop: 0, ...mobileButtonSafe }}>📤 Share RentDuo</button>
              </div>

              <div
                style={{
                  marginTop: 18,
                  padding: isMobile ? 14 : 16,
                  borderRadius: 24,
                  background: "rgba(255,255,255,0.9)",
                  border: "1px solid rgba(226,232,240,0.95)",
                  boxShadow: "0 14px 32px rgba(15,23,42,0.06)",
                  ...phoneSectionSafe,
                }}
              >
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 0.8fr auto", gap: isPhoneMobile ? 10 : 12, alignItems: "center", ...phoneSectionSafe }}>
                  <input
                    ref={homeSearchInputRef}
                    style={{ ...styles.input, marginBottom: 0, height: 56, fontSize: 16 }}
                    value={searchArea}
                    onChange={(e) => setSearchArea(e.target.value)}
                    placeholder="Start here: search city, state, neighborhood, or address"
                    autoComplete="off"
                  />
                  <div style={{ ...mobileCenteredRow, gap: 10, marginBottom: 0 }}>
                    <div style={{ ...mobilePillSafe, padding: "10px 14px", borderRadius: 999, background: "#eff6ff", color: "#1d4ed8", fontWeight: 800, fontSize: 13 }}>
                      {propertyCountLabel}
                    </div>
                    <div style={{ ...mobilePillSafe, padding: "10px 14px", borderRadius: 999, background: "#ecfdf5", color: "#047857", fontWeight: 800, fontSize: 13 }}>
                      {reviewCountLabel}
                    </div>
                  </div>
                  <button
                    style={{ ...styles.primaryButton, ...mobileButtonSafe, marginTop: 0, minHeight: 56 }}
                    onClick={() => {
                      if (featuredProperties[0]) {
                        handleSelectProperty(featuredProperties[0]);
                        setPropertyMapCenter({ lat: Number(featuredProperties[0].lat), lng: Number(featuredProperties[0].lng) });
                        setPropertyMapZoom(15);
                        if (isMobile) setShowMobilePropertyModal(true);
                      }
                    }}
                  >
                    Open Top Property
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: isPhoneMobile ? 10 : 12, ...phoneSectionSafe }}>
                {[
                  {
                    icon: "🔍",
                    title: "Find my property",
                    text: "Search the map and see property history before you rent.",
                    action: () => setActiveTab("home"),
                    button: "Search now",
                  },
                  {
                    icon: "🏠",
                    title: "Add a property",
                    text: "Pin a rental so the community can build trusted history over time.",
                    action: goToAddProperty,
                    button: "Add property",
                  },
                  {
                    icon: "⭐",
                    title: "Leave a review",
                    text: "Review a landlord, tenant, property manager, or property from your rental experience.",
                    action: () => setActiveTab("reviews"),
                    button: "Review someone",
                  },
                ].map((step) => (
                  <div key={step.title} style={{ borderRadius: 22, border: "1px solid rgba(226,232,240,0.95)", background: "rgba(255,255,255,0.94)", padding: 16, boxShadow: "0 14px 30px rgba(15,23,42,0.06)", boxSizing: "border-box", overflow: "hidden", width: "100%", maxWidth: "100%", minWidth: 0 }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>{step.icon}</div>
                    <div style={{ fontSize: 17, fontWeight: 900, color: "#0f172a" }}>{step.title}</div>
                    <div style={{ marginTop: 6, fontSize: 14, color: "#64748b", lineHeight: 1.55 }}>{step.text}</div>
                    <button
                      type="button"
                      onClick={step.action}
                      style={{ marginTop: 14, width: "100%", maxWidth: "100%", borderRadius: 999, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", fontWeight: 900, padding: "11px 14px", cursor: "pointer", boxSizing: "border-box", overflow: "visible", flexShrink: 0, ...(isPhoneMobile ? mobileButtonSafe : {}) }}
                    >
                      {step.button}
                    </button>
                  </div>
                ))}
              </div>

              {featuredProperties.length ? (
                <div style={{ marginTop: 18, ...phoneSectionSafe }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "#0f6fff" }}>Featured now</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>Top properties around your map</div>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: isPhoneMobile ? 10 : 14, ...phoneSectionSafe }}>
                    {featuredProperties.map((prop, index) => {
                      const claimState = getPropertyClaimState(prop);
                      return (
                      <button
                        key={prop.id}
                        type="button"
                        onClick={() => {
                          handleSelectProperty(prop);
                          setPropertyMapCenter({ lat: Number(prop.lat), lng: Number(prop.lng) });
                          setPropertyMapZoom(15);
                          if (isMobile) {
                            setShowMobilePropertyModal(true);
                          } else {
                            setHoveredProperty(prop);
                          }
                        }}
                        style={{
                          textAlign: isPhoneMobile ? "center" : "left",
                          width: "100%",
                          maxWidth: "100%",
                          minWidth: 0,
                          boxSizing: "border-box",
                          transform: "none",
                          position: "relative",
                          left: "auto",
                          right: "auto",
                          top: "auto",
                          bottom: "auto",
                          border: "1px solid rgba(226,232,240,0.95)",
                          background: "rgba(255,255,255,0.92)",
                          borderRadius: 24,
                          padding: 0,
                          overflow: isPhoneMobile ? "visible" : "hidden",
                          boxShadow: "0 16px 32px rgba(15,23,42,0.08)",
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            minHeight: 124,
                            padding: 16,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            background: prop.imageUrl
                              ? `linear-gradient(180deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.55) 100%), url(${prop.imageUrl}) center/cover no-repeat`
                              : index % 2 === 0
                              ? "linear-gradient(135deg, #dbeafe 0%, #eff6ff 45%, #ecfdf5 100%)"
                              : "linear-gradient(135deg, #ecfeff 0%, #f0fdf4 45%, #dbeafe 100%)",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: isMobile ? "center" : "space-between", gap: 8, alignItems: "center", flexWrap: "wrap", width: "100%" }}>
                            <div style={{ ...mobilePillSafe, padding: "7px 10px", borderRadius: 999, background: "rgba(255,255,255,0.72)", fontWeight: 800, fontSize: 12, color: "#0f172a" }}>
                              #{index + 1} Pick
                            </div>
                            <div style={{ ...mobilePillSafe, padding: "7px 10px", borderRadius: 999, background: "rgba(15,23,42,0.72)", color: "#fff", fontWeight: 800, fontSize: 12 }}>
                              {prop.ratingData.average.toFixed(1)} ★
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", lineHeight: 1.1 }}>{prop.title || prop.address || "Property"}</div>
                            <div style={{ marginTop: 6, color: "#334155", fontSize: 13 }}>{formatCityState(prop) || prop.address || "Community added"}</div>
                          </div>
                        </div>
                        <div style={{ padding: 14 }}>
                          <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.55, minHeight: 40 }}>
                            {prop.description || "Open this property profile to see ratings, reviews, and claim status."}
                          </div>
                          <div style={{ ...mobileCenteredRow, gap: 8, marginTop: 12, marginBottom: 0 }}>
                            <div style={{ ...mobilePillSafe, padding: "7px 10px", borderRadius: 999, background: "#eff6ff", color: "#1d4ed8", fontWeight: 800, fontSize: 12 }}>
                              {prop.ratingData.count} reviews
                            </div>
                            <div style={{ ...mobilePillSafe, padding: "7px 10px", borderRadius: 999, background: claimState.tone === "success" ? "#dcfce7" : claimState.tone === "pending" ? "#fef3c7" : "#f8fafc", color: claimState.tone === "success" ? "#166534" : claimState.tone === "pending" ? "#92400e" : "#475569", fontWeight: 800, fontSize: 12 }}>
                              {claimState.label === "Claim Property" ? "Community added" : claimState.label}
                            </div>
                          </div>
                        </div>
                      </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {savedProperties.length ? (
                <div style={{ marginTop: 18 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "#10b981" }}>Saved list</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>Your saved properties</div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 14 }}>
                    {savedProperties.map((prop) => (
                      <button
                        key={prop.id}
                        type="button"
                        onClick={() => {
                          handleSelectProperty(prop);
                          setPropertyMapCenter({ lat: Number(prop.lat), lng: Number(prop.lng) });
                          setPropertyMapZoom(15);
                          if (isMobile) setShowMobilePropertyModal(true);
                        }}
                        style={styles.savedPropertyCard}
                      >
                        <div style={{
                          ...styles.savedPropertyImage,
                          background: prop.imageUrl
                            ? `linear-gradient(180deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.52) 100%), url(${prop.imageUrl}) center/cover no-repeat`
                            : "linear-gradient(135deg, #dbeafe 0%, #eff6ff 45%, #ecfdf5 100%)",
                        }} />
                        <div style={{ padding: 14 }}>
                          <div style={{ fontSize: 16, fontWeight: 900, color: "#0f172a" }}>{prop.title || prop.address}</div>
                          <div style={{ marginTop: 4, color: "#475569", fontSize: 13 }}>{formatCityState(prop) || prop.address}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div
              style={{
                ...phoneSectionSafe,
                borderRadius: 28,
                padding: isMobile ? 16 : 18,
                background: "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(30,41,59,0.96) 100%)",
                color: "#fff",
                boxShadow: "0 26px 58px rgba(15,23,42,0.20)",
                minHeight: 420,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ ...mobilePillSafe, padding: "8px 12px", borderRadius: 999, background: "rgba(255,255,255,0.12)", display: "inline-flex", justifyContent: "center", alignItems: "center", fontSize: 12, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase" }}>
                  Spotlight Property
                </div>
                <div style={{ marginTop: 18, fontSize: isMobile ? 26 : 32, fontWeight: 900, lineHeight: 1.05 }}>
                  {spotlightProperty?.title || "Your next property card upgrade"}
                </div>
                <div style={{ marginTop: 10, color: "rgba(255,255,255,0.8)", lineHeight: 1.7, fontSize: 15 }}>
                  {spotlightProperty?.description || "Your Home tab now has a stronger hero area, highlighted properties, and a better browsing flow that feels closer to a real rental marketplace."}
                </div>
              </div>

              <div style={{ display: "grid", gap: isPhoneMobile ? 10 : 12, marginTop: 22, ...phoneSectionSafe }}>
                <div style={{ display: "grid", gridTemplateColumns: isPhoneMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: isPhoneMobile ? 10 : 12, ...phoneSectionSafe }}>
                  <div style={{ borderRadius: 20, padding: 14, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.68)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 800 }}>Average rating</div>
                    <div style={{ fontSize: 28, fontWeight: 900, marginTop: 8 }}>{spotlightProperty ? spotlightProperty.ratingData.average.toFixed(1) : "0.0"}</div>
                  </div>
                  <div style={{ borderRadius: 20, padding: 14, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.68)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 800 }}>Review volume</div>
                    <div style={{ fontSize: 28, fontWeight: 900, marginTop: 8 }}>{spotlightProperty ? spotlightProperty.ratingData.count : 0}</div>
                  </div>
                </div>

                <button
                  style={{ ...styles.primaryButton, ...mobileButtonSafe, marginTop: 0 }}
                  onClick={() => {
                    if (!spotlightProperty) return;
                    handleSelectProperty(spotlightProperty);
                    setPropertyMapCenter({ lat: Number(spotlightProperty.lat), lng: Number(spotlightProperty.lng) });
                    setPropertyMapZoom(15);
                    if (isMobile) setShowMobilePropertyModal(true);
                  }}
                >
                  View Spotlight Property
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ ...styles.grid, gridTemplateColumns: isMobile ? "1fr" : "1.2fr 0.8fr", gap: isMobile ? 16 : 18 }}>
          <div style={styles.cardWide}>
            <SectionTitle
              title="Map + property discovery"
              subtitle="Tap pins, compare the top properties, and open a stronger property profile right from the map."
            />

            {mapLoadError ? <p style={styles.smallText}>Google Maps failed to load.</p> : null}
            {!isMapLoaded && !mapLoadError ? <p style={styles.smallText}>Loading Google Map...</p> : null}

            {isMapLoaded ? (
              <div style={{ position: "relative" }}>
                <div style={{ position: isMobile ? "relative" : "absolute", top: isPhoneMobile ? "auto" : isMobile ? "auto" : 16, left: isPhoneMobile ? "auto" : isMobile ? "auto" : 16, zIndex: 5, display: "flex", justifyContent: "center", alignItems: "center", width: isPhoneMobile || isMobile ? "100%" : "auto", maxWidth: isPhoneMobile ? 340 : "100%", margin: isPhoneMobile ? "0 auto 12px" : 0, marginBottom: isPhoneMobile || isMobile ? 12 : 0, boxSizing: "border-box", overflow: "hidden", padding: "10px 14px", borderRadius: 999, background: "rgba(255,255,255,0.94)", border: "1px solid rgba(226,232,240,0.95)", boxShadow: "0 12px 26px rgba(15,23,42,0.08)", fontWeight: 800, color: "#0f172a", fontSize: 13 }}>
                  {featuredProperties.length ? `Top pick: ${featuredProperties[0].title || featuredProperties[0].address}` : "Tap any blue pin"}
                </div>
                <div style={styles.mapWrap}>
                  <MapTypeControls
                    mapType={mapType}
                    onChange={handlePropertyMapTypeChange}
                    isPhoneMobile={isPhoneMobile}
                  />
                  <GoogleMap
                    onLoad={handleGoogleMapLoad}
                    zoom={propertyMapZoom}
                    center={propertyMapCenter}
                    onClick={handlePropertyMapClick}
                    options={{
                      ...mobileCleanMapOptions,
                      mapTypeId: mapType,
                      gestureHandling: "greedy",
                      clickableIcons: false,
                      streetViewControl: false,
                      fullscreenControl: true,
                    }}
                    mapContainerStyle={{
                      width: "100%",
                      height: isMobile ? "420px" : "540px",
                      minHeight: isMobile ? "420px" : "540px",
                      borderRadius: "28px",
                      overflow: "hidden",
                      boxShadow: isMobile ? "0 18px 36px rgba(15, 23, 42, 0.14)" : "0 26px 52px rgba(15, 23, 42, 0.14)",
                      border: "1px solid rgba(207, 226, 255, 0.9)",
                    }}
                  >
                    {pendingPropertyLocation ? <Marker position={pendingPropertyLocation} icon={markerIcon} /> : null}

                    {visibleProperties
                      .filter((prop) => prop && isFiniteCoordinate(prop.lat) && isFiniteCoordinate(prop.lng))
                      .map((prop) => (
                        <Marker
                          key={prop.id}
                          position={{ lat: Number(prop.lat), lng: Number(prop.lng) }}
                          icon={buildPropertyMarkerIcon(prop, selectedProperty?.id === prop.id)}
                          onClick={() => {
                            handleSelectProperty(prop);
                            setSelectedProperty(prop);
                            setPropertyMapCenter({ lat: Number(prop.lat), lng: Number(prop.lng) });
                            setPropertyMapZoom(15);
                            setShowMobilePropertyModal(true);
                            setHoveredProperty(prop);
                          }}
                        />
                      ))}

                    {hoveredProperty && hoveredProperty.lat != null && hoveredProperty.lng != null ? (
                      <InfoWindow
                        position={{ lat: Number(hoveredProperty.lat), lng: Number(hoveredProperty.lng) }}
                        onCloseClick={() => setHoveredProperty(null)}
                      >
                        <div style={{ ...styles.infoWindowShell, width: isPhoneMobile ? 260 : 320 }}>
                          <PropertyPreviewCard property={hoveredProperty} compact />
                        </div>
                      </InfoWindow>
                    ) : null}
                  </GoogleMap>
                </div>
              </div>
            ) : null}
          </div>

          <div ref={propertyCreateCardRef} style={styles.card}>
            <SectionTitle
              title="Create property"
              subtitle="Drop a cleaner listing into the map so the platform keeps growing."
            />

            <label style={styles.label}>Property Title</label>
            <input
              style={styles.input}
              value={propertyTitle}
              onChange={(e) => setPropertyTitle(e.target.value)}
              placeholder="2BR Apartment, Duplex, House..."
            />

            <label style={styles.label}>Address</label>
            <input
              ref={propertyAddressInputRef}
              style={styles.input}
              value={propertyAddress}
              onChange={(e) => setPropertyAddress(e.target.value)}
              placeholder="Start typing street address, city, state, zip"
              autoComplete="off"
            />

            {(propertyAddressMeta.streetLine || propertyAddressMeta.city || propertyAddressMeta.state || propertyAddressMeta.zip) ? (
              <div style={styles.autoFillHint}>
                Auto-filled: {[propertyAddressMeta.streetLine, propertyAddressMeta.city, propertyAddressMeta.state, propertyAddressMeta.zip].filter(Boolean).join(" • ")}
              </div>
            ) : null}

            <label style={styles.label}>Description</label>
            <textarea
              style={styles.textarea}
              value={propertyDescription}
              onChange={(e) => setPropertyDescription(e.target.value)}
              placeholder="Describe the property..."
            />

            {false ? (
              <div
                style={{
                  display: "grid",
                  gap: 12,
                  padding: 14,
                  borderRadius: 18,
                  border: "1px solid rgba(16,185,129,0.24)",
                  background: "linear-gradient(135deg, rgba(236,253,245,0.92), rgba(239,246,255,0.92))",
                  marginBottom: 14,
                }}
              >
                <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={propertyIsForRent}
                    onChange={(event) => setPropertyIsForRent(event.target.checked)}
                  />
                  Is this property currently for rent?
                </label>

                {propertyIsForRent ? (
                  <div style={{ display: "grid", gap: 10 }}>
                    <label style={styles.label}>Monthly rent price</label>
                    <input
                      style={styles.input}
                      value={propertyMonthlyRent}
                      onChange={(event) => setPropertyMonthlyRent(event.target.value)}
                      inputMode="decimal"
                      placeholder="1850"
                    />

                    <label style={styles.label}>Available date</label>
                    <input
                      style={styles.input}
                      type="date"
                      value={propertyAvailableDate}
                      onChange={(event) => setPropertyAvailableDate(event.target.value)}
                    />

                    <div style={{ display: "grid", gridTemplateColumns: isPhoneMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                      <div>
                        <label style={styles.label}>Bedrooms</label>
                        <input
                          style={styles.input}
                          value={propertyBedrooms}
                          onChange={(event) => setPropertyBedrooms(event.target.value)}
                          inputMode="decimal"
                          placeholder="2"
                        />
                      </div>
                      <div>
                        <label style={styles.label}>Bathrooms</label>
                        <input
                          style={styles.input}
                          value={propertyBathrooms}
                          onChange={(event) => setPropertyBathrooms(event.target.value)}
                          inputMode="decimal"
                          placeholder="1"
                        />
                      </div>
                    </div>

                    <label style={styles.label}>Lease type</label>
                    <select
                      style={styles.input}
                      value={propertyLeaseType}
                      onChange={(event) => setPropertyLeaseType(event.target.value)}
                    >
                      <option value="">Select lease type</option>
                      <option value="12-month">12-month lease</option>
                      <option value="month-to-month">Month-to-month</option>
                      <option value="short-term">Short-term</option>
                      <option value="sublet">Sublet</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                ) : null}
              </div>
            ) : null}

            <label style={styles.label}>Property Photo</label>
            <div style={styles.propertyUploadCard}>
              <div style={styles.propertyUploadTopRow}>
                <strong style={styles.strongHeading}>Choose from your device</strong>
                {(propertyImageFile || propertyImageUrl.trim() || propertyImagePreviewUrl) ? (
                  <button
                    type="button"
                    style={styles.ghostButton}
                    onClick={clearPropertyImageSelection}
                  >
                    Remove
                  </button>
                ) : null}
              </div>

              <div style={styles.uploadHintText}>
                Pick a photo directly from your phone or computer. If that does not work, you can still paste an image link below.
              </div>

              <input
                type="file"
                accept="image/*"
                onChange={handlePropertyImageFileChange}
                style={styles.input}
              />

              <input
                style={styles.input}
                value={propertyImageUrl}
                onChange={(e) => {
                  setPropertyImageUrl(e.target.value);
                  if (e.target.value.trim()) {
                    if (propertyImagePreviewUrl) {
                      try {
                        URL.revokeObjectURL(propertyImagePreviewUrl);
                      } catch (error) {
                        console.error("Property image preview cleanup error:", error);
                      }
                    }
                    setPropertyImageFile(null);
                    setPropertyImagePreviewUrl("");
                  }
                }}
                placeholder="Or paste an image URL (optional)"
              />

              {(propertyImagePreviewUrl || propertyImageUrl.trim()) ? (
                <div style={styles.propertyImagePreviewCard}>
                  <img
                    src={propertyImagePreviewUrl || propertyImageUrl}
                    alt="Property preview"
                    style={styles.propertyImagePreview}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              ) : null}

              {propertyImageFile ? (
                <div style={styles.autoFillHint}>
                  Ready to upload: {propertyImageFile.name}
                </div>
              ) : null}
            </div>

            <div style={styles.mapSelectionBox}>
              <strong style={styles.strongHeading}>Selected Pin</strong>
              {pendingPropertyLocation ? (
                <p style={styles.smallText}>lat {pendingPropertyLocation.lat.toFixed(5)} | lng {pendingPropertyLocation.lng.toFixed(5)}</p>
              ) : (
                <p style={styles.smallText}>No pin selected yet. Choose a Google address above or click the map.</p>
              )}
            </div>

            <button
              style={{ ...styles.primaryButton, width: "100%", opacity: isUploadingPropertyImage ? 0.8 : 1 }}
              onClick={handleCreateProperty}
              disabled={isUploadingPropertyImage}
            >
              {isUploadingPropertyImage ? "Uploading Photo & Adding Property..." : "Add Property"}
            </button>
          </div>
        </div>

        <div style={{ ...styles.card, ...styles.scrollableColumnCard, marginTop: 18 }}>
          <SectionTitle
            title="Browse property cards"
            subtitle="Bigger cards, stronger badges, and quicker actions so the listing section feels like a real marketplace."
          />

          {properties.length === 0 ? (
            <div style={styles.emptyReviewsBox}>No properties yet — be the first to add one and start building rental history.</div>
          ) : (
            <div style={{ ...styles.propertyGrid, ...styles.scrollableInnerList }}>
              {rankedProperties.map((prop) => {
                const ratingData = prop.ratingData;
                const claimState = getPropertyClaimState(prop);

                return (
                  <div key={prop.id} style={styles.propertyCardShell}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <div style={{ padding: "7px 10px", borderRadius: 999, background: "#eff6ff", color: "#1d4ed8", fontWeight: 800, fontSize: 12 }}>
                          {ratingData.average.toFixed(1)} average
                        </div>
                        <div style={{ padding: "7px 10px", borderRadius: 999, background: "#f8fafc", color: "#475569", fontWeight: 800, fontSize: 12 }}>
                          {ratingData.count} reviews
                        </div>
                      </div>
                      {claimState.label !== "Claim Property" ? (
                        <div style={{ padding: "7px 10px", borderRadius: 999, background: claimState.tone === "success" ? "#dcfce7" : claimState.tone === "pending" ? "#fef3c7" : "#f8fafc", color: claimState.tone === "success" ? "#166534" : claimState.tone === "pending" ? "#92400e" : "#475569", fontWeight: 800, fontSize: 12 }}>
                          {claimState.label}
                        </div>
                      ) : null}
                    </div>

                    <PropertyPreviewCard property={prop} />

                    <div style={{ ...styles.propertyFooterRow, flexWrap: "wrap", rowGap: 10 }}>
                      <div style={styles.footerStatPill}>Avg: {ratingData.average.toFixed(1)} / 5</div>
                      <div style={styles.footerStatPill}>Reviews: {ratingData.count}</div>
                      <div style={styles.footerStatPill}>{prop.city || "Unknown city"}</div>
                    </div>

                    <div style={{ display: "grid", gap: 10, width: "100%" }}>
                      <button
                        style={{ ...styles.secondaryButton, width: "100%" }}
                        onClick={() => {
                          handleSelectProperty(prop);
                          setPropertyMapCenter({ lat: Number(prop.lat), lng: Number(prop.lng) });
                          setPropertyMapZoom(15);
                          if (isMobile) setShowMobilePropertyModal(true);
                        }}
                      >
                        Open Property Profile
                      </button>

                      {claimState.canOpenClaim ? (
                        <button
                          style={{ ...styles.primaryButton, width: "100%", ...(canRoleClaimProperty(role, verificationStatus) ? {} : { opacity: 0.6, cursor: "not-allowed" }) }}
                          onClick={() => {
                            handleSelectProperty(prop);
                            if (!canRoleClaimProperty(role, verificationStatus)) {
                              setStatusMessage("Only landlord and property manager accounts can claim a property.");
                              return;
                            }
                            setTimeout(() => {
                              openClaimVerificationModal(prop);
                            }, 0);
                          }}
                        >
                          {claimState.label}
                        </button>
                      ) : (
                        <span style={{ ...getPropertyClaimBadgeStyle(prop), width: "100%", justifyContent: "center", boxSizing: "border-box" }}>
                          {claimState.label}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      {selectedProperty && (!isMobile || showMobilePropertyModal) ? (
      <div
        style={showMobilePropertyModal ? styles.propertyProfileOverlay : undefined}
        onClick={showMobilePropertyModal ? handleCloseMobilePropertyModal : undefined}
      >
        <div
          ref={propertyProfileModalRef}
          style={
            showMobilePropertyModal
              ? styles.propertyProfileModalCard
              : isMobile
              ? styles.mobileModalCard
              : styles.card
          }
          onClick={(e) => {
            if (showMobilePropertyModal) e.stopPropagation();
          }}
        >
        <div ref={propertyProfileTopRef} style={{ width: "100%", height: 1 }} />
        <SectionTitle
          title={t("selectedProperty")}
          subtitle="See the property profile, click from a pin, and leave a property review."
        />

        {showMobilePropertyModal || isMobile ? (
          <div style={styles.mobileModalHeaderRow}>
            <button
              style={styles.mobileModalCloseButton}
              onClick={handleCloseMobilePropertyModal}
            >
              Close
            </button>
          </div>
        ) : null}

        {selectedProperty ? (
          <div style={{ display: "contents" }}>
            <div style={styles.userCard}>
              <PropertyPreviewCard property={selectedProperty} />

              <div style={{ ...styles.ratingHeroBox, flexWrap: "wrap", rowGap: 12 }}>
                <div style={styles.ratingHeroValue}>
                  {averagePropertyRating
                    ? averagePropertyRating.toFixed(1)
                    : "0.0"}
                </div>
                <div>
                  <div>
                    {renderStars(
                      Math.round(averagePropertyRating),
                      false,
                      null,
                      26
                    )}
                  </div>
                  <div style={styles.smallText}>Official verified property rating</div>
                </div>
              </div>

              {getPropertyRatingData(selectedProperty?.id).communityCount ? (
                <div style={{ ...styles.communityRatingBanner, marginTop: 12 }}>
                  <span style={styles.communityRatingLabel}>Community rating</span>
                  <span style={styles.communityRatingValue}>
                    {getPropertyRatingData(selectedProperty?.id).communityAverage.toFixed(1)} / 5 ·{" "}
                    {getPropertyRatingData(selectedProperty?.id).communityCount} unverified rating
                    {getPropertyRatingData(selectedProperty?.id).communityCount !== 1 ? "s" : ""}
                  </span>
                </div>
              ) : null}

              <p style={styles.smallText}>
                Verified reviews: {propertyReviews.length}
              </p>
              <p style={styles.smallText}>
                Claim Status:{" "}
                {getPropertyClaimState(selectedProperty).label === "Claim Property"
                  ? "Unverified / community added"
                  : getPropertyClaimState(selectedProperty).label}
              </p>

              <div style={styles.aiRiskCard}>
                <div style={styles.profileTitleRowWrap}>
                  <div>
                    <div style={styles.aiDraftEyebrow}>RentDuo AI</div>
                    <strong style={styles.strongHeading}>Property Risk Intelligence</strong>
                  </div>
                  {selectedProperty?.aiRiskIntelligence?.riskLevel ? (
                    <span
                      style={{
                        ...styles.statusPill,
                        ...(selectedProperty.aiRiskIntelligence.riskLevel === "High"
                          ? styles.aiRiskHigh
                          : selectedProperty.aiRiskIntelligence.riskLevel === "Moderate"
                          ? styles.aiRiskModerate
                          : styles.aiRiskLow),
                      }}
                    >
                      {selectedProperty.aiRiskIntelligence.riskLevel} Risk
                    </span>
                  ) : null}
                </div>
                {selectedProperty?.aiRiskIntelligence ? (
                  <>
                    <div style={styles.aiRiskScoreRow}>
                      <strong style={styles.aiRiskScoreValue}>{Number(selectedProperty.aiRiskIntelligence.riskScore || 0)}</strong>
                      <span style={styles.smallText}>/ 100 risk score</span>
                    </div>
                    <p style={styles.smallText}>{selectedProperty.aiRiskIntelligence.summary}</p>
                    <div style={styles.aiRiskCategoryGrid}>
                      {[
                        ["Safety", "safety"],
                        ["Maintenance", "maintenance"],
                        ["Cleanliness", "cleanliness"],
                        ["Noise", "noise"],
                        ["Responsiveness", "responsiveness"],
                        ["Scam Risk", "scamRisk"],
                      ].map(([label, key]) => (
                        <div key={key} style={styles.aiRiskCategoryCard}>
                          <span>{label}</span>
                          <strong>{Number(selectedProperty.aiRiskIntelligence.categories?.[key] || 0)}</strong>
                        </div>
                      ))}
                    </div>
                    {selectedProperty.aiRiskIntelligence.commonSignals?.length ? (
                      <p style={styles.smallText}>
                        Signals: {selectedProperty.aiRiskIntelligence.commonSignals.join(" | ")}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p style={styles.smallText}>
                    Generate a cautious AI summary based on available verified reviews and issue trends. Limited data produces limited insight.
                  </p>
                )}
                {propertyRiskError ? <p style={styles.safeReviewText}>{propertyRiskError}</p> : null}
                <p style={styles.aiRiskDisclaimer}>
                  AI-generated guidance only. RentDuo does not independently verify every claim, and this is not legal, financial, or professional advice.
                </p>
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, width: "100%", marginTop: 10 }}
                  onClick={handleAnalyzePropertyRisk}
                  disabled={propertyRiskLoadingId === selectedProperty.id}
                >
                  {propertyRiskLoadingId === selectedProperty.id
                    ? "Analyzing Property Risk..."
                    : selectedProperty?.aiRiskIntelligence
                    ? "Refresh AI Risk Score"
                    : "Generate AI Risk Score"}
                </button>
              </div>

              <div style={styles.aiRiskCard}>
                <div style={styles.profileTitleRowWrap}>
                  <div>
                    <div style={styles.aiDraftEyebrow}>RentDuo AI</div>
                    <strong style={styles.strongHeading}>Scam Screening</strong>
                  </div>
                  {selectedProperty?.aiScamIntelligence?.riskLevel ? (
                    <span
                      style={{
                        ...styles.statusPill,
                        ...(selectedProperty.aiScamIntelligence.riskLevel === "High"
                          ? styles.aiRiskHigh
                          : selectedProperty.aiScamIntelligence.riskLevel === "Medium"
                          ? styles.aiRiskModerate
                          : styles.aiRiskLow),
                      }}
                    >
                      {selectedProperty.aiScamIntelligence.riskLevel} Risk
                    </span>
                  ) : null}
                </div>
                {selectedProperty?.aiScamIntelligence ? (
                  <>
                    <div style={styles.aiRiskScoreRow}>
                      <strong style={styles.aiRiskScoreValue}>{Number(selectedProperty.aiScamIntelligence.scamRiskScore || 0)}</strong>
                      <span style={styles.smallText}>/ 100 scam signal score</span>
                    </div>
                    <p style={styles.smallText}>{selectedProperty.aiScamIntelligence.explanation}</p>
                    {selectedProperty.aiScamIntelligence.signals?.length ? (
                      <p style={styles.smallText}>Signals: {selectedProperty.aiScamIntelligence.signals.join(" | ")}</p>
                    ) : null}
                    {selectedProperty.aiScamIntelligence.safetyTips?.length ? (
                      <div style={styles.aiMaintenanceCard}>
                        <strong style={styles.strongHeading}>Safety tips</strong>
                        {selectedProperty.aiScamIntelligence.safetyTips.map((tip) => (
                          <p key={tip} style={styles.smallText}>{tip}</p>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p style={styles.smallText}>
                    Screen this listing for payment pressure, suspicious pricing, identity mismatch, and off-platform payment warning signals.
                  </p>
                )}
                {propertyScamError ? <p style={styles.safeReviewText}>{propertyScamError}</p> : null}
                <p style={styles.aiRiskDisclaimer}>
                  AI screening may be inaccurate. Confirm identity, property access, and payment terms independently before sending money.
                </p>
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, width: "100%", marginTop: 10 }}
                  onClick={handleAnalyzePropertyScamRisk}
                  disabled={propertyScamLoadingId === selectedProperty.id}
                >
                  {propertyScamLoadingId === selectedProperty.id
                    ? "Screening Listing..."
                    : selectedProperty?.aiScamIntelligence
                    ? "Refresh Scam Screening"
                    : "Screen for Scam Signals"}
                </button>
              </div>

              {isRentalListing(selectedProperty) ? (
                <div
                  style={{
                    display: "grid",
                    gap: 10,
                    marginTop: 12,
                    padding: 14,
                    borderRadius: 18,
                    border: "1px solid rgba(16,185,129,0.24)",
                    background: "linear-gradient(135deg, rgba(236,253,245,0.96), rgba(239,246,255,0.96))",
                    boxSizing: "border-box",
                  }}
                >
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ padding: "7px 10px", borderRadius: 999, background: "#10b981", color: "#fff", fontSize: 12, fontWeight: 950 }}>
                      For Rent
                    </span>
                    {selectedProperty?.monthlyRent ? (
                      <strong style={{ color: "#0f172a", fontSize: 20 }}>
                        {formatMonthlyRent(selectedProperty.monthlyRent)}
                      </strong>
                    ) : null}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {formatBedsBaths(selectedProperty) ? (
                      <span style={{ ...styles.miniMetaPill, background: "#fff" }}>{formatBedsBaths(selectedProperty)}</span>
                    ) : null}
                    {selectedProperty?.availableDate ? (
                      <span style={{ ...styles.miniMetaPill, background: "#fff" }}>Available {formatAvailableDate(selectedProperty.availableDate)}</span>
                    ) : null}
                    {selectedProperty?.leaseType ? (
                      <span style={{ ...styles.miniMetaPill, background: "#fff" }}>{selectedProperty.leaseType}</span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {canManageRentalListing(selectedProperty) ? (
                <div
                  style={{
                    display: "grid",
                    gap: 10,
                    marginTop: 12,
                    padding: 14,
                    borderRadius: 18,
                    border: "1px solid rgba(16,185,129,0.24)",
                    background: "#f8fffb",
                    boxSizing: "border-box",
                  }}
                >
                  <strong style={styles.strongHeading}>Rental availability</strong>
                  <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(rentalEditDraft.isForRent)}
                      onChange={(event) => setRentalEditDraft((prev) => ({ ...prev, isForRent: event.target.checked }))}
                    />
                    Is this verified property currently for rent?
                  </label>
                  {rentalEditDraft.isForRent ? (
                    <div style={{ display: "grid", gap: 10 }}>
                      <label style={styles.label}>Monthly rent price</label>
                      <input
                        style={styles.input}
                        value={rentalEditDraft.monthlyRent}
                        onChange={(event) => setRentalEditDraft((prev) => ({ ...prev, monthlyRent: event.target.value }))}
                        inputMode="decimal"
                        placeholder="1850"
                      />
                      <label style={styles.label}>Available date</label>
                      <input
                        style={styles.input}
                        type="date"
                        value={rentalEditDraft.availableDate}
                        onChange={(event) => setRentalEditDraft((prev) => ({ ...prev, availableDate: event.target.value }))}
                      />
                      <div style={{ display: "grid", gridTemplateColumns: isPhoneMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                        <div>
                          <label style={styles.label}>Bedrooms</label>
                          <input
                            style={styles.input}
                            value={rentalEditDraft.bedrooms}
                            onChange={(event) => setRentalEditDraft((prev) => ({ ...prev, bedrooms: event.target.value }))}
                            inputMode="decimal"
                            placeholder="2"
                          />
                        </div>
                        <div>
                          <label style={styles.label}>Bathrooms</label>
                          <input
                            style={styles.input}
                            value={rentalEditDraft.bathrooms}
                            onChange={(event) => setRentalEditDraft((prev) => ({ ...prev, bathrooms: event.target.value }))}
                            inputMode="decimal"
                            placeholder="1"
                          />
                        </div>
                      </div>
                      <label style={styles.label}>Lease type</label>
                      <select
                        style={styles.input}
                        value={rentalEditDraft.leaseType}
                        onChange={(event) => setRentalEditDraft((prev) => ({ ...prev, leaseType: event.target.value }))}
                      >
                        <option value="">Select lease type</option>
                        <option value="12-month">12-month lease</option>
                        <option value="month-to-month">Month-to-month</option>
                        <option value="short-term">Short-term</option>
                        <option value="sublet">Sublet</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    style={{ ...styles.primaryButton, width: "100%", marginTop: 0 }}
                    onClick={handleSaveRentalListing}
                    disabled={isSavingRentalListing}
                  >
                    {isSavingRentalListing ? "Saving..." : "Save Rental Listing"}
                  </button>
                </div>
              ) : null}

              {false && role === "tenant" && selectedProperty?.id ? (
                <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                  {getTenantResidencyClaimForSelectedProperty() ? (
                    <button
                      type="button"
                      style={{ ...styles.secondaryButton, width: "100%", marginTop: 0 }}
                      onClick={handleMoveOutResidency}
                      disabled={isSavingResidencyClaim}
                    >
                      {isSavingResidencyClaim ? "Updating..." : "I moved out"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      style={{ ...styles.secondaryButton, width: "100%", marginTop: 0 }}
                      onClick={handleClaimResidency}
                      disabled={isSavingResidencyClaim}
                    >
                      {isSavingResidencyClaim ? "Saving..." : "I live here"}
                    </button>
                  )}
                  <p style={styles.smallText}>Residency claims are private and visible only to the claimed landlord/manager or admin.</p>
                </div>
              ) : null}

              {false && canViewConnectedTenants(selectedProperty) ? (
                <div style={{ ...styles.propertyHistoryCard, marginTop: 12 }}>
                  <strong style={styles.strongHeading}>Connected Tenants</strong>
                  {propertyResidencyClaims.filter((claim) => claim.propertyId === selectedProperty?.id).length ? (
                    <div style={styles.propertyTimelineList}>
                      {propertyResidencyClaims
                        .filter((claim) => claim.propertyId === selectedProperty?.id)
                        .map((claim) => (
                          <div key={claim.id} style={styles.propertyTimelineItem}>
                            <span>{claim.tenantDisplayName || "Tenant"}</span>
                            <strong>
                              {claim.status || "active"} · claimed {formatFeedTimeAgo(claim.claimedAt) || "recently"}
                              {claim.movedOutAt ? ` · moved out ${formatFeedTimeAgo(claim.movedOutAt) || ""}` : ""}
                            </strong>
                            {String(claim.status || "").toLowerCase() === "pending" ? (
                              <div style={styles.adminButtonGrid}>
                                <button type="button" style={styles.primaryButton} onClick={() => handleUpdateResidencyClaimStatus(claim, "approved")} disabled={isSavingResidencyClaim}>
                                  Approve Tenant
                                </button>
                                <button type="button" style={styles.rejectButton} onClick={() => handleUpdateResidencyClaimStatus(claim, "rejected")} disabled={isSavingResidencyClaim}>
                                  Reject
                                </button>
                              </div>
                            ) : null}
                            {["approved", "active"].includes(String(claim.status || "").toLowerCase()) ? (
                              <button type="button" style={{ ...styles.secondaryButton, width: "100%", marginTop: 8 }} onClick={() => handleUpdateResidencyClaimStatus(claim, "moved_out")} disabled={isSavingResidencyClaim}>
                                Mark Moved Out
                              </button>
                            ) : null}
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p style={styles.smallText}>No connected tenants yet.</p>
                  )}
                </div>
              ) : null}

              <TenantClaimsPanel
                role={role}
                selectedProperty={selectedProperty}
                tenantClaim={getTenantResidencyClaimForSelectedProperty()}
                connectedClaims={propertyResidencyClaims.filter((claim) => claim.propertyId === selectedProperty?.id)}
                canViewConnectedTenants={canViewConnectedTenants(selectedProperty)}
                isSaving={isSavingResidencyClaim}
                onClaimResidency={handleClaimResidency}
                onMoveOutResidency={handleMoveOutResidency}
                onApproveResidency={(claim) => handleUpdateResidencyClaimStatus(claim, "approved")}
                onRejectResidency={(claim) => handleUpdateResidencyClaimStatus(claim, "rejected")}
                onMarkTenantMovedOut={(claim) => handleUpdateResidencyClaimStatus(claim, "moved_out")}
                formatTime={formatFeedTimeAgo}
                styles={styles}
              />

              <div style={{ ...styles.propertyHistoryCard, marginTop: 12 }}>
                <div style={styles.profileTitleRowWrap}>
                  <strong style={styles.strongHeading}>Property Issues</strong>
                  <span style={styles.statusPillPending}>
                    {getOpenPropertyIssueCount(selectedProperty)} open issues reported
                  </span>
                </div>
                <p style={styles.smallText}>
                  Issue details are private. Tenants can see their own reports, and only the claimed landlord/property manager or admins can see and respond to property issue details.
                </p>

                {role === "tenant" && user?.uid ? (
                  <div style={{ ...styles.verificationBox, marginTop: 12 }}>
                    <strong style={styles.strongHeading}>Report an Issue</strong>
                    <p style={styles.smallText}>{LEGAL_GUIDANCE_DISCLAIMER}</p>
                    <label style={styles.label}>Issue Type</label>
                    <select
                      style={styles.input}
                      value={newIssueType}
                      onChange={(event) => setNewIssueType(event.target.value)}
                    >
                      <option value="maintenance">Maintenance</option>
                      <option value="safety">Safety</option>
                      <option value="noise">Noise</option>
                      <option value="deposit_payment">Deposit / Payment</option>
                      <option value="communication">Communication</option>
                      <option value="other">Other</option>
                    </select>
                    <label style={styles.label}>Issue Details</label>
                    <textarea
                      style={styles.textarea}
                      value={newIssueText}
                      onChange={(event) => setNewIssueText(event.target.value)}
                      placeholder="Describe the issue for this property. Do not include sensitive personal information."
                    />
                    <button type="button" style={{ ...styles.primaryButton, width: "100%" }} onClick={handleCreateIssue}>
                      Submit Issue
                    </button>
                  </div>
                ) : null}

                {getVisiblePropertyIssues(selectedProperty).length ? (
                  <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
                    {getVisiblePropertyIssues(selectedProperty).map((issue) => (
                      <div key={issue.id} style={styles.issueCard}>
                        <div style={styles.profileTitleRowWrap}>
                          <strong style={styles.strongHeading}>
                            {String(issue.issueType || "other").replace(/_/g, " ")}
                          </strong>
                          <span
                            style={
                              issue.status === "resolved"
                                ? styles.issueStatusResolved
                                : issue.status === "in_progress"
                                ? styles.issueStatusProgress
                                : styles.issueStatusOpen
                            }
                          >
                            {(issue.status || "open").replace(/_/g, " ")}
                          </span>
                        </div>
                        {canManagePropertyIssues(selectedProperty) ? (
                          <p style={styles.smallText}>Reported by: {issue.tenantDisplayName || issue.tenantName || "Tenant"}</p>
                        ) : null}
                        <p style={styles.messageBubbleText}>{issue.issueText || issue.description || ""}</p>
                        {issue.aiMaintenanceAnalysis ? (
                          <div style={styles.aiMaintenanceCard}>
                            <div style={styles.profileTitleRowWrap}>
                              <span style={styles.aiDraftEyebrow}>RentDuo AI Maintenance Summary</span>
                              <span style={styles.statusPill}>
                                {String(issue.aiMaintenanceAnalysis.severity || "moderate").replace(/_/g, " ")}
                              </span>
                            </div>
                            <p style={styles.smallText}>
                              Category: {String(issue.aiMaintenanceAnalysis.category || "other").replace(/_/g, " ")}
                              {issue.aiMaintenanceAnalysis.urgentSafetyRisk ? " | Potential urgent safety concern" : ""}
                            </p>
                            <p style={styles.smallText}>{issue.aiMaintenanceAnalysis.summaryForManager || ""}</p>
                            {issue.aiMaintenanceAnalysis.tenantNextSteps?.length ? (
                              <p style={styles.smallText}>
                                Suggested next steps: {issue.aiMaintenanceAnalysis.tenantNextSteps.join(" | ")}
                              </p>
                            ) : null}
                            <p style={styles.aiRiskDisclaimer}>{issue.aiMaintenanceAnalysis.disclaimer}</p>
                          </div>
                        ) : (
                          <button
                            type="button"
                            style={{ ...styles.secondaryButton, width: "100%", marginTop: 8 }}
                            onClick={() => handleAnalyzeMaintenanceIssue(issue)}
                            disabled={maintenanceAnalysisLoadingId === issue.id}
                          >
                            {maintenanceAnalysisLoadingId === issue.id ? "Analyzing Issue..." : "Analyze with RentDuo AI"}
                          </button>
                        )}
                        {issue.landlordResponse ? (
                          <div style={styles.reviewReplyCard}>
                            <div style={styles.reviewReplyHeader}>
                              <span style={styles.reviewReplyBadge}>Landlord / Manager Response</span>
                              <span style={styles.reviewReplyName}>{formatFeedTimeAgo(issue.landlordRespondedAt) || "Recently"}</span>
                            </div>
                            <div style={styles.reviewReplyText}>{issue.landlordResponse}</div>
                          </div>
                        ) : null}
                        {canManagePropertyIssues(selectedProperty) ? (
                          <div style={{ display: "grid", gap: 10 }}>
                            <textarea
                              style={styles.textarea}
                              value={issueResponseDrafts[issue.id] || ""}
                              onChange={(event) => setIssueResponseDrafts((prev) => ({ ...prev, [issue.id]: event.target.value }))}
                              placeholder="Respond privately to this property issue..."
                            />
                            <div style={styles.issueActionsRow}>
                              <button type="button" style={styles.secondaryButton} onClick={() => updateIssueStatus(issue.id, "in_progress")}>
                                In Progress
                              </button>
                              <button type="button" style={styles.primaryButton} onClick={() => handleRespondToIssue(issue)}>
                                Send Response
                              </button>
                              <button type="button" style={styles.secondaryGreenButton} onClick={() => updateIssueStatus(issue.id, "resolved")}>
                                Resolve
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={styles.smallText}>No private issues visible to you for this property.</p>
                )}
              </div>

              <div style={styles.propertyTrustStrip}>
                <div style={styles.propertyTrustCard}>
                  <strong>{averagePropertyRating ? averagePropertyRating.toFixed(1) : "0.0"}</strong>
                  <span>Verified rating</span>
                </div>
                <div style={styles.propertyTrustCard}>
                  <strong>{getPropertyRatingData(selectedProperty?.id).count}</strong>
                  <span>Total reviews</span>
                </div>
                <div style={styles.propertyTrustCard}>
                  <strong>
                    {getPropertyClaimState(selectedProperty).key === "claimable"
                      ? "Community"
                      : getPropertyClaimState(selectedProperty).label}
                  </strong>
                  <span>Trust status</span>
                </div>
              </div>

              <div style={styles.propertyHistoryCard}>
                <div style={styles.profileTitleRowWrap}>
                  <strong style={styles.strongHeading}>Property History</strong>
                  <span style={styles.communityReviewTag}>Live</span>
                </div>
                <div style={styles.propertyTimelineList}>
                  <div style={styles.propertyTimelineItem}>
                    <span>Added to RentDuo</span>
                    <strong>{formatFeedTimeAgo(selectedProperty?.createdAt || selectedProperty?.updatedAt) || "Recently"}</strong>
                  </div>
                  <div style={styles.propertyTimelineItem}>
                    <span>Claim status</span>
                    <strong>
                      {getPropertyClaimState(selectedProperty).key === "claimable"
                        ? "Open community property"
                        : getPropertyClaimState(selectedProperty).label}
                    </strong>
                  </div>
                  <div style={styles.propertyTimelineItem}>
                    <span>Maintenance responsiveness</span>
                    <strong>Coming soon</strong>
                  </div>
                </div>
              </div>

              <div style={styles.aiRiskCard}>
                <div style={styles.profileTitleRowWrap}>
                  <div>
                    <div style={styles.aiDraftEyebrow}>RentDuo AI</div>
                    <strong style={styles.strongHeading}>Reputation Timeline</strong>
                  </div>
                  {selectedProperty?.aiReputationTimeline?.trend ? (
                    <span style={styles.communityReviewTag}>
                      {String(selectedProperty.aiReputationTimeline.trend).replace("_", " ")}
                    </span>
                  ) : null}
                </div>
                {selectedProperty?.aiReputationTimeline ? (
                  <>
                    <p style={styles.smallText}>{selectedProperty.aiReputationTimeline.summary}</p>
                    {selectedProperty.aiReputationTimeline.commonPraise?.length ? (
                      <p style={styles.smallText}>Common praise: {selectedProperty.aiReputationTimeline.commonPraise.join(" | ")}</p>
                    ) : null}
                    {selectedProperty.aiReputationTimeline.commonComplaints?.length ? (
                      <p style={styles.smallText}>Common concerns: {selectedProperty.aiReputationTimeline.commonComplaints.join(" | ")}</p>
                    ) : null}
                    {selectedProperty.aiReputationTimeline.maintenanceTrends ? (
                      <p style={styles.smallText}>Maintenance: {selectedProperty.aiReputationTimeline.maintenanceTrends}</p>
                    ) : null}
                    {selectedProperty.aiReputationTimeline.responsivenessTrends ? (
                      <p style={styles.smallText}>Responsiveness: {selectedProperty.aiReputationTimeline.responsivenessTrends}</p>
                    ) : null}
                  </>
                ) : (
                  <p style={styles.smallText}>Summarize visible property trends over time as activity accumulates.</p>
                )}
                <p style={styles.aiRiskDisclaimer}>
                  AI summary of available RentDuo activity only. Patterns are not proof of any claim.
                </p>
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, width: "100%", marginTop: 10 }}
                  onClick={handleAnalyzePropertyReputation}
                  disabled={propertyReputationLoadingId === selectedProperty.id}
                >
                  {propertyReputationLoadingId === selectedProperty.id
                    ? "Summarizing Timeline..."
                    : selectedProperty?.aiReputationTimeline
                    ? "Refresh Timeline Summary"
                    : "Summarize Reputation Timeline"}
                </button>
              </div>

              <div style={styles.aiRiskCard}>
                <div style={styles.aiDraftEyebrow}>RentDuo AI</div>
                <strong style={styles.strongHeading}>Lease Explainer</strong>
                <p style={styles.smallText}>
                  Paste lease language for a plain-English educational summary. RentDuo does not save this pasted text here.
                </p>
                <textarea
                  style={{ ...styles.textarea, minHeight: 110 }}
                  value={leaseText}
                  onChange={(event) => setLeaseText(event.target.value)}
                  placeholder="Paste a lease section about fees, deposits, renewal, notice, or termination..."
                  maxLength={12000}
                />
                {leaseError ? <p style={styles.safeReviewText}>{leaseError}</p> : null}
                {leaseExplanation ? (
                  <div style={{ ...styles.aiMaintenanceCard, marginTop: 10 }}>
                    <p style={styles.smallText}>{leaseExplanation.plainSummary}</p>
                    {leaseExplanation.clausesToReview?.length ? (
                      <p style={styles.smallText}>Review closely: {leaseExplanation.clausesToReview.join(" | ")}</p>
                    ) : null}
                    {leaseExplanation.feesAndDeposits?.length ? (
                      <p style={styles.smallText}>Fees and deposits: {leaseExplanation.feesAndDeposits.join(" | ")}</p>
                    ) : null}
                    {leaseExplanation.renewalAndTermination?.length ? (
                      <p style={styles.smallText}>Renewal and termination: {leaseExplanation.renewalAndTermination.join(" | ")}</p>
                    ) : null}
                    {leaseExplanation.questionsToAsk?.length ? (
                      <p style={styles.smallText}>Questions to ask: {leaseExplanation.questionsToAsk.join(" | ")}</p>
                    ) : null}
                    <p style={styles.aiRiskDisclaimer}>{leaseExplanation.disclaimer}</p>
                  </div>
                ) : null}
                <p style={styles.aiRiskDisclaimer}>
                  General educational guidance only, not legal advice. Laws and lease terms vary; consult a qualified local professional when needed.
                </p>
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, width: "100%", marginTop: 10 }}
                  onClick={handleExplainLease}
                  disabled={leaseExplaining}
                >
                  {leaseExplaining ? "Explaining Lease..." : "Explain Lease Language"}
                </button>
              </div>

              <div style={styles.aiRiskCard}>
                <div style={styles.profileTitleRowWrap}>
                  <div>
                    <div style={styles.aiDraftEyebrow}>RentDuo AI</div>
                    <strong style={styles.strongHeading}>Neighborhood Intelligence</strong>
                  </div>
                </div>
                {selectedProperty?.aiNeighborhoodIntelligence ? (
                  <>
                    <p style={styles.smallText}>{selectedProperty.aiNeighborhoodIntelligence.summary}</p>
                    {selectedProperty.aiNeighborhoodIntelligence.pros?.length ? (
                      <p style={styles.smallText}>Potential positives: {selectedProperty.aiNeighborhoodIntelligence.pros.join(" | ")}</p>
                    ) : null}
                    {selectedProperty.aiNeighborhoodIntelligence.considerations?.length ? (
                      <p style={styles.smallText}>Considerations: {selectedProperty.aiNeighborhoodIntelligence.considerations.join(" | ")}</p>
                    ) : null}
                    <div style={styles.aiRiskCategoryGrid}>
                      {[
                        ["Parking", selectedProperty.aiNeighborhoodIntelligence.parking],
                        ["Noise", selectedProperty.aiNeighborhoodIntelligence.noise],
                        ["Transit", selectedProperty.aiNeighborhoodIntelligence.transit],
                        ["Renter fit", selectedProperty.aiNeighborhoodIntelligence.renterFit],
                      ].filter(([, value]) => value).map(([label, value]) => (
                        <div key={label} style={{ ...styles.aiRiskCategoryCard, display: "block" }}>
                          <strong>{label}</strong>
                          <p style={{ ...styles.smallText, margin: "6px 0 0" }}>{value}</p>
                        </div>
                      ))}
                    </div>
                    <p style={styles.aiRiskDisclaimer}>{selectedProperty.aiNeighborhoodIntelligence.disclaimer}</p>
                  </>
                ) : (
                  <p style={styles.smallText}>Summarize available RentDuo observations about parking, noise, commute considerations, and renter fit.</p>
                )}
                {neighborhoodError ? <p style={styles.safeReviewText}>{neighborhoodError}</p> : null}
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, width: "100%", marginTop: 10 }}
                  onClick={handleAnalyzeNeighborhood}
                  disabled={neighborhoodLoadingId === selectedProperty.id}
                >
                  {neighborhoodLoadingId === selectedProperty.id
                    ? "Analyzing Neighborhood..."
                    : selectedProperty?.aiNeighborhoodIntelligence
                    ? "Refresh Neighborhood Insight"
                    : "Generate Neighborhood Insight"}
                </button>
              </div>

              <div style={styles.aiRiskCard}>
                <div style={styles.aiDraftEyebrow}>RentDuo AI</div>
                <strong style={styles.strongHeading}>Market Rent Estimate</strong>
                {selectedProperty?.aiMarketRentEstimate ? (
                  <>
                    <div style={styles.profileTitleRowWrap}>
                      <span style={styles.communityReviewTag}>
                        {selectedProperty.aiMarketRentEstimate.comparison === "insufficient_data"
                          ? "Not enough data"
                          : String(selectedProperty.aiMarketRentEstimate.comparison || "").replace(/_/g, " ")}
                      </span>
                      {selectedProperty.aiMarketRentEstimate.medianComparableRent ? (
                        <strong style={styles.strongHeading}>
                          Median comparison: {formatMonthlyRent(selectedProperty.aiMarketRentEstimate.medianComparableRent)}
                        </strong>
                      ) : null}
                    </div>
                    <p style={styles.smallText}>{selectedProperty.aiMarketRentEstimate.summary}</p>
                    <p style={styles.smallText}>
                      Comparable RentDuo listings: {Number(selectedProperty.aiMarketRentEstimate.comparableCount || 0)}
                    </p>
                    {selectedProperty.aiMarketRentEstimate.factors?.length ? (
                      <p style={styles.smallText}>Factors: {selectedProperty.aiMarketRentEstimate.factors.join(" | ")}</p>
                    ) : null}
                    <p style={styles.aiRiskDisclaimer}>{selectedProperty.aiMarketRentEstimate.disclaimer}</p>
                  </>
                ) : (
                  <p style={styles.smallText}>Compare an available listed rent against similar RentDuo rental listings when enough local data exists.</p>
                )}
                {marketRentError ? <p style={styles.safeReviewText}>{marketRentError}</p> : null}
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, width: "100%", marginTop: 10 }}
                  onClick={handleEstimateMarketRent}
                  disabled={marketRentLoadingId === selectedProperty.id}
                >
                  {marketRentLoadingId === selectedProperty.id
                    ? "Comparing Rent..."
                    : selectedProperty?.aiMarketRentEstimate
                    ? "Refresh Rent Comparison"
                    : "Estimate Market Rent"}
                </button>
              </div>

              <div style={styles.aiRiskCard}>
                <div style={styles.aiDraftEyebrow}>RentDuo AI</div>
                <strong style={styles.strongHeading}>Move Assistant</strong>
                <div style={styles.messageFolderTabs}>
                  <button
                    type="button"
                    style={moveChecklistMode === "move_in" ? styles.messageFolderTabActive : styles.messageFolderTab}
                    onClick={() => setMoveChecklistMode("move_in")}
                  >
                    Move In
                  </button>
                  <button
                    type="button"
                    style={moveChecklistMode === "move_out" ? styles.messageFolderTabActive : styles.messageFolderTab}
                    onClick={() => setMoveChecklistMode("move_out")}
                  >
                    Move Out
                  </button>
                </div>
                {moveChecklist?.items?.length ? (
                  <div style={{ display: "grid", gap: 9 }}>
                    {moveChecklist.items.map((item) => (
                      <label key={item.id} style={{ ...styles.smallText, display: "flex", alignItems: "flex-start", gap: 9, margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={Boolean(item.completed)}
                          onChange={() => handleToggleMoveChecklistItem(item.id)}
                        />
                        <span style={{ textDecoration: item.completed ? "line-through" : "none" }}>{item.label}</span>
                      </label>
                    ))}
                    <p style={styles.aiRiskDisclaimer}>{moveChecklist.disclaimer}</p>
                  </div>
                ) : (
                  <p style={styles.smallText}>Create a private checklist for photos, documents, utilities, deposit records, keys, and walkthrough reminders.</p>
                )}
                {moveChecklistError ? <p style={styles.safeReviewText}>{moveChecklistError}</p> : null}
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, width: "100%", marginTop: 10 }}
                  onClick={handleGenerateMoveChecklist}
                  disabled={moveChecklistLoading}
                >
                  {moveChecklistLoading ? "Creating Checklist..." : moveChecklist ? "Reset Checklist" : "Create Checklist"}
                </button>
              </div>

              <div style={styles.aiRiskCard}>
                <div style={styles.aiDraftEyebrow}>RentDuo AI</div>
                <strong style={styles.strongHeading}>Smart Matching</strong>
                <p style={styles.smallText}>Find listed rentals that may fit your preferences as availability grows.</p>
                <div style={{ display: "grid", gridTemplateColumns: isPhoneMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                  <input
                    style={styles.input}
                    inputMode="numeric"
                    placeholder="Monthly budget"
                    value={smartMatchPreferences.budget}
                    onChange={(event) => setSmartMatchPreferences((prev) => ({ ...prev, budget: event.target.value }))}
                  />
                  <input
                    style={styles.input}
                    placeholder="Preferred city"
                    value={smartMatchPreferences.preferredCity}
                    onChange={(event) => setSmartMatchPreferences((prev) => ({ ...prev, preferredCity: event.target.value }))}
                  />
                  <select
                    style={styles.input}
                    value={smartMatchPreferences.riskTolerance}
                    onChange={(event) => setSmartMatchPreferences((prev) => ({ ...prev, riskTolerance: event.target.value }))}
                  >
                    <option value="low">Prefer lower risk</option>
                    <option value="moderate">Moderate risk tolerance</option>
                    <option value="flexible">Flexible</option>
                  </select>
                  <div style={{ display: "grid", gap: 7 }}>
                    <label style={{ ...styles.smallText, display: "flex", gap: 7, alignItems: "center", margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={smartMatchPreferences.quietPreferred}
                        onChange={(event) => setSmartMatchPreferences((prev) => ({ ...prev, quietPreferred: event.target.checked }))}
                      />
                      Prefer quiet
                    </label>
                    <label style={{ ...styles.smallText, display: "flex", gap: 7, alignItems: "center", margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={smartMatchPreferences.petsRequired}
                        onChange={(event) => setSmartMatchPreferences((prev) => ({ ...prev, petsRequired: event.target.checked }))}
                      />
                      Pets needed
                    </label>
                  </div>
                </div>
                {smartMatches.length ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    {smartMatches.map((match) => (
                      <button
                        key={match.id}
                        type="button"
                        style={{ ...styles.publicProfilePropertyButton, width: "100%" }}
                        onClick={() => handleSelectProperty(properties.find((property) => property.id === match.id) || match)}
                      >
                        <strong>{match.title || match.address}</strong>
                        <span>{formatMonthlyRent(match.monthlyRent)} | {match.matchScore}% fit | {match.riskLevel} risk</span>
                        <span>{match.reasons?.join(" | ") || "Review details before deciding"}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
                {smartMatchError ? <p style={styles.safeReviewText}>{smartMatchError}</p> : null}
                {smartMatchDisclaimer ? <p style={styles.aiRiskDisclaimer}>{smartMatchDisclaimer}</p> : null}
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, width: "100%", marginTop: 10 }}
                  onClick={handleFindSmartMatches}
                  disabled={smartMatchLoading}
                >
                  {smartMatchLoading ? "Finding Matches..." : "Find Recommended Rentals"}
                </button>
              </div>

              <RightsResources
                stateCode={getRightsStateCode(selectedProperty?.state, selectedProperty?.fullAddress, selectedProperty?.address, profile?.state, profile?.city)}
                defaultRole={role || profile?.role || ""}
                location={profile?.location || profileLocation}
                title={language === "es" ? "Conozca sus derechos" : "Know Your Rights"}
                language={language}
              />

              <div style={styles.propertyProfileActionRow}>
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, width: "100%", marginTop: 0 }}
                  onClick={() => {
                    if (selectedProperty?.ownerId) {
                      openPublicProfile({ uid: selectedProperty.ownerId, displayName: selectedProperty.ownerDisplayName || "Property contact" });
                    } else {
                      setStatusMessage("No public owner or manager profile is linked to this property yet.");
                    }
                  }}
                >
                  Contact / View Owner
                </button>
                <button
                  type="button"
                  style={{ ...styles.rejectButton, width: "100%", marginTop: 0 }}
                  onClick={() => setStatusMessage("Property report tools are coming soon. For now, report unsafe review content directly from the review card.")}
                >
                  {language === "es" ? "Reportar propiedad" : "Report Property"}
                </button>
              </div>

              <div style={styles.propertyProfileActionRow}>
                <button
                  type="button"
                  style={{
                    ...styles.secondaryButton,
                    width: "100%",
                    marginTop: 0,
                    ...(isFavoriteProperty(selectedProperty?.id) ? styles.secondaryButtonActive : {}),
                  }}
                  onClick={() => toggleFavoriteProperty(selectedProperty?.id)}
                >
                  {isFavoriteProperty(selectedProperty?.id) ? "♥ Saved Property" : "♡ Save Property"}
                </button>
              </div>

              <div style={styles.profileStatGrid}>
                <div style={styles.profileStatCard}>
                  <div style={styles.profileStatLabel}>Address</div>
                  <div style={styles.profileStatValueSmall}>{selectedProperty?.fullAddress || selectedProperty?.address || "Not available"}</div>
                </div>
                <div style={styles.profileStatCard}>
                  <div style={styles.profileStatLabel}>Reviews</div>
                  <div style={styles.profileStatValue}>{getPropertyRatingData(selectedProperty?.id).count}</div>
                </div>
                <div style={styles.profileStatCard}>
                  <div style={styles.profileStatLabel}>Community</div>
                  <div style={styles.profileStatValue}>{getPropertyRatingData(selectedProperty?.id).communityCount}</div>
                </div>
              </div>

              {renderPartnerServiceCards("property_profile")}

              <ClaimPropertyButton
                claimState={getPropertyClaimState(selectedProperty)}
                canRoleClaim={canRoleClaimProperty(role, verificationStatus)}
                styles={styles}
                badgeStyle={getPropertyClaimBadgeStyle(selectedProperty)}
                onClaim={() => {
                    if (!canRoleClaimProperty(role, verificationStatus)) {
                      setStatusMessage("Only landlord and property manager accounts can claim a property.");
                      return;
                    }
                    setPropertyClaimTarget(selectedProperty);
                    openClaimVerificationModal(selectedProperty);
                }}
              />

              <div style={{ marginTop: 16 }}>
                <p style={styles.smallText}>
                  Selected lat: {String(selectedProperty?.lat)} | lng:{" "}
                  {String(selectedProperty?.lng)}
                </p>

                {mapLoadError ? (
                  <p style={styles.smallText}>Google Maps failed to load.</p>
                ) : null}
                {!isMapLoaded && !mapLoadError ? (
                  <p style={styles.smallText}>Loading Google Map...</p>
                ) : null}
                {selectedProperty?.lat == null ||
                selectedProperty?.lng == null ? (
                  <p style={styles.smallText}>
                    This property does not have coordinates yet.
                  </p>
                ) : null}

                {isMapLoaded &&
                selectedProperty?.lat != null &&
                selectedProperty?.lng != null ? (
                  <GoogleMap
                    onLoad={handleGoogleMapLoad}
                    zoom={15}
                    center={{
                      lat: Number(selectedProperty.lat),
                      lng: Number(selectedProperty.lng),
                    }}
                    options={mobileCleanMapOptions}
                    mapContainerStyle={{
                      width: "100%",
                      height: isMobile ? "250px" : "320px",
                      borderRadius: "18px",
                      overflow: "hidden",
                      boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
                      border: "1px solid rgba(207, 226, 255, 0.9)",
                    }}
                  >
                    <Marker
                      position={{
                        lat: Number(selectedProperty.lat),
                        lng: Number(selectedProperty.lng),
                      }}
                      icon={buildPropertyMarkerIcon(selectedProperty, true)}
                    />
                  </GoogleMap>
                ) : null}
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              <h3 style={{ ...styles.subsectionHeading, fontSize: isMobile ? 18 : styles.subsectionHeading.fontSize }}>{language === "es" ? "Escribir una reseña de propiedad" : "Write a Property Review"}</h3>
              <p style={styles.smallText}>
                Your review will still post even if you are unverified. It will be labeled as verified or unverified automatically.
              </p>

              <RightsResources
                stateCode={getRightsStateCode(selectedProperty?.state, selectedProperty?.fullAddress, selectedProperty?.address, profile?.state, profile?.city)}
                defaultRole={role || profile?.role || ""}
                location={profile?.location || profileLocation}
                title={language === "es" ? "Antes de publicar" : "Before You Post"}
                language={language}
              />

              {renderReviewLegalAwarenessCard("property review")}

              {renderStars(
                propertyRatingValue,
                true,
                setPropertyRatingValue,
                30
              )}

              {!isCurrentUserVerified ? (
                <div style={styles.communityRatingBanner}>
                  <span style={styles.communityRatingLabel}>Unverified account</span>
                  <span style={styles.communityRatingValue}>
                    You can leave a star-only community rating here. Verify your account to post a full written property review.
                  </span>
                </div>
              ) : null}

              <textarea
                style={{
                  ...styles.textarea,
                  marginTop: 14,
                  opacity: isCurrentUserVerified ? 1 : 0.6,
                  background: isCurrentUserVerified ? styles.textarea.background : "#f8fafc",
                }}
                value={propertyReviewText}
                onChange={(e) => setPropertyReviewText(e.target.value)}
                placeholder={
                  isCurrentUserVerified
                    ? "Write your property review..."
                    : "Verify your account to unlock written property reviews"
                }
                disabled={!isCurrentUserVerified}
              />

              {isCurrentUserVerified ? (
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, width: isMobile ? "100%" : "auto", marginTop: 10 }}
                  onClick={handleMakePropertyReviewSafer}
                  disabled={propertyReviewImproving || !String(propertyReviewText || livingExperience.notes || "").trim()}
                >
                  {propertyReviewImproving ? "Making Safer..." : "Make this review safer"}
                </button>
              ) : null}

              <div style={styles.livingExperienceCard}>
                <div style={styles.livingExperienceHeader}>
                  <div style={styles.sectionTitle}>Living Experience</div>
                  <div style={styles.sectionSubtitle}>
                    Help others understand what daily life is really like at this property.
                  </div>
                </div>

                <div style={styles.livingExperienceGrid}>
                  {LIVING_EXPERIENCE_FIELDS.map((field) => (
                    <div key={field.key} style={styles.livingExperienceRow}>
                      <div style={styles.livingExperienceLabel}>
                        <span>{field.label}</span>
                      </div>
                      {renderStars(
                        Number(livingExperience[field.key] || 0),
                        true,
                        (value) => updateLivingExperience(field.key, value),
                        22
                      )}
                    </div>
                  ))}
                </div>

                <label style={styles.label}>What’s the day-to-day living experience here?</label>
                <textarea
                  style={{
                    ...styles.textarea,
                    marginTop: 8,
                    opacity: isCurrentUserVerified ? 1 : 0.75,
                    background: isCurrentUserVerified ? styles.textarea.background : "#f8fafc",
                  }}
                  value={livingExperience.notes}
                  onChange={(e) => updateLivingExperience("notes", e.target.value)}
                  placeholder="Share what it’s actually like to live here — noise, neighbors, safety, parking, and shared spaces. Avoid naming specific people."
                  disabled={!isCurrentUserVerified}
                  rows={4}
                />

                <div style={styles.livingExperienceWarning}>
                  Do not identify individual neighbors or include names, unit numbers, threats, or personal attacks.
                  Keep feedback about the overall living experience.
                </div>
              </div>

              <button
                style={{ ...styles.primaryButton, width: isMobile ? "100%" : "auto", marginBottom: isMobile ? 20 : 32 }}
                onClick={handleSubmitPropertyReview}
              >
                {isCurrentUserVerified ? "Post Property Review" : "Post Community Rating"}
              </button>

              <div style={{ marginTop: 6 }}>
                <h3 style={{ ...styles.subsectionHeading, fontSize: isMobile ? 18 : styles.subsectionHeading.fontSize }}>
                  Property Reviews
                </h3>

                {propertyLivingExperienceSummary ? (
                  <div style={styles.livingSummaryCard}>
                    <div style={styles.livingSummaryTop}>
                      <div>
                        <div style={styles.sectionTitle}>Living Experience</div>
                        <div style={styles.sectionSubtitle}>
                          Based on {propertyLivingExperienceSummary.count} review{propertyLivingExperienceSummary.count === 1 ? "" : "s"}
                        </div>
                      </div>
                      <div style={styles.livingSummaryBadge}>{propertyLivingExperienceSummary.tag}</div>
                    </div>

                    <div style={styles.livingSummaryGrid}>
                      <div style={styles.livingSummaryItem}><span>🔊 Noise Level</span><strong>{propertyLivingExperienceSummary.noiseLevel.toFixed(1)}</strong></div>
                      <div style={styles.livingSummaryItem}><span>🤝 Neighbor Behavior</span><strong>{propertyLivingExperienceSummary.neighborBehavior.toFixed(1)}</strong></div>
                      <div style={styles.livingSummaryItem}><span>🛡️ Safety</span><strong>{propertyLivingExperienceSummary.safety.toFixed(1)}</strong></div>
                      <div style={styles.livingSummaryItem}><span>🏢 Building Culture</span><strong>{propertyLivingExperienceSummary.buildingCulture.toFixed(1)}</strong></div>
                      <div style={styles.livingSummaryItem}><span>🚗 Parking</span><strong>{propertyLivingExperienceSummary.parking.toFixed(1)}</strong></div>
                      <div style={styles.livingSummaryItem}><span>🛋️ Shared Spaces</span><strong>{propertyLivingExperienceSummary.sharedSpaces.toFixed(1)}</strong></div>
                    </div>
                  </div>
                ) : null}

                {propertyReviews.length === 0 ? (
                  <div style={styles.communityRatingNotice}>
                    <strong>No property reviews yet.</strong>
                    <p style={styles.smallText}>
                      Lived here? Be the first to review.
                    </p>
                  </div>
                ) : (
                  <div style={styles.scrollableInnerList}>
                    {[...propertyReviews]
                      .sort((a, b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0))
                      .map((item) => (
                        <div
                          key={item.id}
                          style={{
                            ...styles.reviewCard,
                            border: item?.reviewerVerified ? "1px solid #86efac" : "1px solid #fed7aa",
                            background: item?.reviewerVerified ? "#f0fdf4" : "#fff7ed",
                          }}
                        >
                          <div style={styles.userHeader}>
                            <Avatar
                              src={item.reviewerPhotoURL}
                              name={item.reviewerDisplayName || item.reviewerEmail}
                              size={44}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={styles.profileTitleRowWrap}>
                                <PublicProfileNameButton
                                  userData={{
                                    uid: item.reviewerUserId,
                                    displayName: item.reviewerDisplayName || "Anonymous",
                                    photoURL: item.reviewerPhotoURL,
                                    verified: item.reviewerVerified,
                                  }}
                                  style={styles.strongHeading}
                                >
                                  {item.reviewerDisplayName || "Anonymous"}
                                </PublicProfileNameButton>
                                {item.reviewerVerified ? <VerifiedBadge /> : null}
                              </div>
                              <div style={styles.smallText}>
                                {item.reviewerVerified ? "Verified Reviewer" : "Unverified Reviewer"}
                              </div>
                            </div>
                          </div>

                          <div style={{ margin: "10px 0" }}>
                            {renderStars(Number(item.rating || 0), false, null, 22)}
                          </div>

                          {item.isUpdatedReview ? (
                            <p style={styles.communityReviewTag}>Updated Review</p>
                          ) : null}

                          {item.text ? (
                            <>
                              <TranslatableText text={item.text} label="Review" bodyStyle={styles.bodyCopy} compact />
                              <div style={styles.reviewTimestamp}>
                                {item?.createdAt?.seconds
                                  ? `${formatReviewDateTime(item.createdAt)}`
                                  : "Recent review"}
                              </div>
                            </>
                          ) : (
                            <div style={styles.reviewTimestamp}>
                              {item?.createdAt?.seconds
                                ? `${formatReviewDateTime(item.createdAt)}`
                                : "Recent review"}
                            </div>
                          )}

                          {item.livingExperience ? (
                            <div style={styles.reviewLivingBlock}>
                              <div style={styles.reviewLivingTitle}>Living Experience</div>

                              <div style={styles.reviewLivingMiniGrid}>
                                {[
                                  ["🔊 Noise Level", item?.livingExperience?.noiseLevel],
                                  ["🤝 Neighbor Behavior", item?.livingExperience?.neighborBehavior],
                                  ["🛡️ Safety", item?.livingExperience?.safety],
                                  ["🏢 Building Culture", item?.livingExperience?.buildingCulture],
                                  ["🚗 Parking", item?.livingExperience?.parking],
                                  ["🛋️ Shared Spaces", item?.livingExperience?.sharedSpaces],
                                ]
                                  .filter(([, value]) => Number(value || 0) > 0)
                                  .map(([label, value]) => (
                                    <div key={label} style={styles.reviewLivingMiniItem}>
                                      <span>{label}</span>
                                      <strong>{Number(value).toFixed(1)}</strong>
                                    </div>
                                  ))}
                              </div>

                              {item?.livingExperience?.notes ? (
                                <div style={styles.reviewLivingNotes}>
                                  {item.livingExperience.notes}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p style={styles.smallText}>No property selected yet.</p>
        )}
        </div>
      </div>
      ) : null}


      {showVerifyPrompt ? (
        <div style={styles.modalOverlay} onClick={() => setShowVerifyPrompt(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <p style={styles.badgePill}>Make your voice count</p>
            <h3 style={styles.modalTitle}>Verify now so your review affects ratings</h3>
            <p style={styles.modalText}>
              Your review will be visible, but it won’t impact landlord, tenant, or property ratings until your account is verified.
            </p>
            <div style={styles.modalBulletList}>
              <div style={styles.modalBulletItem}>✅ Make future reviews count</div>
              <div style={styles.modalBulletItem}>🏆 Earn a verified badge</div>
              <div style={styles.modalBulletItem}>🔒 Build more trust on RentDuo</div>
            </div>
            <div style={styles.modalActionRow}>
              <button
                style={{ ...styles.primaryButton, marginTop: 0, flex: 1 }}
                onClick={() => {
                  setShowVerifyPrompt(false);
                  setActiveTab("verification");
                }}
              >
                Verify Now
              </button>
              <button
                style={{ ...styles.secondaryButton, marginTop: 0, flex: 1 }}
                onClick={() => setShowVerifyPrompt(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showClaimVerificationModal ? (
        <div
          style={styles.mobileModalOverlay}
          onClick={closeClaimVerificationModal}
        >
          <div
            style={{
              ...styles.mobileModalCard,
              maxWidth: 560,
              alignSelf: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.mobileModalHeaderRow}>
              <h3 style={{ ...styles.subsectionHeading, margin: 0 }}>
                Claim Verification
              </h3>
              <button
                style={styles.mobileModalCloseButton}
                onClick={closeClaimVerificationModal}
              >
                Close
              </button>
            </div>

            <p style={styles.smallText}>
              Submit your verification to claim this property. This does not show your private address to the public.
            </p>

            {propertyClaimTarget ? (
              <div style={styles.mapSelectionBox}>
                <strong style={styles.strongHeading}>Property To Claim</strong>
                <p style={styles.smallText}>
                  {propertyClaimTarget.title || "Property"} — {propertyClaimTarget.address || "No address"}
                </p>
                <p style={styles.smallText}>
                  Once approved by admin, this property will show as claimed by the verified landlord or property manager.
                </p>
              </div>
            ) : null}

          <div style={styles.optionalVerificationNotice}>
            Verification is optional. You can still use RentDuo without verifying, but verified users and claimed properties build more trust.
          </div>

            <label style={styles.label}>Verification Type</label>
            <select style={styles.input} value={selectedVerificationType} onChange={(e) => setVerificationType(e.target.value)}>
              <option value="tenant_verification">Tenant verification</option>
              <option value="landlord_verification">Landlord verification</option>
              <option value="property_manager_verification">Property manager verification</option>
              <option value="property_ownership_verification">Property ownership verification</option>
            </select>
            <p style={styles.smallText}>Selected verification type: {selectedVerificationType}</p>

          <label style={styles.label}>Private Address For Verification</label>
            <input
              ref={privateAddressInputRef}
              style={styles.input}
              value={privateAddress}
              onChange={(e) => setPrivateAddress(e.target.value)}
              placeholder="Used only for verification. Hidden from everyone else."
              autoComplete="off"
            />

            <div style={styles.formGrid}>
              {getVerificationUploadFields(selectedVerificationType).map((field) => (
                <div key={`claim-${selectedVerificationType}-${field.key}`}>
                  <label style={styles.label}>{field.label}</label>
                  <input
                    style={styles.input}
                    type="file"
                    accept=".pdf,image/*"
                    onChange={handleVerificationAuxFileChange(field.setter)}
                  />
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
              <button
                style={{ ...styles.primaryButton, width: "100%" }}
                onClick={handleSubmitVerification}
              >
                Submit Claim Verification
              </button>
              <button
                style={{ ...styles.secondaryButton, width: "100%" }}
                onClick={closeClaimVerificationModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
  };

  const renderSearchResultsCards = (mode = "reviews") => {
    if (!searchResults.length) return null;

    return (
      <div style={styles.searchResultsList}>
        {searchResults.map((result) => {
          const trustScore = Number(result?.rentScore || result?.trustScore || 0);
          const rating = Number(result?.averageRating || result?.ratingAverage || 0);

          return (
            <div key={result.uid || result.id || result.email} style={styles.searchResultCard}>
              <div style={styles.searchResultTopRow}>
                <div style={styles.userHeader}>
                  <Avatar
                    src={result.photoURL}
                    name={result.displayName || result.email}
                    size={48}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.profileTitleRowWrap}>
                      <h3 style={{ ...styles.cardPersonName, marginBottom: 2 }}>
                        {result.displayName || "Unnamed User"}
                      </h3>
                      <VerificationStatusBadge status={getEffectiveVerificationStatus(result)} />
                      {result.statusVisibility !== false && result.profileStatus ? (
                        <span style={{ ...styles.statusChip, ...getProfileStatusMeta(result.profileStatus) }}>
                          {getProfileStatusMeta(result.profileStatus).label}
                        </span>
                      ) : null}
                    </div>
                    <p style={styles.smallText}>
                      {(result.role || "Role not selected").replace(/_/g, " ")} • {result.city || "No city"}
                    </p>
                  </div>
                </div>
                <div style={styles.searchScoreGroup}>
                  <div style={styles.searchScorePill}>
                    {trustScore ? `Rent Score ${trustScore}` : `${rating ? rating.toFixed(1) : "0.0"}★`}
                  </div>
                  <div style={styles.searchMiniStars}>{renderStars(Math.round(rating || 0))}</div>
                </div>
              </div>

              <div style={styles.searchResultMetaLine}>
                {getPublicStatusText(result)}
              </div>

              {getPublicAreaSummary(result) ? (
                <div style={styles.searchResultMetaLine}>
                  {result.role === "tenant" ? "Preferred areas" : "Service areas"}: {getPublicAreaSummary(result)}
                </div>
              ) : null}

              <div style={styles.searchResultActions}>
                <button
                  style={styles.secondaryButton}
                  onClick={() => openPublicProfile(result)}
                >
                  Open Profile
                </button>
                {mode === "messages" ? (
                  <button
                    style={styles.primaryButton}
                    onClick={() => openConversationWithUser(result)}
                  >
                    Message
                  </button>
                ) : (
                  <button
                    style={styles.primaryButton}
                    onClick={() => {
                      openPublicProfile(result);
                      selectReviewUser(result);
                    }}
                  >
                    View / Review
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const MessagesTab = () => {
    const activeThreads =
      messageFolderTab === "inbox" ? inboxThreads : outboxThreads;

    return (
      <div style={{ ...styles.grid, gridTemplateColumns: isMobile ? "1fr" : styles.grid.gridTemplateColumns, gap: isMobile ? 16 : styles.grid.gap }}>
        <div style={styles.card}>
          <SectionTitle
            title="Find User"
            subtitle="Search another profile by name, email, city, property address, area, or live status and open reviews or messages."
          />

          <div style={styles.filterGrid}>
            <div>
              <label style={styles.label}>Area</label>
              <input
                ref={reviewAreaInputRef}
                className="review-search-input"
                style={styles.input}
                type="text"
                name="review-area-search"
                id="review-area-search"
                value={searchArea}
                onChange={(e) => setSearchArea(e.target.value)}
                placeholder="Start typing city or state"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                aria-autocomplete="none"
              />
            </div>
            <div>
              <label style={styles.label}>Role</label>
              <select style={styles.input} value={searchRoleFilter} onChange={(e) => setSearchRoleFilter(e.target.value)}>
                <option value="all">All Roles</option>
                <option value="tenant">Tenant</option>
                <option value="landlord">Landlord</option>
                <option value="property_manager">Property Manager</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Status</label>
              <select style={styles.input} value={searchStatusFilter} onChange={(e) => setSearchStatusFilter(e.target.value)}>
                <option value="all">Any Status</option>
                <option value="actively_looking">Actively Looking</option>
                <option value="browsing">Browsing</option>
                <option value="already_renting">Already Renting</option>
                <option value="not_looking">Not Looking</option>
                <option value="units_available">Units Available</option>
                <option value="leasing_soon">Leasing Soon</option>
                <option value="fully_occupied">Fully Occupied</option>
                <option value="not_accepting">Not Accepting Applications</option>
              </select>
            </div>
          </div>

          <label style={styles.label}>
            Search by name, email, city, or property address
          </label>
          <input
            ref={reviewUserSearchInputRef}
            className="review-search-input"
            style={styles.input}
            type="text"
            name="review-user-search"
            id="review-user-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Start typing property address, city, or area"
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            aria-autocomplete="none"
          />

          <button style={styles.primaryButton} onClick={handleFindUser}>
            Search / Auto-Fill
          </button>

          {!!searchMessage && <p style={styles.message}>{searchMessage}</p>}
          {renderSearchResultsCards("messages")}

          {foundUser ? (
            <div style={styles.userCard}>
              <div style={styles.userHeader}>
                <Avatar
                  src={foundUser.photoURL}
                  name={foundUser.displayName || foundUser.email}
                  size={56}
                />
                <div>
                  <h3 style={styles.cardPersonName}>
                    {foundUser.displayName || "Unnamed User"}
                  </h3>
                  <p style={styles.smallText}>{getRoleLabel(foundUser.role) || "RentDuo member"}</p>
                </div>
              </div>

              <div style={styles.profileTitleRowWrap}>
                <VerificationStatusBadge
                  status={getEffectiveVerificationStatus(foundUser)}
                />
              </div>

              <p style={styles.smallText}>Role: {getRoleLabel(foundUser.role)}</p>
              <p style={styles.smallText}>City: {foundUser.city || "N/A"}</p>
              <p style={styles.profileStatusLine}>{getPublicStatusText(foundUser)}</p>
              {foundUser.statusVisibility !== false ? (
                <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <ProfileStatusBadge
                    role={foundUser.role || ""}
                    status={foundUser.profileStatus}
                    isPublic={foundUser.statusVisibility !== false}
                  />
                </div>
              ) : null}
              {getPublicAreaSummary(foundUser) ? (
                <p style={styles.smallText}>
                  {foundUser.role === "tenant" ? "Preferred areas" : "Service areas"}: {getPublicAreaSummary(foundUser)}
                </p>
              ) : null}
              {foundUser.bio ? (
                <p style={styles.smallText}>Bio: {foundUser.bio}</p>
              ) : null}

              <button
                style={styles.secondaryButton}
                onClick={() => openPublicProfile(foundUser)}
              >
                View Full Profile
              </button>

              <button
                style={{ ...styles.secondaryButton, marginTop: 10 }}
                onClick={() => openConversationWithUser(foundUser)}
              >
                Open Conversation
              </button>

              <button
                style={{ ...styles.primaryButton, marginTop: 10 }}
                onClick={() => selectReviewUser(foundUser)}
              >
                Review This User
              </button>
            </div>
          ) : null}

          <div style={{ marginTop: 24 }}>
            <SectionTitle
              title="Mailbox"
              subtitle="Switch between incoming and sent conversations."
            />

            <div style={styles.messageFolderTabs}>
              <button
                style={
                  messageFolderTab === "inbox"
                    ? styles.messageFolderTabActive
                    : styles.messageFolderTab
                }
                onClick={() => setMessageFolderTab("inbox")}
              >
                Inbox ({inboxThreads.length})
              </button>
              <button
                style={
                  messageFolderTab === "outbox"
                    ? styles.messageFolderTabActive
                    : styles.messageFolderTab
                }
                onClick={() => setMessageFolderTab("outbox")}
              >
                Outbox ({outboxThreads.length})
              </button>
            </div>

            <div style={{ ...styles.threadList, ...styles.threadListScrollable }} className="rentduo-scrollbar">
              {activeThreads.length === 0 ? (
                <p style={styles.smallText}>
                  No {messageFolderTab === "inbox" ? "incoming" : "sent"} conversations yet.
                </p>
              ) : (
                activeThreads.map((thread) => (
                  <button
                    key={`${messageFolderTab}-${thread.conversationId}`}
                    style={styles.threadCardButton}
                    onClick={() => openConversationWithUser(thread)}
                  >
                    <div style={styles.userHeader}>
                      <Avatar
                        src={thread.photoURL}
                        name={thread.displayName || thread.email}
                        size={46}
                      />
                      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                        <div style={styles.profileTitleRowWrap}>
                          <div style={styles.cardPersonName}>
                            {thread.displayName || "Unnamed User"}
                          </div>
                          {thread.verified ? <VerifiedBadge /> : null}
                        </div>
                        <div style={styles.smallText}>{getRoleLabel(thread.role) || "RentDuo member"}</div>
                        <div style={styles.threadSnippet}>
                          {thread.lastMessage || "No message preview yet."}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <div style={styles.cardWide}>
          <SectionTitle
            title="Messages"
            subtitle="Real-time conversation thread between users."
          />

          {!selectedUser ? (
            <p style={styles.smallText}>
              Select a user from search, inbox, or outbox to open the thread.
            </p>
          ) : (
            <>
              <div style={styles.messageSelectedUserBar}>
                <div style={styles.userHeader}>
                  <Avatar
                    src={selectedUser.photoURL}
                    name={selectedUser.displayName || selectedUser.email}
                    size={48}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.profileTitleRowWrap}>
                      <PublicProfileNameButton userData={selectedUser} style={styles.cardPersonName}>
                        {selectedUser.displayName || "Unnamed User"}
                      </PublicProfileNameButton>
                      <VerificationStatusBadge status={getEffectiveVerificationStatus(selectedUser)} />
                    </div>
                    <div style={styles.smallText}>{getRoleLabel(selectedUser.role) || "RentDuo member"}</div>
                  </div>
                </div>
              </div>

              {renderPartnerServiceCards("messages")}

              <div style={styles.messageBox} className="rentduo-scrollbar">
                {messages.length === 0 ? (
                  <div style={styles.emptyReviewsBox}>{t("noMessagesYet")}</div>
                ) : (
                  messages.map((msg) => {
                    const mine = msg.senderId === user.uid;

                    return (
                      <div
                        key={msg.id}
                        style={{
                          ...styles.bubbleWrap,
                          justifyContent: mine ? "flex-end" : "flex-start",
                        }}
                      >
                        <div
                          style={{
                            ...styles.bubble,
                            background: mine
                              ? "linear-gradient(135deg, rgba(15,111,255,0.16) 0%, rgba(20,184,106,0.14) 100%)"
                              : "#f8fafc",
                            border: mine
                              ? "1px solid rgba(15,111,255,0.14)"
                              : "1px solid #e2e8f0",
                          }}
                        >
                          <div style={styles.bubbleHeader}>
                            <Avatar
                              src={msg.senderPhotoURL}
                              name={msg.senderDisplayName || msg.senderEmail}
                              size={32}
                            />
                            <div style={styles.profileTitleRowWrap}>
                              <div style={styles.bubbleName}>
                                {msg.senderDisplayName || msg.senderEmail}
                              </div>
                              {msg.senderVerified ? <VerifiedBadge /> : null}
                            </div>
                          </div>
                          <TranslatableText text={msg.text} label="Message" bodyStyle={styles.bodyCopy} compact />
                          {!mine ? (
                            <button
                              type="button"
                              style={{ ...styles.secondaryButton, width: "100%", marginTop: 8 }}
                              onClick={() => handleAnalyzeMessageScamRisk(msg.text)}
                              disabled={messageScamLoading}
                            >
                              Check for Scam Signals
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <div style={styles.messageComposer}>
                <input
                  ref={messageInputRef}
                  style={styles.input}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Type a message..."
                />
                <button style={styles.primaryButton} onClick={handleSendMessage}>
                  Send
                </button>
              </div>
              <div style={{ ...styles.aiRiskCard, marginTop: 12 }}>
                <div style={styles.profileTitleRowWrap}>
                  <div>
                    <div style={styles.aiDraftEyebrow}>RentDuo AI</div>
                    <strong style={styles.strongHeading}>Message Scam Check</strong>
                  </div>
                  {messageScamAnalysis?.riskLevel ? (
                    <span
                      style={{
                        ...styles.statusPill,
                        ...(messageScamAnalysis.riskLevel === "High"
                          ? styles.aiRiskHigh
                          : messageScamAnalysis.riskLevel === "Medium"
                          ? styles.aiRiskModerate
                          : styles.aiRiskLow),
                      }}
                    >
                      {messageScamAnalysis.riskLevel} Risk
                    </span>
                  ) : null}
                </div>
                <p style={styles.smallText}>
                  Before replying or paying, screen pasted rental requests for payment pressure and suspicious off-platform instructions.
                </p>
                {messageScamAnalysis ? (
                  <>
                    <p style={styles.smallText}>{messageScamAnalysis.explanation}</p>
                    {messageScamAnalysis.safetyTips?.length ? (
                      <p style={styles.smallText}>Tips: {messageScamAnalysis.safetyTips.join(" | ")}</p>
                    ) : null}
                  </>
                ) : null}
                {messageScamError ? <p style={styles.safeReviewText}>{messageScamError}</p> : null}
                <p style={styles.aiRiskDisclaimer}>AI screening may be incomplete. Never send funds or sensitive documents until identity and property access are verified.</p>
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, width: "100%", marginTop: 10 }}
                  onClick={handleAnalyzeMessageScamRisk}
                  disabled={messageScamLoading}
                >
                  {messageScamLoading ? "Screening Message..." : "Screen Current Message"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const ManagerTab = () => {
    if (role !== "property_manager") return null;

    return (
      <div style={styles.grid}>
        <div style={styles.card}>
          <SectionTitle
            title="Property Manager Dashboard"
            subtitle="Portfolio health, tenant risk, and property-level records in one place."
          />

          <div style={responsiveStatGridStyle}>
            <div style={responsiveStatCardStyle}>
              <div style={styles.managerStatValue}>{managerPortfolioStats.totalProperties}</div>
              <div style={responsiveStatValueStyle}>Managed props</div>
            </div>
            <div style={responsiveStatCardStyle}>
              <div style={styles.managerStatValue}>{managerPortfolioStats.claimedProperties}</div>
              <div style={responsiveStatValueStyle}>Claimed</div>
            </div>
            <div style={responsiveStatCardStyle}>
              <div style={styles.managerStatValue}>{managerInsights.riskyTenants}</div>
              <div style={responsiveStatValueStyle}>Risk flags</div>
            </div>
            <div style={responsiveStatCardStyle}>
              <div style={styles.managerStatValue}>{issues.length}</div>
              <div style={responsiveStatValueStyle}>Issues</div>
            </div>
          </div>

          <div style={styles.verificationBox}>
            <p style={styles.smallText}>Late payment signal rate: {managerInsights.latePaymentRate}%</p>
            <p style={styles.smallText}>Top property in view: {managerInsights.topPropertyTitle}</p>
          </div>
        </div>

        <div style={{ ...styles.card, ...styles.scrollableColumnCard }}>
          <SectionTitle
            title="Managed Properties"
            subtitle="Properties you created, manage, or are currently claiming."
          />
          {managedProperties.length === 0 ? (
            <p style={styles.smallText}>No managed properties yet.</p>
          ) : (
            <div style={styles.scrollableInnerList}>
              {managedProperties.map((property) => (
                <div key={property.id} style={styles.managerPropertyCard}>
                  <div style={styles.profileTitleRowWrap}>
                    <strong style={styles.strongHeading}>{property.title || "Untitled Property"}</strong>
                    <span style={styles.issueStatusOpen}>
                      {property.managementType === "managed_property" ? "Managed Property" : "Community Added"}
                    </span>
                  </div>
                  <p style={styles.smallText}>{property.address || "No address"}</p>
                  <div style={styles.issueActionsRow}>
                    <button style={styles.secondaryButton} onClick={() => handleSelectProperty(property)}>
                      Open Full Property Card
                    </button>
                    <button
                      style={styles.primaryButton}
                      onClick={() => {
                        handleSelectProperty(property);
                        setActiveTab("home");
                      }}
                    >
                      View on Map
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.card}>
          <SectionTitle
            title="Tenant Risk Snapshot"
            subtitle="Visible only to property managers."
          />
          {tenantProfilesForManager.length === 0 ? (
            <p style={styles.smallText}>Search for or select a tenant in Reviews to populate this panel.</p>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {tenantProfilesForManager.map((tenant) => (
                <button
                  key={tenant.uid}
                  style={{ ...styles.managerScoreBox, textAlign: "left" }}
                  onClick={() => handleManagerOpenTenant(tenant)}
                >
                  <div style={styles.profileTitleRowWrap}>
                    <strong style={styles.strongHeading}>{tenant.displayName || tenant.email || "Tenant"}</strong>
                    <span style={styles.managerScoreBadge}>
                      {tenant.managerScorecard.score}/100 · {tenant.managerScorecard.label}
                    </span>
                  </div>
                  <div style={styles.smallText}>
                    Approved reviews: {tenant.managerScorecard.approvedReviewCount} · Late payment signals: {tenant.managerScorecard.latePaymentMentions} · Rule violation signals: {tenant.managerScorecard.ruleViolationMentions}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

      </div>
    );
  };

  const ReviewsTab = () => (
    <div style={styles.grid}>
      <div style={styles.card}>
        <SectionTitle
          title="Selected Renter / Owner Profile"
          subtitle="Review them, message them, or report them with a cleaner Airbnb-style layout."
        />

        <div style={{ marginBottom: 20 }}>
          {selectedProperty?.id && selectedUser ? (
            <div style={{ ...styles.verificationBox, marginBottom: 16 }}>
              <div style={styles.profileTitleRowWrap}>
                <strong style={styles.strongHeading}>Auto-Filled From Selected Property</strong>
                <VerificationStatusBadge status={getEffectiveVerificationStatus(selectedUser)} />
              </div>
              <p style={styles.smallText}>
                {selectedUser.displayName || selectedUser.email || "User"} was auto-selected from the current property claim or owner information.
              </p>
            </div>
          ) : null}

          <div style={styles.filterGrid}>
            <div>
              <label style={styles.label}>Area</label>
              <input
                ref={reviewAreaInputRef}
                className="review-search-input"
                style={styles.input}
                type="text"
                name="review-area-search-review-tab"
                id="review-area-search-review-tab"
                value={searchArea}
                onChange={(e) => setSearchArea(e.target.value)}
                placeholder="Start typing city or state"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                aria-autocomplete="none"
              />
            </div>
            <div>
              <label style={styles.label}>Role</label>
              <select style={styles.input} value={searchRoleFilter} onChange={(e) => setSearchRoleFilter(e.target.value)}>
                <option value="all">All Roles</option>
                <option value="tenant">Tenant</option>
                <option value="landlord">Landlord</option>
                <option value="property_manager">Property Manager</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Status</label>
              <select style={styles.input} value={searchStatusFilter} onChange={(e) => setSearchStatusFilter(e.target.value)}>
                <option value="all">Any Status</option>
                <option value="actively_looking">Actively Looking</option>
                <option value="browsing">Browsing</option>
                <option value="already_renting">Already Renting</option>
                <option value="not_looking">Not Looking</option>
                <option value="units_available">Units Available</option>
                <option value="leasing_soon">Leasing Soon</option>
                <option value="fully_occupied">Fully Occupied</option>
                <option value="not_accepting">Not Accepting Applications</option>
              </select>
            </div>
          </div>

          <label style={styles.label}>Find User To Review</label>
          <input
            ref={reviewUserSearchInputRef}
            className="review-search-input"
            style={styles.input}
            type="text"
            name="review-user-search-review-tab"
            id="review-user-search-review-tab"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Start typing property address, city, or area"
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            aria-autocomplete="none"
          />

          <button style={styles.primaryButton} onClick={handleFindUser}>
            Search / Auto-Fill User
          </button>

          {!!searchMessage && <p style={styles.message}>{searchMessage}</p>}
          {renderSearchResultsCards("reviews")}

          {foundUser ? (
            <div style={{ ...styles.userCard, marginTop: 12 }}>
              <div style={styles.userHeader}>
                <Avatar
                  src={foundUser.photoURL}
                  name={foundUser.displayName || foundUser.email}
                  size={56}
                />
                <div>
                  <div style={styles.profileTitleRowWrap}>
                    <h3 style={styles.cardPersonName}>
                      {foundUser.displayName || "Unnamed User"}
                    </h3>
                    <VerificationStatusBadge status={getEffectiveVerificationStatus(foundUser)} />
                  </div>
                  <p style={styles.smallText}>Role: {getRoleLabel(foundUser.role)}</p>
                  <p style={styles.smallText}>City: {foundUser.city || "N/A"}</p>
                  <p style={styles.profileStatusLine}>{getPublicStatusText(foundUser)}</p>
                  {getPublicAreaSummary(foundUser) ? (
                    <p style={styles.smallText}>
                      {foundUser.role === "tenant" ? "Preferred areas" : "Service areas"}: {getPublicAreaSummary(foundUser)}
                    </p>
                  ) : null}
                </div>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                <ProfileStatusBadge
                  role={foundUser.role || ""}
                  status={foundUser.profileStatus}
                  isPublic={foundUser.statusVisibility !== false}
                />
              </div>

              {(foundUserTrustSnapshot.reviewCount > 0 || Number(foundUser?.rentScore || 0) > 0) ? (
                renderTrustSummary(foundUserTrustSnapshot, "Stored Rent Score")
              ) : null}

              <button
                style={{ ...styles.secondaryButton, marginTop: 10 }}
                onClick={() => openPublicProfile(foundUser)}
              >
                Open Public Profile
              </button>

              <button
                style={{
                  ...styles.primaryButton,
                  opacity: canReviewTargetRole(role, foundUser?.role || "") ? 1 : 0.6,
                  cursor: canReviewTargetRole(role, foundUser?.role || "") ? "pointer" : "not-allowed",
                }}
                onClick={() =>
                  canReviewTargetRole(role, foundUser?.role || "")
                    ? selectReviewUser(foundUser)
                    : setStatusMessage(
                        "Review not allowed. Tenants can review landlords and property managers. Landlords and property managers can only review tenants."
                      )
                }
              >
                {canReviewTargetRole(role, foundUser?.role || "") ? "Review This User" : "Review Not Allowed"}
              </button>
            </div>
          ) : null}
        </div>

        {selectedUser ? (
          <div style={{ display: "contents" }}>
            <div style={styles.userCard}>
              <div style={styles.userHeader}>
                <Avatar
                  src={selectedUser.photoURL}
                  name={selectedUser.displayName || selectedUser.email}
                  size={68}
                />
                <div style={{ flex: 1 }}>
                  <div style={styles.profileTitleRowWrap}>
                    <h3 style={styles.cardPersonName}>
                      {selectedUser.displayName || "Unnamed User"}
                    </h3>
                    <VerificationStatusBadge
                      status={getEffectiveVerificationStatus(selectedUser)}
                    />
                  </div>
                </div>
              </div>

              <p style={styles.smallText}>Role: {getRoleLabel(selectedUser.role)}</p>
              <p style={styles.smallText}>City: {selectedUser.city || "N/A"}</p>
              <p style={styles.profileStatusLine}>{getPublicStatusText(selectedUser)}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                <ProfileStatusBadge
                  role={selectedUser.role || ""}
                  status={selectedUser.profileStatus}
                  isPublic={selectedUser.statusVisibility !== false}
                />
              </div>
              {getPublicAreaSummary(selectedUser) ? (
                <p style={styles.smallText}>
                  {selectedUser.role === "tenant" ? "Preferred areas" : "Service areas"}: {getPublicAreaSummary(selectedUser)}
                </p>
              ) : null}
              {selectedUser.bio ? (
                <p style={styles.smallText}>Bio: {selectedUser.bio}</p>
              ) : null}

              <div style={{ ...styles.ratingHeroBox, flexWrap: "wrap", rowGap: 12 }}>
                <div style={styles.ratingHeroValue}>
                  {averageRating ? averageRating.toFixed(1) : "0.0"}
                </div>
                <div>
                  <div>{renderStars(Math.round(averageRating), false, null, 26)}</div>
                  <div style={styles.smallText}>Average user rating</div>
                </div>
              </div>

              {renderTrustSummary(selectedUserTrustSnapshot)}

              <div style={{ marginTop: 14 }}>
                <button style={styles.secondaryButton} onClick={() => openPublicProfile(selectedUser)}>
                  Open Public Profile
                </button>
                <button style={styles.rejectButton} onClick={() => handleReportUser(selectedUser)}>
                  {t("reportUser")}
                </button>
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              <h3 style={styles.subsectionHeading}>{t("writeReview")}</h3>
              <p style={styles.smallText}>
                Rate the key categories below. RentDuo will calculate the overall score automatically.
              </p>

              <RightsResources
                stateCode={getRightsStateCode(selectedUser?.state, selectedUser?.city, profile?.state, profile?.city)}
                defaultRole={role || profile?.role || ""}
                location={profile?.location || profileLocation}
                title={language === "es" ? "Antes de publicar" : "Before You Post"}
                language={language}
              />

              {renderReviewLegalAwarenessCard("profile review")}

              <div style={{ ...styles.ratingHeroBox, flexWrap: "wrap", rowGap: 12 }}>
                <div style={styles.ratingHeroValue}>
                  {reviewOverallRating ? reviewOverallRating.toFixed(1) : "0.0"}
                </div>
                <div>
                  <div>{renderStars(Math.round(reviewOverallRating), false, null, 26)}</div>
                  <div style={styles.smallText}>Overall from category ratings</div>
                </div>
              </div>

              <div style={{ marginTop: 0, display: "grid", gap: 12 }}>
                {selectedReviewCategories.map((category) => (
                  <div key={category.key} style={styles.categoryRatingRow}>
                    <div style={styles.categoryRatingLabel}>{category.label}</div>
                    {renderStars(
                      Number(reviewCategoryRatings[category.key] || 0),
                      true,
                      (star) => {
                        setReviewCategoryRatings((prev) => {
                          const next = {
                            ...prev,
                            [category.key]: star,
                          };
                          setRatingValue(calculateCategoryOverall(next) || star);
                          return next;
                        });
                      },
                      26
                    )}
                  </div>
                ))}
              </div>

              {!profile?.verified ? (
                <div style={{ ...styles.safeReviewCard, marginTop: 14 }}>
                  <div style={styles.safeReviewTitle}>Review Safety</div>
                  <p style={styles.safeReviewText}>{REVIEW_SAFETY_WARNING}</p>
                  <p style={styles.safeReviewText}>{LEGAL_GUIDANCE_DISCLAIMER}</p>
                </div>
              ) : null}

              {profile?.verified ? (
                <>
                  <div style={styles.safeReviewCard}>
                    <div style={styles.safeReviewTitle}>Rental History Feedback</div>
                    <p style={styles.safeReviewText}>
                      Share factual rental-history feedback based on a real rental relationship. Avoid accusations, insults, protected-class references, medical/legal claims, or anything you cannot support.
                    </p>

                    <div style={styles.safeReviewGrid}>
                      {[
                        ["rentalRelationship", "Rental relationship"],
                        ["rentalAgreement", "Was there a lease or rental agreement?"],
                        ["paymentHistory", "Payment history"],
                        ["propertyCondition", "Property condition at move-out"],
                        ["leaseCompliance", "Lease compliance"],
                        ["communication", "Communication"],
                        ["wouldRentAgain", "Would rent to / rent from again?"],
                      ].map(([fieldKey, label]) => (
                        <label key={fieldKey} style={styles.safeReviewField}>
                          <span style={styles.safeReviewLabel}>{label}</span>
                          <select
                            value={rentalFeedback[fieldKey] || ""}
                            onChange={(e) =>
                              setRentalFeedback((prev) => ({ ...prev, [fieldKey]: e.target.value }))
                            }
                            style={styles.safeReviewSelect}
                          >
                            <option value="">Select one</option>
                            {(RENTAL_FEEDBACK_OPTIONS[fieldKey] || []).map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </label>
                      ))}
                    </div>

                    <div style={styles.safeReviewWarning}>
                      {REVIEW_SAFETY_WARNING}
                    </div>
                    <div style={styles.safeReviewWarning}>
                      {LEGAL_GUIDANCE_DISCLAIMER}
                    </div>

                  </div>

                  <textarea
                    style={{ ...styles.textarea, marginTop: 14 }}
                    value={reviewText}
                    onChange={(e) => {
                      setReviewText(e.target.value);
                      setAiReviewDraft("");
                      setAiOriginalReviewText("");
                    }}
                    placeholder="Optional notes: Keep this factual and neutral. Example: Late rent reported twice during tenancy. Avoid personal attacks or unsupported accusations."
                  />

                  <div style={styles.aiToneRow}>
                    <button
                      type="button"
                      style={{ ...styles.primaryButton, marginTop: 0, padding: "10px 14px", minWidth: isMobile ? "100%" : 0 }}
                      onClick={() => handleImproveReview("review-safety")}
                      disabled={improvingReview || !reviewText.trim()}
                    >
                      {improvingReview && aiTone === "review-safety" ? "Making Safer..." : "Make this review safer"}
                    </button>
                    {["professional", "stronger", "neutral"].map((tone) => (
                      <button
                        key={tone}
                        style={{
                          ...(aiTone === tone ? styles.primaryButton : styles.secondaryButton),
                          marginTop: 0,
                          padding: "10px 14px",
                          minWidth: isMobile ? "100%" : 0,
                          flex: isMobile ? "1 1 100%" : "0 0 auto",
                        }}
                        onClick={() => handleImproveReview(tone)}
                        disabled={improvingReview || !reviewText.trim()}
                      >
                        {improvingReview && aiTone === tone
                          ? "Thinking..."
                          : tone.charAt(0).toUpperCase() + tone.slice(1)}
                      </button>
                    ))}
                  </div>

                  {aiReviewDraft ? (
                    <div style={styles.aiDraftCard}>
                      <div style={styles.aiDraftHeader}>
                        <div>
                          <div style={styles.aiDraftEyebrow}>AI Improved Draft</div>
                          <h4 style={styles.aiDraftTitle}>Stronger review. More trusted voice.</h4>
                        </div>
                        <span style={styles.badgePill}>AI helped improve clarity</span>
                      </div>

                      <p style={styles.aiDraftDisclaimer}>
                        You are responsible for the final content you submit.
                      </p>

                      <div style={styles.aiDraftCompareWrap}>
                        <div style={styles.aiDraftCompareCol}>
                          <div style={styles.aiDraftCompareLabel}>Original</div>
                          <div style={styles.aiDraftCompareText}>
                            {aiOriginalReviewText || reviewText || "No original text yet."}
                          </div>
                        </div>

                        <div style={styles.aiDraftCompareCol}>
                          <div style={styles.aiDraftCompareLabel}>Improved</div>
                          <div style={styles.aiDraftCompareText}>{aiReviewDraft}</div>
                        </div>
                      </div>

                      <div style={styles.aiDraftConversionCard}>
                        <strong style={styles.aiDraftConversionTitle}>Verified reviews help shape the real score.</strong>
                        <p style={styles.aiDraftConversionText}>
                          Your review will be visible, but it won’t impact the official score until you verify your identity.
                        </p>
                        <p style={styles.aiDraftPremiumText}>
                          Premium: Get AI-assisted reviews, priority visibility, and full review history.
                        </p>
                      </div>

                      <div style={styles.aiDraftActionRow}>
                        <button
                          style={{ ...styles.primaryButton, marginTop: 0, flex: isMobile ? "1 1 100%" : "1 1 0" }}
                          onClick={() => {
                            setReviewText(aiReviewDraft);
                            setAiReviewDraft("");
                            setAiOriginalReviewText("");
                            setStatusMessage("Improved review applied.");
                          }}
                        >
                          Use Improved Review
                        </button>
                        <button
                          style={{ ...styles.secondaryButton, marginTop: 0, flex: isMobile ? "1 1 100%" : "1 1 0" }}
                          onClick={() => {
                            setShowVerifyPrompt(true);
                            setAiReviewDraft("");
                            setAiOriginalReviewText("");
                          }}
                        >
                          Verify to Make It Count
                        </button>
                        <button
                          style={{ ...styles.secondaryButton, marginTop: 0, flex: isMobile ? "1 1 100%" : "1 1 0" }}
                          onClick={() => handleImproveReview(aiTone || "professional")}
                          disabled={improvingReview || !reviewText.trim()}
                        >
                          Try Again
                        </button>
                        <button
                          style={{ ...styles.secondaryButton, marginTop: 0, flex: isMobile ? "1 1 100%" : "1 1 0" }}
                          onClick={() => {
                            setAiReviewDraft("");
                            setAiOriginalReviewText("");
                            setStatusMessage("Keeping original review.");
                          }}
                        >
                          Post as Community Rating
                        </button>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <div style={styles.communityRatingNotice}>
                  <strong>Unverified users can leave a star-only community rating.</strong>
                  <p style={styles.smallText}>
                    Your rating will show publicly, but it will not impact the official score until you verify.
                  </p>
                </div>
              )}

              {selectedUserAlreadyReviewed ? (
                <div style={styles.communityRatingNotice}>
                  <strong>Review cooldown active.</strong>
                  <p style={styles.smallText}>
                    {REVIEW_COOLDOWN_PERSON_MESSAGE}
                  </p>
                </div>
              ) : null}

              {!selectedUserReviewAllowed && selectedUser ? (
                <div style={styles.communityRatingNotice}>
                  <strong>Review not allowed for this role combination.</strong>
                  <p style={styles.smallText}>
                    Only tenants can review landlords or property managers. Landlords and property managers can only review tenants.
                  </p>
                </div>
              ) : null}

              {!selectedUserAlreadyReviewed && selectedUserReviewAllowed ? (
                <button
                  style={{
                    ...styles.primaryButton,
                    opacity: submittingReview ? 0.7 : 1,
                    pointerEvents: submittingReview ? "none" : "auto",
                  }}
                  onClick={handleSubmitReview}
                  disabled={submittingReview}
                >
                  {submittingReview
                    ? "Submitting..."
                    : profile?.verified
                    ? "Post Review"
                    : "Post Community Rating"}
                </button>
              ) : null}

              {reviewAnalysis?.summary ? (
                <div style={styles.analysisCard}>
                  <h4 style={styles.analysisTitle}>AI Review Summary</h4>
                  <p style={styles.smallText}>{reviewAnalysis.summary}</p>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <p style={styles.smallText}>No user selected yet.</p>
        )}
      </div>

      <div style={styles.card}>
        <SectionTitle
          title={t("reviews")}
          subtitle="Approved reviews are public. Pending reviews are still visible to the reviewer and admin."
        />

        {!selectedUser ? (
          <p style={styles.smallText}>Select a user to see reviews.</p>
        ) : (
          <>
            <div
              style={responsiveStatGridStyle}
            >
              <div style={responsiveStatCardStyle}>
                <strong style={responsiveStatLabelStyle}>Official</strong>
                <div style={responsiveStatValueStyle}>
                  {userReviewSummary.officialAverage
                    ? userReviewSummary.officialAverage.toFixed(1)
                    : "0.0"}
                </div>
              </div>
              <div style={responsiveStatCardStyle}>
                <strong style={responsiveStatLabelStyle}>Community</strong>
                <div style={responsiveStatValueStyle}>
                  {userReviewSummary.communityAverage
                    ? userReviewSummary.communityAverage.toFixed(1)
                    : "0.0"}
                </div>
              </div>
              <div style={responsiveStatCardStyle}>
                <strong style={responsiveStatLabelStyle}>Verified</strong>
                <div style={responsiveStatValueStyle}>{userReviewSummary.verifiedCount}</div>
              </div>
              <div style={responsiveStatCardStyle}>
                <strong style={responsiveStatLabelStyle}>Helpful</strong>
                <div style={responsiveStatValueStyle}>{userReviewSummary.helpfulVotes}</div>
              </div>
            </div>

            {reviews.length === 0 ? (
              <div style={styles.communityRatingNotice}>
                <strong>No verified reviews yet.</strong>
                <p style={styles.smallText}>
                  Lived here? Be the first to review. Unverified users can leave a star-only community rating, or verify to make a full written review count.
                </p>
              </div>
            ) : (
              <div style={styles.list}>
                {[...reviews]
                  .sort((a, b) => {
                    if (a?.reviewerVerified && !b?.reviewerVerified) return -1;
                    if (!a?.reviewerVerified && b?.reviewerVerified) return 1;
                    return (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0);
                  })
                  .map((item) => (
                    <div
                      key={item.id}
                      style={{
                        ...styles.reviewCard,
                        border: item.reviewerVerified ? "1px solid #86efac" : "1px solid #fed7aa",
                        background: item.reviewerVerified ? "#f0fdf4" : "#fff7ed",
                      }}
                    >
                      <div style={styles.userHeader}>
                        <Avatar
                          src={item.reviewerPhotoURL}
                          name={item.reviewerDisplayName || item.reviewerEmail}
                          size={44}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.profileTitleRowWrap}>
                            <PublicProfileNameButton
                              userData={{
                                uid: item.reviewerUserId,
                                displayName: item.reviewerDisplayName || "Anonymous",
                                photoURL: item.reviewerPhotoURL,
                                verified: item.reviewerVerified,
                              }}
                              style={styles.strongHeading}
                            >
                              {item.reviewerDisplayName || "Anonymous"}
                            </PublicProfileNameButton>
                            {item.reviewerVerified ? <VerifiedBadge /> : null}
                          </div>
                          {item.reviewerVerified ? (
                            <div
                              style={{
                                display: "inline-block",
                                padding: "4px 10px",
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                background: "#dcfce7",
                                color: "#166534",
                                marginTop: 6,
                              }}
                            >
                              ✅ Verified Review
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "inline-block",
                                padding: "4px 10px",
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                background: "#ffedd5",
                                color: "#c2410c",
                                marginTop: 6,
                              }}
                            >
                              ⚠️ Community Rating
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ margin: "10px 0" }}>
                        {renderStars(Number(item.rating || 0), false, null, 22)}
                      </div>
                      {item.reviewerVerified ? (
                        <div style={{ fontSize: 12, color: "#166534", marginBottom: 8 }}>
                          Counted in official score
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: "#c2410c", marginBottom: 8 }}>
                          Visible, but not counted in official score
                        </div>
                      )}
                      {!item.affectsScore ? (
                        <p style={styles.communityReviewTag}>
                          Community rating only — does not affect official score
                        </p>
                      ) : null}
                      {item.isUpdatedReview ? (
                        <p style={styles.communityReviewTag}>Updated Review</p>
                      ) : null}
                      {item.text ? (
                        <>
                          <TranslatableText text={item.text} label="Review" bodyStyle={styles.bodyCopy} compact />
                          <div style={styles.reviewTimestamp}>
                            {item?.createdAt?.seconds
                              ? `${formatReviewDateTime(item.createdAt)}`
                              : "Recent review"}
                          </div>
                        </>
                      ) : (
                        <div style={styles.smallText}>{t("starOnlyRating")}</div>
                      )}
                      {item.status && item.status !== "approved" ? (
                        <p style={styles.smallText}>
                          Status: {item.status === "pending" ? "Pending admin/AI review" : item.status}
                        </p>
                      ) : null}

                      <div style={item?.disputeStatus === "open" || item?.status === "disputed" ? styles.disputedReviewNotice : styles.reviewDisclaimerBox}>
                        {getReviewPublicDisclaimer(item)}
                      </div>

                      {item?.rentalHistoryFeedback && Object.keys(item.rentalHistoryFeedback).length ? (
                        <div style={styles.rentalFeedbackSummary}>
                          <strong>Rental History Feedback</strong>
                          <div style={styles.rentalFeedbackMiniGrid}>
                            {Object.entries(item.rentalHistoryFeedback).map(([key, value]) => (
                              <span key={key} style={styles.rentalFeedbackPill}>{value}</span>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {item?.response?.text ? (
                        <div style={styles.reviewReplyCard}>
                          <div style={styles.reviewReplyHeader}>
                            <span style={styles.reviewReplyBadge}>
                              Response from {item?.response?.responderRole === "property_manager" ? "Property Manager" : item?.response?.responderRole === "landlord" ? "Landlord" : "User"}
                            </span>
                            <span style={styles.reviewReplyName}>
                              {item?.response?.responderDisplayName || "Response posted"}
                            </span>
                          </div>
                          <div style={styles.reviewReplyText}>{item.response.text}</div>
                        </div>
                      ) : null}

                      {canReplyToReview(item) ? (
                        <div style={styles.reviewReplyComposer}>
                          <div style={styles.reviewReplyComposerTitle}>
                            {item?.response?.text ? "Update your response" : "Reply to this review"}
                          </div>
                          <div style={styles.replyMetaRow}>
                            <span style={styles.smallText}>Open a full reply window so typing stays stable.</span>
                            <div style={styles.replyButtonRow}>
                              <button
                                style={styles.secondaryButton}
                                onClick={() => openReplyModal(item)}
                              >
                                {item?.response?.text ? "Open Reply Window" : "Write Reply"}
                              </button>
                            </div>
                          </div>
                          {item?.response?.flaggedForReview || item?.response?.finalStatus === "pending" ? (
                            <div style={styles.replyWarningText}>This reply was flagged for admin review.</div>
                          ) : null}
                          {item?.response?.aiRiskLabel && item?.response?.aiRiskLabel !== "low" && item?.response?.aiRiskLabel !== "unknown" ? (
                            <div style={styles.replyWarningText}>AI risk: {item.response.aiRiskLabel}</div>
                          ) : null}
                        </div>
                      ) : null}

                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                        <button
                          style={styles.secondaryButton}
                          onClick={() => setActiveReviewDetail({ ...item, collectionName: "reviews" })}
                        >
                          Open Review
                        </button>
                        <button
                          style={styles.secondaryButton}
                          onClick={() => handleHelpfulReview(item, "reviews")}
                          disabled={Array.isArray(item.helpfulBy) && item.helpfulBy.includes(user?.uid)}
                        >
                          👍 Helpful ({Number(item.helpfulCount || 0)})
                        </button>

                        <button
                          style={styles.reportGhostButton}
                          onClick={() => handleReportReview(item.id)}
                        >
                          Report Review
                        </button>

                        <button
                          style={styles.secondaryButton}
                          onClick={() => handleDisputeReview(item, "reviews")}
                          disabled={isSubmittingDispute || item?.disputeStatus === "open" || item?.status === "disputed"}
                        >
                          {item?.disputeStatus === "open" || item?.status === "disputed" ? "Disputed" : "Dispute Review"}
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}
      </div>

      <div style={styles.card}>
        <SectionTitle
          title="Property Reviews"
          subtitle="Recent feedback for the currently selected property."
        />

        {!selectedProperty ? (
          <p style={styles.smallText}>Select a property to see reviews.</p>
        ) : (
          <div style={{ display: "contents" }}>
            <div
              style={responsiveStatGridStyle}
            >
              <div style={responsiveStatCardStyle}>
                <strong style={responsiveStatLabelStyle}>Official</strong>
                <div style={responsiveStatValueStyle}>
                  {propertyReviewSummary.officialAverage
                    ? propertyReviewSummary.officialAverage.toFixed(1)
                    : "0.0"}
                </div>
              </div>
              <div style={responsiveStatCardStyle}>
                <strong style={responsiveStatLabelStyle}>Community</strong>
                <div style={responsiveStatValueStyle}>
                  {propertyReviewSummary.communityAverage
                    ? propertyReviewSummary.communityAverage.toFixed(1)
                    : "0.0"}
                </div>
              </div>
              <div style={responsiveStatCardStyle}>
                <strong style={responsiveStatLabelStyle}>Verified</strong>
                <div style={responsiveStatValueStyle}>{propertyReviewSummary.verifiedCount}</div>
              </div>
              <div style={responsiveStatCardStyle}>
                <strong style={responsiveStatLabelStyle}>Helpful</strong>
                <div style={responsiveStatValueStyle}>{propertyReviewSummary.helpfulVotes}</div>
              </div>
              <div style={responsiveStatCardStyle}>
                <strong style={responsiveStatLabelStyle}>Rent Score</strong>
                <div style={responsiveStatValueStyle}>{propertyRentScore.score} · {propertyRentScore.label}</div>
              </div>
            </div>

            {propertyReviews.length === 0 ? (
              <div style={styles.communityRatingNotice}>
                <strong>No property reviews yet.</strong>
                <p style={styles.smallText}>
                  Lived here? Be the first to review. Unverified users can leave a star-only community rating, or verify to post a full written property review.
                </p>
              </div>
            ) : (
              <div style={styles.list}>
                {[...propertyReviews]
                  .sort((a, b) => {
                    if (a?.reviewerVerified && !b?.reviewerVerified) return -1;
                    if (!a?.reviewerVerified && b?.reviewerVerified) return 1;
                    return (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0);
                  })
                  .map((item) => (
                    <div
                      key={item.id}
                      style={{
                        ...styles.reviewCard,
                        border: item.reviewerVerified ? "1px solid #86efac" : "1px solid #fed7aa",
                        background: item.reviewerVerified ? "#f0fdf4" : "#fff7ed",
                      }}
                    >
                      <div style={styles.userHeader}>
                        <Avatar
                          src={item.reviewerPhotoURL}
                          name={item.reviewerDisplayName || item.reviewerEmail}
                          size={44}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.profileTitleRowWrap}>
                            <PublicProfileNameButton
                              userData={{
                                uid: item.reviewerUserId,
                                displayName: item.reviewerDisplayName || "Anonymous",
                                photoURL: item.reviewerPhotoURL,
                                verified: item.reviewerVerified,
                              }}
                              style={styles.strongHeading}
                            >
                              {item.reviewerDisplayName || "Anonymous"}
                            </PublicProfileNameButton>
                            {item.reviewerVerified ? <VerifiedBadge /> : null}
                          </div>
                          {item.reviewerVerified ? (
                            <div
                              style={{
                                display: "inline-block",
                                padding: "4px 10px",
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                background: "#dcfce7",
                                color: "#166534",
                                marginTop: 6,
                              }}
                            >
                              Verified Review
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "inline-block",
                                padding: "4px 10px",
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                background: "#ffedd5",
                                color: "#c2410c",
                                marginTop: 6,
                              }}
                            >
                              Community Rating
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ margin: "10px 0" }}>
                        {renderStars(Number(item.rating || 0), false, null, 22)}
                      </div>
                      {item.reviewerVerified ? (
                        <div style={{ fontSize: 12, color: "#166534", marginBottom: 8 }}>
                          Counted in official score
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: "#c2410c", marginBottom: 8 }}>
                          Visible, but not counted in official score
                        </div>
                      )}
                      {!item.affectsScore ? (
                        <p style={styles.communityReviewTag}>
                          Community rating only — does not affect official score
                        </p>
                      ) : null}
                      {item.isUpdatedReview ? (
                        <p style={styles.communityReviewTag}>Updated Review</p>
                      ) : null}
                      {item.text ? (
                        <>
                          <TranslatableText text={item.text} label="Review" bodyStyle={styles.bodyCopy} compact />
                          <div style={styles.reviewTimestamp}>
                            {item?.createdAt?.seconds
                              ? `${formatReviewDateTime(item.createdAt)}`
                              : "Recent review"}
                          </div>
                        </>
                      ) : (
                        <div style={styles.smallText}>{t("starOnlyRating")}</div>
                      )}
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                        <button
                          style={styles.secondaryButton}
                          onClick={() => setActiveReviewDetail({ ...item, collectionName: "propertyReviews" })}
                        >
                          Open Review
                        </button>
                        <button
                          style={styles.secondaryButton}
                          onClick={() => handleHelpfulReview(item, "propertyReviews")}
                          disabled={Array.isArray(item.helpfulBy) && item.helpfulBy.includes(user?.uid)}
                        >
                          👍 Helpful ({Number(item.helpfulCount || 0)})
                        </button>

                        <button
                          style={styles.secondaryButton}
                          onClick={() => handleDisputeReview(item, "propertyReviews")}
                          disabled={isSubmittingDispute || item?.disputeStatus === "open" || item?.status === "disputed"}
                        >
                          {item?.disputeStatus === "open" || item?.status === "disputed" ? "Disputed" : "Dispute Review"}
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const profileRoleForForm = role || profile?.role || "";

  const AdminTab = () => {
    if (!isAdmin) {
      return (
        <div style={styles.cardWide}>
          <SectionTitle title="Admin" subtitle="Founder controls are only available to authorized admins." />
          <p style={styles.smallText}>This area is not available for this account.</p>
        </div>
      );
    }

    const pendingPropertyClaims = properties.filter((item) => ["pending", "pending_claim", "pending_review"].includes(String(item?.claimStatus || "")));
    const flaggedReviewItems = [...pendingReviews, ...reportedReviews]
      .filter((item, index, list) => list.findIndex((candidate) => candidate.id === item.id) === index);

    return (
      <div style={styles.grid}>
        <div style={{ ...styles.cardWide, padding: isMobile ? 16 : 20, background: "linear-gradient(135deg, rgba(15,111,255,0.08) 0%, rgba(16,185,129,0.08) 100%)", border: "1px solid rgba(15,111,255,0.12)" }}>
          <div style={{ ...styles.profileTitleRowWrap, justifyContent: "space-between", gap: 12 }}>
            <div>
              <strong style={styles.strongHeading}>Growth Dashboard</strong>
              <div style={{ ...styles.smallText, fontWeight: 700, color: "#0f766e" }}>Founder Analytics</div>
            </div>
            <button
              type="button"
              style={{ ...styles.secondaryButton, marginTop: 0, padding: "9px 12px", width: isPhoneMobile ? "100%" : "auto" }}
              onClick={() => setShowGrowthBaselineModal(true)}
            >
              Reset Baseline
            </button>
          </div>
          <p style={styles.smallText}>
            Tracking since: {showRawGrowthTotals ? "Raw totals" : growthTrackingSinceLabel}
          </p>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800, color: "#475569", marginTop: 8 }}>
            <input
              type="checkbox"
              checked={showRawGrowthTotals}
              onChange={(event) => setShowRawGrowthTotals(event.target.checked)}
            />
            Show raw totals
          </label>
          <div style={{ ...responsiveStatGridStyle, marginTop: 14, marginBottom: 0 }}>
            {dashboardStats.map((stat) => (
              <div
                key={stat.label}
                style={{
                  ...responsiveStatCardStyle,
                  background: stat.accent === "blue" ? "linear-gradient(135deg, rgba(15,111,255,0.12) 0%, rgba(96,165,250,0.08) 100%)" : "linear-gradient(135deg, rgba(16,185,129,0.14) 0%, rgba(52,211,153,0.08) 100%)",
                  border: stat.accent === "blue" ? "1px solid rgba(59,130,246,0.16)" : "1px solid rgba(16,185,129,0.18)",
                  boxShadow: "0 10px 20px rgba(15,23,42,0.06)",
                }}
              >
                <div style={{ ...responsiveStatLabelStyle, fontSize: 12, color: "#475569", textTransform: "uppercase" }}>
                  {isPhoneMobile && stat.shortLabel ? stat.shortLabel : stat.label}
                </div>
                <div style={{ fontSize: isMobile ? 24 : 28, fontWeight: 900, color: "#0f172a" }}>{stat.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.cardWide}>
          <SectionTitle
            title="Admin Review Panel"
            subtitle="Verification approvals, review moderation, property claims, and platform safety queues."
          />
          <p style={styles.smallText}>Admin account: {user.email}</p>

          <div style={styles.adminSectionBlock}>
            <h3 style={styles.subsectionHeading}>AI-Assisted Verification Submissions</h3>
            {pendingVerificationSubmissions.length === 0 ? (
              <p style={styles.smallText}>No pending AI-assisted verification submissions.</p>
            ) : (
              <div style={styles.list}>
                {pendingVerificationSubmissions.map((item) => (
                  <div key={item.id} style={styles.adminCard}>
                    <div style={styles.userHeader}>
                      <Avatar src={item.userPhotoURL} name={item.userDisplayName || item.userEmail} size={52} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={styles.profileTitleRowWrap}>
                          <strong style={styles.strongHeading}>{item.userDisplayName || item.userEmail || "Verification submission"}</strong>
                          <span style={styles.statusPill}>{item.aiRiskLevel || "AI pending"}</span>
                        </div>
                        <p style={styles.smallText}>{getVerificationTypeLabel(item.verificationType)} · Role: {item.role || "N/A"}</p>
                        <p style={styles.smallText}>{item.propertyAddress || item.propertyTitle || "No property attached"}</p>
                      </div>
                    </div>
                    {item.aiAnalysis?.summary ? <p style={styles.smallText}>AI summary: {item.aiAnalysis.summary}</p> : null}
                    {Array.isArray(item.aiFlags) && item.aiFlags.length ? (
                      <p style={styles.smallText}>AI flags: {item.aiFlags.join(", ")}</p>
                    ) : null}
                    <div style={styles.adminDocsRow}>
                      {(item.uploadedFiles || []).map((file, index) => (
                        <a key={`${item.id}-${file.docType || index}`} href={file.url} target="_blank" rel="noreferrer" style={styles.linkPill}>
                          Open {file.docType || `file ${index + 1}`}
                        </a>
                      ))}
                    </div>
                    <label style={styles.label}>Admin Review Note</label>
                    <textarea
                      style={styles.textarea}
                      value={adminNoteDrafts[item.id] || ""}
                      onChange={(e) => handleAdminNoteChange(item.id, e.target.value)}
                      placeholder="Add internal review note..."
                    />
                    <div style={styles.adminButtonGrid}>
                      <button style={styles.primaryButton} onClick={() => handleAdminReviewVerificationSubmission(item, "approved")}>
                        Approve Verification
                      </button>
                      <button style={styles.rejectButton} onClick={() => handleAdminReviewVerificationSubmission(item, "denied")}>
                        Deny Verification
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.adminSectionBlock}>
            <h3 style={styles.subsectionHeading}>Legacy Profile Verification Queue</h3>
            {pendingVerifications.length === 0 ? (
              <p style={styles.smallText}>No pending verification submissions.</p>
            ) : (
              <div style={styles.list}>
                {pendingVerifications.map((item) => (
                  <div key={item.uid} style={styles.adminCard}>
                    <div style={styles.userHeader}>
                      <Avatar src={item.photoURL} name={item.displayName || item.email} size={52} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={styles.profileTitleRowWrap}>
                          <strong style={styles.strongHeading}>{item.displayName || "Unnamed User"}</strong>
                          <VerificationStatusBadge status={getEffectiveVerificationStatus(item)} />
                        </div>
                        <p style={styles.smallText}>{item.email}</p>
                        <p style={styles.smallText}>Role: {item.role || "N/A"} · City: {item.city || "N/A"}</p>
                      </div>
                    </div>
                    {item.verificationNote ? <p style={styles.smallText}>User Note: {item.verificationNote}</p> : null}
                    <div style={styles.adminDocsRow}>
                      {item.verificationDocs?.idUrl ? <a href={item.verificationDocs.idUrl} target="_blank" rel="noreferrer" style={styles.linkPill}>Open ID</a> : null}
                      {item.verificationDocs?.deedUrl ? <a href={item.verificationDocs.deedUrl} target="_blank" rel="noreferrer" style={styles.linkPill}>Open Proof</a> : null}
                    </div>
                    <label style={styles.label}>Admin Review Note</label>
                    <textarea
                      style={styles.textarea}
                      value={adminNoteDrafts[item.uid] || ""}
                      onChange={(e) => handleAdminNoteChange(item.uid, e.target.value)}
                      placeholder="Add internal review note..."
                    />
                    <div style={styles.adminButtonGrid}>
                      <button style={styles.primaryButton} onClick={() => handleAdminUpdateVerification(item, "tenant_verified")}>Approve Tenant</button>
                      <button style={styles.secondaryGreenButton} onClick={() => handleAdminUpdateVerification(item, "landlord_verified")}>Approve Landlord</button>
                      <button style={styles.secondaryPurpleButton} onClick={() => handleAdminUpdateVerification(item, "property_manager_verified")}>Approve Property Manager</button>
                      <button style={styles.rejectButton} onClick={() => handleAdminUpdateVerification(item, "unverified")}>Reject / Reset</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.adminSectionBlock}>
            <h3 style={styles.subsectionHeading}>Property Claim Approvals</h3>
            {pendingPropertyClaims.length === 0 ? (
              <p style={styles.smallText}>No pending property claims.</p>
            ) : (
              <div style={styles.list}>
                {pendingPropertyClaims.map((item) => (
                  <div key={item.id} style={styles.adminCard}>
                    <strong style={styles.strongHeading}>{item.title || "Property claim"}</strong>
                    <p style={styles.smallText}>{item.address || "No address listed"}</p>
                    <p style={styles.smallText}>Requested by: {item.claimRequestedByDisplayName || item.claimRequestedByEmail || item.claimRequestedByUserId || "Unknown"}</p>
                    <div style={styles.adminButtonGrid}>
                      <button
                        style={styles.primaryButton}
                        onClick={() => handleAdminApprovePropertyClaim(item, "landlord")}
                      >
                        Approve as Landlord
                      </button>
                      <button
                        style={styles.secondaryGreenButton}
                        onClick={() => handleAdminApprovePropertyClaim(item, "property_manager")}
                      >
                        Approve as Manager
                      </button>
                      <button style={styles.rejectButton} onClick={() => handleAdminRejectPropertyClaim(item)}>
                        Reject Claim
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.adminSectionBlock}>
            <h3 style={styles.subsectionHeading}>Review Moderation</h3>
            {flaggedReviewItems.length === 0 ? (
              <p style={styles.smallText}>No pending or flagged reviews.</p>
            ) : (
              <div style={styles.list}>
                {flaggedReviewItems.map((item) => (
                  <div key={item.id} style={styles.adminCard}>
                    <div style={styles.profileTitleRowWrap}>
                      <PublicProfileNameButton userData={{ uid: item.reviewerUserId, displayName: item.reviewerDisplayName || "Reviewer", photoURL: item.reviewerPhotoURL, verified: item.reviewerVerified }} style={styles.strongHeading}>
                        {item.reviewerDisplayName || "Reviewer"}
                      </PublicProfileNameButton>
                      {item.reviewerVerified ? <VerifiedBadge /> : null}
                    </div>
                    <p style={styles.smallText}>For: {item.targetDisplayName || item.propertyTitle || "RentDuo item"}</p>
                    <div style={{ margin: "10px 0" }}>{renderStars(Number(item.rating || 0))}</div>
                    <TranslatableText text={item.text} label="Review" bodyStyle={styles.bodyCopy} compact />
                    {item?.disputeStatus === "open" || item?.status === "disputed" ? (
                      <div style={styles.disputedReviewNotice}>
                        Dispute reason: {item.disputeReason || "No reason provided"}
                        <br />Evidence status: {item.evidenceStatus || "not requested"}
                      </div>
                    ) : null}
                    <div style={styles.adminButtonGrid}>
                      <button style={styles.primaryButton} onClick={() => item?.disputeStatus === "open" || item?.status === "disputed" ? handleResolveDisputedReview(item, "keep", "reviews") : handleApproveReview(item.id)}>
                        {item?.disputeStatus === "open" || item?.status === "disputed" ? "Resolve + Keep" : "Approve / Keep"}
                      </button>
                      <button style={styles.secondaryButton} onClick={() => handleRequestReviewEvidence(item, "reviews")}>Request Evidence</button>
                      <button style={styles.rejectButton} onClick={() => item?.disputeStatus === "open" || item?.status === "disputed" ? handleResolveDisputedReview(item, "remove", "reviews") : handleRejectReview(item.id)}>
                        {item?.disputeStatus === "open" || item?.status === "disputed" ? "Resolve + Remove" : "Reject / Remove"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.adminSectionBlock}>
            <h3 style={styles.subsectionHeading}>Reported Users</h3>
            {reportedUsers.filter((r) => r.status !== "resolved").length === 0 ? (
              <p style={styles.smallText}>No open user reports.</p>
            ) : (
              <div style={styles.list}>
                {reportedUsers.filter((r) => r.status !== "resolved").map((item) => (
                  <div key={item.id} style={styles.adminCard}>
                    <strong style={styles.strongHeading}>{item.reportedUserDisplayName || item.reportedUserEmail}</strong>
                    <p style={styles.smallText}>Reporter: {item.reporterDisplayName || item.reporterEmail}</p>
                    <p style={styles.smallText}>Reason: {item.reason || "User reported"}</p>
                    <button style={styles.primaryButton} onClick={() => handleResolveUserReport(item.id)}>Resolve Report</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.adminSectionBlock}>
            <h3 style={styles.subsectionHeading}>AI Moderation Tools</h3>
            <p style={styles.smallText}>AI review analysis, improvement, and moderation callables remain connected in the review queues above.</p>
          </div>
        </div>
      </div>
    );
  };

  const ProfileTab = () => (
    <div style={styles.grid}>
      <div style={styles.card}>
        <SectionTitle
          title={t("myRentDuoProfile")}
          subtitle="Polish your public profile, keep your trust high, and look investor-ready."
        />

        <LanguageToggle
          language={language}
          onChange={handleLanguageChange}
          label={t("language")}
        />

        {!((profile?.displayName || "").trim() && (profile?.city || "").trim()) ? (
          <div style={{ display: "contents" }}>
            <label style={styles.label}>Role</label>
            <select
              style={styles.input}
              value={role}
              onChange={(e) => {
                const nextRole = e.target.value;
                setRole(nextRole);
                setProfileStatus(nextRole ? getDefaultProfileStatus(nextRole) : "");
              }}
              disabled={profile?.verificationStatus === "landlord_verified" || profile?.verificationStatus === "property_manager_verified"}
            >
              <option value="">Choose a role</option>
              <option value="tenant">Tenant</option>
              <option value="landlord">Landlord</option>
              <option value="property_manager">Property Manager</option>
            </select>

            <label style={styles.label}>Display Name</label>
            <input
              style={styles.input}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              disabled={profile?.verificationStatus === "landlord_verified" || profile?.verificationStatus === "property_manager_verified"}
            />

            <label style={styles.label}>City / State</label>
            <input
              ref={profileLocationInputRef}
              style={styles.input}
              value={city}
              onChange={(e) => {
                const nextCity = e.target.value;
                setCity(nextCity);
                const parts = nextCity.split(",").map((item) => item.trim()).filter(Boolean);
                setProfileLocation((prev) =>
                  buildProfileLocationPayload({
                    location: {
                      ...(prev || {}),
                      city: parts.length > 1 ? parts.slice(0, -1).join(", ") : nextCity,
                      state: parts.length > 1 ? parts[parts.length - 1] : prev?.state || "",
                      formattedAddress: nextCity,
                    },
                    fallbackCity: nextCity,
                    fallbackState: parts.length > 1 ? parts[parts.length - 1] : prev?.state || "",
                  })
                );
              }}
              placeholder="Start typing your city and state"
              autoComplete="off"
              disabled={profile?.verificationStatus === "landlord_verified" || profile?.verificationStatus === "property_manager_verified"}
            />
            <p style={styles.smallText}>
              Your public profile shows only your city/state. Your full address is private and only used in Verification Center.
            </p>

            <label style={styles.label}>Status Badge</label>
            <select
              style={styles.input}
              value={profileStatus}
              onChange={(e) => setProfileStatus(e.target.value)}
            >
              {getProfileStatusOptions(role).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={statusVisibility}
                onChange={(e) => setStatusVisibility(e.target.checked)}
              />
              Show my status publicly
            </label>

            <label style={styles.label}>{role === "tenant" ? "Preferred Areas" : "Service Areas"}</label>
            <input
              style={styles.input}
              value={role === "tenant" ? preferredAreasText : serviceAreasText}
              onChange={(e) =>
                role === "tenant"
                  ? setPreferredAreasText(e.target.value)
                  : setServiceAreasText(e.target.value)
              }
              placeholder={role === "tenant" ? "Newark, Jersey City, Hoboken" : "Newark, Elizabeth, Jersey City"}
            />
            <p style={styles.smallText}>
              Separate areas with commas so people can find you by location.
            </p>

            <label style={styles.label}>Bio</label>
            <textarea
              style={styles.textarea}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Short bio"
            />

            <label style={styles.label}>Profile Photo</label>
            <input
              style={styles.input}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
            />

            {profile?.verificationStatus === "landlord_verified" || profile?.verificationStatus === "property_manager_verified" ? (
              <p style={styles.smallText}>
                🔒 Verified profiles cannot change role, display name, or city/state.
              </p>
            ) : null}

            <button style={styles.primaryButton} onClick={handleSaveProfile}>
              Save Profile
            </button>
            <p style={{ ...styles.smallText, marginTop: 10 }}>
              Your profile only updates after you tap Save Profile.
            </p>
          </div>
        ) : (
          <div style={{ ...styles.verificationBox, marginBottom: 18 }}>
            <div style={styles.profileTitleRowWrap}>
              <strong style={styles.strongHeading}>Profile Setup Complete</strong>
              <VerificationStatusBadge status={getEffectiveVerificationStatus({ ...(profile || {}), verificationStatus, verified: isCurrentUserVerified })} />
            </div>
            <p style={styles.smallText}>
              {(displayName || profile?.displayName || "User")} • {((profileRoleForForm || "Role not selected").replace(/_/g, " "))} • {(city || profile?.city || "")}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
              <ProfileStatusBadge
                role={profileRoleForForm}
                status={profileStatus || profile?.profileStatus}
                isPublic={statusVisibility}
              />
            </div>
            {getPublicAreaSummary({
              role: profileRoleForForm,
              city: city || profile?.city || "",
              preferredAreasText,
              serviceAreasText,
            }) ? (
              <p style={styles.smallText}>
                {profileRoleForForm === "tenant" ? "Preferred areas" : "Service areas"}: {getPublicAreaSummary({
                  role: profileRoleForForm,
                  city: city || profile?.city || "",
                  preferredAreasText,
                  serviceAreasText,
                })}
              </p>
            ) : null}
            {bio || profile?.bio ? (
              <p style={styles.smallText}>Bio: {bio || profile?.bio}</p>
            ) : null}
          </div>
        )}

        <div style={{ ...styles.verificationBox, marginTop: 18, marginBottom: 18 }}>
          <div style={styles.profileTitleRowWrap}>
            <strong style={styles.strongHeading}>Discovery Settings</strong>
            <ProfileStatusBadge
              role={profileRoleForForm}
              status={profileStatus || profile?.profileStatus}
              isPublic={statusVisibility}
            />
          </div>
          <p style={styles.smallText}>
            Let people find you by area and see whether you are actively looking or have units available.
          </p>

          <label style={styles.label}>Status Badge</label>
          <select
            style={styles.input}
            value={profileStatus}
            onChange={(e) => setProfileStatus(e.target.value)}
          >
            {!profileRoleForForm ? <option value="">Choose a role first</option> : null}
            {profileRoleForForm ? getProfileStatusOptions(profileRoleForForm).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            )) : null}
          </select>

          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={statusVisibility}
              onChange={(e) => setStatusVisibility(e.target.checked)}
            />
            Show my status publicly
          </label>

          <label style={styles.label}>{profileRoleForForm === "tenant" ? "Preferred Areas" : "Service Areas"}</label>
          <input
            style={styles.input}
            value={profileRoleForForm === "tenant" ? preferredAreasText : serviceAreasText}
            onChange={(e) =>
              profileRoleForForm === "tenant"
                ? setPreferredAreasText(e.target.value)
                : setServiceAreasText(e.target.value)
            }
            placeholder={profileRoleForForm === "tenant" ? "Newark, Jersey City, Hoboken" : "Newark, Elizabeth, Jersey City"}
          />

          <button style={{ ...styles.primaryButton, marginTop: 14 }} onClick={handleSaveProfile}>
            Save Discovery Settings
          </button>
        </div>

        <RightsResources
          stateCode={getRightsStateCode(profile?.state, city, profile?.city)}
          defaultRole={profileRoleForForm}
          location={profile?.location || profileLocation}
          title={language === "es" ? "Conozca sus derechos" : "Know Your Rights"}
          language={language}
        />

        {false && isAdmin ? (
          <div style={{ ...styles.verificationBox, marginTop: 18, marginBottom: 18, background: "linear-gradient(135deg, rgba(15,111,255,0.08) 0%, rgba(16,185,129,0.08) 100%)", border: "1px solid rgba(15,111,255,0.12)" }}>
            <div style={{ ...styles.profileTitleRowWrap, justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <strong style={styles.strongHeading}>Growth Dashboard</strong>
                <span style={{ ...styles.smallText, fontWeight: 700, color: "#0f766e" }}>Founder View</span>
              </div>
              <button
                type="button"
                style={{
                  ...styles.secondaryButton,
                  marginTop: 0,
                  padding: "9px 12px",
                  width: isPhoneMobile ? "100%" : "auto",
                }}
                onClick={() => setShowGrowthBaselineModal(true)}
              >
                Reset Baseline
              </button>
            </div>
            <p style={styles.smallText}>
              Track your traction across users, reviews, messages, properties, engagement, and partner clicks.
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 10 }}>
              <span style={{ ...styles.smallText, fontWeight: 800, color: "#0f766e" }}>
                Tracking since: {showRawGrowthTotals ? "Raw totals" : growthTrackingSinceLabel}
              </span>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800, color: "#475569" }}>
                <input
                  type="checkbox"
                  checked={showRawGrowthTotals}
                  onChange={(event) => setShowRawGrowthTotals(event.target.checked)}
                />
                Show raw totals
              </label>
            </div>
            <div style={{ ...responsiveStatGridStyle, marginTop: 14, marginBottom: 0 }}>
              {dashboardStats.map((stat) => (
                <div
                  key={stat.label}
                  style={{
                    ...responsiveStatCardStyle,
                    background: stat.accent === "blue" ? "linear-gradient(135deg, rgba(15,111,255,0.12) 0%, rgba(96,165,250,0.08) 100%)" : "linear-gradient(135deg, rgba(16,185,129,0.14) 0%, rgba(52,211,153,0.08) 100%)",
                    border: stat.accent === "blue" ? "1px solid rgba(59,130,246,0.16)" : "1px solid rgba(16,185,129,0.18)",
                    boxShadow: "0 10px 20px rgba(15,23,42,0.06)",
                  }}
                >
                  <div style={{ ...responsiveStatLabelStyle, fontSize: 12, color: "#475569", textTransform: "uppercase" }}>
                    {isPhoneMobile && stat.shortLabel ? stat.shortLabel : stat.label}
                  </div>
                  <div style={{ fontSize: isMobile ? 24 : 28, fontWeight: 900, color: "#0f172a" }}>{stat.value}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div style={{ ...styles.verificationBox, marginTop: 18 }}>
          <div style={styles.profileTitleRowWrap}>
            <strong style={styles.strongHeading}>Referral Program</strong>
            <span style={styles.smallText}>Invites: {referralCount}</span>
          </div>
          <p style={styles.smallText}>
            Share your invite link. When someone signs up through it, their profile stores you as the referrer.
          </p>
          <label style={styles.label}>Your Referral Link</label>
          <input
            style={styles.input}
            value={referralLink}
            readOnly
            placeholder="Referral link will appear after login"
          />
          <button style={styles.secondaryButton} onClick={handleCopyReferralLink}>
            Copy Referral Link
          </button>
        </div>



        <div style={styles.legalSection}>
          <div style={styles.sectionTitle}>Legal</div>

          <button
            type="button"
            onClick={() => openExternal("https://rentduo.app/terms.html")}
            style={styles.legalButton}
          >
            📄 Terms of Service
          </button>

          <button
            type="button"
            onClick={() => openExternal("https://rentduo.app/privacy.html")}
            style={styles.legalButton}
          >
            🔒 Privacy Policy
          </button>

          <button
            type="button"
            onClick={() => openExternal("https://rentduo.app/delete-account.html")}
            style={styles.legalButton}
          >
            🗑️ Delete Account
          </button>
        </div>

        <div style={{ ...styles.verificationBox, marginTop: 18, border: "1px solid rgba(239,68,68,0.24)", background: "linear-gradient(135deg, rgba(254,242,242,0.96) 0%, rgba(255,255,255,0.98) 100%)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <strong style={{ ...styles.strongHeading, color: "#b91c1c", lineHeight: 1.2 }}>Delete Account</strong>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                alignSelf: "center",
                padding: "4px 9px",
                borderRadius: 999,
                background: "#fee2e2",
                border: "1px solid #fecaca",
                color: "#b91c1c",
                fontSize: 11,
                lineHeight: 1,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: 0.3,
                whiteSpace: "nowrap",
              }}
            >
              Permanent
            </span>
          </div>
          <p style={styles.smallText}>
            This permanently deletes your RentDuo account and removes related data. This cannot be undone.
          </p>
          <label style={styles.label}>Current Password</label>
          <input
            style={styles.input}
            type="password"
            value={deleteAccountPassword}
            onChange={(e) => setDeleteAccountPassword(e.target.value)}
            placeholder="Enter your password"
            autoComplete="current-password"
          />
          <label style={styles.label}>Type DELETE to confirm</label>
          <input
            style={styles.input}
            value={deleteAccountConfirmText}
            onChange={(e) => setDeleteAccountConfirmText(e.target.value)}
            placeholder="Type DELETE"
          />
          <button
            style={{
              ...styles.primaryButton,
              background: isDeletingAccount ? "#fca5a5" : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              boxShadow: "0 16px 32px rgba(239,68,68,0.24)",
              marginTop: 14,
            }}
            onClick={handleDeleteAccount}
            disabled={isDeletingAccount}
          >
            {isDeletingAccount ? "Deleting Account..." : "Delete Account"}
          </button>
        </div>
      </div>

      <div style={{ ...styles.card, marginBottom: isMobile ? 24 : 0 }}>
        <SectionTitle
          title="My Rating & Reviews"
          subtitle="This is what other users currently see on your profile."
        />

        <div style={{ ...styles.ratingHeroBox, flexWrap: "wrap", rowGap: 12 }}>
          <div style={styles.ratingHeroValue}>
            {myAverageRating ? myAverageRating.toFixed(1) : "0.0"}
          </div>
          <div>
            <div>{renderStars(Math.round(myAverageRating), false, null, 26)}</div>
            <div style={styles.smallText}>Your average rating</div>
          </div>
        </div>

        {renderTrustSummary(myTrustSnapshot, "Your Rent Score")}

        <div style={{ ...styles.rentScoreBanner, marginBottom: 12, ...(myRentScore.tone === "success" ? styles.rentScoreBannerSuccess : myRentScore.tone === "warning" ? styles.rentScoreBannerWarning : myRentScore.tone === "danger" ? styles.rentScoreBannerDanger : {}) }}>
          <span style={styles.rentScoreLabel}>Your Rent Score</span>
          <span style={styles.rentScoreValue}>{myRentScore.score} · {myRentScore.label}</span>
        </div>

        <div style={styles.reviewsSectionBox}>
          <div style={styles.reviewsSectionHeader}>
            <div>
              <div style={styles.reviewsSectionEyebrow}>Approved reviews visible on your profile: {myReceivedReviews.length}</div>
              <div style={styles.reviewsSectionTitle}>Reviews on your profile</div>
            </div>
            <div style={styles.reviewsSectionCount}>{myReceivedReviews.length}</div>
          </div>

          {myReceivedReviews.length === 0 ? (
            <div style={styles.emptyReviewsBox}>No reviews on your profile yet — complete your profile and ask someone you rented with to leave honest feedback.</div>
          ) : (
            <div
              style={{
                ...styles.profileReviewsScrollBox,
                maxHeight: "none",
                minHeight: 0,
                overflowY: "visible",
                paddingRight: 0,
                paddingBottom: 16,
              }}
            >
            {myReceivedReviews.map((item) => (
              <div key={item.id} style={styles.reviewCard}>
                <div style={styles.userHeader}>
                  <Avatar
                    src={item.reviewerPhotoURL}
                    name={item.reviewerDisplayName || item.reviewerEmail}
                    size={44}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.profileTitleRowWrap}>
                      <PublicProfileNameButton
                        userData={{
                          uid: item.reviewerUserId,
                          displayName: item.reviewerDisplayName || "Anonymous",
                          photoURL: item.reviewerPhotoURL,
                          verified: item.reviewerVerified,
                        }}
                        style={styles.strongHeading}
                      >
                        {item.reviewerDisplayName || "Anonymous"}
                      </PublicProfileNameButton>
                      {item.reviewerVerified ? <VerifiedBadge /> : null}
                    </div>
                    <div style={styles.smallText}>
                      {item.reviewerVerified ? "Verified Reviewer" : "Unverified Reviewer"}
                    </div>
                  </div>
                </div>

                <div style={{ margin: "10px 0" }}>
                  {renderStars(Number(item.rating || 0), false, null, 22)}
                </div>
                {item.isUpdatedReview ? (
                  <p style={styles.communityReviewTag}>Updated Review</p>
                ) : null}
                <TranslatableText text={item.text} label="Review" bodyStyle={styles.bodyCopy} compact />
                {item?.categoryRatings ? (
                  <div style={styles.reviewCategoryChipRow}>
                    {Object.entries(item.categoryRatings)
                      .filter(([, value]) => Number(value) > 0)
                      .slice(0, 4)
                      .map(([key, value]) => (
                        <span key={key} style={styles.reviewCategoryChip}>
                          {getCategoryLabel(item?.targetRole || "", key)} {Number(value).toFixed(0)}★
                        </span>
                      ))}
                  </div>
                ) : null}
                <div style={styles.reviewTimestamp}>
                  {item?.createdAt?.seconds
                    ? `${formatReviewDateTime(item.createdAt)}`
                    : "Recent review"}
                </div>

                {item?.response?.text ? (
                  <div style={styles.reviewReplyCard}>
                    <div style={styles.reviewReplyHeader}>
                      <span style={styles.reviewReplyBadge}>
                        Response from {item?.response?.responderRole === "property_manager" ? "Property Manager" : item?.response?.responderRole === "landlord" ? "Landlord" : "User"}
                      </span>
                      <span style={styles.reviewReplyName}>
                        {item?.response?.responderDisplayName || "Response posted"}
                      </span>
                    </div>
                    <div style={styles.reviewReplyText}>{item.response.text}</div>
                  </div>
                ) : null}

                {canReplyToReview(item) ? (
                  <div style={styles.reviewReplyComposer}>
                    <div style={styles.reviewReplyComposerTitle}>
                      {item?.response?.text ? "Update your response" : "Reply to this review"}
                    </div>
                    <div style={styles.replyMetaRow}>
                      <span style={styles.smallText}>Open a full reply window so typing stays stable.</span>
                      <div style={styles.replyButtonRow}>
                        <button
                          style={styles.secondaryButton}
                          onClick={() => openReplyModal(item)}
                        >
                          {item?.response?.text ? "Open Reply Window" : "Write Reply"}
                        </button>
                      </div>
                    </div>
                    {item?.response?.flaggedForReview || item?.response?.finalStatus === "pending" ? (
                      <div style={styles.replyWarningText}>This reply was flagged for admin review.</div>
                    ) : null}
                    {item?.response?.aiRiskLabel && item?.response?.aiRiskLabel !== "low" && item?.response?.aiRiskLabel !== "unknown" ? (
                      <div style={styles.replyWarningText}>AI risk: {item.response.aiRiskLabel}</div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      <div style={styles.card}>
        <SectionTitle
          title="Verification Center"
          subtitle="Submit private proof for admin review. AI helps summarize documents, but admins make final decisions."
        />

        <div style={styles.verificationBox}>
          <div style={styles.profileTitleRowWrap}>
            <strong style={styles.strongHeading}>Verification Status</strong>
            <VerificationStatusBadge status={getVerificationDisplayStatus().badgeStatus} />
          </div>
          <div style={{ ...styles.rentScoreBanner, ...(getVerificationDisplayStatus().tone === "success" ? styles.rentScoreBannerSuccess : getVerificationDisplayStatus().tone === "pending" ? styles.rentScoreBannerWarning : getVerificationDisplayStatus().tone === "danger" ? styles.rentScoreBannerDanger : {}) }}>
            <span style={styles.rentScoreLabel}>Status</span>
            <span style={styles.rentScoreValue}>{getVerificationDisplayStatus().label}</span>
          </div>
          <p style={styles.smallText}>
            Your ID details and proof documents are private. RentDuo uses them only for trust review, property claims, and verification decisions.
          </p>
          {myVerificationSubmissions.length ? (
            <div style={styles.list}>
              {myVerificationSubmissions.slice(0, 3).map((item) => (
                <div key={item.id} style={styles.adminCard}>
                  <div style={styles.profileTitleRowWrap}>
                    <strong style={styles.strongHeading}>{getVerificationTypeLabel(item.verificationType)}</strong>
                    <span style={styles.statusPill}>{item.adminStatus === "approved" ? "Verified" : item.adminStatus === "denied" ? "Denied" : "Pending verification"}</span>
                  </div>
                  <p style={styles.smallText}>Review support: {item.aiRiskLevel || "pending"} · Files: {item.uploadedFiles?.length || 0}</p>
                  {item.aiAnalysis?.summary ? <p style={styles.smallText}>Review summary: {item.aiAnalysis.summary}</p> : null}
                  {item.adminNotes ? <p style={styles.smallText}>Admin note: {item.adminNotes}</p> : null}
                </div>
              ))}
            </div>
          ) : (
            <p style={styles.smallText}>No verification submission yet.</p>
          )}
        </div>

        {false && (verificationStatus === "pending_review" || verificationDocs.idUrl || verificationDocs.deedUrl) ? (
          <div style={styles.verificationBox}>
            <div style={styles.profileTitleRowWrap}>
              <strong style={styles.strongHeading}>Verification Submitted</strong>
              <VerificationStatusBadge status={getEffectiveVerificationStatus({ ...(profile || {}), verificationStatus, verified: isCurrentUserVerified })} />
            </div>
            <p style={styles.smallText}>
              Your verification has been submitted. You can still open your uploaded documents below while admin reviews them.
            </p>
            {propertyClaimTarget ? (
              <div style={styles.mapSelectionBox}>
                <strong style={styles.strongHeading}>Property Claim Target</strong>
                <p style={styles.smallText}>
                  {propertyClaimTarget.title || "Property"} — {propertyClaimTarget.address || "No address"}
                </p>
              </div>
            ) : null}
            {verificationDocs.idUrl ? (
              <a href={verificationDocs.idUrl} target="_blank" rel="noreferrer" style={styles.link}>
                View uploaded ID
              </a>
            ) : null}
            {verificationDocs.deedUrl ? (
              <a href={verificationDocs.deedUrl} target="_blank" rel="noreferrer" style={styles.link}>
                View uploaded deed / management proof
              </a>
            ) : null}
          </div>
        ) : (
          <div style={styles.verificationBox}>
            <div style={styles.profileTitleRowWrap}>
              <strong style={styles.strongHeading}>Upload Proof</strong>
              <span style={styles.statusPill}>Private review</span>
            </div>

            <p style={styles.smallText}>
              {getVerificationTypeHelp(selectedVerificationType)}
            </p>

            <div style={styles.miniMetaGrid}>
              {[
                selectedVerificationType === "tenant_verification" ? ["Tenant verification", "Lease, proof of residence, utility bill, and optional ID/selfie."] : null,
                selectedVerificationType === "landlord_verification" ? ["Landlord verification", "Property ownership proof plus deed, tax record, or closing document."] : null,
                selectedVerificationType === "property_manager_verification" ? ["Property manager verification", "Management authorization proof or company/owner authorization letter."] : null,
                selectedVerificationType === "property_ownership_verification" ? ["Property ownership verification", "Ownership proof plus deed, tax record, or closing document."] : null,
              ].filter(Boolean).map(([title, copy]) => (
                <div key={title} style={styles.miniMetaPill}>
                  <strong>{title}</strong>
                  <span>{copy}</span>
                </div>
              ))}
            </div>

            <label style={styles.label}>Verification Type</label>
            <select style={styles.input} value={selectedVerificationType} onChange={(e) => setVerificationType(e.target.value)}>
              <option value="tenant_verification">Tenant verification</option>
              <option value="landlord_verification">Landlord verification</option>
              <option value="property_manager_verification">Property manager verification</option>
              <option value="property_ownership_verification">Property ownership verification</option>
            </select>
            <p style={styles.smallText}>Selected verification type: {selectedVerificationType}</p>

            {propertyClaimTarget ? (
              <div style={styles.mapSelectionBox}>
                <strong style={styles.strongHeading}>Property Claim Target</strong>
                <p style={styles.smallText}>
                  {propertyClaimTarget.title || "Property"} — {propertyClaimTarget.address || "No address"}
                </p>
                <p style={styles.smallText}>
                  Submit verification to request ownership claim for this property.
                </p>
              </div>
            ) : null}
            <label style={styles.label}>Private Address For Verification</label>
            <input
              ref={privateAddressInputRef}
              style={styles.input}
              value={privateAddress}
              onChange={(e) => setPrivateAddress(e.target.value)}
              placeholder="Used only for ID / verification review. Hidden from other users."
              autoComplete="off"
            />
            <p style={styles.smallText}>
              This address stays private and is only stored for verification and admin review.
            </p>

            <div style={styles.formGrid}>
              {getVerificationUploadFields(selectedVerificationType).map((field) => (
                <div key={`${selectedVerificationType}-${field.key}`}>
                  <label style={styles.label}>{field.label}</label>
                  <input
                    style={styles.input}
                    type="file"
                    accept=".pdf,image/*"
                    onChange={handleVerificationAuxFileChange(field.setter)}
                  />
                </div>
              ))}
            </div>

            <label style={styles.label}>Context for Your Submission</label>
            <textarea
              style={styles.textarea}
              value={verificationNote}
              onChange={(e) => setVerificationNote(e.target.value)}
              placeholder="Add helpful context for your verification or property claim. Do not include sensitive ID numbers."
            />

            <button
              type="button"
              style={{ ...styles.primaryButton, opacity: isSubmittingVerification ? 0.7 : 1 }}
              onClick={handleSubmitVerification}
              disabled={isSubmittingVerification}
            >
              {isSubmittingVerification ? "Submitting..." : "Submit Verification"}
            </button>
          </div>
        )}
      </div>

      {false && isAdmin ? (
        <div style={styles.cardWide}>
          <SectionTitle
            title="Admin Review Panel"
            subtitle="Verification workflow, review moderation, and user reports."
          />
          <p style={styles.smallText}>Admin account: {user.email}</p>

          <div style={styles.adminSectionBlock}>
            <h3 style={styles.subsectionHeading}>
              Pending Verification Submissions
            </h3>
            {pendingVerifications.length === 0 ? (
              <p style={styles.smallText}>
                No pending verification submissions.
              </p>
            ) : (
              <div style={styles.list}>
                {pendingVerifications.map((item) => (
                  <div key={item.uid} style={styles.adminCard}>
                    <div style={styles.userHeader}>
                      <Avatar
                        src={item.photoURL}
                        name={item.displayName || item.email}
                        size={60}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={styles.profileTitleRowWrap}>
                          <h3 style={styles.cardPersonName}>
                            {item.displayName || "Unnamed User"}
                          </h3>
                          <VerificationStatusBadge
                            status={getEffectiveVerificationStatus(item)}
                          />
                        </div>
                        <p style={styles.smallText}>{item.email}</p>
                        <p style={styles.smallText}>Role: {item.role || "N/A"}</p>
                        <p style={styles.smallText}>City: {item.city || "N/A"}</p>
                      </div>
                    </div>

                    {item.verificationNote ? (
                      <p style={styles.smallText}>
                        User Note: {item.verificationNote}
                      </p>
                    ) : null}

                    <div style={styles.adminDocsRow}>
                      {item.verificationDocs?.idUrl ? (
                        <a
                          href={item.verificationDocs.idUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={styles.linkPill}
                        >
                          Open ID
                        </a>
                      ) : null}
                      {item.verificationDocs?.deedUrl ? (
                        <a
                          href={item.verificationDocs.deedUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={styles.linkPill}
                        >
                          Open Deed / Management Proof
                        </a>
                      ) : null}
                    </div>

                    <label style={styles.label}>Admin Review Note</label>
                    <textarea
                      style={styles.textarea}
                      value={adminNoteDrafts[item.uid] || ""}
                      onChange={(e) =>
                        handleAdminNoteChange(item.uid, e.target.value)
                      }
                      placeholder="Add internal review note..."
                    />

                    <div style={styles.adminButtonGrid}>
                      <button
                        style={styles.primaryButton}
                        onClick={() =>
                          handleAdminUpdateVerification(item, "tenant_verified")
                        }
                      >
                        Approve Tenant
                      </button>
                      <button
                        style={styles.secondaryGreenButton}
                        onClick={() =>
                          handleAdminUpdateVerification(item, "landlord_verified")
                        }
                      >
                        Approve Landlord
                      </button>
                      <button
                        style={styles.secondaryPurpleButton}
                        onClick={() =>
                          handleAdminUpdateVerification(
                            item,
                            "property_manager_verified"
                          )
                        }
                      >
                        Approve Property Manager
                      </button>
                      <button
                        style={styles.rejectButton}
                        onClick={() =>
                          handleAdminUpdateVerification(item, "unverified")
                        }
                      >
                        Reject / Reset
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.adminSectionBlock}>
            <h3 style={styles.subsectionHeading}>Pending Reviews</h3>
            {pendingReviews.length === 0 ? (
              <p style={styles.smallText}>No pending reviews.</p>
            ) : (
              <div style={styles.list}>
                {pendingReviews.map((item) => (
                  <div key={item.id} style={styles.adminCard}>
                    <div style={styles.profileTitleRowWrap}>
                      <PublicProfileNameButton
                        userData={{
                          uid: item.reviewerUserId,
                          displayName: item.reviewerDisplayName || "Reviewer",
                          photoURL: item.reviewerPhotoURL,
                          verified: item.reviewerVerified,
                        }}
                        style={styles.strongHeading}
                      >
                        {item.reviewerDisplayName || "Reviewer"}
                      </PublicProfileNameButton>
                      {item.reviewerVerified ? <VerifiedBadge /> : null}
                    </div>
                    <p style={styles.smallText}>
                      For: <PublicProfileNameButton userData={{ uid: item.targetUserId, displayName: item.targetDisplayName || "User", verified: item.targetVerified }}>
                        {item.targetDisplayName || "User"}
                      </PublicProfileNameButton>
                    </p>
                    <div style={{ margin: "10px 0" }}>
                      {renderStars(Number(item.rating || 0))}
                    </div>
                    <TranslatableText text={item.text} label="Review" bodyStyle={styles.bodyCopy} compact />

                    <div style={styles.adminButtonGrid}>
                      <button
                        style={styles.primaryButton}
                        onClick={() => handleApproveReview(item.id)}
                      >
                        Approve Review
                      </button>
                      <button
                        style={styles.rejectButton}
                        onClick={() => handleRejectReview(item.id)}
                      >
                        Reject Review
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.adminSectionBlock}>
            <h3 style={styles.subsectionHeading}>Reported Reviews</h3>
            {reportedReviews.length === 0 ? (
              <p style={styles.smallText}>No reported reviews.</p>
            ) : (
              <div style={styles.list}>
                {reportedReviews.map((item) => (
                  <div key={item.id} style={styles.adminCard}>
                    <div style={styles.profileTitleRowWrap}>
                      <PublicProfileNameButton
                        userData={{
                          uid: item.reviewerUserId,
                          displayName: item.reviewerDisplayName || "Reviewer",
                          photoURL: item.reviewerPhotoURL,
                          verified: item.reviewerVerified,
                        }}
                        style={styles.strongHeading}
                      >
                        {item.reviewerDisplayName || "Reviewer"}
                      </PublicProfileNameButton>
                      {item.reviewerVerified ? <VerifiedBadge /> : null}
                    </div>
                    <p style={styles.smallText}>
                      For: <PublicProfileNameButton userData={{ uid: item.targetUserId, displayName: item.targetDisplayName || "User", verified: item.targetVerified }}>
                        {item.targetDisplayName || "User"}
                      </PublicProfileNameButton>
                    </p>
                    <div style={{ margin: "10px 0" }}>
                      {renderStars(Number(item.rating || 0))}
                    </div>
                    <TranslatableText text={item.text} label="Review" bodyStyle={styles.bodyCopy} compact />
                    {item?.disputeStatus === "open" || item?.status === "disputed" ? (
                      <div style={styles.disputedReviewNotice}>
                        Dispute reason: {item.disputeReason || "No reason provided"}
                        <br />Evidence status: {item.evidenceStatus || "not requested"}
                      </div>
                    ) : null}

                    <div style={styles.adminButtonGrid}>
                      <button
                        style={styles.primaryButton}
                        onClick={() => item?.disputeStatus === "open" || item?.status === "disputed" ? handleResolveDisputedReview(item, "keep", "reviews") : handleApproveReview(item.id)}
                      >
                        {item?.disputeStatus === "open" || item?.status === "disputed" ? "Resolve + Keep" : "Keep Review"}
                      </button>
                      <button
                        style={styles.secondaryButton}
                        onClick={() => handleRequestReviewEvidence(item, "reviews")}
                      >
                        Request Evidence
                      </button>
                      <button
                        style={styles.rejectButton}
                        onClick={() => item?.disputeStatus === "open" || item?.status === "disputed" ? handleResolveDisputedReview(item, "remove", "reviews") : handleRejectReview(item.id)}
                      >
                        {item?.disputeStatus === "open" || item?.status === "disputed" ? "Resolve + Remove" : "Remove Review"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.adminSectionBlock}>
            <h3 style={styles.subsectionHeading}>Reported Users</h3>
            {reportedUsers.filter((r) => r.status !== "resolved").length ===
            0 ? (
              <p style={styles.smallText}>No open user reports.</p>
            ) : (
              <div style={styles.list}>
                {reportedUsers
                  .filter((r) => r.status !== "resolved")
                  .map((item) => (
                    <div key={item.id} style={styles.adminCard}>
                      <strong style={styles.strongHeading}>
                        {item.reportedUserDisplayName || item.reportedUserEmail}
                      </strong>
                      <p style={styles.smallText}>
                        Reported user email: {item.reportedUserEmail}
                      </p>
                      <p style={styles.smallText}>
                        Reporter: {item.reporterDisplayName || item.reporterEmail}
                      </p>
                      <p style={styles.smallText}>
                        Reason: {item.reason || "User reported"}
                      </p>

                      <button
                        style={styles.primaryButton}
                        onClick={() => handleResolveUserReport(item.id)}
                      >
                        Resolve Report
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div style={styles.adminSectionBlock}>
            <h3 style={styles.subsectionHeading}>All Verification Profiles</h3>
            {allVerifications.length === 0 ? (
              <p style={styles.smallText}>No profiles yet.</p>
            ) : (
              <div style={styles.list}>
                {allVerifications.slice(0, 10).map((item) => (
                  <div key={item.uid} style={styles.userCard}>
                    <div style={styles.profileTitleRowWrap}>
                      <strong style={styles.strongHeading}>
                        {item.displayName || item.email || "User"}
                      </strong>
                      <VerificationStatusBadge
                        status={getEffectiveVerificationStatus(item)}
                      />
                    </div>
                    <p style={styles.smallText}>{item.email}</p>
                    <p style={styles.smallText}>Role: {item.role || "N/A"}</p>
                    {item.adminVerificationNote ? (
                      <p style={styles.smallText}>
                        Admin Note: {item.adminVerificationNote}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );

  if (loading || (user && (!profileHydrated || !activeTab))) {
    return (
      <div
        className="rentduo-app"
        style={{
          ...styles.page,
          ...(isPhoneMobile
            ? {
                padding: "0 10px calc(176px + env(safe-area-inset-bottom, 0px))",
              }
            : {}),
        }}
      >
        <div style={styles.viewportBackground} />
        <div style={styles.loadingShell}>
          <div style={styles.loadingCard}>Loading RentDuo...</div>
        </div>
      </div>
    );
  }

  if (!user && isPreviewMode) {
    return (
      <PreviewMode
        isPhoneMobile={isPhoneMobile}
        onCreateAccount={() => {
          setIsPreviewMode(false);
          switchAuthMode("signup");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        onSignIn={() => {
          setIsPreviewMode(false);
          switchAuthMode("login");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        onExit={() => {
          setIsPreviewMode(false);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    );
  }

  if (!user) {
    return (
      <div
        className="rentduo-app"
        style={{
          ...styles.page,
          ...(isPhoneMobile
            ? {
                padding: "0 10px calc(176px + env(safe-area-inset-bottom, 0px))",
              }
            : {}),
        }}
      >
        <div style={styles.viewportBackground} />
        <div style={styles.authShell}>
          <div style={styles.authHero}>
            <div style={styles.landingTopRow}>
              <div style={styles.brandPill}>Rental trust before you sign</div>
              <div style={styles.landingMiniLinks}>
                <a style={styles.landingMiniLink} href="/privacy.html" target="_blank" rel="noreferrer">Privacy</a>
                <a style={styles.landingMiniLink} href="/terms.html" target="_blank" rel="noreferrer">Terms</a>
                <a style={styles.landingMiniLink} href="/safety.html" target="_blank" rel="noreferrer">Safety</a>
              </div>
            </div>

            <h1 style={styles.brandLogo}>
              <span style={styles.brandRent}>Rent</span>
              <span style={styles.brandDuo}>Duo</span>
            </h1>

            <h2 style={styles.landingHeadline}>
              Know who you’re renting from — and who you’re renting to.
            </h2>

            <p style={styles.authHeroText}>
              RentDuo helps tenants, landlords, and property managers review rental
              experiences, verify profiles, view property history, and message with
              more confidence before problems happen.
            </p>

            <div style={styles.landingCtaRow}>
              <button
                type="button"
                style={styles.landingPrimaryCta}
                onClick={() => switchAuthMode("signup")}
              >
                {t("createFreeAccount")}
              </button>
              <button
                type="button"
                style={styles.landingSecondaryCta}
                onClick={() => switchAuthMode("login")}
              >
                {language === "es" ? "Ya tengo una cuenta" : "I Already Have an Account"}
              </button>
              <button
                type="button"
                style={styles.landingSecondaryCta}
                onClick={() => setIsPreviewMode(true)}
              >
                Explore Demo
              </button>
            </div>

            <div style={styles.landingStatsRow}>
              <div style={styles.landingStatCard}>
                <strong style={styles.landingStatNumber}>2-sided</strong>
                <span style={styles.landingStatLabel}>tenant + landlord reviews</span>
              </div>
              <div style={styles.landingStatCard}>
                <strong style={styles.landingStatNumber}>Map</strong>
                <span style={styles.landingStatLabel}>property history by address</span>
              </div>
              <div style={styles.landingStatCard}>
                <strong style={styles.landingStatNumber}>Verify</strong>
                <span style={styles.landingStatLabel}>profiles for stronger trust</span>
              </div>
            </div>

            <div style={styles.landingFeatureGrid}>
              <div style={styles.landingFeatureCard}>
                <div style={styles.landingFeatureIcon}>⭐</div>
                <strong style={styles.landingFeatureTitle}>Reviews that go both ways</strong>
                <p style={styles.landingFeatureText}>
                  Tenants can review landlords and properties. Landlords and property managers
                  can review rental experiences with tenants.
                </p>
              </div>

              <div style={styles.landingFeatureCard}>
                <div style={styles.landingFeatureIcon}>📍</div>
                <strong style={styles.landingFeatureTitle}>Property-level history</strong>
                <p style={styles.landingFeatureText}>
                  Search addresses, see property ratings, add community properties, and help
                  renters understand a home before applying.
                </p>
              </div>

              <div style={styles.landingFeatureCard}>
                <div style={styles.landingFeatureIcon}>🛡️</div>
                <strong style={styles.landingFeatureTitle}>Trust and safety built in</strong>
                <p style={styles.landingFeatureText}>
                  Verification, moderation, disclaimers, disputes, and safer review prompts
                  help reduce fake, abusive, or risky content.
                </p>
              </div>
            </div>

            <LanguageToggle
              language={language}
              onChange={handleLanguageChange}
              label={t("language")}
            />

            <div style={styles.landingTrustBar}>
              <span>✅ Verified-user signals</span>
              <span>🔒 Privacy-first profiles</span>
              <span>⚖️ Fair review guidelines</span>
            </div>
          </div>

          <form
            ref={authFormRef}
            style={styles.authCard}
            onSubmit={(event) => {
              event.preventDefault();
              handleAuth(false);
            }}
          >
            <div style={styles.authCardGlow} />
            <div style={styles.authCardInner}>
              <p style={styles.kicker}>{t("welcomeBackKicker")}</p>
              <h2 style={styles.authTitle}>{authMode === "signup" ? t("signUpTitle") : t("signInTitle")}</h2>
              <p style={styles.authSub}>
                {t("authSubtitle")}
              </p>

              <div style={styles.switchRow}>
                <button
                  type="button"
                  style={authMode === "login" ? styles.activeTabButton : styles.tabButton}
                  onClick={() => switchAuthMode("login")}
                >
                  {t("login")}
                </button>
                <button
                  type="button"
                  style={authMode === "signup" ? styles.activeTabButton : styles.tabButton}
                  onClick={() => switchAuthMode("signup")}
                >
                  {t("signUp")}
                </button>
              </div>

              <input
                style={styles.input}
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                inputMode="email"
              />

              <div style={{ ...styles.passwordFieldWrap, ...(isPhoneMobile ? styles.passwordFieldWrapMobile : {}) }}>
                <input
                  style={{
                    ...styles.input,
                    ...styles.passwordInput,
                    ...(isPhoneMobile ? styles.passwordInputMobile : {}),
                    marginTop: 0,
                  }}
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={authMode === "signup" ? "new-password" : "current-password"}
                />
                <button
                  type="button"
                  style={{ ...styles.passwordToggleButton, ...(isPhoneMobile ? styles.passwordToggleButtonMobile : {}) }}
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>

              <button type="submit" style={styles.primaryButton}>
                {authMode === "signup" ? t("createProfile") : t("continue")}
              </button>

              <button
                type="button"
                style={{ ...styles.secondaryButton, width: "100%", marginTop: 10 }}
                onClick={() => setIsPreviewMode(true)}
              >
                Explore Demo
              </button>

              {!!authErrorMessage && <p style={styles.errorText}>{authErrorMessage}</p>}
              {!!statusMessage && <p style={styles.message}>{statusMessage}</p>}

              <div style={styles.loginAiCard}>
                <div style={styles.loginAiHeader}>
                  <span style={styles.loginAiBadge}>RentDuo AI</span>
                  <strong>Account helper</strong>
                </div>
                <div style={styles.loginAiPromptGrid}>
                  {loginAiPrompts.map((prompt) => (
                    <button
                      key={prompt.key}
                      type="button"
                      style={{
                        ...styles.loginAiPromptButton,
                        ...(activeLoginAiPrompt === prompt.key ? styles.loginAiPromptButtonActive : {}),
                      }}
                      onClick={() => setActiveLoginAiPrompt((prev) => (prev === prompt.key ? "" : prompt.key))}
                    >
                      {prompt.question}
                    </button>
                  ))}
                </div>
                {activeLoginAiPrompt ? (
                  <div style={styles.loginAiAnswer}>
                    {loginAiPrompts.find((prompt) => prompt.key === activeLoginAiPrompt)?.answer}
                  </div>
                ) : null}
              </div>
            </div>
          </form>

          {showAuthDisclaimer ? (
            <div style={styles.fullscreenDisclaimer}>
              <div style={styles.fullscreenDisclaimerInner}>
                <div style={styles.disclaimerShell}>
                  <div style={styles.disclaimerTopGlow} />

                  <div style={styles.disclaimerHeaderBar}>
                    <div>
                      <p style={styles.disclaimerMiniLabel}>RentDuo</p>
                      <h1
                        style={{
                          ...styles.fullscreenDisclaimerTitle,
                          fontSize: isMobile ? 22 : styles.fullscreenDisclaimerTitle.fontSize,
                          marginBottom: 8,
                        }}
                      >
                        {t("disclaimerTitle")}
                      </h1>
                      <p style={styles.disclaimerHeaderText}>
                        {t("disclaimerReviewCarefully")}
                      </p>
                      <LanguageToggle
                        language={language}
                        onChange={handleLanguageChange}
                        label={t("language")}
                        compact
                      />
                    </div>
                  </div>

                  <div
                    ref={disclaimerScrollRef}
                    onScroll={handleDisclaimerScroll}
                    style={styles.fullscreenDisclaimerBody}
                  >
                    <p style={styles.fullscreenDisclaimerLead}>
                      {t("disclaimerLead")}
                    </p>

                    <div style={styles.disclaimerSection}>
                      <h3 style={styles.disclaimerSectionTitle}>{t("disclaimerNoAdvice")}</h3>
                      <p style={styles.fullscreenDisclaimerText}>
                        {t("disclaimerNoAdviceText")}
                      </p>
                    </div>

                    <div style={styles.disclaimerSection}>
                      <h3 style={styles.disclaimerSectionTitle}>{t("disclaimerUserContent")}</h3>
                      <p style={styles.fullscreenDisclaimerText}>
                        {t("disclaimerUserContentText")}
                      </p>
                    </div>

                    <div style={styles.disclaimerSection}>
                      <h3 style={styles.disclaimerSectionTitle}>{t("disclaimerNoGuarantee")}</h3>
                      <p style={styles.fullscreenDisclaimerText}>
                        {t("disclaimerNoGuaranteeText")}
                      </p>
                    </div>

                    <div style={styles.disclaimerSection}>
                      <h3 style={styles.disclaimerSectionTitle}>{t("disclaimerLiability")}</h3>
                      <p style={styles.fullscreenDisclaimerText}>
                        {t("disclaimerLiabilityText")}
                      </p>
                    </div>

                    <div style={styles.disclaimerSection}>
                      <h3 style={styles.disclaimerSectionTitle}>{t("disclaimerModeration")}</h3>
                      <p style={styles.fullscreenDisclaimerText}>
                        {t("disclaimerModerationText")}
                      </p>
                    </div>

                    <div style={styles.disclaimerSection}>
                      <h3 style={styles.disclaimerSectionTitle}>{t("disclaimerVerification")}</h3>
                      <p style={styles.fullscreenDisclaimerText}>
                        {t("disclaimerVerificationText")}
                      </p>
                    </div>

                    <div style={styles.disclaimerSection}>
                      <h3 style={styles.disclaimerSectionTitle}>{t("disclaimerFairUse")}</h3>
                      <p style={styles.fullscreenDisclaimerText}>
                        {t("disclaimerFairUseText")}
                      </p>
                    </div>

                    <div style={styles.disclaimerSection}>
                      <h3 style={styles.disclaimerSectionTitle}>{t("disclaimerExternalLinks")}</h3>
                      <p style={styles.fullscreenDisclaimerText}>
                        {t("disclaimerExternalLinksText")}
                      </p>
                    </div>

                    <div style={styles.disclaimerSection}>
                      <h3 style={styles.disclaimerSectionTitle}>{t("disclaimerTerms")}</h3>
                      <p style={styles.fullscreenDisclaimerText}>
                        {t("disclaimerTermsText")}
                      </p>
                    </div>

                    <div style={styles.disclaimerFooterNote}>
                      {t("disclaimerNotAffiliated")}
                    </div>
                  </div>

                  <div style={styles.fullscreenDisclaimerActions}>
                    <div style={styles.disclaimerScrollHint}>
                      {disclaimerHasScrolledToEnd
                        ? t("disclaimerAcceptReady")
                        : t("disclaimerReviewCarefully")}
                    </div>

                    <div
                      style={{
                        ...styles.disclaimerActionRow,
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <button
                        type="button"
                        style={{
                          ...styles.modalSecondaryButton,
                          ...styles.disclaimerActionButton,
                          width: isMobile ? "100%" : "auto",
                        }}
                        onClick={() => {
                          setShowAuthDisclaimer(false);
                          setPendingAuthAction(null);
                          setDisclaimerHasScrolledToEnd(false);
                        }}
                      >
                        {t("decline")}
                      </button>

                      <button
                        type="button"
                        style={{
                          ...styles.primaryButton,
                          ...styles.disclaimerActionButton,
                          width: isMobile ? "100%" : "auto",
                        }}
                        onClick={handleAcceptDisclaimer}
                      >
                        {t("acceptContinue")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="rentduo-app"
        style={{
          ...styles.page,
          ...(isPhoneMobile
            ? {
                padding: "0 10px calc(176px + env(safe-area-inset-bottom, 0px))",
              }
            : {}),
        }}
      >
        <div style={styles.viewportBackground} />
        <div style={styles.topGradient} />
        <div style={styles.container}>
          <div
            style={{
              ...styles.heroHeader,
              ...(isPhoneMobile
                ? {
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 12,
                    marginBottom: 16,
                  }
                : {}),
            }}
          >
            <div style={{ minWidth: 0, width: isPhoneMobile ? "100%" : "auto" }}>
              <h1 style={{ ...styles.brandLogo, ...(isPhoneMobile ? { margin: "10px 0 6px", fontSize: 42, letterSpacing: 0 } : {}) }}>
                <span style={styles.brandRent}>Rent</span>
                <span style={styles.brandDuo}>Duo</span>
              </h1>
              <p style={styles.heroSubtext}>
                {t("welcomeBack")} {profile?.displayName || user.email}
              </p>
            </div>

            <div
              style={{
                ...styles.heroActions,
                ...(isPhoneMobile
                  ? {
                      width: "100%",
                      justifyContent: "flex-end",
                      flexWrap: "wrap",
                    }
                  : {}),
              }}
            >
              <NotificationDropdown
                notifications={notifications}
                unreadNotificationCount={totalUnreadCount}
                showNotifications={showNotifications}
                setShowNotifications={setShowNotifications}
                handleNotificationClick={handleNotificationClick}
                markAllNotificationsAsRead={markAllNotificationsAsRead}
                isMobile={isMobile}
                styles={styles}
                language={language}
              />

              <button
                type="button"
                style={{
                  ...styles.aiHeaderButton,
                  ...(isPhoneMobile ? styles.aiHeaderButtonMobile : {}),
                }}
                onClick={() => setIsAiAssistantOpen(true)}
              >
                <span style={styles.aiHeaderButtonIcon}>AI</span>
                <span>{isPhoneMobile ? "Ask AI" : "RentDuo AI"}</span>
              </button>

              <button
                style={{
                  ...styles.logoutButton,
                  ...(isPhoneMobile ? { minHeight: 46, padding: "12px 14px", flexShrink: 0 } : {}),
                }}
                onClick={handleLogout}
              >
                {t("logout")}
              </button>
            </div>
          </div>

          {!!statusMessage && <div style={styles.banner}>{statusMessage}</div>}

          {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
            <div style={styles.warningBanner}>
              Google Maps API key is missing. Add
              REACT_APP_GOOGLE_MAPS_API_KEY to your .env file and restart the app.
            </div>
          )}

          {mapLoadError && (
            <div style={styles.warningBanner}>Google Maps failed to load.</div>
          )}

          <div
            style={{
              ...styles.topProfileCard,
              ...(isPhoneMobile
                ? {
                    alignItems: "flex-start",
                    padding: 18,
                    borderRadius: 28,
                    gap: 14,
                    marginBottom: 18,
                  }
                : {}),
            }}
          >
            <Avatar src={photoURL} name={displayName || user.email} size={92} />
            <div style={{ flex: 1, minWidth: 0, width: "100%", maxWidth: "100%" }}>
              <div style={styles.profileTitleRowWrap}>
                <h2 style={styles.profileHeroName}>
                  {displayName || t("completeProfile")}
                </h2>
                {profileHydrated ? (
                  <VerificationStatusBadge
                    status={getEffectiveVerificationStatus({ ...(profile || {}), verificationStatus, verified: isCurrentUserVerified })}
                  />
                ) : null}
              </div>
              <p style={styles.heroProfileMeta}>
                {getRoleLabel(role)} {city ? `· ${city}` : "· Add your city"}
              </p>
              <p style={styles.heroProfileBio}>
                {bio || (language === "es" ? "Agrega una biografia para que otros usuarios sepan quien eres." : "Add a bio so other users know who you are.")}
              </p>
            </div>
          </div>

          {profileCompletionPercent < 100 ? (
            <div style={{ ...styles.cardWide, marginBottom: 18, padding: isMobile ? 16 : 20, border: "1px solid rgba(253,224,71,0.65)", background: "linear-gradient(135deg, rgba(254,252,232,0.96), rgba(255,247,237,0.92))" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: 0.6, textTransform: "uppercase", color: "#b45309" }}>{t("buildTrust")}</div>
                  <div style={{ marginTop: 6, fontSize: isMobile ? 20 : 24, fontWeight: 950, color: "#0f172a" }}>{t("completeProfilePrompt")}</div>
                  <div style={{ marginTop: 6, color: "#475569", fontSize: 14, lineHeight: 1.55 }}>{t("completeProfileText")}</div>
                  <div style={{ marginTop: 12, height: 10, borderRadius: 999, background: "rgba(148,163,184,0.22)", overflow: "hidden" }}>
                    <div style={{ width: `${profileCompletionPercent}%`, height: "100%", borderRadius: 999, background: "linear-gradient(90deg, #0f6fff, #10b981)" }} />
                  </div>
                  <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {profileCompletionItems.map((item) => (
                      <span key={item.label} style={{ padding: "7px 10px", borderRadius: 999, fontSize: 12, fontWeight: 800, background: item.done ? "#ecfdf5" : "#fff7ed", color: item.done ? "#047857" : "#c2410c", border: item.done ? "1px solid #bbf7d0" : "1px solid #fed7aa" }}>
                        {item.done ? "✓" : "•"} {item.label}
                      </span>
                    ))}
                  </div>
                </div>
                <button type="button" onClick={() => setActiveTab("profile")} style={{ ...styles.primaryButton, marginTop: 0, width: isMobile ? "100%" : "auto" }}>{t("completeProfile")}</button>
              </div>
            </div>
          ) : null}

          <div style={{ ...styles.cardWide, marginBottom: 18, padding: isMobile ? 16 : 20, border: "1px solid rgba(191,219,254,0.9)", background: "linear-gradient(135deg, rgba(239,246,255,0.95), rgba(236,253,245,0.9))" }}>
            <div style={{ display: "flex", flexDirection: isPhoneMobile ? "column" : "row", alignItems: isPhoneMobile ? "stretch" : "center", justifyContent: isPhoneMobile ? "center" : "space-between", gap: isPhoneMobile ? 10 : 14, flexWrap: "wrap", ...phoneSectionSafe }}>
              <div style={{ flex: 1, minWidth: 0, width: "100%", maxWidth: "100%" }}>
                <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: 0.6, textTransform: "uppercase", color: "#0f6fff" }}>{t("startHere")}</div>
                <div style={{ marginTop: 6, fontSize: isMobile ? 22 : 26, fontWeight: 950, color: "#0f172a", lineHeight: 1.12 }}>{t("startQuestion")}</div>
                <div style={{ marginTop: 6, color: "#475569", fontSize: 14, lineHeight: 1.55 }}>{t("startText")}</div>
              </div>
              <div style={{ ...mobileCenteredRow, gap: 10, width: isMobile ? "100%" : "auto", marginBottom: 0 }}>
                <button type="button" onClick={() => setActiveTab("home")} style={{ ...styles.primaryButton, marginTop: 0, ...mobileButtonSafe }}>{t("findProperty")}</button>
                <button type="button" onClick={goToAddProperty} style={{ ...styles.secondaryButton, marginTop: 0, ...mobileButtonSafe }}>{t("addProperty")}</button>
                <button type="button" onClick={() => setActiveTab("reviews")} style={{ ...styles.secondaryButton, marginTop: 0, ...mobileButtonSafe }}>{t("leaveReview")}</button>
                <button type="button" onClick={handleShareRentDuo} style={{ ...styles.secondaryButton, marginTop: 0, ...mobileButtonSafe }}>{t("shareRentDuo")}</button>
              </div>
            </div>
          </div>

          <div style={styles.tabWrap}>
            <div style={{ ...styles.tabHeader, ...(isPhoneMobile ? { flexDirection: "row", alignItems: "stretch", justifyContent: "center", gap: 8, overflowX: "hidden" } : {}) }}>
              <button
                style={{
                  ...(activeTab === "profile" ? styles.topTabActive : styles.topTab),
                  ...(isPhoneMobile ? { flex: "1 1 calc(50% - 8px)", minWidth: 0, maxWidth: 160, textAlign: "center", transform: "none", boxSizing: "border-box", overflow: "hidden" } : {}),
                }}
                onClick={() => setActiveTab("profile")}
              >
                {t("profile")}
              </button>
              <button
                style={{ ...(activeTab === "home" ? styles.topTabActive : styles.topTab), ...(isPhoneMobile ? { flex: "1 1 calc(50% - 8px)", minWidth: 0, maxWidth: 160, textAlign: "center", transform: "none", boxSizing: "border-box", overflow: "hidden" } : {}) }}
                onClick={() => setActiveTab("home")}
              >
                {t("home")}
              </button>
              <button
                style={{ ...(activeTab === "feed" ? styles.topTabActive : styles.topTab), ...(isPhoneMobile ? { flex: "1 1 calc(50% - 8px)", minWidth: 0, maxWidth: 160, textAlign: "center", transform: "none", boxSizing: "border-box", overflow: "hidden" } : {}) }}
                onClick={() => setActiveTab("feed")}
              >
                {t("feed")}
              </button>
              {role === "property_manager" ? (
                <button
                  style={{ ...(activeTab === "manager" ? styles.topTabActive : styles.topTab), ...(isPhoneMobile ? { flex: "1 1 calc(50% - 8px)", minWidth: 0, maxWidth: 160, textAlign: "center", transform: "none", boxSizing: "border-box", overflow: "hidden" } : {}) }}
                  onClick={() => setActiveTab("manager")}
                >
                  {t("manager")}
                </button>
              ) : null}
              <button
                style={{
                  ...(activeTab === "reviews" ? styles.topTabActive : styles.topTab),
                  ...(isPhoneMobile ? { flex: "1 1 calc(50% - 8px)", minWidth: 0, maxWidth: 160, textAlign: "center", transform: "none", boxSizing: "border-box", overflow: "hidden" } : {}),
                }}
                onClick={() => setActiveTab("reviews")}
              >
                {t("reviews")}
              </button>
              <button
                style={{
                  ...(activeTab === "messages" ? styles.topTabActive : styles.topTab),
                  ...(isPhoneMobile ? { flex: "1 1 calc(50% - 8px)", minWidth: 0, maxWidth: 160, textAlign: "center", transform: "none", boxSizing: "border-box", overflow: "hidden" } : {}),
                }}
                onClick={() => setActiveTab("messages")}
              >
                {t("messages")}
              </button>
              <button
                style={{
                  ...(activeTab === "ai_center" ? styles.topTabActive : styles.topTab),
                  ...(isPhoneMobile ? { flex: "1 1 calc(50% - 8px)", minWidth: 0, maxWidth: 160, textAlign: "center", transform: "none", boxSizing: "border-box", overflow: "hidden" } : {}),
                }}
                onClick={() => setActiveTab("ai_center")}
              >
                AI Center
              </button>
              {isAdmin ? (
                <button
                  style={{
                    ...(activeTab === "admin" ? styles.topTabActive : styles.topTab),
                    ...(isPhoneMobile ? { flex: "1 1 calc(50% - 8px)", minWidth: 0, maxWidth: 160, textAlign: "center", transform: "none", boxSizing: "border-box", overflow: "hidden" } : {}),
                  }}
                  onClick={() => setActiveTab("admin")}
                >
                  Admin
                </button>
              ) : null}
            </div>

            {activeTab === "profile" && ProfileTab()}
            {activeTab === "home" && HomeTab()}
            {activeTab === "feed" && FeedTab()}
            {activeTab === "manager" && ManagerTab()}
            {activeTab === "reviews" && ReviewsTab()}
            {activeTab === "messages" && MessagesTab()}
            {activeTab === "ai_center" && AICenterTab()}
            {activeTab === "admin" && AdminTab()}
          </div>
        </div>

        <BottomNav />
      </div>

      <PublicProfileModal />

      {showGrowthBaselineModal ? (
        <div style={styles.replyModalOverlay} onClick={() => setShowGrowthBaselineModal(false)}>
          <div style={{ ...styles.replyModalCard, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.replyModalHeader}>
              <div>
                <div style={styles.replyModalEyebrow}>Founder analytics</div>
                <div style={styles.replyModalTitle}>Reset Growth Baseline</div>
              </div>
              <button
                type="button"
                style={styles.replyModalCloseButton}
                onClick={() => setShowGrowthBaselineModal(false)}
              >
                Close
              </button>
            </div>

            <p style={{ ...styles.bodyCopy, marginTop: 12 }}>
              This resets visible growth tracking to zero from today forward. No users, reviews,
              messages, or properties will be deleted.
            </p>

            <div style={{ ...responsiveStatGridStyle, marginTop: 14, marginBottom: 14 }}>
              {[
                { label: "Users", value: rawDashboardTotals.users },
                { label: "Reviews", value: rawDashboardTotals.reviews },
                { label: "Messages", value: rawDashboardTotals.messages },
                { label: "Properties", value: rawDashboardTotals.properties },
              ].map((item) => (
                <div key={item.label} style={{ ...responsiveStatCardStyle, minHeight: 88 }}>
                  <div style={responsiveStatLabelStyle}>{item.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>{item.value}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button
                type="button"
                style={{ ...styles.secondaryButton, marginTop: 0, width: isPhoneMobile ? "100%" : "auto" }}
                onClick={() => setShowGrowthBaselineModal(false)}
                disabled={isResettingGrowthBaseline}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ ...styles.primaryButton, marginTop: 0, width: isPhoneMobile ? "100%" : "auto" }}
                onClick={handleConfirmResetGrowthBaseline}
                disabled={isResettingGrowthBaseline}
              >
                {isResettingGrowthBaseline ? "Resetting..." : "Confirm Reset Baseline"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ReviewDetailModal
        activeReviewDetail={activeReviewDetail}
        setActiveReviewDetail={setActiveReviewDetail}
        PublicProfileNameButton={PublicProfileNameButton}
        renderStars={renderStars}
        formatReviewDateTime={formatReviewDateTime}
        openPropertyFromNotification={openPropertyFromNotification}
        openPublicProfile={openPublicProfile}
        styles={styles}
      />

      {showReplyModal && activeReplyReview ? (
        <div style={styles.replyModalOverlay} onClick={closeReplyModal}>
          <div style={styles.replyModalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.replyModalHeader}>
              <div>
                <div style={styles.replyModalEyebrow}>Review response</div>
                <div style={styles.replyModalTitle}>Reply to this review</div>
              </div>
              <button style={styles.replyModalCloseButton} onClick={closeReplyModal}>
                Close
              </button>
            </div>

            <div style={styles.replyModalReviewCard}>
              <div style={styles.reviewHeaderRow}>
                <div>
                  <strong>{activeReplyReview?.reviewerDisplayName || activeReplyReview?.reviewerEmail || "Reviewer"}</strong>
                  <div style={styles.smallText}>
                    {activeReplyReview?.reviewerRole === "property_manager"
                      ? "Property Manager"
                      : activeReplyReview?.reviewerRole === "landlord"
                      ? "Landlord"
                      : "Tenant"}
                  </div>
                </div>
                <div style={styles.starText}>{renderStars(Number(activeReplyReview?.rating || 0))}</div>
              </div>
              {activeReplyReview?.text ? (
                <TranslatableText text={activeReplyReview.text} label="Review" bodyStyle={styles.bodyCopy} compact />
              ) : (
                <div style={styles.smallText}>{t("starOnlyRating")}</div>
              )}
            </div>

            {activeReplyReview?.response?.text ? (
              <div style={styles.reviewReplyCard}>
                <div style={styles.reviewReplyHeader}>
                  <span style={styles.reviewReplyBadge}>Current response</span>
                  <span style={styles.reviewReplyName}>{activeReplyReview?.response?.responderDisplayName || "Response posted"}</span>
                </div>
                <div style={styles.reviewReplyText}>{activeReplyReview.response.text}</div>
              </div>
            ) : null}

            <div style={styles.reviewReplyComposer}>
              <div style={styles.reviewReplyComposerTitle}>
                {activeReplyReview?.response?.text ? "Update your response" : "Write your response"}
              </div>
              <textarea
                style={styles.replyModalTextarea}
                value={reviewReplyDrafts[activeReplyReview.id] ?? activeReplyReview?.response?.text ?? ""}
                onChange={(e) => handleReviewReplyDraftChange(activeReplyReview.id, e.target.value)}
                placeholder="Write one professional response. Keep it short and factual."
                maxLength={500}
                autoFocus
              />
              <div style={styles.replyMetaRow}>
                <span style={styles.smallText}>{String(reviewReplyDrafts[activeReplyReview.id] ?? activeReplyReview?.response?.text ?? "").length}/500</span>
                <div style={styles.replyToneRow}>
                  {["professional", "friendly", "firm", "short"].map((tone) => (
                    <button
                      key={tone}
                      style={aiTone === tone ? styles.replyToneButtonActive : styles.replyToneButton}
                      onClick={() => handleImproveReviewReply(activeReplyReview, tone)}
                      disabled={reviewReplyImprovingId === activeReplyReview.id || reviewReplySubmittingId === activeReplyReview.id}
                    >
                      {tone.charAt(0).toUpperCase() + tone.slice(1)}
                    </button>
                  ))}
                </div>
                <div style={styles.replyButtonRow}>
                  <button
                    style={styles.secondaryButton}
                    onClick={() => handleImproveReviewReply(activeReplyReview, aiTone || "professional")}
                    disabled={reviewReplyImprovingId === activeReplyReview.id || reviewReplySubmittingId === activeReplyReview.id}
                  >
                    {reviewReplyImprovingId === activeReplyReview.id ? "Improving..." : "Make Professional"}
                  </button>
                  <button
                    style={styles.secondaryButton}
                    onClick={() => handleSubmitReviewReply(activeReplyReview)}
                    disabled={reviewReplySubmittingId === activeReplyReview.id || reviewReplyImprovingId === activeReplyReview.id}
                  >
                    {reviewReplySubmittingId === activeReplyReview.id ? "Saving..." : activeReplyReview?.response?.text ? "Update Reply" : "Post Reply"}
                  </button>
                </div>
              </div>
              {activeReplyReview?.response?.flaggedForReview || activeReplyReview?.response?.finalStatus === "pending" ? (
                <div style={styles.replyWarningText}>This reply was flagged for admin review.</div>
              ) : null}
              {activeReplyReview?.response?.aiRiskLabel && activeReplyReview?.response?.aiRiskLabel !== "low" && activeReplyReview?.response?.aiRiskLabel !== "unknown" ? (
                <div style={styles.replyWarningText}>AI risk: {activeReplyReview.response.aiRiskLabel}</div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {renderRentDuoAIAssistant()}
    </>
  );
}

const styles = {
  publicProfileOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1300,
    background: "rgba(15,23,42,0.55)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 0,
    boxSizing: "border-box",
    overflow: "hidden",
  },
  publicProfileModal: {
    width: "min(900px, 100%)",
    background: "#f5f7fb",
    color: "#0f172a",
    overflowY: "auto",
    overflowX: "hidden",
    boxSizing: "border-box",
    padding: "0 14px 22px",
    boxShadow: "0 28px 80px rgba(15,23,42,0.32)",
    scrollPaddingTop: 0,
  },
  publicProfileTopBar: {
    position: "sticky",
    top: 0,
    zIndex: 3,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "12px 0",
    background: "rgba(248,250,252,0.96)",
    backdropFilter: "blur(10px)",
  },
  publicProfileHero: {
    width: "100%",
    maxWidth: "100%",
    borderRadius: 24,
    background: "#ffffff",
    border: "1px solid rgba(226,232,240,0.95)",
    boxShadow: "0 16px 36px rgba(15,23,42,0.08)",
    overflow: "hidden",
    marginBottom: 12,
    boxSizing: "border-box",
  },
  publicProfileCover: {
    height: 156,
    background:
      "linear-gradient(135deg, rgba(15,111,255,0.95) 0%, rgba(16,185,129,0.86) 52%, rgba(15,23,42,0.92) 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: 20,
    boxSizing: "border-box",
    color: "rgba(255,255,255,0.26)",
    fontSize: 96,
    fontWeight: 900,
    lineHeight: 1,
  },
  publicProfileIdentityRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: 14,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    padding: "0 18px 18px",
    marginTop: -38,
  },
  publicProfileAvatarWrap: {
    width: 120,
    height: 120,
    borderRadius: "50%",
    border: "5px solid #ffffff",
    background: "#ffffff",
    boxShadow: "0 12px 28px rgba(15,23,42,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  publicProfileIdentityText: {
    flex: 1,
    minWidth: 0,
    paddingTop: 42,
  },
  publicProfileName: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.1,
    fontWeight: 900,
    color: "#0f172a",
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "break-word",
    writingMode: "horizontal-tb",
  },
  publicProfileMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },
  publicProfileStatusPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 34,
    marginTop: 10,
    padding: "8px 12px",
    borderRadius: 999,
    background: "#ecfdf5",
    color: "#047857",
    fontSize: 13,
    fontWeight: 900,
    boxSizing: "border-box",
    maxWidth: "100%",
    whiteSpace: "normal",
    textAlign: "center",
  },
  publicProfileActionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    width: "100%",
    margin: "12px 0 14px",
  },
  publicProfileTabs: {
    display: "flex",
    flexWrap: "nowrap",
    justifyContent: "space-between",
    gap: 6,
    width: "100%",
    marginBottom: 14,
    padding: 4,
    borderRadius: 999,
    background: "#ffffff",
    border: "1px solid rgba(226,232,240,0.95)",
    boxSizing: "border-box",
    overflowX: "auto",
  },
  publicProfileTabButton: {
    borderRadius: 999,
    border: "1px solid transparent",
    background: "transparent",
    color: "#475569",
    fontWeight: 900,
    padding: "10px 12px",
    cursor: "pointer",
    boxSizing: "border-box",
    minHeight: 44,
    flex: "1 0 auto",
    textAlign: "center",
  },
  publicProfileTabActive: {
    borderRadius: 999,
    border: "1px solid #0f6fff",
    background: "#eff6ff",
    color: "#0f6fff",
    fontWeight: 900,
    padding: "10px 12px",
    cursor: "pointer",
    boxSizing: "border-box",
    minHeight: 44,
    flex: "1 0 auto",
    textAlign: "center",
  },
  publicProfileSection: {
    display: "grid",
    gap: 12,
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  },
  publicProfileStatGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
    width: "100%",
    marginBottom: 12,
  },
  publicProfileStatCard: {
    borderRadius: 18,
    border: "1px solid rgba(226,232,240,0.95)",
    background: "#ffffff",
    padding: 16,
    minHeight: 110,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    boxSizing: "border-box",
    boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "normal",
    hyphens: "none",
    lineHeight: 1.2,
  },
  publicProfileListCard: {
    borderRadius: 18,
    border: "1px solid rgba(226,232,240,0.95)",
    background: "#ffffff",
    padding: 14,
    boxSizing: "border-box",
    overflow: "hidden",
    width: "100%",
    minWidth: 0,
  },
  publicProfileEmpty: {
    borderRadius: 18,
    border: "1px dashed #cbd5e1",
    background: "#ffffff",
    color: "#64748b",
    padding: 16,
    textAlign: "center",
    fontWeight: 800,
  },
  publicProfilePropertyButton: {
    borderRadius: 18,
    border: "1px solid rgba(226,232,240,0.95)",
    background: "#ffffff",
    padding: 14,
    textAlign: "left",
    display: "grid",
    gap: 6,
    cursor: "pointer",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    color: "#0f172a",
  },
  publicProfileLinkButton: {
    border: 0,
    background: "transparent",
    color: "inherit",
    padding: 0,
    margin: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "visible",
    display: "inline",
    position: "relative",
    transform: "none",
    font: "inherit",
    fontWeight: 900,
    textAlign: "left",
    cursor: "pointer",
    textDecoration: "underline",
    textDecorationColor: "rgba(15,111,255,0.35)",
    textUnderlineOffset: 3,
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "break-word",
  },
  propertyTrustStrip: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
    gap: 10,
    width: "100%",
    margin: "14px 0",
    boxSizing: "border-box",
  },
  propertyTrustCard: {
    borderRadius: 16,
    border: "1px solid rgba(191,219,254,0.95)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    padding: 12,
    display: "grid",
    gap: 4,
    minWidth: 0,
    boxSizing: "border-box",
  },
  propertyHistoryCard: {
    borderRadius: 18,
    border: "1px solid rgba(226,232,240,0.95)",
    background: "#ffffff",
    padding: 14,
    margin: "14px 0",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  aiRiskCard: {
    borderRadius: 18,
    border: "1px solid rgba(15,111,255,0.18)",
    background: "linear-gradient(135deg, rgba(239,246,255,0.98), rgba(236,253,245,0.92))",
    padding: 14,
    margin: "14px 0",
    display: "grid",
    gap: 10,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflow: "hidden",
  },
  aiRiskLow: {
    background: "#dcfce7",
    color: "#166534",
    border: "1px solid #86efac",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
  },
  aiRiskModerate: {
    background: "#fef3c7",
    color: "#92400e",
    border: "1px solid #fde68a",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
  },
  aiRiskHigh: {
    background: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
  },
  aiRiskScoreRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 6,
  },
  aiRiskScoreValue: {
    color: "#0f172a",
    fontSize: 30,
    lineHeight: 1,
  },
  aiRiskCategoryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
    width: "100%",
    minWidth: 0,
  },
  aiRiskCategoryCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 6,
    background: "#ffffff",
    border: "1px solid rgba(191,219,254,0.85)",
    borderRadius: 12,
    padding: "9px 10px",
    color: "#475569",
    fontSize: 12,
    fontWeight: 800,
    minWidth: 0,
    boxSizing: "border-box",
  },
  aiRiskDisclaimer: {
    margin: 0,
    color: "#64748b",
    fontSize: 11,
    lineHeight: 1.5,
  },
  aiMaintenanceCard: {
    marginTop: 10,
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(20,184,166,0.2)",
    background: "#f0fdfa",
    display: "grid",
    gap: 6,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  propertyTimelineList: {
    display: "grid",
    gap: 10,
    marginTop: 12,
  },
  propertyTimelineItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    padding: "10px 0",
    borderTop: "1px solid rgba(226,232,240,0.9)",
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.4,
  },
  rentScoreBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 16,
    border: "1px solid rgba(148, 163, 184, 0.22)",
    background: "#f8fafc",
    marginTop: 10,
  },
  rentScoreBannerSuccess: {
    background: "#ecfdf5",
    border: "1px solid #86efac",
  },
  rentScoreBannerWarning: {
    background: "#fff7ed",
    border: "1px solid #fdba74",
  },
  rentScoreBannerDanger: {
    background: "#fef2f2",
    border: "1px solid #fca5a5",
  },
  rentScoreLabel: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "#334155",
  },
  rentScoreValue: {
    fontSize: 15,
    fontWeight: 800,
    color: "#0f172a",
  },
  reviewCategoryChipRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 10,
  },
  reviewCategoryChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "6px 10px",
    borderRadius: 999,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontSize: 12,
    fontWeight: 700,
  },
  notificationWrap: {
    position: "relative",
    maxWidth: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  notificationBellButton: {
    width: 46,
    height: 46,
    borderRadius: 999,
    border: "1px solid rgba(15, 111, 255, 0.18)",
    background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(236,253,245,0.98) 100%)",
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
    cursor: "pointer",
    color: "#0f6fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    lineHeight: 1,
    boxSizing: "border-box",
    flexShrink: 0,
  },
  notificationBadge: {
    position: "absolute",
    top: -8,
    right: -8,
    minWidth: 20,
    height: 20,
    borderRadius: 999,
    background: "#ef4444",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 900,
    padding: "0 5px",
    boxShadow: "0 10px 24px rgba(239, 68, 68, 0.35)",
    border: "2px solid #fff",
    zIndex: 6,
    pointerEvents: "none",
    lineHeight: 1,
    boxSizing: "border-box",
  },
  notificationBackdrop: {
    position: "fixed",
    inset: 0,
    background: "transparent",
    zIndex: 9998,
  },
  notificationBackdropDesktop: {
    position: "fixed",
    inset: 0,
    background: "transparent",
    zIndex: 9998,
  },
  notificationDropdown: {
    overflowY: "auto",
    overflowX: "hidden",
    maxWidth: "calc(100vw - 24px)",
    minWidth: 0,
    whiteSpace: "normal",
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
    background: "#ffffff",
    borderRadius: 20,
    border: "1px solid rgba(148, 163, 184, 0.22)",
    boxShadow: "0 25px 60px rgba(15, 23, 42, 0.18)",
    padding: 12,
    zIndex: 9999,
  },
  notificationDropdownDesktop: {
    position: "fixed",
    top: 78,
    right: 24,
    width: "min(420px, calc(100vw - 32px))",
    maxHeight: "65vh",
  },
  notificationDropdownMobile: {
    position: "fixed",
    top: 72,
    left: 10,
    right: 10,
    transform: "none",
    width: "auto",
    minWidth: 0,
    maxWidth: "calc(100vw - 20px)",
    maxHeight: "calc(100dvh - 96px)",
    borderRadius: 16,
  },
  notificationDropdownHeader: {
    fontSize: 16,
    fontWeight: 800,
    color: "#0f172a",
    padding: "8px 10px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  notificationMarkAllButton: {
    border: 0,
    background: "transparent",
    color: "#0f6fff",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    padding: "6px 0",
    whiteSpace: "nowrap",
  },
  notificationItem: {
    width: "100%",
    minWidth: 0,
    textAlign: "left",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    border: "1px solid transparent",
    borderRadius: 18,
    padding: 14,
    marginBottom: 10,
    cursor: "pointer",
    background: "#fff",
  },
  notificationItemUnread: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
  },
  notificationItemRead: {
    background: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.9)",
  },
  notificationItemTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: 6,
    overflowWrap: "anywhere",
  },
  notificationItemBody: {
    fontSize: 14,
    color: "#475569",
    lineHeight: 1.55,
    overflowWrap: "anywhere",
  },
  notificationEmpty: {
    padding: 16,
    color: "#64748b",
    fontSize: 15,
  },
  replyModalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.6)",
    zIndex: 99999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    overflowY: "auto",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
  },
  replyModalCard: {
    width: "100%",
    maxWidth: 720,
    maxHeight: "calc(100vh - 32px)",
    overflowY: "auto",
    background: "#ffffff",
    borderRadius: 26,
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.24)",
    border: "1px solid rgba(148, 163, 184, 0.22)",
    padding: 20,
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
  },
  replyModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  replyModalEyebrow: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: "#0f6fff",
    marginBottom: 4,
  },
  replyModalTitle: {
    fontSize: 26,
    fontWeight: 800,
    color: "#0f172a",
  },
  replyModalCloseButton: {
    border: "none",
    borderRadius: 999,
    padding: "10px 14px",
    background: "#e2e8f0",
    color: "#0f172a",
    cursor: "pointer",
    fontWeight: 700,
  },
  replyModalReviewCard: {
    border: "1px solid #dbeafe",
    background: "#f8fbff",
    borderRadius: 18,
    padding: 16,
    marginBottom: 16,
  },
  replyModalTextarea: {
    width: "100%",
    minHeight: 180,
    resize: "vertical",
    borderRadius: 18,
    border: "1px solid #cbd5e1",
    padding: 14,
    background: "#fff",
    color: "#0f172a",
  },
  replyToneRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  replyToneButton: {
    border: "1px solid #dbeafe",
    background: "#ffffff",
    color: "#0f6fff",
    padding: "8px 12px",
    borderRadius: 999,
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 12,
  },
  replyToneButtonActive: {
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    color: "#1d4ed8",
    padding: "8px 12px",
    borderRadius: 999,
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 12,
  },

  page: {
    minHeight: "100dvh",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflowX: "hidden",
    overscrollBehaviorX: "none",
    touchAction: "pan-y pinch-zoom",
    background:
      "radial-gradient(circle at top left, rgba(15,111,255,0.12), transparent 28%), radial-gradient(circle at top right, rgba(20,184,106,0.10), transparent 26%), linear-gradient(180deg, #f8fbff 0%, #f6fbf8 55%, #f8fafc 100%)",
    padding: "0 16px calc(96px + env(safe-area-inset-bottom, 0px))",
    marginTop: 0,
    fontFamily: "Inter, Arial, sans-serif",
    color: "#0f172a",
    position: "relative",
    isolation: "isolate",
    overflowX: "hidden",
    overflowY: "visible",
    WebkitOverflowScrolling: "touch",
  },
  viewportBackground: {
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100dvh",
    background:
      "linear-gradient(180deg, #f8fbff 0%, #f6fbf8 55%, #f8fafc 100%)",
    pointerEvents: "none",
    zIndex: -2,
  },
  topGradient: {
    position: "absolute",
    inset: "-72px 0 auto 0",
    width: "100%",
    height: 220,
    background:
      "linear-gradient(135deg, rgba(15,111,255,0.14) 0%, rgba(20,184,106,0.12) 100%)",
    filter: "blur(60px)",
    pointerEvents: "none",
    zIndex: -1,
  },
  container: {
    maxWidth: 1280,
    width: "100%",
    margin: "0 auto",
    position: "relative",
    zIndex: 1,
    boxSizing: "border-box",
    overflowX: "hidden",
    overscrollBehaviorX: "none",
    paddingBottom: 12,
  },
  loadingShell: {
    minHeight: "100dvh",
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loadingCard: {
    background: "rgba(255,255,255,0.86)",
    backdropFilter: "blur(12px)",
    padding: 28,
    borderRadius: 26,
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.10)",
    border: "1px solid rgba(255,255,255,0.8)",
    fontSize: 26,
    fontWeight: 800,
  },
  authShell: {
    minHeight: "100dvh",
    position: "relative",
    zIndex: 1,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 24,
    alignItems: "center",
    maxWidth: 1120,
    width: "100%",
    margin: "0 auto",
    paddingTop: 0,
    boxSizing: "border-box",
  
    paddingTop: 0,
  },
  authHero: {
    paddingTop: 6,
    marginTop: 0,
    padding: 26,
  },
  brandPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 14px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.86)",
    border: "1px solid rgba(226,232,240,0.95)",
    boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
    fontSize: 12,
    fontWeight: 800,
    color: "#334155",
    letterSpacing: 0.3,
  },
  brandLogo: {
    margin: "18px 0 10px",
    fontSize: "clamp(42px, 6vw, 68px)",
    lineHeight: 0.95,
    fontWeight: 900,
    letterSpacing: -1.6,
  },
  brandRent: { color: "#1e40af" },
  brandDuo: { color: "#14b8a6" },
  authHeroText: {
    maxWidth: 520,
    fontSize: 18,
    lineHeight: 1.65,
    color: "#475569",
    margin: 0,
  },
  landingTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 8,
  },
  landingMiniLinks: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  landingMiniLink: {
    color: "#0f6fff",
    fontSize: 13,
    fontWeight: 800,
    textDecoration: "none",
    padding: "7px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.72)",
    border: "1px solid rgba(191,219,254,0.9)",
  },
  landingHeadline: {
    margin: "0 0 14px",
    maxWidth: 680,
    fontSize: "clamp(30px, 4.8vw, 54px)",
    lineHeight: 1.02,
    letterSpacing: -1.2,
    color: "#0f172a",
    fontWeight: 950,
  },
  landingCtaRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 22,
    marginBottom: 20,
  },
  landingPrimaryCta: {
    border: "none",
    borderRadius: 18,
    padding: "14px 18px",
    background: "linear-gradient(135deg, #0f6fff 0%, #14b86a 100%)",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 18px 34px rgba(15,111,255,0.22)",
  },
  landingSecondaryCta: {
    border: "1px solid rgba(148,163,184,0.34)",
    borderRadius: 18,
    padding: "14px 18px",
    background: "rgba(255,255,255,0.88)",
    color: "#0f172a",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 12px 26px rgba(15,23,42,0.07)",
  },
  landingStatsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: 10,
    marginTop: 8,
    marginBottom: 18,
  },
  landingStatCard: {
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(226,232,240,0.9)",
    borderRadius: 18,
    padding: 14,
    boxShadow: "0 12px 28px rgba(15,23,42,0.06)",
  },
  landingStatNumber: {
    display: "block",
    color: "#0f172a",
    fontSize: 18,
    fontWeight: 950,
    marginBottom: 3,
  },
  landingStatLabel: {
    display: "block",
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.35,
    fontWeight: 700,
  },
  landingFeatureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginTop: 14,
  },
  landingFeatureCard: {
    background: "rgba(255,255,255,0.82)",
    border: "1px solid rgba(226,232,240,0.9)",
    borderRadius: 22,
    padding: 16,
    boxShadow: "0 14px 34px rgba(15,23,42,0.07)",
  },
  landingFeatureIcon: {
    width: 38,
    height: 38,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(15,111,255,0.12), rgba(20,184,106,0.12))",
    marginBottom: 10,
    fontSize: 19,
  },
  landingFeatureTitle: {
    display: "block",
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 900,
    marginBottom: 6,
  },
  landingFeatureText: {
    margin: 0,
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.5,
  },
  landingTrustBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 16,
    color: "#334155",
    fontSize: 13,
    fontWeight: 800,
  },
  authCard: {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    background: "rgba(255,255,255,0.88)",
    backdropFilter: "blur(22px)",
    borderRadius: 32,
    border: "1px solid rgba(226,232,240,0.92)",
    boxShadow: "0 28px 80px rgba(15,23,42,0.12)",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  authCardGlow: {
    position: "absolute",
    inset: "auto -100px -100px auto",
    width: 240,
    height: 240,
    borderRadius: "50%",
    background: "rgba(20,184,106,0.10)",
    filter: "blur(30px)",
    pointerEvents: "none",
  },
  authCardInner: {
    position: "relative",
    padding: 32,
  },
  kicker: {
    margin: 0,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: "#0f6fff",
  },
  authTitle: {
    margin: "8px 0 8px",
    fontSize: 30,
    lineHeight: 1.1,
    fontWeight: 900,
    color: "#0f172a",
  },
  authSub: {
    margin: "0 0 20px",
    color: "#64748b",
    lineHeight: 1.5,
  },
  switchRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 16,
  },
  tabButton: {
    border: "1px solid #dbe4f0",
    background: "#fff",
    color: "#475569",
    padding: "13px 16px",
    borderRadius: 18,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 8px 18px rgba(15,23,42,0.04)",
  },
  activeTabButton: {
    border: "1px solid #cfe2ff",
    background: "linear-gradient(135deg, #0f6fff 0%, #14b86a 100%)",
    color: "#fff",
    padding: "13px 16px",
    borderRadius: 18,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 14px 28px rgba(15,111,255,0.22)",
  },
  heroHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 18,
    flexWrap: "wrap",
    marginTop: 0,
    paddingTop: 0,
    marginBottom: 20,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    minWidth: 0,
  },
  heroSubtext: {
    margin: "8px 0 0",
    fontSize: 16,
    color: "#475569",
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
    minWidth: 0,
    boxSizing: "border-box",
  },
  ghostButton: {
    border: "1px solid rgba(203,213,225,0.9)",
    background: "rgba(255,255,255,0.96)",
    color: "#334155",
    padding: "12px 16px",
    borderRadius: 18,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 10px 20px rgba(15,23,42,0.04)",
  },
  logoutButton: {
    border: "none",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: 18,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 14px 28px rgba(15,23,42,0.18)",
    minHeight: 46,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "normal",
    textAlign: "center",
    lineHeight: 1.2,
  },
  banner: {
    background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    border: "1px solid rgba(191,219,254,0.95)",
    borderLeft: "5px solid #0f6fff",
    borderRadius: 20,
    padding: "15px 18px",
    marginBottom: 18,
    fontWeight: 800,
    color: "#0f172a",
    boxShadow: "0 14px 32px rgba(15,23,42,0.06)",
    animation: "rentduoFadeUp 0.22s ease",
  },
  warningBanner: {
    background: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)",
    border: "1px solid #fdba74",
    borderLeft: "5px solid #f97316",
    color: "#9a3412",
    borderRadius: 20,
    padding: "15px 18px",
    marginBottom: 18,
    fontWeight: 800,
    boxShadow: "0 14px 32px rgba(249,115,22,0.10)",
    animation: "rentduoFadeUp 0.22s ease",
  },
  systemGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
    marginBottom: 18,
  },
  systemCard: {
    background: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(226,232,240,0.98)",
    borderRadius: 22,
    padding: 20,
    boxShadow: "0 14px 30px rgba(15,23,42,0.05)",
  },
  systemLabel: {
    fontSize: 12,
    color: "#64748b",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 6,
  },
  systemValue: {
    fontSize: 18,
    fontWeight: 900,
    color: "#0f172a",
  },
  topProfileCard: {
    display: "flex",
    alignItems: "center",
    gap: 24,
    flexWrap: "wrap",
    background: "linear-gradient(135deg, rgba(255,255,255,0.99) 0%, rgba(219,234,254,0.98) 46%, rgba(220,252,231,0.98) 100%)",
    border: "1px solid rgba(191,219,254,0.95)",
    borderRadius: 40,
    padding: 40,
    boxShadow: "0 38px 92px rgba(15,23,42,0.18)",
    marginBottom: 28,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "visible",
  },
  profileTitleRowWrap: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  profileHeroName: {
    margin: 0,
    fontSize: 32,
    fontWeight: 900,
    lineHeight: 1.05,
    letterSpacing: -0.5,
  },
  heroProfileMeta: {
    margin: "10px 0 6px",
    fontSize: 15,
    color: "#334155",
    fontWeight: 700,
  },
  heroProfileBio: {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.6,
    color: "#64748b",
  },
  tabWrap: {
    marginTop: 22,
    background: "rgba(255,255,255,0.82)",
    backdropFilter: "blur(22px)",
    border: "1px solid rgba(226,232,240,0.95)",
    borderRadius: 36,
    padding: 24,
    boxShadow: "0 28px 68px rgba(15,23,42,0.12)",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "visible",
  },
  tabHeader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    width: "100%",
    maxWidth: "100%",
    marginBottom: 20,
    boxSizing: "border-box",
    overflow: "hidden",
  },
  topTab: {
    border: "1px solid rgba(203,213,225,0.95)",
    background: "rgba(255,255,255,0.88)",
    color: "#334155",
    fontWeight: 800,
    borderRadius: 999,
    padding: "15px 24px",
    cursor: "pointer",
    boxShadow: "0 10px 22px rgba(15,23,42,0.05)",
  },
  topTabActive: {
    border: "1px solid #cfe2ff",
    background: "linear-gradient(135deg, #0f6fff 0%, #14b86a 100%)",
    color: "#fff",
    fontWeight: 900,
    borderRadius: 999,
    padding: "15px 24px",
    cursor: "pointer",
    boxShadow: "0 16px 34px rgba(15,111,255,0.24)",
  },
  grid: {
    alignItems: "start",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 18,
    alignItems: "start",
    overflow: "visible",
  },
  card: {
    width: "100%",
    background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
    borderRadius: 28,
    padding: 32,
    boxShadow: "0 26px 56px rgba(15,23,42,0.10)",
    border: "1px solid rgba(226,232,240,0.98)",
    overflow: "visible",
  },
  cardWide: {
    width: "100%",
    background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
    borderRadius: 28,
    padding: 32,
    boxShadow: "0 26px 56px rgba(15,23,42,0.10)",
    border: "1px solid rgba(226,232,240,0.98)",
    gridColumn: "1 / -1",
    overflow: "visible",
  },
  scrollableColumnCard: {
    maxHeight: "min(90vh, 1200px)",
    overflow: "visible",
    display: "flex",
    flexDirection: "column",
  },
  scrollableInnerList: {
    display: "grid",
    gap: 20,
    overflowY: "auto",
    paddingRight: 10,
    paddingBottom: 20,
    minHeight: 420,
    flex: 1,
  },
  reviewsSectionBox: {
    background: "#ffffff",
    border: "1px solid #dbe4ee",
    borderRadius: 22,
    padding: 18,
    marginBottom: 14,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
  },
  reviewsSectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 14,
  },
  reviewsSectionEyebrow: {
    fontSize: 14,
    lineHeight: 1.45,
    fontWeight: 500,
    color: "#64748b",
    marginBottom: 4,
  },
  reviewsSectionTitle: {
    fontSize: 22,
    lineHeight: 1.1,
    fontWeight: 900,
    color: "#0f172a",
  },
  reviewsSectionCount: {
    minWidth: 64,
    height: 64,
    borderRadius: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(15,111,255,0.10), rgba(16,185,129,0.12))",
    border: "1px solid rgba(148, 163, 184, 0.24)",
    color: "#0f172a",
    fontSize: 24,
    fontWeight: 900,
    boxShadow: "0 10px 20px rgba(15, 23, 42, 0.06)",
  },
  profileReviewsScrollBox: {
    display: "grid",
    gap: 20,
    overflowY: "auto",
    paddingRight: 10,
    paddingBottom: 8,
    minHeight: 320,
    maxHeight: 460,
    alignContent: "start",
  },
  emptyReviewsBox: {
    minHeight: 180,
    borderRadius: 18,
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#64748b",
    fontWeight: 700,
    padding: 24,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 26,
    lineHeight: 1.1,
    fontWeight: 900,
    letterSpacing: -0.4,
    color: "#0f172a",
  },
  sectionSubtitle: {
    margin: "8px 0 0",
    fontSize: 15,
    lineHeight: 1.6,
    color: "#64748b",
  },
  label: {
    display: "block",
    fontSize: 14,
    fontWeight: 800,
    color: "#334155",
    margin: "14px 0 8px",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #dbe4f0",
    background: "#fff",
    borderRadius: 18,
    padding: "15px 16px",
    fontSize: 15,
    outline: "none",
    color: "#0f172a",
    boxShadow: "0 4px 10px rgba(15,23,42,0.03)",
  },
  passwordFieldWrap: {
    position: "static",
    width: "100%",
    boxSizing: "border-box",
    marginTop: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #dbe4f0",
    background: "#fff",
    borderRadius: 18,
    padding: 6,
    boxShadow: "0 4px 10px rgba(15,23,42,0.03)",
  },
  passwordFieldWrapMobile: {
    position: "static",
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #dbe4f0",
    background: "#fff",
    borderRadius: 18,
    padding: 6,
    boxShadow: "0 4px 10px rgba(15,23,42,0.03)",
  },
  passwordInput: {
    flex: "1 1 auto",
    minWidth: 0,
    height: 46,
    minHeight: 46,
    padding: "0 10px",
    border: "0",
    borderRadius: 12,
    boxShadow: "none",
    lineHeight: 1.25,
  },
  passwordInputMobile: {
    flex: "1 1 auto",
    minWidth: 0,
    height: 46,
    minHeight: 46,
    padding: "0 10px",
    border: "0",
    borderRadius: 12,
    boxShadow: "none",
  },
  passwordToggleButton: {
    position: "static",
    transform: "none",
    flex: "0 0 76px",
    width: 76,
    height: 46,
    minHeight: 46,
    padding: "0 14px",
    border: "1px solid rgba(15,111,255,0.18)",
    borderRadius: 999,
    background: "#eff6ff",
    color: "#0f6fff",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    zIndex: 3,
    touchAction: "manipulation",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    boxShadow: "0 4px 10px rgba(15,111,255,0.08)",
    margin: 0,
    alignSelf: "center",
  },
  passwordToggleButtonMobile: {
    position: "static",
    transform: "none",
    flex: "0 0 auto",
    height: 46,
    minHeight: 46,
    margin: 0,
    alignSelf: "center",
  },
  loginAiCard: {
    display: "grid",
    gap: 10,
    marginTop: 14,
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,111,255,0.14)",
    background: "linear-gradient(135deg, rgba(239,246,255,0.96), rgba(236,253,245,0.92))",
    boxShadow: "0 12px 26px rgba(15,23,42,0.06)",
  },
  loginAiHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    color: "#0f172a",
    fontSize: 14,
  },
  loginAiBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 9px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #0f6fff 0%, #14b86a 100%)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 900,
  },
  loginAiPromptGrid: {
    display: "grid",
    gap: 8,
  },
  loginAiPromptButton: {
    border: "1px solid rgba(191,219,254,0.95)",
    background: "#fff",
    color: "#1d4ed8",
    borderRadius: 14,
    padding: "10px 12px",
    fontSize: 13,
    fontWeight: 850,
    textAlign: "left",
    cursor: "pointer",
  },
  loginAiPromptButtonActive: {
    background: "#eff6ff",
    borderColor: "#60a5fa",
    color: "#0f6fff",
  },
  loginAiAnswer: {
    padding: 12,
    borderRadius: 14,
    background: "rgba(255,255,255,0.82)",
    color: "#334155",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    width: "100%",
    minHeight: 110,
    boxSizing: "border-box",
    border: "1px solid #dbe4f0",
    background: "#fff",
    borderRadius: 18,
    padding: "15px 16px",
    fontSize: 15,
    outline: "none",
    resize: "vertical",
    color: "#0f172a",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 14,
    color: "#334155",
    fontWeight: 700,
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 12,
  },
  primaryButton: {
    border: "none",
    background: "linear-gradient(135deg, #0f6fff 0%, #14b86a 100%)",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: 14,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 12px 24px rgba(15,111,255,0.18)",
    marginTop: 0,
  },
  secondaryButton: {
    border: "1px solid #cfe2ff",
    background: "#eff6ff",
    color: "#0f6fff",
    padding: "12px 16px",
    borderRadius: 14,
    fontWeight: 800,
    cursor: "pointer",
    marginTop: 0,
  },
  secondaryGreenButton: {
    border: "1px solid #bbf7d0",
    background: "#f0fdf4",
    color: "#15803d",
    padding: "12px 16px",
    borderRadius: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
  secondaryPurpleButton: {
    border: "1px solid #ddd6fe",
    background: "#f5f3ff",
    color: "#6d28d9",
    padding: "12px 16px",
    borderRadius: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
  rejectButton: {
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#b91c1c",
    padding: "12px 16px",
    borderRadius: 14,
    fontWeight: 800,
    cursor: "pointer",
    marginTop: 0,
  },
  reportGhostButton: {
    border: "none",
    background: "transparent",
    color: "#b91c1c",
    padding: 0,
    marginTop: 0,
    fontWeight: 800,
    cursor: "pointer",
  },
  message: {
    marginTop: 14,
    fontSize: 15,
    color: "#475569",
    fontWeight: 700,
  },
  smallText: {
    margin: "8px 0 0",
    fontSize: 15,
    lineHeight: 1.55,
    color: "#64748b",
  },
  profileStatusLine: {
    margin: "8px 0 0",
    fontSize: 15,
    lineHeight: 1.5,
    color: "#0f766e",
    fontWeight: 800,
  },
  bodyCopy: {
    fontSize: 15,
    lineHeight: 1.7,
    color: "#334155",
  },
  reviewTimestamp: {
    marginTop: 14,
    marginBottom: 4,
    fontSize: 14,
    lineHeight: 1.7,
    color: "#64748b",
    fontWeight: 600,
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  strongHeading: {
    fontSize: 15,
    fontWeight: 900,
    color: "#0f172a",
  },
  subsectionHeading: {
    margin: "0 0 10px",
    fontSize: 26,
    fontWeight: 900,
    color: "#0f172a",
  },
  fullscreenDisclaimer: {
    position: "fixed",
    inset: 0,
    zIndex: 3000,
    background: "radial-gradient(circle at top left, rgba(15,111,255,0.28), transparent 28%), linear-gradient(135deg, #0f5ed7 0%, #0c79ff 38%, #19b56c 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    overflow: "hidden",
    boxSizing: "border-box",
  },
  fullscreenDisclaimerInner: {
    width: "100%",
    maxWidth: 760,
    height: "min(92dvh, 860px)",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  disclaimerShell: {
    position: "relative",
    width: "100%",
    height: "100%",
    margin: "0 auto",
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(255,255,255,0.5)",
    borderRadius: 28,
    boxShadow: "0 35px 80px rgba(2, 18, 48, 0.35)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    backdropFilter: "blur(8px)",
  },
  disclaimerTopGlow: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background: "linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 22%)",
  },
  disclaimerHeaderBar: {
    position: "relative",
    padding: "28px 28px 22px",
    background: "linear-gradient(90deg, #0f6fff 0%, #16b56c 100%)",
    color: "#ffffff",
  },
  disclaimerMiniLabel: {
    margin: 0,
    fontSize: 12,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    fontWeight: 800,
    opacity: 0.92,
  },
  fullscreenDisclaimerTitle: {
    margin: "8px 0 16px",
    fontSize: 32,
    lineHeight: 1.08,
    fontWeight: 900,
    color: "#0f172a",
  },
  disclaimerHeaderText: {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.5,
    color: "rgba(255,255,255,0.95)",
    maxWidth: 540,
  },
  fullscreenDisclaimerBody: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "26px 28px 10px",
    background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 48%, #f4fbf7 100%)",
  },
  fullscreenDisclaimerLead: {
    margin: "0 0 18px",
    fontSize: 15,
    lineHeight: 1.65,
    color: "#334155",
  },
  disclaimerSection: {
    background: "#ffffff",
    border: "1px solid #dbe6f1",
    borderRadius: 18,
    padding: "16px 16px 2px",
    marginBottom: 14,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
  },
  disclaimerSectionTitle: {
    margin: "0 0 8px",
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 800,
    color: "#24344d",
  },
  fullscreenDisclaimerText: {
    margin: "0 0 14px",
    fontSize: 15,
    lineHeight: 1.65,
    color: "#475569",
  },
  disclaimerFooterNote: {
    marginTop: 6,
    marginBottom: 8,
    padding: "14px 16px",
    borderRadius: 14,
    background: "rgba(15,111,255,0.08)",
    border: "1px solid rgba(15,111,255,0.12)",
    color: "#1e3a5f",
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  fullscreenDisclaimerActions: {
    display: "flex",
    gap: 14,
    flexDirection: "column",
    padding: "18px 28px max(24px, env(safe-area-inset-bottom))",
    borderTop: "1px solid rgba(226,232,240,0.95)",
    background: "rgba(255,255,255,0.98)",
    flexShrink: 0,
  },
  disclaimerScrollHint: {
    fontSize: 14,
    lineHeight: 1.4,
    color: "#64748b",
    fontWeight: 700,
  },
  disclaimerActionRow: {
    display: "flex",
    gap: 12,
    flexWrap: "nowrap",
    width: "100%",
  },
  disclaimerActionButton: {
    flex: 1,
    minHeight: 60,
  },
  disclaimerDisabledButton: {
    opacity: 0.55,
    cursor: "not-allowed",
    boxShadow: "none",
    transform: "none",
  },

  verifiedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 13px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #0f6fff 0%, #14b86a 100%)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.2,
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "break-word",
    maxWidth: "100%",
    boxSizing: "border-box",
    position: "relative",
    transform: "none",
    flexShrink: 0,
    boxShadow: "0 12px 24px rgba(15,111,255,0.20)",
    animation: "rentduoSoftPulse 2.2s ease infinite",
  },
  verificationBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    padding: 16,
  },
  link: {
    display: "inline-block",
    marginTop: 8,
    color: "#0f6fff",
    fontWeight: 700,
    textDecoration: "none",
  },
  linkPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "10px 14px",
    borderRadius: 999,
    border: "1px solid #dbeafe",
    background: "#eff6ff",
    color: "#1d4ed8",
    textDecoration: "none",
    fontWeight: 800,
    fontSize: 14,
  },
  adminSectionBlock: {
    marginTop: 24,
    paddingTop: 12,
    borderTop: "1px solid #e2e8f0",
  },
  adminCard: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 20,
    padding: 20,
    boxShadow: "0 12px 28px rgba(15,23,42,0.04)",
  },
  adminDocsRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    margin: "14px 0",
  },
  adminButtonGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginTop: 14,
  },
  searchResultsList: {
    display: "grid",
    gap: 12,
    marginTop: 12,
    marginBottom: 12,
  },
  searchResultCard: {
    border: "1px solid #dbeafe",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    borderRadius: 18,
    padding: 14,
    boxShadow: "0 10px 26px rgba(15,23,42,0.05)",
  },
  searchResultTopRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  searchScorePill: {
    padding: "8px 12px",
    borderRadius: 999,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontWeight: 800,
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  searchScoreGroup: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 8,
  },
  searchMiniStars: {
    fontSize: 12,
    color: "#f59e0b",
    fontWeight: 800,
  },
  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
  searchResultMetaLine: {
    marginTop: 10,
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.45,
  },
  searchResultActions: {
    display: "flex",
    gap: 10,
    marginTop: 12,
    flexWrap: "wrap",
  },
  userCard: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 20,
    padding: 20,
    boxShadow: "0 12px 28px rgba(15,23,42,0.04)",
  },
  userHeader: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  cardPersonName: {
    margin: 0,
    fontSize: 26,
    lineHeight: 1.1,
    fontWeight: 900,
    color: "#0f172a",
  },
  ratingHeroBox: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginTop: 0,
    padding: 20,
    borderRadius: 20,
    background:
      "linear-gradient(135deg, rgba(15,111,255,0.08) 0%, rgba(20,184,106,0.08) 100%)",
    border: "1px solid rgba(203,213,225,0.9)",
  },
  ratingHeroValue: {
    fontSize: 42,
    lineHeight: 1,
    fontWeight: 900,
    color: "#0f172a",
    letterSpacing: -1,
  },
  list: {
    display: "grid",
    gap: 14,
  },
  reviewCard: {
    background: "#ffffff",
    borderRadius: "24px",
    padding: "28px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
    minHeight: 320,
    maxHeight: 320,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: 12,
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 18,
  },
  mapWrap: {
    position: "relative",
    width: "100%",
    overflow: "hidden",
    borderRadius: 26,
    background: "#f8fbff",
    boxShadow: "0 18px 40px rgba(15,23,42,0.08)",
    border: "1px solid #e2e8f0",
  },
  infoWindowShell: {
    width: "min(320px, calc(100vw - 48px))",
    maxWidth: "calc(100vw - 48px)",
    minWidth: 0,
    padding: 4,
    boxSizing: "border-box",
    overflow: "hidden",
  },
  mapSelectionBox: {
    marginTop: 14,
    padding: 14,
    borderRadius: 18,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    minWidth: 0,
    boxSizing: "border-box",
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "normal",
    hyphens: "none",
    lineHeight: 1.2,
  },
  profileStatGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
    width: "100%",
    marginBottom: 18,
    alignItems: "stretch",
  },
  profileStatCard: {
    padding: 16,
    minHeight: 110,
    borderRadius: 18,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "normal",
    hyphens: "none",
    lineHeight: 1.2,
  },
  profileStatLabel: {
    fontSize: 12,
    fontWeight: 900,
    color: "#475569",
    textTransform: "uppercase",
    lineHeight: 1.2,
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "normal",
    hyphens: "none",
  },
  profileStatValue: {
    fontSize: 28,
    fontWeight: 900,
    color: "#0f172a",
    lineHeight: 1.1,
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "normal",
    hyphens: "none",
  },
  profileStatValueSmall: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0f172a",
    lineHeight: 1.3,
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "break-word",
    hyphens: "none",
  },
  managerStatValue: {
    fontSize: 30,
    fontWeight: 900,
    color: "#0f172a",
    lineHeight: 1,
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "normal",
    hyphens: "none",
  },
  propertyGrid: {
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 16,
  },
  propertyCardShell: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    width: "100%",
    background: "#fff",
    border: "1px solid #dbeafe",
    borderRadius: 30,
    padding: 24,
    boxShadow: "0 28px 60px rgba(15,23,42,0.16)",
  },
  propertyShowcaseCard: {
    width: "100%",
    boxSizing: "border-box",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
    border: "1px solid #dbeafe",
    borderRadius: 30,
    boxShadow: "0 28px 60px rgba(15,23,42,0.16)",
  },
  propertyCardTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  propertyTitle: {
    fontSize: 26,
    fontWeight: 900,
    lineHeight: 1.15,
    color: "#0f172a",
  },
  propertyAddress: {
    marginTop: 8,
    fontSize: 16,
    color: "#64748b",
    lineHeight: 1.5,
  },
  propertyMetaRow: {
    marginTop: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  propertyRatingText: {
    fontSize: 14,
    fontWeight: 800,
    color: "#334155",
  },
  communityRatingBanner: {
    marginTop: 10,
    padding: "10px 12px",
    borderRadius: 14,
    background: "rgba(15, 111, 255, 0.06)",
    border: "1px solid rgba(15, 111, 255, 0.14)",
    display: "grid",
    gap: 6,
  },
  communityRatingLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "#0f6fff",
  },
  communityRatingValue: {
    fontSize: 14,
    lineHeight: 1.5,
    color: "#334155",
    fontWeight: 700,
  },
  propertyDescription: {
    marginTop: 14,
    fontSize: 15,
    lineHeight: 1.65,
    color: "#475569",
  },
  miniMetaGrid: {
    display: "flex",
    gap: 10,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 14,
  },
  miniMetaPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: 999,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#334155",
    fontSize: 14,
    fontWeight: 700,
  },
  propertyFooterRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 14,
  },
  footerStatPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: 999,
    background: "#eff6ff",
    border: "1px solid #dbeafe",
    color: "#1d4ed8",
    fontSize: 14,
    fontWeight: 800,
  },
  mobileModalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.48)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    padding: 12,
    zIndex: 10020,
  },
  mobileModalCard: {
    width: "min(720px, 100%)",
    maxHeight: "92vh",
    overflowY: "auto",
    borderRadius: 26,
    background: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    boxShadow: "0 28px 80px rgba(15,23,42,0.28)",
    padding: 20,
    paddingBottom: 170,
  },
  mobileModalHeaderRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 12,
  },
  mobileModalCloseButton: {
    border: "1px solid #dbe7f3",
    background: "#ffffff",
    color: "#475569",
    borderRadius: 999,
    padding: "9px 14px",
    cursor: "pointer",
    fontWeight: 800,
  },

  propertyProfileOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.48)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "max(12px, env(safe-area-inset-top)) 12px max(12px, env(safe-area-inset-bottom))",
    overflowY: "auto",
    overscrollBehavior: "contain",
    zIndex: 10020,
  },
  propertyProfileModalCard: {
    width: "min(980px, 96vw)",
    maxHeight: "calc(100dvh - 24px)",
    overflowY: "auto",
    borderRadius: 26,
    background: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    boxShadow: "0 28px 80px rgba(15,23,42,0.28)",
    padding: 28,
    paddingBottom: 190,
    margin: "0 auto",
    overscrollBehavior: "contain",
    scrollPaddingTop: 0,
  },
  messageSelectedUserBar: {
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(214,226,240,0.95)",
    borderRadius: 20,
    padding: 16,
    marginBottom: 16,
    boxShadow: "0 10px 26px rgba(15,23,42,0.05)",
  },
  messageBox: {
    minHeight: 420,
    maxHeight: 560,
    overflowY: "auto",
    padding: 12,
    borderRadius: 20,
    background: "rgba(255,255,255,0.82)",
    border: "1px solid #e2e8f0",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
  },
  bubbleWrap: {
    display: "flex",
    marginBottom: 12,
  },
  bubble: {
    maxWidth: 680,
    borderRadius: 20,
    padding: 14,
    boxShadow: "0 10px 24px rgba(15,23,42,0.04)",
  },
  bubbleHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
  },
  bubbleName: {
    fontSize: 14,
    fontWeight: 900,
    color: "#0f172a",
  },
  messageComposer: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    alignItems: "center",
    marginTop: 0,
  },
  messageFolderTabs: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  messageFolderTab: {
    border: "1px solid #dbe7f3",
    background: "#ffffff",
    color: "#475569",
    borderRadius: 999,
    padding: "10px 16px",
    cursor: "pointer",
    fontWeight: 800,
  },
  messageFolderTabActive: {
    border: "1px solid rgba(15,111,255,0.16)",
    background:
      "linear-gradient(135deg, rgba(15,111,255,0.12) 0%, rgba(20,184,106,0.10) 100%)",
    color: "#0f6fff",
    borderRadius: 999,
    padding: "10px 16px",
    cursor: "pointer",
    fontWeight: 800,
  },
  threadList: {
    display: "grid",
    gap: 12,
  },
  threadListScrollable: {
    maxHeight: 420,
    overflowY: "auto",
    paddingRight: 4,
  },
  threadCardButton: {
    border: "1px solid #e2e8f0",
    background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)",
    borderRadius: 20,
    padding: 15,
    cursor: "pointer",
    textAlign: "left",
    boxShadow: "0 12px 28px rgba(15,23,42,0.05)",
  },
  threadSnippet: {
    fontSize: 14,
    color: "#64748b",
    marginTop: 6,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  legalSection: {
    marginTop: 24,
    paddingTop: 16,
    borderTop: "1px solid rgba(148,163,184,0.2)",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  legalButton: {
    background: "none",
    border: "none",
    color: "#0f6fff",
    textAlign: "left",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    padding: 0,
  },
  bottomNav: {
    position: "fixed",
    bottom: 14,
    left: 16,
    right: 16,
    width: "auto",
    maxWidth: "calc(100vw - 32px)",
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 8,
    padding: "12px 12px calc(12px + env(safe-area-inset-bottom))",
    margin: "0 auto",
    boxSizing: "border-box",
    overflow: "hidden",
    background: "rgba(255,255,255,0.94)",
    backdropFilter: "blur(22px)",
    borderTop: "1px solid rgba(203,213,225,0.85)",
    borderRadius: 24,
    zIndex: 1000,
    boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
  },
  bottomNavButton: {
    border: "none",
    background: "transparent",
    color: "#64748b",
    borderRadius: 18,
    padding: "12px 8px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontWeight: 800,
    minHeight: 60,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "visible",
    flexShrink: 0,
    transition: "background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
  },
  bottomNavButtonActive: {
    background:
      "linear-gradient(135deg, rgba(15,111,255,0.16) 0%, rgba(20,184,106,0.16) 100%)",
    color: "#0f6fff",
    boxShadow: "0 10px 22px rgba(15,111,255,0.14)",
  },
  bottomNavIcon: {
    fontSize: 18,
    lineHeight: 1,
  },
  bottomNavLabel: {
    fontSize: 11,
    lineHeight: 1.2,
    maxWidth: "100%",
    overflow: "visible",
    textOverflow: "clip",
    whiteSpace: "normal",
    wordBreak: "break-word",
    textAlign: "center",
  },
  communityRatingNotice: {
    marginTop: 14,
    padding: 16,
    borderRadius: 18,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#0f172a",
  },
  communityReviewTag: {
    margin: "0 0 10px",
    fontSize: 14,
    fontWeight: 800,
    color: "#7c3aed",
  },
  analysisCard: {
    marginTop: 14,
    padding: 14,
    borderRadius: 18,
    background: "#f8fafc",
    border: "1px solid #dbeafe",
  },
  analysisTitle: {
    margin: "0 0 8px",
    fontSize: 15,
    fontWeight: 900,
    color: "#0f172a",
  },
  modalBulletList: {
    display: "grid",
    gap: 10,
    marginTop: 14,
    marginBottom: 18,
  },
  modalBulletItem: {
    fontSize: 15,
    color: "#334155",
    fontWeight: 700,
  },
  modalActionRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.52)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    zIndex: 2000,
    backdropFilter: "blur(4px)",
  },
  modalCard: {
    width: "100%",
    maxWidth: 560,
    background: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
    borderRadius: 28,
    padding: 28,
    boxShadow: "0 28px 80px rgba(15,23,42,0.20)",
    border: "1px solid rgba(226,232,240,0.95)",
    boxSizing: "border-box",
    animation: "rentduoFadeUp 0.22s ease",
  },
  modalTitle: {
    margin: "8px 0 12px",
    fontSize: 26,
    lineHeight: 1.15,
    fontWeight: 900,
    color: "#0f172a",
  },
  modalText: {
    margin: "0 0 12px",
    fontSize: 15,
    lineHeight: 1.65,
    color: "#475569",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 0,
  },
  modalSecondaryButton: {
    border: "1px solid #dbe4f0",
    background: "#fff",
    color: "#475569",
    padding: "12px 16px",
    borderRadius: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
  aiToneRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 0,
  },
  aiDraftCard: {
    marginTop: 0,
    border: "1px solid #dbe4f0",
    borderRadius: 18,
    padding: 16,
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
  },
  aiDraftHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },
  aiDraftEyebrow: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "#2563eb",
  },
  aiDraftTitle: {
    margin: "6px 0 0 0",
    fontSize: 26,
    lineHeight: 1.2,
    color: "#0f172a",
  },
  aiDraftDisclaimer: {
    margin: "12px 0 0 0",
    fontSize: 14,
    color: "#64748b",
    lineHeight: 1.5,
  },
  aiDraftCompareWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
    marginTop: 14,
  },
  aiDraftCompareCol: {
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 12,
    background: "#fff",
  },
  aiDraftCompareLabel: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: "#475569",
    marginBottom: 8,
  },
  aiDraftCompareText: {
    fontSize: 15,
    lineHeight: 1.65,
    color: "#0f172a",
    whiteSpace: "pre-wrap",
  },
  aiDraftConversionCard: {
    marginTop: 14,
    borderRadius: 14,
    padding: 14,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
  },
  aiDraftConversionTitle: {
    display: "block",
    fontSize: 15,
    color: "#1e3a8a",
    marginBottom: 6,
  },
  aiDraftConversionText: {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.6,
    color: "#1e40af",
  },
  aiDraftPremiumText: {
    margin: "10px 0 0 0",
    fontSize: 14,
    lineHeight: 1.5,
    color: "#475569",
  },
  aiDraftActionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 14,
  },
  autocompleteWrap: {
    position: "relative",
    zIndex: 9999,
    overflow: "visible",
  },
  reviewReplyCard: {
    marginTop: 14,
    padding: 14,
    borderRadius: 18,
    background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
    border: "1px solid #dbeafe",
  },
  reviewReplyHeader: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: 8,
  },
  reviewReplyBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.2,
    background: "#dbeafe",
    color: "#1d4ed8",
  },
  reviewReplyName: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
  },
  reviewReplyText: {
    fontSize: 15,
    lineHeight: 1.6,
    color: "#0f172a",
    whiteSpace: "pre-wrap",
  },
  reviewReplyComposer: {
    marginTop: 14,
    padding: 14,
    borderRadius: 18,
    background: "#ffffff",
    border: "1px solid #dbe4f0",
    boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
  },
  reviewReplyComposerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: 8,
  },
  replyTextarea: {
    width: "100%",
    minHeight: 96,
    borderRadius: 14,
    border: "1px solid #cbd5e1",
    padding: 12,
    resize: "vertical",
    background: "#f8fafc",
    color: "#0f172a",
    lineHeight: 1.5,
  },
  replyMetaRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 10,
  },

  sectionTitleWrap: {
    marginBottom: 18,
  },
  sectionEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: 999,
    background: "linear-gradient(135deg, rgba(15,111,255,0.12) 0%, rgba(20,184,106,0.10) 100%)",
    color: "#0f6fff",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.9,
    textTransform: "uppercase",
    marginBottom: 10,
  },
  sectionTitle: {
    margin: 0,
    fontSize: "clamp(24px, 3vw, 34px)",
    lineHeight: 1.05,
    letterSpacing: -0.8,
    fontWeight: 900,
    color: "#0f172a",
  },
  sectionSubtitle: {
    margin: "10px 0 0",
    color: "#526174",
    fontSize: 15,
    lineHeight: 1.7,
    maxWidth: 760,
  },
  card: {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    background: "rgba(255,255,255,0.88)",
    backdropFilter: "blur(18px)",
    border: "1px solid rgba(226,232,240,0.95)",
    borderRadius: 28,
    boxShadow: "0 20px 50px rgba(15,23,42,0.08)",
    padding: 22,
    overflow: "visible",
    minWidth: 0,
  },
  cardWide: {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    background: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(18px)",
    border: "1px solid rgba(226,232,240,0.95)",
    borderRadius: 30,
    boxShadow: "0 24px 60px rgba(15,23,42,0.09)",
    padding: 22,
    overflow: "visible",
    minWidth: 0,
  },
  input: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    minHeight: 54,
    borderRadius: 18,
    border: "1px solid #d7e2ee",
    background: "rgba(248,250,252,0.95)",
    color: "#0f172a",
    padding: "14px 16px",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
  },
  textarea: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    minHeight: 132,
    borderRadius: 20,
    border: "1px solid #d7e2ee",
    background: "rgba(248,250,252,0.95)",
    color: "#0f172a",
    padding: "14px 16px",
    resize: "vertical",
    lineHeight: 1.6,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 0.2,
    color: "#334155",
    margin: "0 0 8px",
  },
  primaryButton: {
    border: "none",
    borderRadius: 999,
    padding: "14px 18px",
    background: "linear-gradient(135deg, #0f6fff 0%, #14b86a 100%)",
    color: "#ffffff",
    fontWeight: 900,
    fontSize: 14,
    letterSpacing: 0.15,
    boxShadow: "0 18px 34px rgba(15,111,255,0.22)",
    cursor: "pointer",
    minHeight: 44,
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    whiteSpace: "normal",
    lineHeight: 1.2,
  },
  secondaryButton: {
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 999,
    padding: "14px 18px",
    background: "rgba(255,255,255,0.92)",
    color: "#0f172a",
    fontWeight: 800,
    fontSize: 14,
    boxShadow: "0 12px 24px rgba(15,23,42,0.06)",
    cursor: "pointer",
    minHeight: 44,
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    whiteSpace: "normal",
    lineHeight: 1.2,
  },
  secondaryButtonLabel: {
    display: "inline-flex",
  },
  propertyUploadCard: {
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(148, 163, 184, 0.22)",
    background: "rgba(248, 250, 252, 0.95)",
    marginBottom: 8,
  },
  propertyUploadTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  uploadHintText: {
    marginTop: 10,
    marginBottom: 12,
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.5,
  },
  propertyImagePreviewCard: {
    borderRadius: 18,
    overflow: "hidden",
    border: "1px solid rgba(148, 163, 184, 0.18)",
    marginTop: 12,
    background: "#ffffff",
  },
  propertyImagePreview: {
    display: "block",
    width: "100%",
    height: 220,
    objectFit: "cover",
  },
  propertyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 18,
  },
  propertyCardShell: {
    background: "transparent",
  },
  propertyShowcaseCard: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    background: "#ffffff",
    borderRadius: 26,
    border: "1px solid rgba(226,232,240,0.9)",
    boxShadow: "0 18px 44px rgba(15,23,42,0.08)",
    overflow: "hidden",
  },
  propertyMedia: {
    position: "relative",
    height: 176,
    borderRadius: 22,
    overflow: "hidden",
    marginBottom: 16,
    background: "linear-gradient(135deg, #dbeafe 0%, #dcfce7 100%)",
  },
  propertyMediaImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  propertyMediaGradient: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(15,23,42,0.04) 0%, rgba(15,23,42,0.48) 100%)",
  },
  propertyFavoriteButton: {
    position: "absolute",
    top: 14,
    right: 14,
    width: 40,
    height: 40,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.8)",
    background: "rgba(255,255,255,0.9)",
    color: "#0f172a",
    fontSize: 18,
    fontWeight: 800,
    cursor: "pointer",
    zIndex: 2,
  },
  propertyMediaContent: {
    position: "absolute",
    left: 16,
    right: 16,
    bottom: 16,
    zIndex: 2,
    color: "#ffffff",
    minWidth: 0,
    maxWidth: "calc(100% - 32px)",
    writingMode: "horizontal-tb",
  },
  propertyMediaChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.18)",
    backdropFilter: "blur(10px)",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 10,
  },
  propertyMediaTitle: {
    fontSize: 24,
    lineHeight: 1.05,
    fontWeight: 900,
    letterSpacing: -0.5,
    textShadow: "0 8px 18px rgba(15,23,42,0.26)",
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "break-word",
    writingMode: "horizontal-tb",
    display: "block",
    width: "100%",
    minWidth: 0,
  },
  propertyMediaSubtitle: {
    marginTop: 6,
    fontSize: 14,
    opacity: 0.95,
    textShadow: "0 8px 18px rgba(15,23,42,0.22)",
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "break-word",
    writingMode: "horizontal-tb",
    display: "block",
    width: "100%",
    minWidth: 0,
  },
  propertyCardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
    minWidth: 0,
  },
  propertyTitle: {
    fontSize: 20,
    fontWeight: 900,
    letterSpacing: -0.4,
    color: "#0f172a",
    marginBottom: 4,
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "break-word",
    writingMode: "horizontal-tb",
    display: "block",
    width: "100%",
    minWidth: 0,
  },
  propertyAddress: {
    fontSize: 14,
    lineHeight: 1.6,
    color: "#64748b",
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "break-word",
    writingMode: "horizontal-tb",
    display: "block",
    width: "100%",
    minWidth: 0,
  },
  propertyMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 14,
    marginBottom: 14,
  },
  propertyRatingText: {
    fontSize: 14,
    fontWeight: 800,
    color: "#334155",
  },
  propertyDescription: {
    marginTop: 2,
    fontSize: 14,
    lineHeight: 1.7,
    color: "#475569",
  },
  miniMetaGrid: {
    display: "flex",
    gap: 8,
    marginTop: 14,
    flexWrap: "wrap",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  miniMetaPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "9px 12px",
    borderRadius: 999,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#334155",
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "normal",
    textAlign: "center",
    overflowWrap: "break-word",
    minWidth: 0,
  },
  footerStatPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "10px 14px",
    borderRadius: 999,
    background: "linear-gradient(135deg, rgba(15,111,255,0.10) 0%, rgba(20,184,106,0.08) 100%)",
    color: "#0f172a",
    border: "1px solid rgba(191,219,254,0.9)",
    fontSize: 12,
    fontWeight: 800,
  },
  propertyFooterRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginTop: 14,
    flexWrap: "wrap",
    width: "100%",
    maxWidth: "100%",
  },
  mapWrap: {
    position: "relative",
    width: "100%",
    borderRadius: 28,
    overflow: "hidden",
    background: "#fff",
  },
  userCard: {
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(226,232,240,0.95)",
    borderRadius: 24,
    boxShadow: "0 18px 42px rgba(15,23,42,0.08)",
    padding: 20,
  },
  threadCardButton: {
    width: "100%",
    textAlign: "left",
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(226,232,240,0.95)",
    borderRadius: 22,
    padding: 16,
    boxShadow: "0 12px 26px rgba(15,23,42,0.05)",
    cursor: "pointer",
  },
  messageBox: {
    minHeight: 420,
    maxHeight: 560,
    overflowY: "auto",
    padding: 16,
    borderRadius: 24,
    border: "1px solid rgba(226,232,240,0.95)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
  },
  bubble: {
    maxWidth: "min(78%, 620px)",
    borderRadius: 24,
    padding: 14,
    boxShadow: "0 14px 28px rgba(15,23,42,0.08)",
  },
  messageComposer: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    alignItems: "center",
    marginTop: 16,
    padding: 14,
    borderRadius: 24,
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(226,232,240,0.95)",
    boxShadow: "0 12px 24px rgba(15,23,42,0.05)",
  },
  verifiedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 12px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #0f6fff 0%, #14b86a 100%)",
    color: "#ffffff",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.3,
    boxShadow: "0 12px 24px rgba(15,111,255,0.18)",
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "break-word",
    maxWidth: "100%",
    boxSizing: "border-box",
    position: "relative",
    transform: "none",
    flexShrink: 0,
  },


  livingExperienceCard: {
    background: "#fff",
    borderRadius: 18,
    padding: 18,
    marginTop: 16,
    marginBottom: 14,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
    border: "1px solid rgba(148, 163, 184, 0.18)",
  },
  livingExperienceHeader: {
    marginBottom: 14,
  },
  livingExperienceGrid: {
    display: "grid",
    gap: 12,
    marginBottom: 14,
  },
  livingExperienceRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 14,
    background: "#f8fafc",
    flexWrap: "wrap",
  },
  livingExperienceLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 600,
    color: "#0f172a",
  },
  livingExperienceIcon: {
    fontSize: 16,
  },
  livingExperienceWarning: {
    marginTop: 10,
    fontSize: 12,
    lineHeight: 1.5,
    color: "#b45309",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 12,
    padding: "10px 12px",
  },
  livingSummaryCard: {
    background: "#fff",
    borderRadius: 18,
    padding: 18,
    marginTop: 12,
    marginBottom: 14,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
    border: "1px solid rgba(148, 163, 184, 0.18)",
  },
  livingSummaryTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  livingSummaryBadge: {
    background: "linear-gradient(135deg, #dbeafe, #dcfce7)",
    color: "#0f172a",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 700,
  },
  livingSummaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 12,
  },
  livingSummaryItem: {
    background: "#f8fafc",
    borderRadius: 14,
    padding: "12px 14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
  },
  reviewLivingBlock: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: "1px solid rgba(148, 163, 184, 0.18)",
  },
  reviewLivingTitle: {
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: 10,
  },
  reviewLivingMiniGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 8,
    marginBottom: 10,
  },
  reviewLivingMiniItem: {
    background: "#f8fafc",
    borderRadius: 12,
    padding: "8px 10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
  },
  reviewLivingNotes: {
    fontSize: 14,
    lineHeight: 1.55,
    color: "#334155",
    background: "#fffdf5",
    borderRadius: 12,
    padding: "10px 12px",
    border: "1px solid #fde68a",
  },

  safeReviewCard: {
    marginTop: 14,
    padding: 16,
    borderRadius: 18,
    border: "1px solid rgba(37,99,235,0.18)",
    background: "linear-gradient(135deg, rgba(239,246,255,0.96), rgba(240,253,244,0.9))",
    boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
  },
  safeReviewTitle: {
    fontSize: 17,
    fontWeight: 900,
    color: "#0f172a",
    marginBottom: 6,
  },
  safeReviewText: {
    margin: "0 0 12px",
    fontSize: 13,
    lineHeight: 1.55,
    color: "#475569",
  },
  reviewSafetyList: {
    margin: "10px 0 0",
    paddingLeft: 18,
    color: "#334155",
    fontSize: 13,
    lineHeight: 1.55,
  },
  reviewSafetyResourceGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 12,
    width: "100%",
    boxSizing: "border-box",
  },
  reviewSafetyResourceText: {
    color: "#334155",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  safeReviewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 10,
  },
  safeReviewField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  safeReviewLabel: {
    fontSize: 12,
    fontWeight: 800,
    color: "#334155",
  },
  safeReviewSelect: {
    width: "100%",
    border: "1px solid #d7e2ee",
    borderRadius: 14,
    padding: "12px 12px",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 650,
  },
  safeReviewWarning: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 14,
    background: "#fffbeb",
    border: "1px solid #fde68a",
    color: "#92400e",
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  safeReviewCheckboxRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    marginTop: 12,
    fontSize: 12,
    lineHeight: 1.45,
    color: "#334155",
    fontWeight: 700,
  },

  reviewDisclaimerBox: {
    marginTop: 10,
    padding: "9px 11px",
    borderRadius: 12,
    background: "#f8fafc",
    border: "1px solid rgba(148,163,184,0.24)",
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.45,
    fontWeight: 650,
  },
  disputedReviewNotice: {
    marginTop: 10,
    padding: "10px 12px",
    borderRadius: 12,
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 800,
  },
  rentalFeedbackSummary: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 14,
    background: "#ffffff",
    border: "1px solid rgba(20,184,166,0.2)",
  },
  rentalFeedbackMiniGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  rentalFeedbackPill: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 9px",
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    color: "#065f46",
    fontSize: 12,
    fontWeight: 800,
  },
  aiFeedCard: {
    borderRadius: 24,
    border: "1px solid rgba(16,185,129,0.28)",
    background:
      "linear-gradient(135deg, rgba(239,246,255,0.96) 0%, rgba(236,253,245,0.94) 100%)",
    boxShadow: "0 16px 32px rgba(15,111,255,0.08)",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflow: "hidden",
  },
  aiFeedCardMobile: {
    padding: 14,
    gap: 10,
  },
  aiFeedCardTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  aiFeedBadgeRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    minWidth: 0,
  },
  aiFeedBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "6px 9px",
    background: "linear-gradient(135deg, #0f6fff 0%, #10b981 100%)",
    color: "#ffffff",
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },
  aiFeedTypeBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "6px 9px",
    background: "#ffffff",
    color: "#0f766e",
    border: "1px solid rgba(16,185,129,0.24)",
    fontSize: 11,
    fontWeight: 900,
  },
  aiFeedDismissButton: {
    border: "1px solid rgba(148,163,184,0.24)",
    background: "rgba(255,255,255,0.72)",
    color: "#64748b",
    borderRadius: 999,
    padding: "7px 10px",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    flexShrink: 0,
  },
  aiFeedTitle: {
    color: "#0f172a",
    fontSize: 18,
    lineHeight: 1.2,
    fontWeight: 950,
    overflowWrap: "break-word",
  },
  aiFeedText: {
    margin: 0,
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.55,
    overflowWrap: "break-word",
  },
  aiFeedActionButton: {
    alignSelf: "flex-start",
    border: "1px solid #bfdbfe",
    background: "#ffffff",
    color: "#0f6fff",
    borderRadius: 999,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  feedActionButton: {
    border: "1px solid rgba(203,213,225,0.95)",
    background: "#ffffff",
    color: "#475569",
    borderRadius: 999,
    padding: "10px 13px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    minHeight: 42,
    boxSizing: "border-box",
    whiteSpace: "normal",
    textAlign: "center",
  },
  feedOpenButton: {
    border: "1px solid #bfdbfe",
    background: "linear-gradient(135deg, rgba(239,246,255,0.98) 0%, rgba(236,253,245,0.98) 100%)",
    color: "#0f6fff",
    borderRadius: 999,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 950,
    cursor: "pointer",
    minHeight: 42,
    boxSizing: "border-box",
    whiteSpace: "normal",
    textAlign: "center",
  },
  aiAssistantFab: {
    position: "fixed",
    right: 22,
    bottom: "calc(22px + env(safe-area-inset-bottom, 0px))",
    zIndex: 1200,
    border: "0",
    borderRadius: 999,
    padding: "13px 18px",
    background: "linear-gradient(135deg, #0f6fff 0%, #10b981 100%)",
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 900,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    boxShadow: "0 18px 40px rgba(15,111,255,0.28)",
    cursor: "pointer",
    boxSizing: "border-box",
  },
  aiAssistantFabMobile: {
    right: 16,
    bottom: "calc(125px + env(safe-area-inset-bottom, 0px))",
    maxWidth: 220,
    padding: "14px 18px",
    fontSize: 16,
    transform: "none",
  },
  aiAssistantFabIcon: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.22)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 950,
  },
  aiHeaderButton: {
    border: "0",
    borderRadius: 999,
    padding: "11px 14px",
    minHeight: 44,
    background: "linear-gradient(135deg, #0f6fff 0%, #10b981 100%)",
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 950,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    cursor: "pointer",
    boxShadow: "0 12px 24px rgba(15,111,255,0.18)",
    boxSizing: "border-box",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  aiHeaderButtonMobile: {
    minHeight: 46,
    padding: "12px 12px",
    fontSize: 13,
  },
  aiHeaderButtonIcon: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.22)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 950,
    flexShrink: 0,
  },
  aiAssistantOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 3000,
    background: "rgba(15,23,42,0.50)",
    backdropFilter: "blur(7px)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    padding: 18,
    boxSizing: "border-box",
    overflow: "hidden",
    maxWidth: "100%",
  },
  aiAssistantModal: {
    width: "min(560px, 100%)",
    height: "min(90vh, 820px)",
    maxHeight: "90vh",
    overflow: "hidden",
    borderRadius: 28,
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    border: "1px solid rgba(191,219,254,0.9)",
    boxShadow: "0 30px 90px rgba(15,23,42,0.28)",
    padding: 18,
    display: "grid",
    gridTemplateRows: "auto auto auto minmax(0, 1fr) auto auto",
    gap: 12,
    boxSizing: "border-box",
    position: "relative",
    zIndex: 3001,
    minWidth: 0,
    maxWidth: "100%",
  },
  aiAssistantModalMobile: {
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100dvh",
    maxHeight: "100dvh",
    borderRadius: 0,
    padding: "12px 12px calc(12px + env(safe-area-inset-bottom, 0px))",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    maxWidth: "100%",
  },
  aiAssistantHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flex: "0 0 auto",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  aiAssistantEyebrow: {
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: 0.7,
    textTransform: "uppercase",
    color: "#0f6fff",
  },
  aiAssistantTitle: {
    margin: "4px 0",
    fontSize: 24,
    lineHeight: 1.1,
    fontWeight: 950,
    color: "#0f172a",
  },
  aiAssistantSubtitle: {
    margin: 0,
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.45,
  },
  aiAssistantCloseButton: {
    border: "1px solid #dbeafe",
    background: "#ffffff",
    color: "#0f6fff",
    borderRadius: 999,
    padding: "9px 12px",
    fontWeight: 900,
    cursor: "pointer",
    flexShrink: 0,
  },
  aiAssistantDisclaimer: {
    padding: 12,
    borderRadius: 16,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#334155",
    fontSize: 12,
    lineHeight: 1.45,
    fontWeight: 700,
    flex: "0 0 auto",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  aiAssistantPromptPanel: {
    display: "grid",
    gap: 8,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflow: "hidden",
    marginBottom: 8,
    flex: "0 0 auto",
  },
  aiAssistantFeaturedChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflow: "hidden",
  },
  aiAssistantQuickButton: {
    border: "1px solid rgba(15,111,255,0.16)",
    background: "#ffffff",
    color: "#0f356f",
    borderRadius: 999,
    padding: "9px 11px",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    whiteSpace: "normal",
    textAlign: "center",
    lineHeight: 1.2,
    flex: "1 1 130px",
    minHeight: 38,
    boxSizing: "border-box",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "clip",
  },
  aiAssistantQuickButtonMobile: {
    flex: "1 1 100%",
    width: "100%",
    maxWidth: "100%",
    padding: "10px 12px",
    minHeight: 42,
  },
  aiAssistantSuggestedToggle: {
    border: "1px solid rgba(16,185,129,0.24)",
    background: "#f0fdf4",
    color: "#075985",
    borderRadius: 14,
    padding: "9px 11px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0,
  },
  aiAssistantSuggestedGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 8,
    width: "100%",
    maxHeight: 180,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "2px 2px 8px",
    boxSizing: "border-box",
    overscrollBehavior: "contain",
  },
  aiAssistantSuggestedGridMobile: {
    gridTemplateColumns: "1fr",
    maxHeight: 160,
  },
  aiAssistantSuggestedButton: {
    border: "1px solid rgba(148,163,184,0.22)",
    background: "#ffffff",
    color: "#0f356f",
    borderRadius: 14,
    padding: "9px 10px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "normal",
    textAlign: "left",
    lineHeight: 1.25,
    minHeight: 40,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflow: "visible",
  },
  aiAssistantSuggestedButtonMobile: {
    width: "100%",
    maxWidth: "100%",
    textAlign: "center",
    padding: "10px 12px",
  },
  aiAssistantMessages: {
    display: "grid",
    alignContent: "start",
    gap: 10,
    minHeight: 0,
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    padding: "8px 6px 18px",
    scrollBehavior: "smooth",
    boxSizing: "border-box",
    overscrollBehavior: "contain",
    borderRadius: 18,
    background: "rgba(248,250,252,0.72)",
    flex: "1 1 auto",
    maxWidth: "100%",
  },
  aiAssistantMessagesMobile: {
    flex: "1 1 auto",
    minHeight: 0,
    height: "auto",
    paddingBottom: "calc(110px + env(safe-area-inset-bottom, 0px))",
  },
  aiAssistantBotBubble: {
    justifySelf: "start",
    maxWidth: "92%",
    borderRadius: "18px 18px 18px 6px",
    padding: "11px 13px",
    background: "#ffffff",
    border: "1px solid #dbeafe",
    color: "#0f172a",
    fontSize: 14,
    lineHeight: 1.45,
    boxShadow: "0 8px 18px rgba(15,23,42,0.05)",
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    wordBreak: "normal",
    minWidth: 0,
    boxSizing: "border-box",
  },
  aiAssistantUserBubble: {
    justifySelf: "end",
    maxWidth: "92%",
    borderRadius: "18px 18px 6px 18px",
    padding: "11px 13px",
    background: "linear-gradient(135deg, #0f6fff 0%, #10b981 100%)",
    color: "#ffffff",
    fontSize: 14,
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    wordBreak: "normal",
    minWidth: 0,
    boxSizing: "border-box",
  },
  aiAssistantTyping: {
    justifySelf: "start",
    padding: "9px 12px",
    borderRadius: 999,
    background: "#ecfdf5",
    color: "#047857",
    fontSize: 12,
    fontWeight: 850,
  },
  aiAssistantError: {
    borderRadius: 14,
    padding: 10,
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#c2410c",
    fontSize: 12,
    fontWeight: 800,
    maxHeight: 92,
    overflowY: "auto",
    overflowX: "hidden",
    boxSizing: "border-box",
  },
  aiAssistantComposer: {
    display: "flex",
    gap: 10,
    alignItems: "stretch",
    flexWrap: "wrap",
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    background: "linear-gradient(180deg, rgba(248,251,255,0.72), #f8fbff 34%)",
    paddingTop: 8,
    paddingBottom: 2,
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    flex: "0 0 auto",
  },
  aiAssistantInput: {
    flex: "1 1 260px",
    minHeight: 54,
    width: "100%",
    borderRadius: 16,
    border: "1px solid #cbd5e1",
    padding: 12,
    resize: "vertical",
    background: "#ffffff",
    color: "#0f172a",
    fontSize: 14,
    lineHeight: 1.4,
    boxSizing: "border-box",
    minWidth: 0,
  },
  aiAssistantMessageEnd: {
    width: "100%",
    height: 1,
  },

};

export default function App() {
  return (
    <RentDuoErrorBoundary>
      <RentDuoApp />
    </RentDuoErrorBoundary>
  );
}
